/*
 * vms-configurator.js
 * ---------------------------------------------------------------------------
 * Ceiling-mount configurator solver + stage UI.
 *
 * [RULE] TERMINOLOGY, OWNER-SET 2026-07-28: THE CUSTOMER-FACING WORD IS
 * "LENGTH". NOT "DROP". The owner ruled "drop" too much like trade jargon, so
 * every string a customer can read or hear says length: the step 4 heading, the
 * field label, the compare-modal columns, the build cards, the summary rows, the
 * announcements, and the drag handle's aria-label.
 *
 * THREE DELIBERATE EXCEPTIONS, none of which is an oversight:
 *   1. "DROP CEILING" STAYS, everywhere. That is the suspended-tile ceiling and
 *      it is standard trade language ("Is there a drop ceiling? / Yes, suspended
 *      tiles"). It is a different word that happens to share four letters. Do NOT
 *      let a find-and-replace touch it; several sentences carry BOTH senses, e.g.
 *      "(a drop ceiling, or drywall with a void above) ... uses up part of the
 *      length", where exactly one of the two had to change.
 *   2. CODE IDENTIFIERS KEEP "drop": applyDrop, dropLimits, resolveDropValue,
 *      sayDrop, renderDropNote, #dropSay, #dropNote, #dropHelp,
 *      state.hasDropCeiling, model.dropMinEnforced, the minDrop reason codes, and
 *      the scene's makeHandle('drop') kind. No customer sees them, several are
 *      cross-file contracts, and renaming them buys nothing but risk.
 *   3. COMMENTS in this file and the two elevation files still say "drop" as
 *      internal shorthand for the measurement. That is intentional shorthand, not
 *      stale copy.
 * So the code says drop and the interface says length, on purpose. If you are
 * about to "fix" that inconsistency in either direction, do not.
 *
 * [RULE] CUSTOMER-FACING COPY IS OWNER-EDITABLE (2026-07-28). Every sentence a
 * customer reads on this page is a theme setting on sections/vms-configurator.liquid.
 * The strings THIS file builds come from COPY_DEFAULTS and the copy layer below;
 * the strings the SECTION prints come straight from section.settings in Liquid.
 * When you add a customer-facing sentence anywhere in here, add the setting in the
 * same commit, or you have built a string the owner cannot reach and nothing will
 * tell either of you. Screen-reader-only text (aria-label, aria-valuetext, the
 * "No length set yet." empty state) stays code-owned by design: it is an interface
 * contract with assistive tech, not copy.
 * EXCEPTION (2026-10-12): the TV lookup readout strings (tvTierMsg, renderTvFit) stay in code on purpose; the tier messages are contract wording shared with assets/vms-tv-lookup.js and must not drift between surfaces.
 * EXCEPTION (2026-10-12, owner): the Step 1 bar suffix after step_pick_size (tvPickSuffix: ", [maker] [model]" or ", your measurements") is code, not a setting (the section already trips ExcessiveSettingsCount).
 * EXCEPTION (2026-10-12, TV placement): while a placed TV is active the two shortest-length notes (renderDropNote), the spans caveat for a height-only row (renderTvSpan) and the summary's "(to the center of the mounting holes)" are code-owned sentences; the owner's settings stay for every other build.
 *
 * TRUE-PARTS-ONLY REBUILD (2026-07-18, owner-approved, dev-staged only).
 * The previous "mounts" architecture (stock configurable mount PDPs offering
 * a No-Mast variant + factory pre-set adjustable-length variants) is GONE.
 * A build is now always three real, independently-priced parts:
 *   CEILING PLATE + PIPE MAST (fixed/telescoping pipes + auto-counted
 *   couplers) + MOUNTING HEAD.
 * Finished drop = plate.length_offset + mast length + head.length_offset.
 * Every build is therefore real parts all the way down: there is no
 * mount-variant lookup, no "No Mast" concept, and no custom-plate credit
 * swap (that whole code path is gone with it) - every completed build is
 * self-checkoutable subject only to per-part availability.
 *
 * KEPT from the prior build: the mast-solving machinery (buildMasts /
 * filterBuilds / qualifiesFlex / rankBuilds and the _9 ranking/guardrail
 * rules folded into it) and the Phase 4 cart plumbing (postCartAdd /
 * wireAddToCart / the _vms_config_id + _vms_config_summary recipe pattern,
 * SPEC-configurator-cart-transform.md, plus the theme-owned _vms_unit_qty
 * added 2026-08-21 - see buildPayloadItems). Both are unchanged in spirit; their
 * inputs move from a "mount" object to plain (head, plate) component pairs.
 *
 * REMOVED: recommendVariant/findVariant/plate-option parsing (no more stock
 * mount variants to match against), the "how many screens" / down-tilt (DD)
 * special-case branch (that flow existed only to route to a specific mount's
 * PDP-sold head; DD has no standalone head SKU in the real-parts pool yet -
 * reintroduce it as a head card the day a DD head SKU joins the pool, no
 * solver change needed), basePriceFor's custom-plate-swap credit branch, and
 * every mount/screen-title-parsing helper (screen filtering is now a plain
 * numeric metafield compare, not a title regex).
 *
 * STAGE / DOM-ID CONTRACT (renegotiated this rebuild - the old s1/s2/s3
 * contract from the mount-based flow no longer applies):
 *   #s1   TV size. Always visible. Filters which heads stage #s2b offers, and
 *         since 2026-07-28 it GATES #s2 (see the D2 reversal below).
 *         [RULE] #tvSize IS A <select> SINCE 2026-07-29, not the number input it
 *         had always been: a phone numeric keypad has no Enter key, so the old
 *         box gave a customer no way to say "done". Five preset buttons and a 10
 *         to 90 dropdown replace it, both committing on the spot. The buttons
 *         write the select and dispatch its events, so this file still has one
 *         commit path and reads el('tvSize').value exactly as before. A size the
 *         list does not carry (an old link at 91 to 120, or a half inch) has its
 *         option injected on restore rather than being clamped away.
 *   #s2   Ceiling PLATE TYPE -> plate. Always visible (does not depend on TV
 *         size). Picking a plate reveals #s2b. RESTRUCTURED 2026-07-26: the
 *         first tier is the five real plate types (#plateTypeChoices), not the
 *         old seven mixed site-condition/product chips; site-condition guidance
 *         moved to server-rendered disclosure panels in the section, and
 *         the drop-ceiling second chip row is gone (see PLATE_TYPES below).
 *         The match table was one of those panels and the owner deleted it on
 *         2026-07-29; the drop-ceiling panel is the survivor and it now sits
 *         BELOW #s2out, because #s2out ends with the compare-plates trigger and
 *         the owner wants the plates followed straight by their compare chart.
 *   #s2b  Mounting head cards, filtered by the #s1 TV size (a head with a
 *         null screen_min/screen_max range is ALWAYS offered; no size
 *         entered = every head offered). Picking a head reveals #s3.
 *   #s3   Target drop input -> mast solve -> ranked build cards (parts
 *         table: plate + pipes + couplers + head, summed price, one
 *         "Add this build to cart" button per card).
 * Changing the plate or the head clears the target and re-hides #s3 (the
 * finished-drop math depends on both). Changing the TV size while a head is
 * selected that no longer qualifies clears the head selection too.
 *
 * THE FOUR-STEP DISCLOSURE (2026-07-28, owner-ordered interaction redesign)
 * -------------------------------------------------------------------------
 * All four steps are on the page from the first paint. They are never added or
 * removed; only their BODIES open and close. Each step is in exactly one of
 * three states, and stepStates() derives all four from state on every pass:
 *
 *   locked  not reachable yet. Collapsed, header button disabled, and a hint
 *           line says what has to happen first.
 *   open    expanded, the step the customer is working on.
 *   done    finished and collapsed, the header carrying a chosen-summary
 *           (photo + name) and the owner's Change word. Clicking it re-opens.
 *
 * [RULE] THE STATES ARE DERIVED, NEVER ACCUMULATED. There is no "current step"
 * variable to fall out of sync with the selections. stepStates() is a pure
 * function of (size, plateSku, headSku) plus the manual open/close overrides,
 * so a URL restore, a cascade and a Change click all land on the same answer by
 * running the same function. That is also why a legacy link with a plate and a
 * head but no size cannot crash: it simply computes locked for steps 2 to 4 and
 * keeps every stored selection, and entering a size reconciles the lot.
 *
 * [RULE] TV SIZE IS REQUIRED, WHICH REVERSES OWNER DECISION D2. D2 (spec
 * SPEC-drag-elevation-2026-07-26.md) made "leave the size blank and fill it in
 * later" a primary supported flow; the owner reversed that on 2026-07-28
 * because the size is what makes step 3's head filter and step 4's clearance
 * geometry mean anything, and a blank one quietly produced a shallower, safer,
 * wronger answer. Step 2 stays locked until a size between 10 and 120 is
 * COMMITTED. Every D2 reference below carries a pointer back to this note.
 *
 * [WARN] THE NO-SIZE CODE PATHS ALL STAY, as graceful fallbacks, and must not
 * be deleted as dead code. They are not dead: a shared link from before this
 * change can carry plate/head/target with no size at all, restoreable state
 * that still has to render something sane, and headScreenOK(h, null) is still
 * the honest answer to "which heads fit an unknown TV". What changed is the
 * FLOW, not the arithmetic.
 *
 * [WARN] THE STEP BODIES ARE HIDDEN, NEVER EMPTIED. #elevScene in step 4 is
 * mounted once and holds live listeners and pointer capture; a collapse that
 * wrote innerHTML would destroy it. Collapse toggles the `hidden` attribute on
 * the body wrapper and calls the scene's own update() on the way back so it
 * re-measures at its restored width.
 *
 * URL state (kept lightweight, "if practical" per the rebuild brief): size/
 * plate/head/target, plus dc/tiles for the drop ceiling since 2026-07-26, are
 * reflected into the query string via history.replaceState (never pushState,
 * so Back is not spammed) and read back on load to restore a shared/bookmarked
 * link. No-op if the values don't resolve against the live pool. A restored
 * drop is re-run through the clamp pipeline rather than trusted as given.
 *
 * DRAG-TO-ADJUST ELEVATION (2026-07-26, SPEC-drag-elevation-2026-07-26.md):
 * step 4 carries a to-scale side view with a draggable TV and a draggable
 * drop-ceiling line, drawn by assets/vms-elevation-scene.js off the pure
 * geometry in assets/vms-elevation.js. The scene emits raw unrounded inches
 * and clamps nothing; ALL clamping, resolving and quantising happens in
 * resolveDropValue() in this file, which is the ONE quantiser in the system.
 * See the ELEVATION section below for why that matters.
 *
 * The drop FLOOR is the maximum of every constraint that applies, not just the
 * drop-ceiling one (fixed 2026-07-27, owner-reported). Two of them are structural
 * and bind with no drop ceiling at all: the shortest mast the pool can build
 * (MIN_MAST_LENGTH below, derived from the same pipe arrays the solver uses) and
 * the fact that a TV cannot rise into the CEILING it hangs from (owner ruling
 * 2026-07-28 evening: the top of the physical TV comes up to within an eighth of
 * an inch of the structure at depth 0, and the ceiling PLATE is not part of that
 * rule, because the TV hangs in front of the plate). minDrop().reason names
 * whichever one is binding; the separate needsTileDepth flag says whether we are
 * still waiting on a tile depth, because that question can be open while
 * something else binds.
 *
 * GRAPHICS (2026-07-26): every component in the data layer now carries an
 * "image" (featured image at width 200, or null). thumbHTML() is the single
 * renderer for all of them: plate-type tiles, plate and head pick cards, the
 * compare modal, and the build summary. A "Compare all" <dialog> (one element,
 * two tables) is the see-everything-at-once affordance; the server-rendered
 * reference tables at the bottom of the section remain the no-JS floor.
 *
 * #summary renders in PHYSICAL ASSEMBLY ORDER (plate, mast, head, top to
 * bottom). It is TEXT ONLY since ruling D2 of 2026-08-19: it lives inside step 4
 * now and the elevation directly above it draws the real mount from the
 * orthographic part cutouts, so the panel no longer carries art of its own.
 * The build CARDS still composite a mast picture each, and that is what the
 * pipe-compositor note below still refers to. Source technique:
 * D:\Dropbox\videomountstore-work\SHOPIFY\Pipe-Compositor - one image column
 * per inch, outer tube painted over inner with a 2-column overlap).
 *
 * ANALYTICS (2026-07-29, owner-approved): exactly three customer events leave
 * this page, published through Shopify.analytics.publish and forwarded to GA4
 * by a custom pixel the owner installs in admin. See the ANALYTICS section
 * below for the rules (guarded publishes, no throw ever reaches the page, and
 * nothing at all is published under ?vmscfg_diag=1) and
 * ANALYTICS-CONFIGURATOR-PIXEL-2026-07-29.md for the pixel half.
 *
 * Self-test panel gated behind ?vmscfg_diag=1 (window.__CHECKS__ when run;
 * window.__VMSCFG__ exposes internals for browser-driven verification).
 * All customer-facing strings remain DRAFT pending the owner voice pass.
 */
(function(){
const dataEl = document.getElementById('vmscfg-data');
if(!dataEl || !document.getElementById('s1')) return;
let DATA;
try { DATA = JSON.parse(dataEl.textContent); } catch(e){ console.warn('vmscfg: bad data JSON', e); return; }

/* ============================================================
   VMS Configurator solver
   Mast-solving machinery ported unchanged from the mount-based build
   (SITE-THEME-SPEC-configurator-logic-2026-07-12.md); only the (mount) ->
   (head, plate) input shape changed.
   ============================================================ */
const COUPLER_ADD = 1.5;        // 11AC001 net add per coupler
const TOL = 0.5;                // within-tolerance inches (FIXED stacks only; see the bracket test in consider())
const MAST_EPS = 1e-6;          // [A] float slack for the adjustable in-range bracket test

/* ---- Ranking and guardrail rules folded in from pipe-configurator_9.html (v2) ---- */
const SURCHARGE = 15.00;        // UPS oversize surcharge per single piece shipping at 48 in or longer.
const SURCHARGE_MIN_LEN = 48;   // The 48 in pipe is excluded from the pool and telescopics ship
                                // collapsed (all under 48 in), so this never fires with today's pool;
                                // kept so ranking always uses true landed cost if the pool changes.
const FLEX_UPGRADE_BUDGET = 5.00;    // _9 rule: paying up to $5 more is worth it when it buys
const FLEX_UPGRADE_MIN_GAIN = 2.00;  // at least 2 in more travel in the tighter direction.
const SMALL_TELES = ['EXT0812','EXT-1220'];  // _9 rule: above a 20 in mast target the small
const SMALL_ADJ_MAX_TARGET = 20;             // telescopics lack useful travel; drop them
                                             // (unless one is the absolute cheapest).
const SMALL_LENGTHS = [2,3,4,5];             // _9 rule: at most 1 small pipe per build
const MAX_SMALL_PER_COMBO = 1;               // (absolute cheapest excepted).
const MIN_TRAVEL_PER_TELE = {EXT0812:1, 'EXT-1220':2, EXT2036:4, EXT3260:7, EXT4472:7};
                                // _9 rule: 25 percent of the unit's range required BOTH
                                // directions before a build may be called flexible.
const MOST_FLEX_TELES = ['EXT3260','EXT4472'];  // only the big telescopics qualify as most flexible
const MOST_FLEX_MIN_BALANCED = 12;              // _9 bar: 12 in of travel both ways
// [RULE] SIX-PIECE MAST BUDGET (owner ruling 2026-09-07). The original pipe length
// calculator (pipe-configurator_page.html) enumerates masts up to `const MAX_PIECES = 6`
// pipes; this solver descends from the same _9 rules and must match it. The 2026-07-12
// configurator prototype quietly capped the enumeration at 3 pieces and it was never
// reconciled with the _9 budget (found 2026-09-07), which is why the deepest heads
// stopped reaching about 156 in. See buildMasts for the enumeration and the pruning.
const MAX_PIECES = 6;
// _9 "cheaper near match" saving floor. The Most adjustable pick clears the 12 in
// balanced bar; a cheaper big-telescopic build that still qualifies both ways but
// missed that bar usually still covers a customer's rough number, so it is offered as
// its own card when it undercuts the Most adjustable landed cost by at least this.
const NEAR_MATCH_MIN_SAVING = 5.00;

const money = p => '$' + (Number(p)).toFixed(2);
const inch  = x => (Math.round(x*10)/10).toFixed(1);
const nice  = x => { const v = Math.round(x*10)/10; return v % 1 === 0 ? String(Math.round(v)) : v.toFixed(1); };

// An angle with its unit on the number.
//
// [WARN] THE DEGREE SIGN HERE IS DELIBERATE AND OWNER-APPROVED. It is one of the
// only two characters exempt from this project's ask-before-any-non-keyboard-
// character rule (the other is the hyphen), so a sweep that strips non-ASCII
// from this file must leave this one alone.
//
// THIS IS THE ONLY PLACE IN THE FILE THAT CHARACTER MAY APPEAR (2026-08-21).
// It used to be typed inline at each site that printed an angle, which meant a
// future non-ASCII sweep had several scattered lines to judge instead of one
// commented line. Every angle in the configurator - the plate chart's tilt
// column, the head cards' spec line, the heads chart's tilt column - routes
// through here. Anything that needs an angle calls deg(); nothing types the
// glyph again. Note what does NOT come through here: a MISSING angle falls
// through to the "not on file" placeholder at its own call site, which must
// never gain a degree sign.
const deg = n => String(n) + '°';

// nice(), plus the unit, singularised. The drop floor with no drop ceiling is
// exactly 1 in (TARGET_MIN), so every sentence that prints a drop can land on
// "1 inches" and read as broken. Prose gets the word, per website-copy-voice.md;
// spec cells keep inch() and the short "in".
// The plural is decided by the STRING nice() prints, not by the raw number, so a
// value that displays as 1 always reads as "1 inch".
const niceIn = x => { const s = nice(x); return s + (s === '1' ? ' inch' : ' inches'); };
const r3    = x => Math.round(x*1000)/1000;

// =====================================================================
// THE COPY LAYER (2026-07-28, owner-authorized)
// =====================================================================
// Every customer-facing sentence this file GENERATES is an owner-editable theme
// setting on sections/vms-configurator.liquid. The section renders one JSON block
// (#vmscfg-copy) of the settings the owner has actually filled in; everything else
// falls back to COPY_DEFAULTS below, which hold the exact strings this file has
// always shipped. A blank or untouched install therefore renders byte for byte
// what it rendered before this layer existed.
//
// [RULE] THE DEFAULT LIVES HERE, NOT ONLY IN THE SCHEMA. The schema "default"
// values exist so the owner SEES the current wording in the theme editor, but the
// running code must never depend on them: a section can be rendered from a
// template JSON written against an older schema, and Shopify strips settings the
// current schema does not define. Keep the two copies identical when you edit
// either.
//
// [WARN] THE SERVER-SIDE STRINGS ARE NOT IN HERE. Anything the section prints
// itself (step headings, labels, the reference tables, the escape lines) is
// rendered straight from section.settings in Liquid. This map is only the strings
// JavaScript builds.
//
// [WARN] THE limit_* KEYS DELIBERATELY HAVE NO DEFAULT HERE. Those sentences
// belong to assets/vms-elevation-scene.js and that file holds their defaults. We
// pass the OVERRIDE ONLY (or nothing) into its mount options, so there is exactly
// one fallback copy of each and the two files cannot drift. See copyRaw().
const PHONE_TEXT = '800-585-8795';
const PHONE_HTML = '<span class="phone">800-585-8795</span>';
const CONTACT_OPEN = '<a href="/pages/contact">';
const CONTACT_CLOSE = '</a>';

const COPY_DEFAULTS = {
  // ---- step 4, the length field ----
  // BOTH REWRITTEN BY THE OWNER 2026-07-29, and both are shorter than what they
  // replaced for the same two reasons: the field now starts at 30 inches, so
  // nothing has to tell anyone to type, and the box now prints "inches" itself, so
  // nothing has to name the unit.
  //
  // The label used to be built from dropRefLabel(), a helper that existed to keep
  // the reference-point sentence in one place. That helper is GONE with this
  // change; its knowledge is not. The sentence it held ("measured from where the
  // top plate mounts down to where you want the center of the screen") was itself
  // an owner fix of 2026-07-28 that replaced the word "ceiling", because a
  // customer under suspended tiles reads "ceiling" as the TILE LINE, which is the
  // opposite of the structure-at-depth-zero reference this whole feature measures
  // from. THE NEW WORDING KEEPS THAT MEANING: "mounting surface" is the same
  // physical thing, in the owner's words, and it must not become "ceiling" again.
  // [RULE] "mounting surface" IS THIS LABEL ONLY. Every other string on the page
  // says "structure" on purpose; do not sweep the word through them.
  step4_intro: 'Drag the TV to adjust the length or enter a value',
  length_label: 'Measured from mounting surface down to the middle of the screen',
  // The horizontal (face-down) head measures to the BACK of the screen, not its
  // middle. Owner ruling 2026-08-26. Kept in all three copy homes (this default,
  // the #vmscfg-copy block, and the section schema) per the standing rule.
  length_label_horizontal: 'Measured from the ceiling plate down to the back of the screen',

  // ---- the standing floor note under the drop-ceiling radios (#dropNote) ----
  note_need_tiles: 'We need one more number. Measure from the structure above down to the underside of the tiles and enter it here. Until we have it we cannot tell you how far the TV has to hang to clear the tile line.',
  note_below_tiles: 'The shortest length we will offer is [floor], which puts the mount itself just below the tiles.',
  note_tv_clears_tiles: 'The shortest length that clears the tiles is [floor]. That lands the top of a [tv] inch TV just under the tile line. We allow an inch for the bezel, so this is the whole TV and not just the picture. Drag lower if you want the TV lower than that.',
  note_shortest_mast: 'The shortest length with the parts you have picked is [floor]. That is your plate and head with the shortest mast we sell between them. A different plate or head can sometimes sit tighter to the ceiling, so call us at [phone] if you need that.',
  note_screen_clears_plate: 'The shortest configuration for a [tv] inch TV is [floor], to prevent hitting the ceiling. This configurator estimates a 1/2 inch of bezel on the top and bottom of the screen and won\'t allow a configuration closer than 1/8th inch from the ceiling.',
  // [2026-09-22 round 2, reworded round 3] The standing notes for the two
  // horizontal (face-down) head floors. The note shows at ANY length, so it leads
  // with the floor like note_tv_clears_tiles; the held-because sentences (shown
  // only when a value is actually held) keep their "as short as it can go" form.
  note_head_clears_tiles: 'The shortest length that clears the tiles is [floor]. The mounting head needs 1/8 inch of clearance under the drop ceiling tiles at [tiles].',
  note_head_clears_structure: 'The shortest length is [floor]. The mounting head needs 1/8 inch of clearance below the ceiling.',
  note_no_size_dc: 'Enter your TV size in step 1, even a guess. With a size we keep the whole TV below the tiles and tell you where its top and bottom land; without one we can only keep the mount below them, which is safe but shallower than you probably want.',
  // [WARN] BOTH no_size NOTES ARE FALLBACKS NOW, not the primary flow they were
  // written for. Step 1 gates step 2 on a size (D2 reversed, see the header), so
  // the only route to step 4 without one is an old shared link. note_no_size used
  // to end "It is not required and it does not change the price", which went
  // false the moment the size became required; the rest of the sentence is still
  // exactly the right thing to say to someone who got here on such a link.
  note_no_size: 'Enter your TV size in step 1, even a guess, and we will draw the screen on the picture and tell you how tall your TV is and where its top and bottom land.',

  // ---- the one sentence we owe when we move a typed number (#dropSay) ----
  say_moved: 'We moved your length to [length]. The masts adjust in one inch steps, so that is the next length we can actually build that still keeps the TV below the tiles.',
  say_held: 'We held your length at [length]. [reason]',
  // [RULE] TWO TEMPLATES, NOT ONE REWORDING (owner, 2026-07-31). His sentence for
  // the drop-ceiling holds is "The shortest length that clears the tiles is N",
  // which is the right thing to say for the two TILE floors and a plain lie for the
  // other two: shortestMast is the parts running out and screenClearsPlate is the
  // TV meeting the ceiling, and neither has a tile in it. sayDrop picks by reason
  // code, so a build with no drop ceiling still gets say_held above. Same [length]
  // and [reason] tokens, so the held-because sentences below are shared unchanged.
  say_held_tiles: 'The shortest length that clears the tiles is [length]. [reason]',

  // ---- why we held it, the [reason] half of say_held ----
  held_tv_clears_tiles: 'Anything shorter puts the top of the TV into the tile line.',
  held_below_tiles: 'Anything shorter puts the mount itself into the tile line. Enter your TV size and we can allow for the height of the TV as well.',
  held_shortest_mast: 'This is as high as the mount can go with the parts you have picked.',
  held_screen_clears_plate: 'The top of the TV has to stay just clear of the ceiling.',
  // [2026-09-22] The two horizontal (face-down) head floors. The drawing carries the
  // same two sentences as limit_high_head_tiles / limit_high_head_structure; edit
  // both. [tiles] is the tile depth with its unit ("12 inches").
  held_head_clears_tiles: 'The mounting head needs 1/8 inch of clearance under the drop ceiling tiles, so this is as short as the length can go with the tiles at [tiles].',
  held_head_clears_structure: 'The mounting head needs 1/8 inch of clearance below the ceiling, so this is as short as the length can go.',
  held_default: 'The length has to be more than zero. The plate and head are never threaded straight together.',

  // ---- builds and cart ----
  no_adjustable: 'No adjustable build has enough downward travel for this length, so the fixed length below is the only one we will put our name on. The telescoping units that reach your number are already near the end of their travel, which leaves you nothing to let out if the TV sits too close to the tiles once it is up. We can usually put an adjustable version together from other parts. Call [phone] or [contact]Contact us[/contact].',
  cart_adding: 'Adding to your cart',
  cart_added: 'Added. Taking you to your cart',
  cart_unavailable: 'One or more parts in this build are currently unavailable. Call [phone] or contact us.',
  cart_error: 'Could not add this to your cart. Call [phone] and we will help.',

  // ---- the head-cleared modal (FEATURE B, owner 2026-09-06) ----------------
  // Shown when a committed TV size leaves the picked head's screen range, so the
  // head is cleared and step 4 collapses. Owner asked for "a pop up modal, click
  // anywhere to close type" that says why. Two body variants: head_cleared_body is
  // the plain case, head_cleared_swapped is used when FEATURE A auto-picked the one
  // head that now fits, in the SAME commit. [WARN] THESE PLACEHOLDERS ARE {curly},
  // not the [square] tokens fill() handles, because the owner specified this exact
  // wording with curly braces; headClearedCopy() substitutes them itself. {head} and
  // {new_head} render as the head's SKU alone (see headLabel). {min}/{max} are the cleared
  // head's rated screen range, {size} the size just entered. Kept in all three copy
  // homes byte-identical per the standing rule (this default, the #vmscfg-copy JSON
  // block, and the copy_js_head_cleared_* schema settings).
  head_cleared_title: 'We cleared your mounting head',
  head_cleared_body: 'The {head} is rated for {min} to {max} inch TVs, and you entered {size} inches. Pick a head that fits your TV in step 3, and the length step will follow.',
  head_cleared_swapped: 'The {head} is rated for {min} to {max} inch TVs, and you entered {size} inches. The {new_head} is the only head that fits, so we picked it for you. Change it in step 3 if you want a different one.',

  // ---- the arrival preselected note (2026-09-07, the no-mast builder link) ----
  // Shown once, in step 1 above the TV size control, when a product page's "Build a
  // TV ceiling mount" link lands here with a head and/or plate in the URL and NO
  // size (the product page cannot know the TV size). Three variants by what arrived;
  // {head} and {plate} render the head's and plate's SKU alone, substituted the same
  // curly-brace way the head-cleared modal uses (see headClearedCopy / showArrivalNote).
  // Kept in all three copy homes byte-identical per the standing rule (this default,
  // the #vmscfg-copy JSON block, and the copy_js_arrival_preselected_* schema settings).
  arrival_preselected_both: 'Your {head} head and {plate} plate are already picked. Choose your TV size to continue.',
  arrival_preselected_head: 'Your {head} head is already picked. Choose your TV size to continue.',
  arrival_preselected_plate: 'Your {plate} plate is already picked. Choose your TV size to continue.',

  // ---- the compare-all triggers and the dialog titles ----
  cmp_trigger_plates: 'View Comparison Chart for all [count] Ceiling Plates',
  cmp_trigger_heads: 'Compare all [count] mounting heads',
  cmp_title_plates: 'Compare ceiling plates',
  cmp_title_heads: 'Compare mounting heads',

  // ---- the four-step disclosure (2026-07-28) ----
  // What a FINISHED step shows on its collapsed heading, beside the Change word.
  // The Change word itself and the locked-step hint are printed by the SECTION,
  // not from here: they are static text on a server-rendered header, so they need
  // no token substitution and belong in exactly one place.
  step_pick_size: '[tv] inch TV',
  step_pick_plate: '[sku] [name]',
  step_pick_head: '[sku] [name]',
  // The head-count line under the size box (#s1out). Its old no-size twin
  // ("No size entered: every head is offered below once a plate is chosen") is
  // GONE rather than reworded, and it stays gone: two sentences asking for one
  // number is how a form starts nagging. (It was cut in favour of the
  // required-size notice under the field, which the owner then blanked on
  // 2026-07-29. Nothing asks for the size in words now: the control is the only
  // thing in an open step 1 and step 2's locked hint says the rest.)
  s1_heads_fit: '[count] of [total] heads fit a [tv] inch screen.',

  // ---- step 3, the head card info affordances (2026-08-22) ----------------
  // The two tooltips the owner specced with the wireframe. info_adds_length is
  // his wording verbatim. The VESA set is the product page's explanation carried
  // over so a shopper reads the same sentence on both surfaces; [size] is the
  // screen the diagram is drawn against, which is the customer's own TV size
  // once step 1 is filled in and the head's maximum before that.
  info_adds_length: 'How much this head adds to total mount length',
  vesa_info_title: 'VESA mounting pattern',
  vesa_info_lead: "VESA is the spacing between the four bolt holes on the back of your TV. If your TV's spacing falls in the range below, this head fits it. Screen size is a guideline: if the pattern fits and your TV is under the weight rating, it works.",
  vesa_info_scale: 'shown to scale on a [size] inch screen (16:9)',
  vesa_info_standards: 'Supported standards',
  vesa_info_note: 'Common standards within this hole range - not an exhaustive list.',
  // The owner's own wording for the way in to the pattern list, 2026-08-22.
  // "some sizes" rather than "the sizes" is deliberate and matches the footnote
  // above: the list is the common standards inside the hole range, not every
  // pattern the head will physically take.
  vesa_info_cta: 'Click to see some sizes this mount will fit',

  // The note under the head cards, rewritten by the owner 2026-08-22 and moved
  // OUT OF THE CODE at the same time. It used to be a hardcoded div, which broke
  // this file's own rule that every customer-facing sentence it generates is an
  // owner-editable setting; it is one now, so the next rewrite does not need us.
  //
  // What he dropped and what he added, both deliberate: the "keep the TV at
  // least 7 feet off the floor" clause is GONE on his instruction ("Leave out
  // the 7 feet part"), and the sentence now says the useful thing outright,
  // that the screen range is not a hard ceiling. Two mechanical corrections were
  // made to his text and nothing else: "reccomended" to "recommended", and the
  // comma after "size" removed, which was splitting the subject from its verb.
  heads_note: 'Screens larger than the recommended size can be used if they weigh less than the weight rating of the head and the VESA mounting hole pattern will fit the screen.',

  // The heads comparison chart's opening paragraph, moved out of the code
  // 2026-08-22. It used to open with the same "screen size is a rough gate"
  // sentence the owner replaced on the step 3 cards, so the chart contradicted
  // the card list one click away. It carries HIS sentence now, and it is a
  // setting for the same reason the note is: it is customer copy.
  //
  // Double-quoted because the sentence contains an apostrophe (a row's) AND a
  // quoted word (\"raises\"); the escaping is deliberate, not accidental.
  cmp_lead_heads: "Every mounting head we stock for 1-1/2 inch NPT pipe. Screens larger than the recommended size can be used if they weigh less than the weight rating of the head and the VESA mounting hole pattern will fit the screen. A head marked \"raises\" sits the TV back up toward the ceiling instead of adding length. VESA shows the smallest and largest standard pattern each head takes; open a row's VESA button for the full list and a drawing of it to scale.",

  // ---- the build's weight rating row in the summary (2026-08-23) ----------
  // [RULE] THE WEAKEST LINK SETS THE RATING. Owner's ask, verbatim: "The
  // component chosen with the lowest weight rating will dictate the rating for
  // the overall product. If a cutomer choses the 11CP005(110lb rating) and a 2
  // screen head with a higher rating, they are limited by the rating of the
  // 11CP005." So the build rating is the MINIMUM weight_capacity across the
  // chosen rated parts, and the row names the part that sets it. "limited by" is
  // the owner's own verb.
  //
  // [WARN] A MISSING RATING IS "AT MOST", NEVER A CONFIDENT NUMBER. See the long
  // note on buildWeightRating(). weight_value is the confident form; weight_unrated
  // is the one printed when a chosen rated part (33CE004HEAD is the live example)
  // carries no rating, so the true minimum could sit below the figure we can see.
  weight_label: 'Weight rating',
  weight_value: '[weight] lb, limited by [part]',
  weight_unrated: 'At most [weight] lb, [part] has no rating on file',

  // ---- the parts table's scrollable-region name (2026-09-22) --------------
  // [BOM PAN] Read by renderStage3Body() and handed to enableDragPan() as the
  // aria-label for each build card's parts table, which becomes a focusable
  // role="region" only on the viewports where it actually scrolls sideways
  // (320px wide; nothing from 375px up). It is the accessible NAME of that tab
  // stop, so it says what the region holds and that it moves sideways. Lives in
  // all three homes like every other sentence: here, the #vmscfg-copy JSON
  // block, and the copy_js_bom_pan_label schema setting, byte-identical.
  bom_pan_label: 'Parts list, scrolls sideways',

  // ---- [UX R3 2026-09-22] backorder note, your picks ------------------------
  // Items 8 and 14 of the configurator UX round 3 build spec (item 6, the sticky
  // running total, was removed on the owner word 2026-09-22). Three homes each,
  // byte-identical, like every other sentence here: this map, the #vmscfg-copy
  // block, and the copy_js_* schema settings of the same name.
  //   backorder_*    the soft note above step 2 / step 3 (renderSoftWarns); [sku],
  //                  [part] and [phone] are its tokens, and the two part words are
  //                  what [part] becomes. The cart gate's own refusal message in
  //                  buildCartRecipe is separate and is NOT this copy.
  //   picks_*        the check strip above the build cards (picksStripHTML)
  backorder_warn_heading: 'This [part] is on backorder',
  backorder_warn: '[sku] is on backorder. It cannot be added to the cart from this page; reserve it from its own product page, or pick a [part] that is in stock. Call [phone] and we can check the date.',
  backorder_part_plate: 'plate',
  backorder_part_head: 'head',
  picks_label: 'Your picks',
  picks_plate: 'Plate',
  picks_head: 'Head',
  picks_length: 'Length',

  // ---- the hand-back line for an AI agent (WebMCP configure-build) ----------
  // [MCP 2026-09-04] Returned to an in-browser agent by the configure-build tool
  // (assets/vms-configurator-mcp.js) for it to relay to its user, once the build
  // is on the page. It is customer-facing copy the agent will speak, so it lives
  // in all three homes like every other sentence: here, the #vmscfg-copy JSON
  // block, and the copy_js_mcp_handback schema setting, byte-identical. It names
  // the drag and the Change controls, which are how the shopper takes over from
  // the agent. Same lane as the slanted wall's mcp_handback default.
  mcp_handback: 'The build is on the page now. Your user can fine-tune the length by dragging the TV in the drawing in step 4, and can change the plate or head with the Change control on each step. The parts list and price update as they go.',

  // ---- [HANDOFF 2026-09-28] the sloped short-length hand-off (pop-over on the drawing, and the note above it) ----
  handoff_heading: 'Short length on an angled ceiling',
  handoff_body: 'It looks like you want to keep your TV close to an angled ceiling. We have a page that might help you visualize against an angled ceiling better. Either page builds a mount that works. Call or email us if you want assistance.',
  handoff_link: 'Open the angled wall/ceiling builder',
  handoff_keep: 'Keep building here',
  handoff_note: 'Short length on an angled ceiling. We have another configurator that might benefit you. This page works, but the {link} might help you visualize better.',
  handoff_note_link: 'angled wall/ceiling builder'
};

// The owner's edits, or an empty object. Parsed ONCE, and every failure mode
// (block absent, block empty, malformed JSON, a non-object payload) lands on the
// same answer: no overrides, shipped copy. A copy edit must never be able to take
// the configurator down.
const COPY_OVERRIDES = (function(){
  try {
    const node = document.getElementById('vmscfg-copy');
    if(!node) return {};
    const parsed = JSON.parse(node.textContent || '{}');
    return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
  } catch(err){
    console.warn('vmscfg: copy overrides unreadable, using shipped copy', err);
    return {};
  }
})();

// The raw override for a key, or null. Blank and whitespace-only count as "not
// set", so clearing a field in the theme editor restores the shipped sentence
// rather than blanking the surface.
function copyRaw(key){
  const v = COPY_OVERRIDES[key];
  return (typeof v === 'string' && v.trim() !== '') ? v : null;
}

function COPY(key){
  const v = copyRaw(key);
  return v != null ? v : (COPY_DEFAULTS[key] || '');
}

// Token substitution. Interpolated values are written as [length], [tv], [floor],
// [tiles], [count], [reason] and [phone] inside the editable string, so the owner
// can move them, reword around them, or delete one outright. A token we have no
// value for renders as NOTHING rather than leaving a bracket on the page, and
// nothing in here can throw: the worst a bad edit does is print a sentence with a
// number missing.
function fill(str, map){
  if(typeof str !== 'string') return '';
  const m = map || {};
  return str.replace(/\[(\/?[a-z_]+)\]/g, (whole, key) =>
    Object.prototype.hasOwnProperty.call(m, key) ? String(m[key]) : '');
}

// The HTML flavour. [phone] becomes the styled span and [contact]...[/contact]
// becomes the contact link, which are the only two pieces of markup any of this
// copy is allowed to carry. The section strips every tag out of the owner's text
// before it reaches us (see the | strip_html in the copy block), so these are the
// ONLY tags that can end up in an innerHTML sink here.
// [WARN] Use fill(), not fillHTML(), for anything written with textContent: a
// #dropSay that says "<span class=..." out loud is the failure this split avoids.
const COPY_HTML_TOKENS = {phone: PHONE_HTML, contact: CONTACT_OPEN, '/contact': CONTACT_CLOSE};
function fillHTML(str, map){
  return fill(str, Object.assign({}, COPY_HTML_TOKENS, map || {}));
}

// ---- Parts-list (BOM) SKU links + photo preview (2026-09-06) ----
// Owner ask: the parts-list SKUs link to their product pages, and hovering (or,
// on a phone, tapping) a SKU shows a product photo. bomRowHTML() is the single
// PURE renderer for a build card's parts rows; the three inline row templates in
// buildSection() that predated it are gone. The behaviour (hover / focus / tap /
// Escape) is delegated once on #s3out further down, so the innerHTML rebuild on
// every keystroke needs no re-wiring.

// A minimal HTML escaper so a hostile sku, title or url can never break out of
// the attribute or text sink. Mirrors the slanted page's esc() (assets/vms-
// slanted-wall.js) exactly. There was no escaper in this file before; the old
// row templates trusted the store data verbatim.
function escHTML(s){
  return String(s == null ? '' : s).replace(/[&<>"']/g, c =>
    ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
}

// The larger preview image URL: the data snippet emits every image at width=200,
// so swap that query value for width=480 (the Shopify CDN honours the param). A
// URL that carries no width param passes through unchanged, so a stored full URL
// is never mangled.
function bomPreviewUrl(u){
  return typeof u === 'string' ? u.replace(/([?&]width=)\d+/, '$1480') : u;
}

// One <tr> of the parts list. `part` carries { sku, title, url, image } from the
// data snippet (or basePriceFor / COUPLER, which now forward url and image).
// The SKU is a clickable product link when `url` is a non-empty string (same tab,
// matching the slanted-page ruling 2026-08-30; the configurator keeps its state
// in the query string via replaceState so Back returns to the build), else the
// escaped SKU as plain text (never a dead link, never href="#"). When `image` is
// a non-empty string a 32px thumbnail button precedes the link (the phone
// tap-toggle path) and a hidden preview follows; the preview's <img> src is
// filled from data-src only the FIRST time it is shown (see the #s3out delegation
// below), so four cards times six rows never preload two dozen large images.
function bomRowHTML(part, titleSuffix, priceText){
  const sku = part && part.sku != null ? String(part.sku) : '';
  const title = part && part.title != null ? String(part.title) : '';
  const url = part && typeof part.url === 'string' ? part.url : '';
  const image = part && typeof part.image === 'string' ? part.image : '';
  const link = url
    ? `<a class="bom__sku" href="${escHTML(url)}" translate="no">${escHTML(sku)}</a>`
    : escHTML(sku);
  const thumb = image
    ? `<button type="button" class="bom__thumb" aria-expanded="false" aria-label="Show photo of ${escHTML(sku)}"><img src="${escHTML(image)}" alt="" width="32" height="32" loading="lazy"></button>`
    : '';
  const pv = image
    ? `<span class="bom__pv" hidden><img alt="${escHTML(title)}" width="180" height="180" data-src="${escHTML(bomPreviewUrl(image))}"></span>`
    : '';
  return `<tr><td class="bom__skucell">${thumb}${link}${pv}</td><td>${escHTML(title)}${titleSuffix || ''}</td><td class="num">${priceText}</td></tr>`;
}

// ---- Pools (metafield-driven, per DATA rules) ----
// Defensive belt-and-suspenders: CP2 and 11WW001 are already excluded server
// side via custom.configurator_exclude (app-owned flag). Kept here too so a
// reverted/forgotten flag can never resurrect them in this pool.
const PLATE_POOL = DATA.components.filter(c =>
  c.component_type === 'ceiling_plate' &&
  typeof c.length_offset === 'number' &&
  c.sku !== 'CP2' && c.sku !== '11WW001');

const TELE = DATA.components.filter(c =>
  c.component_type === 'pipe' && c.length_min != null &&
  c.sku !== 'EXT-4872');                             // EXT-4872 is a TELESCOPING pole, so this
                                                     // (not FIXED) is the pool it would land in the
                                                     // day it gains a component_type metafield.
                                                     // Inert today only because the live product
                                                     // carries none. Guard it here where it matters.

const FIXED = DATA.components.filter(c =>
  c.component_type === 'pipe' && c.nominal_length != null &&
  c.nominal_length !== 48 &&                         // never a 48" pipe (UPS oversize)
  c.sku !== 'EXT-4872' &&                            // defense in depth: EXT-4872 is telescoping and
                                                     // is primarily guarded in TELE. Kept here too so
                                                     // a mistagged nominal_length can never route it
                                                     // into the fixed pool either.
  !/-extension$/i.test(c.sku) &&                     // exclude pipe+coupler bundle kits (coupler counted explicitly)
  !/-extension-/i.test(String(c.handle || '')));      // same rule keyed on handle (live skus may differ)

const COUPLER = DATA.components.find(c => c.component_type === 'coupler'); // 11AC001

// The shortest mast this pool can produce, in inches. It is the shortest SINGLE
// piece, because one piece needs no coupler and every longer mast is that piece
// plus something: the smallest nominal_length across FIXED, or the smallest
// length_min across TELE, whichever is less. Today that is 2 in, 112-NPT-PIPE-2,
// beating the shortest telescopic (EXT0812 collapses to 8 in).
//
// It feeds minDrop's 'shortestMast' floor via elevOpts: the finished drop can
// never be shorter than plate.length_offset + head.length_offset + this, because
// mastTarget = drop - plateOffset - headOffset and no shorter mast exists.
//
// [RULE] DERIVED FROM THE SAME TWO ARRAYS buildMasts() SOLVES FROM, and it has to
// stay that way. Hardcoding today's 2 would put the drawing's hard stop and the
// solver's pool out of step the day a piece joins or leaves the collection, and
// the customer would meet a wall the solver does not believe in, or find no wall
// where the solver has one. Same failure the elevation's one-scale rule exists to
// prevent, one layer down.
//
// [WARN] It is deliberately CONSERVATIVE by up to TOL for the FIXED stacks.
// consider() in buildMasts still accepts a FIXED mast target down to lo - TOL, so
// the solver would offer a fixed piece half an inch shallower than this floor
// allows. That half inch is not real: the parts that ship are still 2 in long, so
// a drop drawn half an inch shorter is a picture of an assembly nobody can build.
// Losing it is the right trade and it is always in the safe direction. (Since [A],
// an ADJUSTABLE stack no longer gets that slack: it must land inside its own
// range, so this conservatism only ever masks fixed builds now.)
//
// null when the pool holds no usable piece at all. minDrop SKIPS the branch on a
// null rather than inventing a floor, which is the discipline everywhere in that
// module: a missing input is never a guessed zero.
const MIN_MAST_LENGTH = (function(){
  let m = null;
  const take = v => {
    if(typeof v === 'number' && isFinite(v) && v > 0 && (m === null || v < m)) m = v;
  };
  FIXED.forEach(f => take(f.nominal_length));
  TELE.forEach(t => take(t.length_min));
  return m;
})();

// [RULE] HORIZONTAL (FACE-DOWN) HEADS, owner ruling 2026-08-26. The store carries
// no orientation metafield, so the one dental head that holds the TV horizontal
// (face down, screen pointing at the floor) is named here by SKU. This is the ONE
// place orientation is decided: head.horizontal is derived from it just below and
// is read by elevOpts() (which forwards it to the geometry so the floors and the
// drawn box change), by the length label, and by the finished-length summary. A
// horizontal head measures ceiling plate to the BACK of the screen, drops the
// screen-reach floor and the tile clearance, and draws the TV as a flat box. Add
// a SKU here the day another face-down head joins the pool; change nothing else.
const HORIZONTAL_HEADS = ['DD-HEAD'];
// Top edge toward the room (owner 2026-10-13): the edge that rises when the head tilts the screen up; the tilt slots sit in the TV plate and only turn clockwise in the cutout view, so the screen faces left ('left' = -X in 3D); notes assets/vms-tv-lookup.notes.md.
const HORIZONTAL_TOP_EDGE = { 'DD-HEAD': 'left' };
// [RULE] HEAD TOP ABOVE THE VESA DISC, in inches, for the horizontal heads only
// (owner ruling 2026-09-22: DD-HEAD 3.82, the renderer's GLB figure from the disc
// face to the top of the tilt yoke). A face-down head hangs its TV flat under the
// disc, so the only thing that can hit the tiles (or the structure) is the head
// itself; this number feeds the horizontal floors in minDrop, clearanceGap and
// maxTileDepth through elevOpts(). Add a value here with the SKU above.
const HEAD_TOP_ABOVE_VESA_IN = { 'DD-HEAD': 3.82 };
const HEADS = DATA.components.filter(c => c.component_type === 'mounting_head');
// Stamp the orientation onto each head object once. getHead()/comp() return these
// same references, so head.horizontal is available everywhere a head is read.
// [WARN, 2026-09-22] A horizontal head with no HEAD_TOP_ABOVE_VESA_IN entry is a
// configuration error: the geometry then falls back to the VERTICAL floors (never
// to no floor at all), and we say so once in the console.
HEADS.forEach(h => {
  h.horizontal = HORIZONTAL_HEADS.indexOf(h.sku) !== -1;
  h.headTopIn = HEAD_TOP_ABOVE_VESA_IN[h.sku] != null ? HEAD_TOP_ABOVE_VESA_IN[h.sku] : null;
  h.topEdge = h.horizontal && HORIZONTAL_TOP_EDGE[h.sku] ? HORIZONTAL_TOP_EDGE[h.sku] : null;
  if(h.horizontal && h.headTopIn == null){
    console.warn('vmscfg: horizontal head ' + h.sku + ' has no HEAD_TOP_ABOVE_VESA_IN entry; using the vertical floors');
  }
});

const getPlate = sku => PLATE_POOL.find(p => p.sku === sku);
const getHead  = sku => HEADS.find(h => h.sku === sku);
const comp     = sku => DATA.components.find(c => c.sku === sku);

// A head with either bound missing/null is ALWAYS offered (the app page's
// screen_min/screen_max validation is both-or-neither, so "either missing"
// and "both missing" are the same no-range case in practice). No TV size
// entered at all also means every head is offered.
function headScreenOK(head, size){
  if(size == null || isNaN(size)) return true;
  if(head.screen_min == null || head.screen_max == null) return true;
  return size >= head.screen_min && size <= head.screen_max;
}

// ---- VESA patterns: DERIVED, never stored (2026-08-21) --------------------
// [WARN] THIS IS A SECOND COPY OF THE PRODUCT PAGE'S DERIVATION. The canonical
// implementation is the "Supported standards" chip row in
// sections/vms-main-product.liquid (search vesa_canon in the specs block). It
// filters this exact 17-pattern canon against custom.vesa_width_min/max and
// custom.vesa_height_min/max, in millimetres, with the same inclusive test. The
// two must stay in step: an edit to the canon or to the filter belongs in BOTH
// files, or a head's product page and its configurator card start listing
// different patterns for the same part, which is worse than listing none.
//
// The product custom.vesa metafield is a CACHE of this same derivation, not the
// source, so it is deliberately not read here. It was used to check this code:
// every head carrying that field derives to exactly its stored list. If this
// ever disagrees with custom.vesa for some head, this logic is the suspect, not
// the canon.
//
// "Common standards within this hole range", not an exhaustive list: a head
// whose bounds admit no canon pattern gets no chips rather than a made-up one.
const VESA_CANON = [
  [75,75],[100,100],[100,200],[200,100],[200,200],[200,300],[200,400],
  [300,200],[300,300],[300,400],[400,200],[400,300],[400,400],[400,600],
  [600,200],[600,400],[800,400]
];

// All four bounds or nothing: a half-populated part cannot be filtered against,
// and guessing the missing side would invent patterns the head may not take.
function vesaPatterns(c){
  if(!c) return [];
  const wmin = c.vesa_width_min, wmax = c.vesa_width_max;
  const hmin = c.vesa_height_min, hmax = c.vesa_height_max;
  if(wmin == null || wmax == null || hmin == null || hmax == null) return [];
  return VESA_CANON.filter(p => p[0] >= wmin && p[0] <= wmax && p[1] >= hmin && p[1] <= hmax);
}

// The chip row itself. Returns '' (not an empty container) when there is nothing
// to show, so no call site can render a stray bordered box; every caller tests
// the string. SPANS, not divs: the head pick card is a <button>, whose content
// model is phrasing content only, and Dawn's base.css hides div:empty anyway.
// &times; is the HTML entity on purpose - it renders the same multiplication
// sign the product page shows while keeping this file's source pure ASCII.
function vesaChipsHTML(c){
  const pats = vesaPatterns(c);
  if(!pats.length) return '';
  return '<span class="vmscfg-vchips">' +
    pats.map(p => `<span class="vmscfg-vchips__chip">${p[0]}&times;${p[1]}</span>`).join('') +
    '</span>';
}

// The head card's VESA tile: the smallest and largest PATTERN, as one string.
//
// [RULE] DERIVED FROM THE FILTERED PATTERN LIST, NOT FROM THE RAW BOUNDS, and
// the difference is not cosmetic. 33HE001's stored bounds are 75 to 490 mm
// wide, but 490 is a hole-spacing LIMIT, not a pattern: no VESA standard is 490
// wide, so no TV a shopper can buy has that spacing. Printing the bound puts a
// number on the card that means nothing to anyone and that appears nowhere in
// the chip list one tap away. Taking the first and last of vesaPatterns()
// instead makes the card read 75x75 to 400x400, the largest pattern the head
// actually takes, and it guarantees the card and the chip list can never
// disagree, because the card IS the two ends of that list.
//
// The canon is ordered by width then height, so first and last read as smallest
// and largest. A head whose bounds pin exactly one pattern prints it once rather
// than saying "200x200 to 200x200"; a head whose bounds admit no pattern gets
// '' here, exactly as it gets no chips.
//
// &times; is the entity for the same reason it is in vesaChipsHTML: it renders
// the multiplication sign the product page uses while this file stays ASCII.
function vesaRangeText(c){
  const pats = vesaPatterns(c);
  if(!pats.length) return '';
  const first = pats[0], last = pats[pats.length - 1];
  const lo = first[0] + '&times;' + first[1];
  const hi = last[0] + '&times;' + last[1];
  return lo === hi ? lo : lo + ' to ' + hi;
}

// Millimetres to inches, one decimal, trailing .0 stripped. Local rather than a
// filter because this is the only place in the JS that converts a length.
function mmIn(mm){
  const v = Math.round((Number(mm) / 25.4) * 10) / 10;
  return String(v);
}

// ---- The to-scale VESA diagram (2026-08-22) ------------------------------
// PORTED from the product page's diagram, sections/vms-main-product.liquid, the
// .vms-specs__diag-svg block. That makes this the THIRD derivation of VESA data
// in the project after VESA_CANON above and the Liquid canon it copies, so the
// same warning applies: the numbers here are the product page's numbers and an
// edit to either belongs in both, or the same head draws two different pictures
// on two pages.
//
// THE NUMBERS, all from that block: the SVG is a 214x138 viewBox with the screen
// panel fixed at x6 y8 192x108. A 16:9 screen measures 22.13864 mm wide and
// 12.45362 mm tall PER INCH OF DIAGONAL. The hole rectangle is scaled off the
// MAX bound, which is the worst case and the honest one to draw, then clamped at
// 177x99 so an outsized pattern cannot escape the panel.
//
// ONE DELIBERATE IMPROVEMENT over the product page. That page has to guess which
// screen to draw against and uses the head's own recommended high. Here the
// customer has ALREADY TOLD US their TV size in step 1, so the drawing is to
// scale on THEIR screen whenever we have it, and falls back to the head's max
// only when they have not typed one yet. The caption says which.
function vesaDiagramSVG(h, diagIn){
  const PW = 192, PH = 108, X0 = 6, Y0 = 8;
  let hw, hh;
  if(diagIn > 0){
    hw = Number(h.vesa_width_max)  * PW / (diagIn * 22.13864);
    hh = Number(h.vesa_height_max) * PH / (diagIn * 12.45362);
  } else {
    // No screen to scale against: the product page's fixed placeholder ratio.
    hw = 86.4; hh = 54.0;
  }
  if(hw > 177) hw = 177;
  if(hh > 99)  hh = 99;
  const r1 = n => Math.round(n * 10) / 10;
  const hx  = r1((PW - hw) / 2 + X0);
  const hy  = r1((PH - hh) / 2 + Y0);
  const hx2 = r1(hx + hw);
  const hy2 = r1(hy + hh);
  const desc = `VESA mounting holes ${nice(h.vesa_width_min)} to ${nice(h.vesa_width_max)} mm wide and ` +
               `${nice(h.vesa_height_min)} to ${nice(h.vesa_height_max)} mm tall` +
               (diagIn > 0 ? `, shown to scale on an approximately ${nice(diagIn)} inch 16 by 9 display` : '') + '.';
  return `<svg class="vmscfg-vdiag__svg" viewBox="0 0 214 138" role="img" preserveAspectRatio="xMidYMid meet">` +
    `<title>VESA hole pattern shown to scale on the back of a display</title>` +
    `<desc>${desc}</desc>` +
    `<rect class="vmscfg-vdiag__panel" x="6" y="8" width="192" height="108" rx="7"></rect>` +
    `<rect class="vmscfg-vdiag__pattern" x="${hx}" y="${hy}" width="${r1(hw)}" height="${r1(hh)}" fill="none" stroke-dasharray="3 3"></rect>` +
    `<g class="vmscfg-vdiag__hole" fill="none">` +
      `<circle cx="${hx}" cy="${hy}" r="3"></circle><circle cx="${hx2}" cy="${hy}" r="3"></circle>` +
      `<circle cx="${hx}" cy="${hy2}" r="3"></circle><circle cx="${hx2}" cy="${hy2}" r="3"></circle>` +
    `</g>` +
    `<g class="vmscfg-vdiag__dot">` +
      `<circle cx="${hx}" cy="${hy}" r="0.9"></circle><circle cx="${hx2}" cy="${hy}" r="0.9"></circle>` +
      `<circle cx="${hx}" cy="${hy2}" r="0.9"></circle><circle cx="${hx2}" cy="${hy2}" r="0.9"></circle>` +
    `</g>` +
    `<g class="vmscfg-vdiag__dim">` +
      `<line x1="${hx}" y1="128" x2="${hx2}" y2="128"></line>` +
      `<line x1="${hx}" y1="124" x2="${hx}" y2="132"></line>` +
      `<line x1="${hx2}" y1="124" x2="${hx2}" y2="132"></line>` +
      `<line x1="206" y1="${hy}" x2="206" y2="${hy2}"></line>` +
      `<line x1="202" y1="${hy}" x2="210" y2="${hy}"></line>` +
      `<line x1="202" y1="${hy2}" x2="210" y2="${hy2}"></line>` +
    `</g>` +
  `</svg>`;
}

// The dialog body. Diagram, the bounds in mm and inches, then the same chip list
// the card used to carry - which is the trade the wireframe makes: the card gets
// one compact range and the full list moves one tap away.
function vesaInfoHTML(h){
  const tv = Number(state.tvSize);
  const diagIn = (tv > 0) ? tv
    : ((h.screen_max != null && !isNaN(h.screen_max)) ? Number(h.screen_max) : 0);
  const chips = vesaChipsHTML(h);
  const wRange = `${nice(h.vesa_width_min)}-${nice(h.vesa_width_max)} mm wide`;
  const hRange = `${nice(h.vesa_height_min)}-${nice(h.vesa_height_max)} mm tall`;
  const wIn = `${mmIn(h.vesa_width_min)}-${mmIn(h.vesa_width_max)} in wide`;
  const hIn = `${mmIn(h.vesa_height_min)}-${mmIn(h.vesa_height_max)} in tall`;
  return `
    <p class="vmscfg-vinfo__lead">${COPY('vesa_info_lead')}</p>
    <div class="vmscfg-vinfo__body">
      <div class="vmscfg-vdiag">
        ${vesaDiagramSVG(h, diagIn)}
        <div class="vmscfg-vdiag__num vmscfg-vdiag__num--h">
          <span class="vmscfg-vdiag__mm">${nice(h.vesa_height_min)}-${nice(h.vesa_height_max)} mm</span>
          <span class="vmscfg-vdiag__in">${mmIn(h.vesa_height_min)}-${mmIn(h.vesa_height_max)} in</span>
        </div>
        <div class="vmscfg-vdiag__num vmscfg-vdiag__num--w">
          <span class="vmscfg-vdiag__mm">${nice(h.vesa_width_min)}-${nice(h.vesa_width_max)} mm</span>
          <span class="vmscfg-vdiag__in">${mmIn(h.vesa_width_min)}-${mmIn(h.vesa_width_max)} in</span>
        </div>
        ${diagIn > 0 ? `<p class="vmscfg-vdiag__cap">${fill(COPY('vesa_info_scale'), {size: nice(diagIn)})}</p>` : ''}
      </div>
      <div class="vmscfg-vinfo__text">
        <p class="vmscfg-vinfo__range">${wRange}, ${hRange}<span class="vmscfg-vinfo__in">(${wIn}, ${hIn})</span></p>
        ${chips ? `<div class="vmscfg-vinfo__chips"><span class="vmscfg-vinfo__chipslabel">${COPY('vesa_info_standards')}</span>${chips}</div>
        <p class="vmscfg-vinfo__note">${COPY('vesa_info_note')}</p>` : ''}
      </div>
    </div>`;
}

// [RULE] THIS CAN OPEN ON TOP OF THE COMPARE MODAL. It is reachable from two
// places: a head pick card, where nothing else is open, and a row of the heads
// comparison chart, which is itself inside #cmpDlg. Nested showModal() is
// allowed and stacks in the top layer, Escape closing the topmost first, but two
// things have to be right for it: the body scroll lock must not be released
// while the chart underneath is still open (see the close handler), and the
// chart must not be rebuilt while this sits over it.
// The drop-ceiling explanation. Unlike the other two openers there is nothing to
// build: the panel is server-rendered, so this only has to show it.
//
// [RULE] NO CAN_DIALOG BAIL-OUT HERE, and the difference from the VESA button is
// deliberate. That one hides itself without <dialog> support because its content
// is a convenience. This one carries the safety sentence "a drop ceiling is not
// structure, never hang a TV from the tile grid", which must stay reachable, so
// without showModal() it falls back to the non-modal `open` attribute and simply
// appears in the flow instead.
function openDcHelp(){
  const dlg = el('dcDlg');
  if(!dlg) return;
  if(CAN_DIALOG && typeof dlg.showModal === 'function'){
    document.body.classList.add('overflow-hidden');
    dlg.showModal();
  } else {
    dlg.setAttribute('open', '');
  }
  const t = el('dcTitle');
  if(t) t.focus();
}

// ---- The head-cleared modal (FEATURE B, owner 2026-09-06) -----------------
// A committed TV size that leaves the picked head's screen range clears the head
// and collapses step 4. That used to happen in silence (a standing open item);
// this modal explains it. Built to MIRROR #dcDlg exactly: showModal with the
// CAN_DIALOG fallback to the non-modal `open` attribute, the title focused, and
// the shared body overflow-hidden lock. commitTvSize is its only caller.

// {head} / {new_head} render as the head's SKU alone. The full product title runs
// long and carries " - " noise ("... Head for - 1 1/2 Pipe - 400x400"), so the SKU
// is the clean identifier for the modal sentence (coordinator ruling 2026-09-06).
function headLabel(h){ return h ? h.sku : ''; }

// The modal's title and body, PURE and DOM-free so scripts/test_vms_auto_select.js
// can assert the variant choice and the substitution without a browser. The
// placeholders are {curly} (owner's wording), so they are substituted here by a
// literal split/join rather than through fill(), which only knows [square] tokens.
// `replacement` (the head FEATURE A auto-picked in the same commit) selects the
// swapped variant and fills {new_head}; null selects the plain body variant.
function headClearedCopy(cleared, size, replacement){
  const map = {
    head: headLabel(cleared),
    min: cleared.screen_min,
    max: cleared.screen_max,
    size: size,
    new_head: replacement ? headLabel(replacement) : ''
  };
  const sub = (s) => Object.keys(map).reduce(
    (acc, k) => acc.split('{' + k + '}').join(String(map[k])), String(s));
  const key = replacement ? 'head_cleared_swapped' : 'head_cleared_body';
  return { title: COPY('head_cleared_title'), body: sub(COPY(key)) };
}

// Set on open, read by the click-to-close handler's 300 ms guard (see that handler).
let headDlgOpenedAt = 0;
function openHeadDlg(cleared, size, replacement){
  const dlg = el('headDlg');
  if(!dlg || !cleared) return;
  const copy = headClearedCopy(cleared, size, replacement);
  const t = el('headDlgTitle');
  if(t) t.textContent = copy.title;
  const body = el('headDlgBody');
  // textContent, not innerHTML: this copy carries no markup and no [phone]/[contact]
  // tokens, unlike the drop-ceiling panel.
  if(body) body.textContent = copy.body;
  headDlgOpenedAt = Date.now();
  // Same fallback shape as openDcHelp(): the message must stay reachable without
  // <dialog> support, so fall back to the non-modal `open` attribute in the flow.
  if(CAN_DIALOG && typeof dlg.showModal === 'function'){
    document.body.classList.add('overflow-hidden');
    dlg.showModal();
  } else {
    dlg.setAttribute('open', '');
  }
  if(t) t.focus();
}

function openVesaInfo(sku){
  const h = getHead(sku);
  const dlg = el('vesaDlg');
  if(!h || !dlg || !CAN_DIALOG) return;
  el('vesaTitle').textContent = COPY('vesa_info_title') + ' - ' + h.sku;
  el('vesaBody').innerHTML = vesaInfoHTML(h);
  el('vesaBody').scrollTop = 0;
  document.body.classList.add('overflow-hidden');
  dlg.showModal();
  el('vesaTitle').focus();
}

// ---- Head spec line pieces (2026-08-21) ----------------------------------
// custom.configuration is a single-line list whose first value is published by
// snippets/vms-configurator-data.liquid as a plain string. The map is the whole
// vocabulary: an unrecognised value renders NOTHING rather than the raw string,
// because a new value the owner adds in the admin should show up as a missing
// line here and get translated deliberately, not leak an internal label onto a
// customer-facing card.
const HEAD_CONFIG_TEXT = {
  'Single': '1 screen',
  'Back-to-back': '2 screens, back to back',
  'Double-wide': '2 screens, side by side',
  'Double-stack': '2 screens, stacked'
};

// custom.adjustability arrives already reduced to the boolean `rotates`. THREE
// states, not two: true is a swivel head, false is a head that only tilts
// (DD-HEAD, the dental head, is the deliberate case), and null is "nothing on
// file" which must stay silent rather than claim either.
function rotationText(h){
  if(h.rotates === true) return 'Rotates';
  if(h.rotates === false) return 'Tilt only';
  return '';
}

// Tilt as one phrase. Deliberately NOT written with `||`: 0 is a real answer on
// the fixed-tilt heads (PDS-LCHB is 0 up / 15 down), so a falsy test would drop
// the down figure with it. Zero-up prints the down figure alone, because
// "0 degrees up / 15 degrees down" is a sentence that makes a customer stop and
// work out that the head does not tip up at all. Both sides zero or absent
// returns '', and the caller decides between omitting the item (pick card) and
// printing a placeholder (comparison table).
function tiltText(h){
  const up = (h.tilt_up == null || isNaN(h.tilt_up)) ? 0 : Number(h.tilt_up);
  const dn = (h.tilt_down == null || isNaN(h.tilt_down)) ? 0 : Number(h.tilt_down);
  if(!up && !dn) return '';
  if(!up) return deg(dn) + ' down';
  if(!dn) return deg(up) + ' up';
  return deg(up) + ' up / ' + deg(dn) + ' down';
}

// ---- Reference-point label: DELETED 2026-07-29 ----
// dropRefLabel() lived here and returned "measured from where the top plate mounts
// down to where you want the center of the screen". It had exactly one caller, the
// length_label default, and the owner replaced that label with his own shorter
// sentence, so the helper went rather than being left as an unused function nobody
// can safely delete later. Everything worth keeping about it, including WHY the
// word "ceiling" may never come back into that label, is recorded at length_label
// in COPY_DEFAULTS above. If a second surface ever needs the long form again,
// reintroduce it there and not as a second transcription.

// ---- Plate notes (owner's spreadsheet, 2026-08-21) ----
// The owner's own "what makes it different" wording, rebuilt wholesale from his
// 14-plate sheet and replacing the 2026-07 draft copy that used to live here.
// These strings feed BOTH the inline plateCard() in step 2 and the "what makes
// it different" column of the compare chart, on purpose: one place to edit, and
// no second transcription of the same sentence to drift out of sync.
// OFFSET6 is NOT in the plate pool (owner ruling 2026-08-11: it stays in the
// 1-1/2 inch pipe accessories section and is never a ceiling plate) and so is
// not on his sheet either. Its note is left exactly as it was.
// [RULE] NO NOTE OPENS WITH THE WORD "CHEAPEST" ANY MORE (owner, 2026-08-22:
// "Remove Cheapest from the note"). Once the green pill was capitalised to
// `Cheapest` the two notes that opened on that same word said it twice on one
// card, badge and first word. 11CP005 lost its opener outright and now leads
// with its hole count; 11CP006 keeps "Angled." and lost only the one word he
// named. The price claim lives in exactly ONE place now, the pill, and that
// pill is computed from live price by cheapestPlateOf() - so a repricing can
// never leave a stale "cheapest" stranded in a hand-written sentence, which is
// precisely what this removal prevents. Do not reintroduce a price word here.
const PLATE_NOTE = {
  '11CP005':'6 Mounting Holes. 5.9 x 4.75 inch rectangle. Cable Pass through in side.',
  '11CP001':'Heavy-duty. 4 Mounting Holes. 4.5 inches square. Cable pass through in side and top.',
  '11CP002':'Heavy-duty. 4 Mounting Holes. 4.5 inches square. Low profile. Shorter version of 11CP001. Cable pass through in TOP only. No side cable pass through.',
  'CP-1':'Extra Heavy-duty. 8 mounting holes 7.5 inches square. Low profile. Small cable pass through in top only. No side cable pass through.',
  'CP-1PT':'Extra Heavy-duty. 8 mounting holes 7.5 inches square. Cable pass through in side and small pass through in top.',
  '11CP006':'Angled. Adjust 83 degrees in both directions. 6 mounting holes. 5.9 x 4.75 inch rectangle. Cable pass through in side',
  '11AC005':'Pivots 90 degrees. Can mount on a vertical surface. 4 Mounting Holes. 4.5 inches square. Cable pass through in side and top',
  'CCA-1':'Pivots 60 degrees. 3 Mounting holes. 4.43 x 3.35 inches. Cable pass through in top only',
  'ANGLE-CEILING':'Heavy-Duty. Pivots 90 degrees. Can mount on vertical surface. 3 Mounting Holes. 5.51 x 3.35 inches. Cable pass through in top only.',
  '11UN001':'Strut (Unistrut) Adapter. Heavy-Duty. 4.5 inches long on strut. Cable Pass through in side and top.',
  '11BC001':'Clamps onto a steel I-beam 2.25 to 14.5 inches in width. Cable pass through top and side.',
  'IBA-1':'Clamps onto a steel I-beam 2.25 to 12 inches in width. Cable pass through top only.',
  'TCA-1':'Truss adapter. Fits maximum diameter of 3 inches or maximum cross section dimensions of 3 x 3 inches. 4.4 inches square. Cable pass through top and side.',
  '11TR002':'Designed for stage truss, or clamps onto 1-1/2 inch pipe. Clamps to 1.47 to 2 inch OD round tubes/pipes. Cable pass through side only.',
  'OFFSET6':'Offset plate that shifts the mast 6 inches from the attachment point.'
};

// ---- Plate SPEC table (owner's spreadsheet, 2026-08-21) ----
// PRICE, ADDS-TO-LENGTH AND WEIGHT RATING ARE DELIBERATELY NOT IN HERE. All three
// arrive live with the component data (p.price, custom.length_offset read as
// c.length_offset, and custom.weight_capacity read as c.weight_capacity), and they
// must keep coming from there so a change the owner makes in admin can never drift
// away from a number hardcoded in this file. Only the four columns the store does
// not serve live to this widget are transcribed below.
//
// [HISTORY] `weight` WAS in this table for part of 2026-08-21, transcribed from the
// spreadsheet. It came out the same day: all 14 plates had custom.weight_capacity
// written and verified, so the store became the single source of truth and a second
// copy here was pure drift risk. Do not re-add it.
//
// `tilt` is a NUMBER (a tilt of 0 is a real value, not a blank); `holes` and `dims`
// are STRINGS because the clamp-style adapters legitimately answer "N/A" rather
// than a hole count or a plate size.
const PLATE_SPEC = {
  '11CP005':      {holes:'6',   dims:'5.9 x 4.75 inches',  cable:'Side only',          tilt:0},
  '11CP001':      {holes:'4',   dims:'4.5 inches square',  cable:'Side and Top',       tilt:0},
  '11CP002':      {holes:'4',   dims:'4.5 inches square',  cable:'Top Only',           tilt:0},
  'CP-1':         {holes:'8',   dims:'7.5 inches square',  cable:'Small Top Only',     tilt:0},
  'CP-1PT':       {holes:'8',   dims:'7.5 inches square',  cable:'Side and Small Top', tilt:0},
  '11CP006':      {holes:'6',   dims:'5.9 x 4.75 inches',  cable:'Side Only',          tilt:83},
  // [RESOLVED 2026-08-21, owner ruling] 11AC005's sheet said 125 lb while the store
  // metafield said 200 lb. Owner chose 125, "the lower value", and the store was
  // corrected to match that same day. There is no discrepancy left to carry here,
  // and the rating is now read live rather than transcribed. Do not re-raise it.
  '11AC005':      {holes:'4',   dims:'4.5 inches square',  cable:'Side and Top',       tilt:90},
  'CCA-1':        {holes:'3',   dims:'4.43 x 3.35 inches', cable:'Top Only',           tilt:60},
  'ANGLE-CEILING':{holes:'3',   dims:'5.51 x 3.35 inches', cable:'Top Only',           tilt:90},
  '11UN001':      {holes:'2',   dims:'4.5 x 2.25 inches',  cable:'Side and Top',       tilt:0},
  '11BC001':      {holes:'N/A', dims:'N/A',                cable:'Top Only',           tilt:0},
  'IBA-1':        {holes:'N/A', dims:'N/A',                cable:'Top Only',           tilt:0},
  'TCA-1':        {holes:'N/A', dims:'N/A',                cable:'Top and Side',       tilt:0},
  '11TR002':      {holes:'N/A', dims:'N/A',                cable:'Side Only',          tilt:0}
};

// ---- Plate TYPE -> plate mapping ----
// RESTRUCTURED 2026-07-26 (owner-approved). This replaced STRUCTURES, whose
// seven entries mixed two axes: four were site conditions ("Exposed wood
// joists", "Drop ceiling (suspended tiles)") and three were products in
// disguise ("Steel I-beam", "Truss", "Metal building strut"). One row that
// reads as one taxonomy but is really two. The tier is now purely the plate
// TYPE, and the site-condition knowledge lives in the two server-rendered
// disclosure panels in sections/vms-configurator.liquid, where it has room to
// answer properly instead of fitting on a chip.
//
// Consequences of the flip, all deliberate:
//   - The old 'joist' entry is GONE, merged into 'flat'. It differed from
//     'flat' by exactly one SKU (it withheld 11CP002); the owner confirmed
//     2026-07-26 that exclusion was not deliberate, so 11CP002 is now offered
//     for wood joists too.
//   - 'drop' is GONE as a selectable. A drop ceiling never resolved to a plate
//     of its own; it forwarded to whatever structure was above the tiles, via
//     a whole second chip row (kase 3 / state.aboveStructId). With the tier
//     already being the plate type, that forwarding IS just "pick the type for
//     what is above", so the second row, the kase field, and aboveStructId all
//     deleted. The safety copy and the tile-depth input moved into the
//     #dropHelp panel and did not get lost.
//   - kase 1 vs 2 was already dead data (only `=== 3` was ever tested).
//
// The five entries partition all 13 pooled plates with no SKU in two types and
// none orphaned; self-test 8 asserts exactly that, so adding a plate to the
// collection without listing it here fails loudly under ?vmscfg_diag=1.
// CP-1 / CP-1PT / ANGLE-CEILING placements carry forward the owner ruling of
// 2026-07-22: CP-1 + CP-1PT sit with 11CP005, ANGLE-CEILING with 11CP006.
// `thumb` is the SKU whose product photo represents the type on its tile. It is
// the first plate listed in each group, but that is a PICTURE choice and nothing
// more: it is not a recommendation and it is not necessarily the cheapest one
// (see the list-order rule below).
// `conditions` deliberately claims only what the catalog copy supports; a
// purlin flange is NOT claimed for the beam clamp (it clamps an I-beam
// flange), so purlins route to the call-us line instead.
// `short` is the CHIP label (2026-07-29, owner). Once a type is picked the other
// four collapse into one row of chips at every width, and four full labels do not
// fit a 375px phone. Both labels are rendered on every tile and the CSS shows one
// of them, so the step-down costs no re-render (see the .ptypes--picked block in
// assets/vms-configurator.css).
const PLATE_TYPES = [
  {id:'flat',  label:'Flat ceiling plate',   short:'Flat',     thumb:'11CP005', conditions:'Drywall with structure behind it, exposed wood joists, or a concrete deck', plates:['11CP005','11CP001','11CP002','CP-1','CP-1PT']},
  {id:'sloped',label:'Angled ceiling plate', short:'Angled',   thumb:'11CP006', conditions:'Sloped, vaulted or cathedral ceilings',                                     plates:['11CP006','11AC005','CCA-1','ANGLE-CEILING']},
  {id:'uni',   label:'Unistrut adapter',     short:'Unistrut', thumb:'11UN001', conditions:'Metal building with strut (unistrut) channel',                              plates:['11UN001']},
  {id:'ibeam', label:'Beam clamp',           short:'Beam',     thumb:'11BC001', conditions:'Exposed steel I-beam',                                                      plates:['11BC001','IBA-1']},
  // Order flipped and thumb changed 2026-08-22 on the owner's word: "TCA-1 vs
  // 11TR002 - There are very different products for use in different
  // applications. I would re-order, but neither should get the 'cheapest'
  // tag." 11TR002 now lists first; thumb stays in step with plates[0]
  // (self-test 9). noCheapest below is the other half of that ruling.
  {id:'truss', label:'Truss adapter',        short:'Truss',    thumb:'11TR002', conditions:'Open web truss, bar joist, or stage/lighting truss',                        plates:['11TR002','TCA-1'], noCheapest:true}
];
// [RULE] LIST ORDER IS NOT A PRICE CLAIM ANY MORE (owner, 2026-08-22). It used
// to be: the first plate in each group got a "recommended" pill and a note under
// the cards said "we list the cheaper plate first". That note is deleted and the
// pill is now computed from live price by cheapestPlateOf(), e.g. the sloped
// group's four plates are priced differently so whichever is currently cheapest
// gets the badge, never whichever is listed first. `thumb` still names the SKU
// whose photo represents the type on its tile and is still plates[0] for every
// group (self-test 9), but that is a picture choice, not a recommendation.
// The truss group is the one EXCLUDED case: see noCheapest and cheapestPlateOf()
// below, owner 2026-08-22, TCA-1 and 11TR002 are different products for
// different applications and a price comparison between them is not a
// recommendation.

// The cheapest plate in a type, or null when there is nothing to be cheapest
// among. Owner, 2026-08-22: "Maybe for one like unistrut where there is only 1
// choice it should not be there", so a single-plate group gets no pill at all.
//
// [RULE] COMPUTED FROM LIVE PRICE, NEVER FROM LIST ORDER. That is the whole
// point: the label it replaced meant plates[0] and could go stale against
// actual price. Deriving it from the component data means a repricing in the
// admin moves the pill on its own instead of leaving a stale claim on a card.
//
// A tie resolves to the earlier list entry, so a group can never show two pills.
//
// [RULE] noCheapest OPTS A GROUP OUT ENTIRELY, DATA DRIVEN. Owner, 2026-08-22,
// on the truss group specifically: "TCA-1 vs 11TR002 - There are very different
// products for use in different applications... neither should get the
// 'cheapest' tag." A price comparison between them is not a recommendation and
// the badge would mislead, so the group is flagged in PLATE_TYPES rather than
// hardcoded here as `type.id === 'truss'`, so a future group can be excluded
// the same way without touching this function.
function cheapestPlateOf(type){
  if(!type || !type.plates || type.plates.length < 2 || type.noCheapest) return null;
  let best = null, bestPrice = Infinity;
  type.plates.forEach(sku => {
    const p = getPlate(sku);
    if(!p) return;
    // [WARN] TEST THE PRICE FOR NULL BEFORE COERCING IT. Number(null) is 0 and
    // Number('') is 0, not NaN, so an isNaN guard alone lets a plate with no
    // price on file win as "cheapest" at zero dollars. Caught by
    // scripts/test_vms_plate_pills.js before this ever rendered.
    if(p.price == null || p.price === '') return;
    const v = Number(p.price);
    if(isNaN(v)) return;
    if(v < bestPrice){ bestPrice = v; best = sku; }
  });
  return best;
}

const getPlateType   = id  => PLATE_TYPES.find(t => t.id === id);
// Reverse lookup, 1:1 because the groups partition the pool (self-test 8).
// Used by the compare modal, which lets a shopper jump straight to a SKU and
// therefore has to back-fill which type tile should read as selected.
const plateTypeOfSku = sku => PLATE_TYPES.find(t => t.plates.indexOf(sku) >= 0);

// =====================================================================
// STATE
// =====================================================================
// plateTypeId replaced structId (and aboveStructId is gone) in the 2026-07-26
// step 2 restructure. case3Dist is the drop-ceiling tile depth, measured from
// the structure down to the underside of the tiles. It is NEVER subtracted from
// the target (the customer's drop is measured from the real structure in the
// first place), but as of the 2026-07-26 elevation work it is no longer
// display-only: it sets the physical MINIMUM drop, because the screen has to
// hang clear below the tile line.
//
// hasDropCeiling is deliberately THREE-valued: true, false, and null meaning
// not asked yet. Null is not the same as false. The geometry applies no clamp
// for either, but only null may be overridden by the back-compatibility rule in
// readURLState (a restored tile depth with no answer counts as yes, because
// entering a tile depth has always been the implicit declaration). Everything
// that reaches VMSElevation gets a real boolean, never the null.
// Since 2026-10-12 (owner): the boot resolves a still-null answer to false after readURLState, so No is checked on first paint; dcTouched (a pick, or dc/tiles in the link) gates writing dc=0, so a plain visit keeps a clean URL.
const state = {
  tvSize:null, headSku:null, plateSku:null, plateTypeId:null,
  target:null, case3Dist:null, hasDropCeiling:null, dcTouched:false,
  // [FEATURE A, spec 7.6] True when the head in state.headSku was picked by the
  // single-option auto-select rather than a click. selectHead() sets it false (any
  // direct selection is manual), and maybeAutoSelectHead() sets it true right after.
  // It is tracked, not gated on: the head-cleared modal (FEATURE B) fires for a head
  // that was chosen either way.
  headAuto:false,
  _price:null, _mast:null
};

// Set by buildSection() on every render; read back by the cart button wiring
// once the HTML it returned is actually in the DOM (see renderStage3Out).
let LAST_BUILD = null;

// =====================================================================
// SOLVER
// =====================================================================
// True landed cost per _9: any single piece shipping at 48 in or longer adds the
// UPS surcharge. Fixed pipes ship at their length; telescopics ship COLLAPSED (min).
function surchargeFor(pieces){
  let s = 0;
  for(const p of pieces){
    const shipLen = p.length_min != null ? p.length_min : p.nominal_length;
    if(shipLen >= SURCHARGE_MIN_LEN) s += SURCHARGE;
  }
  return s;
}

// Mast build generator. head/plate are real component objects (not a mount);
// mastTarget is what the pipe stack alone must bracket once the plate and
// head offsets are subtracted from the customer's target drop.
function buildMasts(head, plate, target){
  const hOff = head.length_offset;
  const pOff = plate.length_offset;
  const mastTarget = target - pOff - hOff;
  const out = [];
  const seen = new Set();

  function consider(pieces){
    const couplers = pieces.length - 1;
    let lo = 0, hi = 0, price = 0, smalls = 0, tele = null;
    for(const p of pieces){
      if(p.length_min != null){ tele = p; lo += p.length_min; hi += p.length_max; }
      else {
        lo += p.nominal_length; hi += p.nominal_length;
        if(SMALL_LENGTHS.indexOf(p.nominal_length) >= 0) smalls++;
      }
      price += Number(p.price);
    }
    lo += COUPLER_ADD * couplers;
    hi += COUPLER_ADD * couplers;
    // Guard the coupler lookup: if the coupler product ever loses its metafield
    // or leaves the collection, COUPLER is undefined. A single-pipe build (zero
    // joints) is still valid; any candidate needing a coupler is discarded here
    // rather than dereferencing undefined mid-solve.
    if(couplers > 0){
      if(!COUPLER) return;
      price += couplers * Number(COUPLER.price);
    }
    // [A] TOL is a FIXED-STACK allowance only. A fixed stack prints its true
    // finished length, so landing within +/-TOL of the mast target is honest
    // rounding. An adjustable (telescoping) stack instead has to REACH the
    // target: with the TOL slack it was accepted up to half an inch outside its
    // own range and then promoted as "Cheapest adjustable option / recommended build" even
    // though the mast cannot extend to the customer's number (finHi below the
    // target, the card reading "0 in longer from your number"). So an adjustable
    // stack qualifies only when the target lies INSIDE [lo, hi], with a hair of
    // float slack. Fixed stacks (tele === null, lo === hi) keep TOL.
    if(tele){
      if(mastTarget < lo - MAST_EPS || mastTarget > hi + MAST_EPS) return;
    } else {
      if(mastTarget < lo - TOL || mastTarget > hi + TOL) return;   // fixed: rounding tolerance
    }
    if(hi < lo) return;
    const sig = pieces.map(p => p.sku).sort().join('+');
    if(seen.has(sig)) return; seen.add(sig);
    const sur = surchargeFor(pieces);
    const down = Math.max(0, Math.min(mastTarget, hi) - lo);     // travel shorter than target
    const up   = Math.max(0, hi - Math.max(mastTarget, lo));     // travel longer than target
    out.push({
      pieces, couplers, mastLo:lo, mastHi:hi, price,
      surcharge: sur, landed: price + sur,                       // _9: rank on landed cost
      width: hi - lo, down, up, useful: Math.min(down, up),
      smalls, tele,
      center: Math.abs(mastTarget - (lo+hi)/2),
      finLo: lo + pOff + hOff, finHi: hi + pOff + hOff,
      // Carried so leaveOffNote(b, pOff, hOff) can test each leave-a-pipe-off
      // sub-build against the customer's mast target without re-solving. Pure
      // scalar, ignored by every other consumer of the build object.
      mastTarget
    });
  }

  // [RULE] SIX-PIECE MAST BUDGET (owner ruling 2026-09-07). The original pipe length
  // calculator enumerates masts of 1 to MAX_PIECES (6) pieces; this solver descends
  // from the same _9 rules and now matches it. The 2026-07-12 prototype capped the
  // enumeration at 3 pieces and it was never reconciled with the _9 budget (found
  // 2026-09-07), so the deepest heads stopped reaching about 156 in. The four-piece
  // fallback added earlier the same evening is gone: this enumerates at ANY length,
  // never gated on "only when three fail", so a build carries as many joints as the
  // length needs and no more (consider() below is the single acceptance test - the
  // bracket rules, smalls rule, dedupe, surcharge and price all run there, unchanged).
  //
  // What is enumerated:
  //   (a) fixed-only stacks of 1 to MAX_PIECES pipes (n-1 couplers), and
  //   (b) one telescoping unit alone, plus that unit with 1 to MAX_PIECES-1 fixed
  //       pipes (k couplers).
  // FIXED is sorted ascending into a LOCAL copy (the FIXED array itself stays in pool
  // order, other code reads it) and the multisets are generated with a non-decreasing
  // index chain by the same recursion the original's forEachMultisetInSumRange uses,
  // so no permutation is produced twice.
  //
  // PRUNING INVARIANT (keeps the enumeration cheap). Because sortedFixed is ascending,
  // the smallest possible completion of a partial stack is "the current pipe repeated
  // for every remaining slot". A branch is dead the moment that smallest completion
  // plus the couplers overshoots the target: for a fixed-only stack, past
  // mastTarget + TOL (a fixed stack must land within the rounding tolerance); for a
  // telescoping kit, past mastTarget + MAST_EPS measured from the unit COLLAPSED
  // (tele.length_min), because if even the collapsed assembly is already longer than
  // the target it can never extend DOWN to bracket it.
  //
  // REACH. The pool's longest fixed pipe is NPT42 (NPT48 stays OUT, the UPS-oversize
  // exclusion), so reach comes from ANOTHER length of pipe, never a longer one. The
  // tallest mast the budget can build is EXT4472 (44 to 72) + 5x NPT42 + 5 couplers =
  // 72 + 210 + 7.5 = 289.5 in, so even the deepest head (33CE008HEAD -6 on 11CP005
  // 3.126, mastTarget = target + 2.874) reaches TARGET_MAX 200 (mastTarget 202.874).
  const sortedFixed = FIXED.slice().sort((a, b) => a.nominal_length - b.nominal_length);

  // Visit every multiset of exactly k fixed pipes (non-decreasing index chain over
  // sortedFixed), pruning a branch as soon as its smallest completion passes sumHi.
  // Ported from the original's forEachMultisetInSumRange: minFinal is this pipe
  // repeated for every remaining slot, and once that exceeds sumHi no later (larger)
  // pipe can pull it back, so the branch is abandoned. sumHi already has the base
  // offset and the couplers folded out by the caller, so it is a pure pipe-sum cap.
  function forEachFixedMultiset(k, sumHi, cb){
    const work = new Array(k);
    (function recurse(pos, start, sum){
      if(pos === k){ cb(work); return; }
      const remaining = k - pos;
      for(let i = start; i < sortedFixed.length; i++){
        const v = sortedFixed[i].nominal_length;
        if(sum + v + (remaining - 1) * v > sumHi) break;   // smallest completion overshoots
        work[pos] = sortedFixed[i];
        recurse(pos + 1, i, sum + v);
      }
    })(0, 0, 0);
  }

  // (a) Fixed-only stacks, 1 to MAX_PIECES pipes. n-1 couplers; the pipe-sum cap is
  // mastTarget + TOL minus the couplers (a fixed stack keeps the rounding tolerance).
  // tele stays null on these, so they route to the fixed-length card category.
  for(let n = 1; n <= MAX_PIECES; n++){
    forEachFixedMultiset(n, mastTarget + TOL - (n - 1) * COUPLER_ADD,
      pipes => consider(pipes.slice()));
  }

  // (b) One telescoping unit alone, then the unit plus 1 to MAX_PIECES-1 fixed pipes.
  // k couplers join the k fixed pipes to the tele. The cap is on the COLLAPSED
  // assembly: a pipe sum past mastTarget + MAST_EPS - tele.length_min - couplers
  // leaves the collapsed low end above the target, so it can never bracket downward.
  for(const t of TELE){
    consider([t]);                                  // single telescoping unit (preferred)
    for(let k = 1; k <= MAX_PIECES - 1; k++){
      forEachFixedMultiset(k, mastTarget + MAST_EPS - t.length_min - k * COUPLER_ADD,
        pipes => consider([t].concat(pipes)));
    }
  }

  return {mastTarget, out};
}

// _9 pool filters: small-pipe limit and the small-telescopic cutoff above 20 in,
// with the absolute-cheapest exception preserved from the prior art.
function filterBuilds(builds, mastTarget){
  if(!builds.length) return [];
  const cheapest = builds.slice().sort((a,b)=> a.landed-b.landed || a.pieces.length-b.pieces.length)[0];
  return builds.filter(b => {
    if(b === cheapest) return true;
    if(b.smalls > MAX_SMALL_PER_COMBO) return false;
    if(mastTarget > SMALL_ADJ_MAX_TARGET && b.tele && SMALL_TELES.indexOf(b.tele.sku) >= 0) return false;
    return true;
  });
}

// [RULE] HALF-INCH TRAVEL GRID (owner ruling 2026-09-07 night: "Yes 3.876 can
// count as 4. That is acceptable rounding."). The mast adjusts in 1 in
// increments and the whole tool works on a 0.5 in grid, so testing the flex
// floors on RAW travel throws a build out for missing a whole-inch floor by a
// fraction the customer can never set: 33HE001 on 11CP005 at a 40 in target has
// a 32.124 mast target, so EXT2036 alone has up 3.876 against its 4 in floor and
// loses Flexible by 0.124. Round each travel figure to the nearest 0.5 in before
// the floor test and before the 12 in balanced bar (3.876 -> 4.0 passes, 3.7 ->
// 3.5 fails). ONE helper serves qualifiesFlex and the balanced-bar test (and so
// the near-match qualification, which calls qualifiesFlex). The PRINTED travel
// numbers, the flex upgrade-gain arithmetic and the D6 headroom test
// (MIN_DOWN_HEADROOM) all stay on RAW travel, by ruling.
const travelGrid = v => Math.round(v * 2) / 2;

// _9 flexibility qualification: travel in BOTH directions must reach 25 percent
// of the telescopic's own range (1/2/4/7/7 in for the five units), measured on
// the half-inch grid (see travelGrid above).
function qualifiesFlex(b){
  if(!b.tele) return false;
  const m = MIN_TRAVEL_PER_TELE[b.tele.sku];
  return m != null && travelGrid(b.down) >= m - 1e-9 && travelGrid(b.up) >= m - 1e-9;
}

// D6 (owner ruling 2026-07-26): under a drop ceiling an adjustable build has to
// be able to extend at least this much FURTHER down, or it is not worth
// recommending as adjustable. b.up is travel longer than the target, in
// mast-length space, measured from the mastTarget and clamped at 0, so a fixed
// build (lo === hi) is up: 0 and the test is well formed.
const MIN_DOWN_HEADROOM = 1;

// Pick ranked, distinct builds using the _9 roles:
// Cheapest / Flexible (with the $5 upgrade rule) / Most Flexible (big telescopics,
// 12 in bar), all drawn from ADJUSTABLE builds only. Fixed-length builds are their
// own category, appended AFTER all adjustable options regardless of price.
//
// `opts` is THREADED IN, never read off the module state. That is deliberate:
// the solver stays a pure function of its arguments, so the self-tests can call
// computeBuildCards with three arguments and get today's unfiltered ranking
// regardless of what the customer has picked on screen. Do not "simplify" this
// by reading state.hasDropCeiling in here.
//   opts.requireDownHeadroom  true -> drop adjustable builds that cannot extend
//                             at least MIN_DOWN_HEADROOM further down (D6).
function rankBuilds(builds, mastTarget, opts){
  const fullPool = filterBuilds(builds, mastTarget);
  if(!fullPool.length) return [];
  const requireDown = !!(opts && opts.requireDownHeadroom);
  const adjustable = fullPool.filter(b => b.tele);  // adjustable builds: the ranked roles
  // D6 applies to the ADJUSTABLE pool only. fixedPool is untouched on purpose:
  // the fixed-length card stays on offer and carries its own plain warning that
  // it cannot be lowered once installed.
  const pool = requireDown ? adjustable.filter(b => b.up >= MIN_DOWN_HEADROOM) : adjustable;
  const fixedPool = fullPool.filter(b => !b.tele);  // fixed-length builds: own category
  const byCost = arr => arr.slice().sort((a,b)=> a.landed-b.landed || a.pieces.length-b.pieces.length);
  const picks = [];
  const add = (b, role, note) => { if(b) picks.push({b, role, note: note || ''}); };

  // Cheapest: lowest landed cost of any ADJUSTABLE build (never a fixed build).
  const cheapest = byCost(pool)[0];
  add(cheapest, 'cheapest');

  // Flexible: cheapest qualifying build, then the _9 upgrade rule: within $5 more,
  // take the build with the most extra travel in the tighter direction (min gain 2 in).
  let flexible = byCost(pool.filter(qualifiesFlex))[0] || null;
  let flexNote = '';
  if(flexible){
    let upgrade = null, bestUseful = flexible.useful;
    for(const c of pool){
      if(!qualifiesFlex(c) || c === flexible) continue;
      if(c.landed > flexible.landed + FLEX_UPGRADE_BUDGET) continue;
      if(c.useful - flexible.useful < FLEX_UPGRADE_MIN_GAIN) continue;
      if(c.useful > bestUseful || (c.useful === bestUseful && (!upgrade || c.landed < upgrade.landed))){
        upgrade = c; bestUseful = c.useful;
      }
    }
    if(upgrade){
      flexNote = 'Chosen over a build ' + money(upgrade.landed - flexible.landed) +
        ' cheaper because it adjusts ' + nice(upgrade.useful - flexible.useful) +
        ' in further in the tighter direction.';
      flexible = upgrade;
    }
  }
  add(flexible, 'flexible', flexNote);

  // Most Flexible: big telescopics only. Cheapest that clears 12 in both ways;
  // symmetry then piece count break ties. If none clears the bar, most useful travel wins.
  const bigs = pool.filter(b => b.tele && MOST_FLEX_TELES.indexOf(b.tele.sku) >= 0 && qualifiesFlex(b));
  let most = null;
  const balanced = bigs.filter(b => travelGrid(b.useful) >= MOST_FLEX_MIN_BALANCED);
  if(balanced.length){
    most = balanced.slice().sort((a,b)=>
      a.landed-b.landed ||
      Math.abs(a.down-a.up) - Math.abs(b.down-b.up) ||
      a.pieces.length - b.pieces.length)[0];
  } else if(bigs.length){
    most = bigs.slice().sort((a,b)=> b.useful-a.useful || a.landed-b.landed)[0];
  }
  add(most, 'mostflex');

  // Dedupe: one card per build, roles merged, order preserved.
  const uniq = [];
  for(const p of picks){
    const ex = uniq.find(u => u.b === p.b);
    if(ex){ ex.roles.push(p.role); if(p.note && !ex.note) ex.note = p.note; }
    else uniq.push({b:p.b, roles:[p.role], note:p.note});
  }
  const ranked = uniq.slice(0,3);

  // Cheaper near match (owner ruling 2026-09-07, ported from the original pipe length
  // calculator's renderSavingsCard). The cheapest big-telescopic build that still
  // qualifies on the 25 percent travel floors both ways but missed the Most adjustable
  // role only for falling short of the 12 in balanced bar, undercutting the Most
  // adjustable landed cost by at least NEAR_MATCH_MIN_SAVING. A customer's number is a
  // rough estimate, so it still covers them for noticeably less. Drawn from `pool` so
  // the drop-ceiling headroom filter still applies; skipped when most is null or when
  // nothing qualifies. Ties break to the more symmetric build (smaller |down - up|).
  // Sits AFTER the entry carrying the 'mostflex' role and BEFORE the fixed card.
  if(most){
    const inUniq = b => uniq.some(u => u.b === b);
    const nearMatch = pool
      .filter(b => b.tele && MOST_FLEX_TELES.indexOf(b.tele.sku) >= 0 && qualifiesFlex(b) &&
        b.landed <= most.landed - NEAR_MATCH_MIN_SAVING && !inUniq(b))
      .sort((a, b) => a.landed - b.landed || Math.abs(a.down - a.up) - Math.abs(b.down - b.up))[0];
    if(nearMatch){
      const note = 'Saves ' + money(most.landed - nearMatch.landed) +
        ' against the Most adjustable build. No need to be exact: it still adjusts ' +
        nice(nearMatch.down) + ' in shorter and ' + nice(nearMatch.up) + ' in longer from your number.';
      const at = ranked.findIndex(u => u.roles.indexOf('mostflex') >= 0);
      const entry = {b: nearMatch, roles: ['nearmatch'], note};
      if(at >= 0) ranked.splice(at + 1, 0, entry);
      else ranked.push(entry);
    }
  }

  // Fixed-length category: the best fixed build (cheapest landed, then closest to
  // the target, then fewest pieces), ALWAYS sorted after every adjustable option.
  const bestFixed = fixedPool.slice().sort((a,b)=>
    a.landed-b.landed || a.center-b.center || a.pieces.length-b.pieces.length)[0];
  if(bestFixed) ranked.push({b:bestFixed, roles:['fixed'], note:''});

  // D6 telltale: adjustable builds existed for this drop and the headroom rule
  // took every one of them. The UI has to SAY that, otherwise a lone fixed card
  // with no explanation reads as "this is all we sell". Carried as a property on
  // the returned array rather than as a new return shape, because three call
  // sites and two self-tests read this value as a plain list of picks.
  ranked.noDownHeadroom = requireDown && adjustable.length > 0 && pool.length === 0;

  return ranked;
}

// [RULE] LEAVE-A-PIPE-OFF NOTE (owner ruling 2026-09-07 night, the MIDDLE PATH:
// "I want the middle path with the note"). The original pipe length calculator
// credited a telescoping-plus-fixed kit with the UNION of every sub-build that
// still reaches the target, on the reasoning that a customer can leave fixed
// pipes off at install. The owner ruled the CARD KEEPS its full-assembly travel
// numbers (a kit is credited only with the full assembly it draws, which is what
// the elevation shows) and instead carries ONE extra note when leaving one or
// more fixed pipes off yields a SHORTER assembly that still contains the
// customer's mast target. His exact sentence shape, from his EXT3260 + NPT12 at a
// 50 in target example: "Leave the NPT12 off and its length is 32 to 60."
//
// Returns the note string, or null when there is nothing to say. Rendered on
// EVERY telescoping-kit card kind (cheapest, flexible, mostflex, nearmatch),
// NEVER on a fixed-only stack and NEVER on a lone telescoping unit.
//
// [WARN] "its" is the possessive, deliberately. The owner typed "it's" in his
// example; website-copy-voice.md defines its = possession, it's = it is, and the
// finished length belongs to the sub-build, so the copy reads "its length". The
// one-letter change was flagged to the owner; do not "correct" it back.
//
//   b     a build object from buildMasts (carries pieces, tele and mastTarget)
//   pOff  plate.length_offset, hOff head.length_offset - added to the mast range
//         so the printed numbers are FINISHED length, comparable to the card's
//         own range line (finLo/finHi), formatted through nice() to match the
//         owner's "32 to 60" (whole numbers print bare, fractions to one place).
function leaveOffNote(b, pOff, hOff){
  const tele = b.tele;
  if(!tele) return null;                                  // fixed-only stack: no note
  const fixed = b.pieces.filter(p => p.length_min == null);
  if(!fixed.length) return null;                          // lone telescoping unit: no note
  const mastTarget = b.mastTarget;
  const off = (pOff || 0) + (hOff || 0);
  let best = null;                                        // {lo, removed:[pieces]}
  // Every NON-EMPTY subset of the fixed pipes to REMOVE (by index, so repeats are
  // distinct instances). A sub-build is tele + the pipes kept; its range low end
  // is tele.length_min + kept pipe lengths + COUPLER_ADD per remaining coupler
  // (couplers = kept pipes, since one tele needs no coupler on its own), high end
  // the same with tele.length_max. It qualifies only when mastTarget lies inside
  // that range with the same MAST_EPS slack consider() uses. Among the qualifying
  // sub-builds pick the LOWEST low end (the most withdrawn assembly the customer
  // could still use), ties broken by the FEWEST pipes removed.
  const n = fixed.length;
  for(let mask = 1; mask < (1 << n); mask++){
    const removed = [], kept = [];
    for(let i = 0; i < n; i++){
      if(mask & (1 << i)) removed.push(fixed[i]);
      else kept.push(fixed[i]);
    }
    let sum = 0;
    for(const p of kept) sum += p.nominal_length;
    const couplers = kept.length;                         // tele + kept pipes: one coupler per kept pipe
    const lo = tele.length_min + sum + COUPLER_ADD * couplers;
    const hi = tele.length_max + sum + COUPLER_ADD * couplers;
    if(mastTarget < lo - MAST_EPS || mastTarget > hi + MAST_EPS) continue;
    if(!best || lo < best.lo - 1e-9 ||
       (Math.abs(lo - best.lo) < 1e-9 && removed.length < best.removed.length)){
      best = {lo, hi, removed};
    }
  }
  if(!best) return null;
  // Name the removed pipes the way the parts list names them: repeats collapse to
  // a "Nx SKU" count form, distinct pipes are joined "A", "A and B", "A, B and C".
  const order = [], counts = {};
  for(const p of best.removed){
    if(counts[p.sku] == null){ counts[p.sku] = 0; order.push(p.sku); }
    counts[p.sku]++;
  }
  const parts = order.map(sku => counts[sku] > 1 ? (counts[sku] + 'x ' + sku) : sku);
  const named = parts.length === 1 ? parts[0]
    : parts.length === 2 ? parts[0] + ' and ' + parts[1]
    : parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1];
  return 'Leave the ' + named + ' off and its length is ' +
    nice(best.lo + off) + ' to ' + nice(best.hi + off) + '.';
}

// Card label. No stock-variant comparison exists anymore (parts-only), so
// labeling is purely about the build's own role, not a delta to a stock SKU.
function roleLabel(roles){
  const r = roles[0];
  if(r === 'fixed') return 'Fixed length (no adjustment)';
  if(r === 'cheapest') return roles.length > 1 ? 'Cheapest, and adjustable both ways' : 'Cheapest adjustable option';
  if(r === 'flexible') return 'Flexible: adjusts both ways from your number';
  if(r === 'nearmatch') return 'Cheaper near match';
  return 'Most adjustable';
}

// Base parts for a build: the chosen plate and the chosen head, each a real
// standalone product now (no mount, no No-Mast variant, no custom-plate
// credit swap - that entire path died with the mount architecture).
function basePriceFor(head, plate){
  // url and image are forwarded so bomRowHTML can render the SKU link and the
  // photo preview; both come straight off the pooled DATA component.
  const lines = [
    {sku:plate.sku, id:plate.id, available:plate.available, backorder_enabled:plate.backorder_enabled, title:plate.title, price:Number(plate.price), url:plate.url, image:plate.image},
    {sku:head.sku,  id:head.id,  available:head.available,  backorder_enabled:head.backorder_enabled,  title:head.title, price:Number(head.price), url:head.url, image:head.image}
  ];
  return {lines, price: Number(plate.price) + Number(head.price)};
}

// Structured build cards: label + total. Used by both the renderer and the
// acceptance self-test.
//
// `opts` is optional and is passed straight through to rankBuilds. The self-
// tests call this with THREE arguments on purpose, so opts is undefined there
// and every ranking rule that hangs off it is inert. That is what keeps the
// existing suite an honest check on the untouched ranking.
function computeBuildCards(head, plate, target, opts){
  const {mastTarget, out} = buildMasts(head, plate, target);
  const picks = rankBuilds(out, mastTarget, opts);
  const base = basePriceFor(head, plate);
  const cards = picks.map(pk => {
    const b = pk.b;
    const isFixed = pk.roles[0] === 'fixed';
    const total = base.price + b.price;
    // Second, separate note channel (owner ruling 2026-09-07): on a telescoping
    // kit whose fixed pipes can be left off for a shorter still-reaching assembly.
    // Null on fixed-only and lone-tele builds, so the renderer skips it there.
    const leaveOff = leaveOffNote(b, plate.length_offset, head.length_offset);
    return {b, roles:pk.roles, note:pk.note, leaveOff, isFixed, total, label: roleLabel(pk.roles)};
  });
  return {base, cards, mastTarget, noDownHeadroom: !!picks.noDownHeadroom};
}

// =====================================================================
// CART (Phase 4: SPEC-configurator-cart-transform.md; parts-only rebuild
// drops the custom-plate-swap block on top of it, since every build is now
// real parts and there is nothing left to block on except availability)
// =====================================================================
// Combine duplicate SKUs in a mast build into one line per SKU with a
// summed quantity (a 3-pipe fixed stack can repeat the same fixed pipe).
function aggregateItems(pieces){
  const map = new Map();
  for(const p of pieces){
    const cur = map.get(p.sku);
    if(cur) cur.quantity += 1;
    else map.set(p.sku, {id:p.id, quantity:1});
  }
  return Array.from(map.values());
}

// True only when every real product this build needs is addable: has a
// numeric variant id and (per the spec's availability section) reports
// available. Everything reports available today (component inventory
// tracking is off); this still runs so the day tracking turns on, the UI
// reacts with zero changes.
function buildIsAddable(base, b){
  if(base.lines.some(l => !l.id || l.available === false)) return false;
  if(b.pieces.some(p => !p.id || p.available === false)) return false;
  if(b.couplers > 0 && (!COUPLER || !COUPLER.id || COUPLER.available === false)) return false;
  return true;
}

// Real cart items for one build card: the plate, the head, the mast pieces,
// and the coupler(s). Every build is self-checkoutable now (parts-only);
// the only block left is a genuinely unavailable part.
// [PAGE TOOLS 2026-09-11] Reserve gate. A build part flagged custom.backorder_enabled
// (reserve mode) is reserved from its OWN product page and must never enter the Shopify
// cart as part of a build. reservedPartTitle returns the title of the first reserved
// chosen part (plate, head, any mast pipe, coupler), or null. The pool components carry
// the boolean straight from snippets/vms-configurator-data.liquid; base.lines forward it
// from the plate and head. Configurator-pool parts are not normally reserved, so this
// almost always returns null.
function reservedPartTitle(base, b){
  const parts = [];
  (base.lines || []).forEach(l => parts.push(l));
  (b.pieces || []).forEach(p => parts.push(p));
  if(b.couplers > 0 && COUPLER) parts.push(COUPLER);
  for(const pt of parts){
    if(pt && pt.backorder_enabled === true) return pt.title || pt.sku || 'This part';
  }
  return null;
}

function buildCartRecipe(base, b){
  if(!buildIsAddable(base, b)){
    return {blocked:true, reason:'unavailable'};
  }
  // [PAGE TOOLS 2026-09-11] Reserve gate: checked BEFORE the /cart/add call, so nothing is
  // added when it fires. Both the per-card Add button (wireAddToCart, which shows
  // recipe.message) and the MCP add-to-cart tool (addToCart, which relays recipe.reason)
  // route through this recipe, so both refuse the same way and name the product.
  const reserved = reservedPartTitle(base, b);
  if(reserved){
    return {blocked:true, reason: '"' + reserved + '" is on backorder and can\'t be added here. Reserve it from its product page instead.',
      message: '"' + reserved + '" is on backorder and can\'t be added here. Reserve it from its product page instead.'};
  }
  // NB: the ORDER of these lines does not control how the build renders in the
  // cart - Shopify does not preserve /cart/add.js insertion order (it groups
  // newest-first and reverses within a batch). Assembly-order display (plate
  // top, mast middle, head bottom) is done at render time in the cart-grouping
  // loop (sections/main-cart-items.liquid + snippets/cart-drawer.liquid),
  // keyed on each line's custom.component_type metafield.
  const items = base.lines.map(l => ({id:l.id, quantity:1}));
  items.push(...aggregateItems(b.pieces));
  if(b.couplers > 0) items.push({id: COUPLER.id, quantity: b.couplers});
  return {blocked:false, items};
}

// Human-readable "Configuration" summary, same value on every component
// line of a build (see the spec). Deviation from the spec's own example:
// this store bans middots and em/en dashes anywhere in copy, so " | " is
// the separator here instead of the spec's middot.
// [B] The summary carries the BUILD's own finished length, never nice(target)
// (the customer's WISH). It used to write the target, so a fixed NPT12 card whose
// mast finishes at 19.5 in still put "Finished length: 20 in" on every cart line.
// A fixed stack prints its single finished length (finLo); an adjustable stack
// prints its "lo to hi" range. The app reads this as a plain display string, so
// the "Head: X | Plate: Y | Finished length: ..." shape stays byte-for-byte (the
// slanted wall page writes its own build figure into the same contract).
function configSummary(head, plate, b){
  const len = b.tele ? nice(b.finLo) + ' to ' + nice(b.finHi) : nice(b.finLo);
  return 'Head: ' + head.sku + ' | Plate: ' + plate.sku + ' | Finished length: ' + len + ' in';
}

// Fresh id per "Add to cart" click. One configId = one configured unit
// (spec rule): two clicks of the same selection make two separate groups
// in the cart rather than combining into one.
function newConfigId(){
  return (window.crypto && crypto.randomUUID) ? crypto.randomUUID()
    : 'vmscfg-' + Date.now() + '-' + Math.random().toString(16).slice(2);
}

// [BUILD PROPERTY 2026-09-06] A VISIBLE cart line property named "Build". Owner
// ask: line items from a build should "carry some custom text into the cart and
// order. A basic summary of the options chosen for the specific build", and since
// "a customer could order multiple custom builds" (on either build page) we must
// "tell which it is from". It rides ALONGSIDE the three hidden _vms_* properties,
// NEVER in place of them: _vms_config_summary stays the app contract byte for
// byte, and "Build" is a plain-language sentence Shopify prints natively in the
// cart, checkout, admin order and the notification emails. The property NAME is
// the constant word "Build" (no code in the name) so Liquid reads it as
// item.properties['Build'].
//
// buildCode(configId): a short human tag for one build, so two builds in one cart
// are told apart while every line of ONE build shares the same tag. First 6 hex
// characters of the config id with hyphens removed, uppercased (crypto.randomUUID
// ids); for the 'vmscfg-<ts>-<rand>' fallback id, the first 6 hex of the random
// tail. Deterministic per config id: same id in, same code out, so it is derived
// from the SAME id that becomes _vms_config_id on the line.
function buildCode(configId){
  var id = String(configId == null ? '' : configId);
  if(!id) return 'NOCODE';   // never open the Build value with a bare comma
  if(id.indexOf('vmscfg-') === 0){
    var parts = id.split('-');
    var rand = parts[parts.length - 1] || '';
    return rand.slice(0, 6).toUpperCase();
  }
  return id.replace(/-/g, '').slice(0, 6).toUpperCase();
}

// buildProperty(...): the visible "Build" value for one configurator build. Plain
// keyboard characters only, no pipes and no em/en dashes (the store's copy rule).
// Numbers are sourced from the build's OWN figures (b.finLo/b.finHi/b.pieces/
// b.couplers), the same variables the card and _vms_config_summary read, never
// recomputed; the TV size and tile depth come from the same accessors the rest of
// the UI uses. Shape (fixed / adjustable finished length; coupler clause dropped
// at zero; drop-ceiling clause only when a tile depth was entered):
//   <CODE>, ceiling mount configurator, <size or "no TV size given">, <head> head
//   on <plate> plate, <finished length> in finished length, mast <pipe + pipe>
//   with <n> coupler(s)[, drop ceiling <tile depth> in]
function buildProperty(configId, head, plate, b, tvSize, tileDepthIn){
  var code = buildCode(configId);
  // Defensive guards: a missing value must never print "undefined", "null",
  // "NaN" or "0". nice() here has NO null guard (unlike the slanted page's), so
  // finLo/finHi are number-checked BEFORE nice runs; every SKU falls back to the
  // word "unknown". The normal-case string is byte-identical to the guardless one.
  var numOk = v => (v != null && v !== '' && !isNaN(Number(v)));
  var skuOf = x => (x && x.sku != null && x.sku !== '') ? x.sku : 'unknown';
  var size = (numOk(tvSize) && Number(tvSize) > 0)
    ? nice(Number(tvSize)) + ' in TV' : 'no TV size given';
  var len = b.tele
    ? ((numOk(b.finLo) && numOk(b.finHi)) ? nice(b.finLo) + ' to ' + nice(b.finHi) : 'unknown')
    : (numOk(b.finLo) ? nice(b.finLo) : 'unknown');
  var mast = (b.pieces && b.pieces.length) ? b.pieces.map(skuOf).join(' + ') : 'unknown';
  var s = code + ', ceiling mount configurator, ' + size + ', '
    + skuOf(head) + ' head on ' + skuOf(plate) + ' plate, '
    + len + ' in finished length, mast ' + mast;
  if(b.couplers > 0) s += ' with ' + b.couplers + ' coupler(s)';
  if(numOk(tileDepthIn) && Number(tileDepthIn) > 0){
    s += ', drop ceiling ' + nice(Number(tileDepthIn)) + ' in';
  }
  return s;
}

// Pulled out of postCartAdd so the self-test can verify, without a network
// call, that every item in one build carries the SAME _vms_config_id and
// _vms_config_summary values (the mechanism the app's merge-or-grouped-cart-
// display depends on: every line of a build shares one _vms_config_id).
//
// [RULE] EACH LINE ALSO CARRIES ITS OWN _vms_unit_qty (added 2026-08-21), the
// count of that component in ONE unit of the build. It is what lets the cart
// page and the cart drawer offer a GROUP-LEVEL quantity stepper: group qty =
// line.quantity / _vms_unit_qty, and setting the group to N writes every line
// to _vms_unit_qty * N in one /cart/update.js call, so the component ratios
// hold by construction. It CANNOT be inferred from the cart afterwards - a
// build of 2 pipes and 4 brackets at group quantity 1 has a GCD of 2 and would
// read back as quantity 2 - so it is stored explicitly at add time and never
// derived. Underscore-prefixed like its siblings, so Dawn hides it from the
// customer's cart display.
//
// [WARN] _vms_config_id and _vms_config_summary are APP-OWNED (VMS Store
// Toolkit, SPEC-configurator-cart-transform.md): never rename them, never
// change the summary's " | " separator format. _vms_unit_qty is THEME-owned
// and additive - the app ignores properties it does not know, and the spec
// explicitly allows extra properties on component lines.
function buildPayloadItems(items, properties){
  return items.map(it => {
    const line = {id: it.id, quantity: it.quantity};
    if(properties){
      // A fresh object per line: same shared values, plus this line's own
      // per-unit count. Line item property values arrive back from Shopify as
      // strings either way, so send a string and keep the round trip honest.
      line.properties = Object.assign({}, properties, {_vms_unit_qty: String(it.quantity)});
    }
    return line;
  });
}

// A multi-item /cart/add.js call is atomic on Shopify's side: if any line
// fails (missing/unpublished variant) none of them are added, which is what
// keeps this "never a silent partial add" without extra client-side
// rollback logic.
async function postCartAdd(items, properties){
  const payloadItems = buildPayloadItems(items, properties);
  const url = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
  const res = await fetch(url, {
    method: 'POST',
    headers: {'Content-Type':'application/json', 'Accept':'application/json'},
    body: JSON.stringify({items: payloadItems})
  });
  let data = null;
  try { data = await res.json(); } catch(e){ /* non-JSON error body */ }
  if(!res.ok || (data && data.status)){
    const msg = (data && (data.description || data.message)) || ('Add to cart failed (status ' + res.status + ').');
    throw new Error(msg);
  }
  return data;
}

function setCartStatus(box, msg, isError){
  if(!box) return;
  box.textContent = msg || '';
  box.classList.toggle('vmscfg-cart-error', !!isError);
}

// The loading spinner on an Add-to-cart button.
//
// [RULE] THE SPINNER IS APPENDED, THE LABEL IS NEVER REPLACED. A loading button
// has to show that it is working AND keep saying what it does; swapping the
// label for "Adding..." loses the second half. It is a bare aria-hidden span
// because the button's accessible name comes from its own aria-label and the
// role=status line beside it is what actually announces progress, so a screen
// reader must not meet this element at all.
//
// [WARN] IT IS NOT AN EMPTY DIV, AND THAT IS DELIBERATE. Dawn's base.css carries
// `div:empty { display: none }` (see the README warning that cost a session on
// the elevation scene). A span is not in that selector list, which is the only
// reason a CSS-drawn ring with no content renders here at all.
function startBtnLoading(btn){
  if(!btn || btn.querySelector('.vmscfg-spinner')) return;
  btn.classList.add('is-loading');
  const spin = document.createElement('span');
  spin.className = 'vmscfg-spinner';
  spin.setAttribute('aria-hidden', 'true');
  btn.appendChild(spin);
}

function stopBtnLoading(btn){
  if(!btn) return;
  btn.classList.remove('is-loading');
  const spin = btn.querySelector('.vmscfg-spinner');
  if(spin) spin.remove();
}

// Wires one "Add to cart" button. recipeFn is called at click time (not
// render time) so it always reflects current state. On success the
// customer goes to /cart, which is the one view that correctly shows every
// line of a multi-component build at once (this theme's cart-notification
// component is built for a single added item). Errors are always shown
// next to the button, never silent.
//
// onAdded (optional) runs ONLY after postCartAdd has resolved without throwing,
// i.e. only when the build is genuinely in the cart. It is the analytics hook
// (see trackAddBuild below); it is called in its own try/catch so that even a
// hook with a bug cannot turn a successful add into a visible error.
function wireAddToCart(btn, statusBox, recipeFn, properties, onAdded){
  if(!btn) return;
  btn.addEventListener('click', async () => {
    const recipe = recipeFn();
    if(recipe.blocked){
      // [PAGE TOOLS 2026-09-11] The reserve gate returns a customer-safe message naming the
      // product; show it the same way the availability refusal shows its notice.
      setCartStatus(statusBox, recipe.message || fill(COPY('cart_unavailable'), {phone: PHONE_TEXT}), true);
      return;
    }
    btn.disabled = true;
    startBtnLoading(btn);
    setCartStatus(statusBox, COPY('cart_adding'), false);
    try {
      await postCartAdd(recipe.items, properties ? properties() : null);
      if(typeof onAdded === 'function'){ try { onAdded(); } catch(e){} }
      setCartStatus(statusBox, COPY('cart_added'), false);
      // The spinner is deliberately LEFT SPINNING through the navigation: the
      // work is not finished when the fetch resolves, the cart page still has to
      // load, and clearing it here would show a settled idle button for the
      // second or so before the page changes under it.
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    } catch(err){
      btn.disabled = false;
      stopBtnLoading(btn);
      setCartStatus(statusBox, err.message || fill(COPY('cart_error'), {phone: PHONE_TEXT}), true);
    }
  });
}

// =====================================================================
// ANALYTICS (2026-07-29, owner-approved: "You are go for analytics build";
// the ruling is in LAUNCH-PLAN-configurator-2026-07-22.md / README)
// =====================================================================
// EXACTLY THREE event families leave this page, and no more:
//   vms_configurator_step_completed     one per step transition into its
//                                       finished state (size, plate, head,
//                                       build ready)
//   vms_configurator_add_build          one per SUCCESSFUL /cart/add.js
//   vms_configurator_deeplink_arrival   one per page load off a shared link
//
// These are published through Shopify's customer-events API, so the GA4
// forwarding is NOT in this file. A CUSTOM PIXEL (admin: Settings > Customer
// events) subscribes to these three names and forwards them to GA4
// G-5QE1PXHCZ8. The pixel source and its install steps live in
// ANALYTICS-CONFIGURATOR-PIXEL-2026-07-29.md in the repo root. Adding an event
// here without adding it there publishes into silence; renaming one here
// without renaming it there kills a live subscription. Keep the two in step.
//
// [RULE] ANALYTICS MAY NEVER BREAK THE CONFIGURATOR. Every publish goes through
// vmsPublish(), which checks the API exists AND swallows any throw. A missing
// Shopify object, a pixel that never loaded, a payload that will not serialise:
// each comes back as a silent no-op, and the page behaves exactly as it does
// with analytics switched off entirely. There is no analytics code anywhere in
// this file outside these helpers and their four call sites.
//
// [RULE] THE DIAG HARNESS PUBLISHES NOTHING. ?vmscfg_diag=1 runs the 13 in-page
// self-tests, which construct builds, solve masts and drive states no customer
// ever chose; letting those reach GA4 would put synthetic funnels in the
// owner's reports. The gate is the SAME location.search test the diag mode
// itself uses (see runChecks, the diag wrapper reveal and the __VMSCFG__
// export), read at call time so it cannot drift away from it. syncURL()
// deliberately preserves ?vmscfg_diag=1 through its rewrites, so this stays
// true for the whole session, not just the first paint.
function vmsAnalyticsSuppressed(){
  return /[?&]vmscfg_diag=1/.test(location.search);
}

function vmsPublish(name, payload){
  try {
    if(vmsAnalyticsSuppressed()) return;
    if(!(window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function')) return;
    Shopify.analytics.publish(name, payload);
  } catch(e){ /* analytics is never allowed to take the page down */ }
}

// ---- 1. Step completion -------------------------------------------------
// [RULE] KEYED BY THE COMPLETING VALUE, NOT BY THE STATE STRING. A step fires
// when the value that completes it CHANGES. Opening a finished step with the
// Change control and closing it again unchanged is therefore silent (the plate
// never moved), while actually swapping the plate is a new completion and does
// fire. Clearing a step - a committed size that invalidates the chosen head -
// resets that step's key to null, so re-picking even the SAME head afterwards
// is a legitimate new fire. Nothing here counts keystrokes: state.tvSize is
// read only on the committed path, because sizeCommitted() is what stepStates()
// itself derives from.
//
// Step 4 has no choice of its own. stepComplete('s3') is already hardcoded true
// in the disclosure model ("reachable means finished"), so its completion IS
// becoming reachable, which is exactly the state in which a build can be added.
// Its dedupe key is therefore a flag and the length rides in the payload.
const STEP_EVENT_NAMES = {s1: 'tv_size', s2: 'ceiling_plate', s2b: 'mounting_head', s3: 'build_ready'};
const STEP_EVENT_NUMBERS = {s1: 1, s2: 2, s2b: 3, s3: 4};
const STEP_EVENT_IDS = ['s1', 's2', 's2b', 's3'];
let stepEventKeys = {s1: null, s2: null, s2b: null, s3: null};
let analyticsArmed = false;

function stepEventKeysNow(st){
  return {
    s1:  sizeCommitted() ? String(state.tvSize) : null,
    s2:  state.plateSku || null,
    s2b: state.headSku || null,
    s3:  (st && st.s3 !== 'locked') ? 'ready' : null
  };
}

function stepEventValue(id){
  if(id === 's1')  return sizeCommitted() ? String(state.tvSize) : '';
  if(id === 's2')  return state.plateSku || '';
  if(id === 's2b') return state.headSku || '';
  return state.target == null ? '' : String(state.target);
}

// Called from applyStepStates(), which is the SINGLE writer of every step's
// open/closed/locked DOM, so there is no completion route that can bypass this
// and no second place to keep in sync. It is idempotent: running it twice in
// one pass publishes once, because the first run has already stored the key.
//
// Before the boot pass arms it, it only SEEDS. A restored deep link is an
// arrival, not four step completions the customer performed, and the arrival
// event is what records it.
function trackStepStates(st){
  const now = stepEventKeysNow(st);
  if(!analyticsArmed){ stepEventKeys = now; return; }
  STEP_EVENT_IDS.forEach(id => {
    const k = now[id];
    if(k != null && k !== stepEventKeys[id]){
      // [WARN] THE FIELD IS step_value, NOT value. `value` is a RESERVED GA4
      // parameter and GA4 expects it to be a currency amount; handing it a plate
      // SKU is how a dimension silently arrives empty in the reports. The name
      // is the same on both sides of the wire on purpose, so nothing has to be
      // renamed in the pixel.
      vmsPublish('vms_configurator_step_completed', {
        step: STEP_EVENT_NUMBERS[id],
        step_name: STEP_EVENT_NAMES[id],
        step_value: stepEventValue(id)
      });
    }
    stepEventKeys[id] = k;
  });
}

// ---- 2. Add this build --------------------------------------------------
// Fires from wireAddToCart's success path ONLY, after postCartAdd has resolved
// without throwing. A click on a blocked build, a failed fetch and a Shopify
// error body all leave this unfired, which is the whole point: the event means
// a build is IN THE CART, not that somebody tried to put one there. It also
// runs before the navigation to /cart, so the publish happens while this
// document is still alive.
function trackAddBuild(head, plate, target, card){
  const b = card && card.b;
  if(!head || !plate || !b) return;
  const mast = b.pieces.map(p => p.sku);
  if(b.couplers > 0 && COUPLER) mast.push(COUPLER.sku + ' x' + b.couplers);
  vmsPublish('vms_configurator_add_build', {
    plate_sku: plate.sku,
    head_sku: head.sku,
    mast_skus: mast.join(', '),
    length_in: target == null ? null : Number(target),
    total_price: Number(card.total)
  });
}

// ---- 3. Deep-link arrival -----------------------------------------------
// One publish per page load, from the boot path immediately after
// readURLState(), and only when the link actually carried build state. It runs
// BEFORE syncURL() has had any chance to rewrite the query string, so what it
// reports is what the customer genuinely arrived on. dc and tiles are
// deliberately NOT counted: the ruling names size/plate/head/target, and a bare
// ?dc=0 is not a shared build.
const DEEPLINK_PARAMS = ['size', 'plate', 'head', 'target'];
function trackDeepLinkArrival(){
  const p = new URLSearchParams(location.search);
  const present = DEEPLINK_PARAMS.filter(k => {
    const v = p.get(k);
    return v != null && v !== '';
  });
  if(!present.length) return;
  vmsPublish('vms_configurator_deeplink_arrival', {
    params: present.join(','),
    param_count: present.length
  });
}

// The boot hook, called once from the initial paint. Seeds the step keys from
// whatever the URL restored WITHOUT firing (see trackStepStates), reports the
// arrival, then arms the step tracker so that everything after this point is a
// real transition the customer made in front of us.
function startConfiguratorAnalytics(){
  stepEventKeys = stepEventKeysNow(stepStates());
  analyticsArmed = true;
  trackDeepLinkArrival();
}

// =====================================================================
// URL STATE (kept lightweight; see header contract)
// =====================================================================
function readURLState(){
  const p = new URLSearchParams(location.search);
  const size = p.get('size'), plate = p.get('plate'), head = p.get('head'), target = p.get('target');
  const dc = p.get('dc'), tiles = p.get('tiles');
  if(size != null && size !== '' && !isNaN(Number(size))) state.tvSize = Number(size);

  // Drop-ceiling state joins the URL as of 2026-07-26, so a DRAGGED result is
  // shareable and survives a refresh. Everything here is Number()'d or turned
  // into a real boolean before it can reach VMSElevation.
  // [WARN] VMSElevation's internal num() REJECTS NUMERIC STRINGS rather than
  // erroring: minDrop({hasDropCeiling:true, tileDepth:'18', tvSize:'70'}) does
  // not throw, it silently downgrades to the weaker belowTiles rule and the
  // clamp comes out 17 in too shallow. A URLSearchParams value is ALWAYS a
  // string. This is the single most likely way to break the clamp.
  if(dc === '1' || dc === '0') state.dcTouched = true;
  if(dc === '1') state.hasDropCeiling = true;
  else if(dc === '0') state.hasDropCeiling = false;
  if(tiles != null && tiles !== '' && !isNaN(Number(tiles)) && Number(tiles) >= 0){
    state.case3Dist = Number(tiles);
  }
  // Back-compatibility, and it is a real case: links shared before the yes/no
  // question existed carry a tile depth and no answer. Entering a tile depth has
  // always BEEN the declaration that there is a drop ceiling (the field only
  // ever lived inside the "Attaching to a drop ceiling?" panel), so read it that
  // way. Only ever upgrades the unasked null, never overrides an explicit "no".
  if(state.hasDropCeiling == null && state.case3Dist != null){ state.hasDropCeiling = true; state.dcTouched = true; }
  // A tile depth with an explicit "no" is contradictory. Trust the answer.
  if(state.hasDropCeiling === false) state.case3Dist = null;

  // Deriving the plate TYPE from the restored SKU is what makes a shared link
  // land in a coherent state. Before the 2026-07-26 restructure a link like
  // ?plate=TCA-1 restored the plate but left the tier unselected, so step 2
  // showed no selection and an empty plate list while steps 3 and 4 were open.
  if(plate && getPlate(plate)){
    state.plateSku = plate;
    const pt = plateTypeOfSku(plate);
    if(pt) state.plateTypeId = pt.id;
  }
  // The restored head must pass the same TV-size gate the UI enforces,
  // else a shared link like ?size=20&head=33HE001 lands in a contradictory
  // state (a "no head fits" message beside addable build cards).
  if(head && getHead(head) && headScreenOK(getHead(head), state.tvSize)) state.headSku = head;
  if(state.plateSku && state.headSku && target != null && target !== '' && !isNaN(Number(target))){
    state.target = Number(target);
  }
}
// Trailing-debounced: syncURL runs from the render path on every keystroke in
// the size/target inputs. Safari rate-limits history.replaceState and then
// SILENTLY stops applying it (same bug + fix documented in
// snippets/vms-pipe-configurator.liquid). In-memory state and the render stay
// instant; only the URL write waits ~400 ms for a typing pause. The URL string
// is built at call time so it always reflects current state.
let urlSyncTimer = null;
// [MCP 2026-09-04] The share URL is now built by a PURE helper, shareURL(), which
// has no side effects and returns the string. syncURL() calls it and does the
// debounced history write; getBuild() (the public API) calls it to report the
// same link an agent can hand back. Splitting it is what lets the snapshot carry
// share_url without duplicating the param rules (and drifting from them). The
// param decisions below are unchanged; only their home moved out of syncURL.
function shareURL(){
  const p = new URLSearchParams();
  // syncURL builds the query from scratch, which silently ATE ?vmscfg_diag=1 on
  // the first paint: the self-test still ran that load, but reloading from the
  // rewritten URL dropped the diag panel, which is a trap for anyone verifying
  // this page. Carried through explicitly (never invented, only preserved).
  if(/[?&]vmscfg_diag=1/.test(location.search)) p.set('vmscfg_diag', '1');
  if(state.tvSize != null) p.set('size', state.tvSize);
  if(state.plateSku) p.set('plate', state.plateSku);
  if(state.headSku) p.set('head', state.headSku);
  // [RULE] THE SEEDED DEFAULT IS NOT PART OF THE LINK. Writing target=30 on a
  // fresh open would make every visit look like a shared build: the URL would say
  // the customer chose a length before they had touched anything, and
  // trackDeepLinkArrival counts `target` as one of the four params that MAKE a
  // deep link. The flag clears the moment they act, and the very next syncURL
  // (renderSummary calls it) writes the value. See TARGET_DEFAULT.
  if(state.target != null && !targetIsDefault) p.set('target', state.target);
  // The drop-ceiling answer and the tile depth are part of the answer now: they
  // move the clamp, so a link without them can restore a drop the live rules
  // would refuse. Null (not asked) writes nothing, which is how a link stays
  // silent about a question the customer never saw.
  if(state.hasDropCeiling === true) p.set('dc', '1');
  else if(state.hasDropCeiling === false && state.dcTouched) p.set('dc', '0');
  // Through tileDepth(), which carries the drop-ceiling gate AND the cap. syncURL
  // is debounced 400 ms and runs from the render path, so a typing pause writes the
  // link: without the clamp a link copied mid-typing could carry a depth past the
  // cap, and the boot path would then have to clamp it back on arrival, which is a
  // shared link that does not restore what it says. See tileDepth().
  // [RULE] AND THE SEEDED DEPTH IS NOT PART OF THE LINK EITHER (2026-07-31), for the
  // reason target=30 is not: a fresh answer of "yes, suspended tiles" would otherwise
  // put tiles=12 in the URL and make every such visit look like a shared build. The
  // link stays faithful because the receiving end seeds the same 12 off dc=1, so what
  // it restores is exactly what the sender was looking at. See TILES_DEFAULT.
  const tileURL = tileDepth();
  if(tileURL != null && !tilesIsDefault) p.set('tiles', tileURL);
  tvURLParams(p, tvActive());
  const qs = p.toString();
  return location.pathname + (qs ? ('?' + qs) : '');
}
function syncURL(){
  const url = shareURL();
  clearTimeout(urlSyncTimer);
  urlSyncTimer = setTimeout(() => {
    try { history.replaceState(null, '', url); } catch(e){}
  }, 400);
}

// =====================================================================
// RENDER
// =====================================================================
function el(id){ return document.getElementById(id); }

// Move keyboard focus to a freshly revealed stage heading. Called ONLY from a
// direct user action (committing a size opens #s2, picking a plate opens #s2b,
// picking a head opens #s3) so focus never jumps on a background re-render or a
// live price update (S1).
//
// It targets the heading BUTTON since the 2026-07-28 disclosure redesign, not
// the <h2>. The button is the disclosure control, so landing there announces the
// heading text, the step's expanded state and a thing the customer can act on,
// where a tabindex="-1" heading announced text and dead-ended.
//
// `opts` is passed straight to focus(), and the only caller that uses it passes
// {preventScroll:true} because it does its own scroll a frame later. Browsers
// that do not know the option ignore it and scroll as before.
function focusStageHeading(stageId, opts){
  if(!stageId) return;
  const stage = el(stageId);
  if(!stage) return;
  const b = stage.querySelector('.vmscfg-steph');
  if(b && !b.disabled){ b.focus(opts); return; }
  const h = stage.querySelector('h2');
  if(h && h.hasAttribute('tabindex')) h.focus(opts);
}

// =====================================================================
// THE FOUR-STEP DISCLOSURE (2026-07-28, owner-ordered interaction redesign)
// =====================================================================
// See the file header for the model and for the D2 reversal. In short: all four
// steps are always on the page, each is locked / open / done, and the state is
// DERIVED from the selections every time rather than accumulated.
const STEP_DEFS = [
  {id: 's1',  body: 's1body',  btn: 's1btn',  pick: 's1pick',  act: 's1act',  hint: null},
  {id: 's2',  body: 's2body',  btn: 's2btn',  pick: 's2pick',  act: 's2act',  hint: 's2hint'},
  {id: 's2b', body: 's2bbody', btn: 's2bbtn', pick: 's2bpick', act: 's2bact', hint: 's2bhint'},
  {id: 's3',  body: 's3body',  btn: 's3btn',  pick: 's3pick',  act: 's3act',  hint: 's3hint'}
];

// The range a committed size has to land in. It is WIDER than the dropdown the
// section renders (10 to 90), and deliberately: 120 was the number input's max
// for as long as this page has existed, so shared links out there carry sizes
// above 90 and ensureSizeOption puts those back on the list rather than clamping
// them. Committed still has to mean in range as well as present: unlocking step 2
// on a size no head could ever fit would be a cascade that leads nowhere.
const TV_SIZE_MIN = 10, TV_SIZE_MAX = 120;

function sizeCommitted(){
  const v = state.tvSize;
  return v != null && !isNaN(v) && v >= TV_SIZE_MIN && v <= TV_SIZE_MAX;
}

// The customer's own open/close clicks, id -> true (open) or false (closed).
// CLEARED on every cascade, because a cascade is the flow telling them where to
// be next and a stale override would fight it. Only a step that is FINISHED can
// be manually closed: closing an unfinished step would hide the thing it is
// asking for and leave a heading that explains nothing.
let stepManual = {};

function stepComplete(id){
  if(id === 's1') return sizeCommitted();
  if(id === 's2') return !!state.plateSku;
  if(id === 's2b') return !!state.headSku;
  return true;   // step 4 has no choice of its own: reachable means finished
}

function stepStates(){
  const ok = sizeCommitted();
  const st = {
    s1:  ok ? 'done' : 'open',
    s2:  !ok ? 'locked' : (state.plateSku ? 'done' : 'open'),
    s2b: (!ok || !state.plateSku) ? 'locked' : (state.headSku ? 'done' : 'open'),
    s3:  (!ok || !state.plateSku || !state.headSku) ? 'locked' : 'open'
  };
  Object.keys(st).forEach(id => {
    if(st[id] === 'locked') return;        // locked wins: there is nothing to open
    const m = stepManual[id];
    if(m === true) st[id] = 'open';
    else if(m === false && stepComplete(id)) st[id] = 'done';
  });
  return st;
}

// True while a step is part of the live build, whether or not its body happens
// to be open. Replaces the old `!el('s3').classList.contains('hidden')` test,
// which conflated "reachable" with "visible" and would now skip the drop
// pipeline any time the customer had simply collapsed step 4 to see the page.
function stepReachable(id){ return stepStates()[id] !== 'locked'; }

// What the DOM is currently showing, so a transition can be detected. Seeded
// from the section's own server-rendered state (step 1 open, the rest locked).
let lastStepStates = {s1: 'open', s2: 'locked', s2b: 'locked', s3: 'locked'};
let applyingSteps = false;

function setHidden(node, hide){
  if(!node) return;
  if(hide) node.setAttribute('hidden', ''); else node.removeAttribute('hidden');
}

// The chosen-summary on a finished step's heading: the product photo and the
// owner's template for its name. Rendered into a <span> inside the header
// button, so it becomes part of that button's accessible name and a screen
// reader hears the step, the choice and the Change word as one control.
function setPick(id, html){
  const n = el(id);
  if(!n) return;
  n.innerHTML = html || '';
  setHidden(n, !html);
}

// `st` is the state map applyStepStates has just written to the DOM, passed in
// rather than re-derived so the summaries can never describe a different pass
// than the classes beside them.
function renderStepPicks(st){
  // A STEP THAT IS OPEN SHOWS NO SUMMARY (owner ask 2026-08-21, and it restores
  // the stated intent: the stylesheet calls this "the chosen-summary on a
  // FINISHED step" and always meant it, but nothing had ever gated it on the
  // state, so it rendered while open too). Open, the control below the heading
  // already shows the answer - the size row highlights the chosen preset AND
  // repeats it in the dropdown, so "65 inch TV" on the heading was the third
  // copy of the same number on one screen. Collapsed, the summary IS how the
  // customer sees what they picked, so it must keep rendering there.
  const shown = id => st && st[id] !== 'open';
  setPick('s1pick', shown('s1') && sizeCommitted()
    ? `<span class="steph__pt">${fill(COPY('step_pick_size'), {tv: nice(Number(state.tvSize))})}</span>`
    : '');
  const s1pt = shown('s1') && sizeCommitted() ? document.querySelector('#s1pick .steph__pt') : null;
  const s1sx = s1pt ? tvPickSuffix(tvActive()) : '';
  if(s1sx) s1pt.appendChild(document.createTextNode(s1sx));
  const p = state.plateSku ? getPlate(state.plateSku) : null;
  setPick('s2pick', shown('s2') && p
    ? thumbHTML(p, 'steph__img') +
      `<span class="steph__pt">${fill(COPY('step_pick_plate'), {sku: p.sku, name: p.title})}</span>`
    : '');
  const h = state.headSku ? getHead(state.headSku) : null;
  setPick('s2bpick', shown('s2b') && h
    ? thumbHTML(h, 'steph__img') +
      `<span class="steph__pt">${fill(COPY('step_pick_head'), {sku: h.sku, name: h.title})}</span>`
    : '');
  // #s3pick exists for symmetry and stays empty: step 4 IS the answer, so there
  // is nothing to summarise onto its heading.
}

// ---- [UX R1 2026-09-21] the four segment progress rail (proposals item 3) ----
// The rail above the step stack. Same shape as renderStepPicks: it is handed the
// map applyStepStates has just written rather than re-deriving one, so the bar
// can never describe a different pass than the cards under it. That is also the
// whole reason a reopened step, and the later steps that reset with it, need no
// code of their own here.
//
// The three state words are read off data attributes on the <ol> (a script
// cannot call a schema setting), ONCE: they are section markup and cannot change
// after first paint. A missing rail, a missing segment or a missing attribute
// each fall through to the shipped English, exactly like the copy layer above.
let railWords = null;
function railStateWords(){
  if(railWords) return railWords;
  const r = el('vmscfgRail');
  const g = (attr, fallback) => {
    const v = r ? r.getAttribute(attr) : null;
    return (typeof v === 'string' && v.trim() !== '') ? v : fallback;
  };
  railWords = {
    done: g('data-state-done', 'done'),
    open: g('data-state-current', 'current step'),
    locked: g('data-state-todo', 'not started')
  };
  return railWords;
}

// [UX R1 FIX 2026-09-21, owner: "length never shows completed in the status bar,
// after changing from default 30 inch number by the +- or entering a number or
// dragging"] Step 4 has no 'done' of its own in stepStates() (reachable means
// finished, and it stays open), so on the step map alone the Length segment
// could never fill. lengthDone is the rail's own answer for that one segment:
// the customer has SET a length (not the seeded default) and that length makes
// a valid build. It is passed IN by the two callers (railLengthDone()) so this
// function stays a pure writer of what it is given. It only ever upgrades an
// 'open' step 4; a locked step 4 reads exactly as before.
function railLengthDone(){
  return !targetIsDefault && state.target != null && !isNaN(state.target) && !!state._price;
}
function renderRail(st, lengthDone){
  const rail = el('vmscfgRail');
  if(!rail || !st) return;
  if(lengthDone && st.s3 === 'open') st = Object.assign({}, st, {s3: 'done'});
  const words = railStateWords();
  // aria-current="step" marks ONE segment. Two steps can legitimately read
  // 'open' at the same time (reopen step 2 on a finished build and step 4 is
  // still open behind it), so the marker goes on the FIRST open one in step
  // order, which is the same "where the customer is" that firstOpenStep() uses
  // for focus. Both open segments still take the outline look, because both
  // really are open and the rail must not claim otherwise.
  const first = STEP_DEFS.find(d => st[d.id] === 'open');
  STEP_DEFS.forEach(d => {
    const seg = rail.querySelector('[data-rail-step="' + d.id + '"]');
    if(!seg) return;
    const s = st[d.id];
    seg.classList.toggle('is-done', s === 'done');
    seg.classList.toggle('is-current', s === 'open');
    seg.classList.toggle('is-todo', s === 'locked');
    if(first && d.id === first.id) seg.setAttribute('aria-current', 'step');
    else seg.removeAttribute('aria-current');
    const w = seg.querySelector('[data-rail-state]');
    // The comma is ours, not the owner's: it is what makes a screen reader say
    // "Plate, done" instead of running the two words together.
    if(w) w.textContent = ', ' + (words[s] || words.locked);
    // [UX R2 2026-09-21] The segment is a jump button now (proposals item 5), so
    // the single writer owns its affordance too: a locked step cannot be tapped,
    // and it ships disabled from the section so the first paint never lies.
    const btn = seg.querySelector('[data-rail-jump]');
    if(btn) btn.disabled = s === 'locked';
  });
}

// The single writer of every step's open/closed/locked DOM. Idempotent, so any
// number of callers can run it in one pass without the result depending on the
// order they ran in.
function applyStepStates(){
  if(applyingSteps) return lastStepStates;   // renderStage3 below can call back in
  applyingSteps = true;
  try {
    const st = stepStates();
    STEP_DEFS.forEach(d => {
      const card = el(d.id);
      if(!card) return;
      const s = st[d.id], open = s === 'open';
      card.classList.toggle('is-open', open);
      card.classList.toggle('is-done', s === 'done');
      card.classList.toggle('is-locked', s === 'locked');
      setHidden(el(d.body), !open);
      const btn = el(d.btn);
      if(btn){
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.disabled = s === 'locked';
      }
      setHidden(el(d.hint), s !== 'locked');
      setHidden(el(d.act), s !== 'done');
    });
    // AFTER the class writes above, and fed the same `st`: the summaries are
    // gated on open/closed, so they have to agree with the state just applied.
    renderStepPicks(st);
    // [UX R1 2026-09-21] The progress rail rides here for the same reason: this
    // is the single writer of step state, so the rail is fed the one map the
    // cards were just written from.
    renderRail(st, railLengthDone());
    // The required-size notice is the standing instruction, not an error, so it
    // is up from the first paint and comes down when the size is committed.
    // [WARN] THE ELEMENT IS OPTIONAL SINCE 2026-07-29. The owner blanked its
    // setting, and the section renders NO #tvSizeReq node when the field is empty,
    // so this call usually hands setHidden a null. That is exactly why setHidden
    // and el() are null-safe and why this line stays: type a sentence back into
    // the setting and the toggle is already wired.
    setHidden(el('tvSizeReq'), sizeCommitted());
    // The preset row rides here for the same reason everything else does: this is
    // the single writer, so a size that arrives from a preset, the dropdown, a
    // restored link or the Change control all reflect through one call. It is a
    // pure attribute sync, so running it on an idle pass costs nothing.
    syncSizePresets();
    const reopened = st.s3 === 'open' && lastStepStates.s3 !== 'open';
    lastStepStates = st;
    // The one analytics hook on the step flow. It sits here because this
    // function is the single writer of every step's state, so every completion
    // route (a size commit, a plate pick, a head pick, a compare-modal Choose,
    // a URL restore and the Change control) arrives through it exactly once.
    // It publishes nothing until the boot pass arms it, and it is keyed by the
    // completing value, so this cannot double-fire on a repeat pass.
    trackStepStates(st);
    // [WARN] STEP 4 COMING BACK NEEDS A REFRESH, and it is not optional. While it
    // was collapsed the scene sat at display:none, so it measured zero width and
    // its px-per-inch is meaningless; and if it was never open before, the scene
    // has not been mounted at all (mountElevScene refuses to mount into a box
    // with no rectangle, precisely so a collapsed step 4 cannot latch a zero
    // scale). renderStage3 mounts if needed and re-runs the layout either way.
    if(reopened && state.plateSku && state.headSku) renderStage3();
    return st;
  } finally {
    applyingSteps = false;
  }
}

// The heading rows are the disclosure controls AND the Change control. Guarded
// element by element: Shopify caches theme assets and section markup
// independently, so this JS can run against a cached section from before the
// redesign, and an unguarded null here would throw mid-module and kill every
// listener below it.
STEP_DEFS.forEach(d => {
  const btn = el(d.btn);
  if(!btn) return;
  btn.addEventListener('click', () => {
    const st = stepStates();
    if(st[d.id] === 'locked') return;      // disabled already, belt and braces
    // Opening a finished step to change it RESETS NOTHING. The later steps keep
    // their state and stay where they are; the existing invalidation in
    // selectPlate / selectHead / commitTvSize is what runs if and when the
    // customer actually picks something different.
    stepManual[d.id] = st[d.id] !== 'open';
    applyStepStates();
    // No focus move: the customer is already standing on this button.
  });
});

// [UX R2 2026-09-21] THE PROGRESS RAIL IS THE JUMP CONTROL (proposals item 5).
// Wired once at module load like the headers above, and guarded element by
// element for the same cached-markup reason. The rail carries the step ids
// themselves (data-rail-jump === the STEP_DEFS id), so there is no translation
// table here to drift out of step with STEP_DEFS.
//
// [RULE] THIS LISTENER WRITES NO DOM STATE. It sets stepManual and calls
// applyStepStates, which is still the single writer (renderRail owns every
// class, state word and disabled flag on the rail). It adds no renderRail
// caller of its own.
//
// Opening a finished step from the rail RESETS NOTHING, the same rule the step
// header carries. Tapping the step that is already open only scrolls to it: it
// must never collapse the card the customer is standing in.
document.querySelectorAll('[data-rail-jump]').forEach(b => {
  b.addEventListener('click', () => {
    const id = b.getAttribute('data-rail-jump');
    const st = stepStates();
    if(!id || st[id] === 'locked') return;   // disabled already, belt and braces
    if(st[id] !== 'open'){
      stepManual[id] = true;
      applyStepStates();
    }
    scrollStepIntoView(id);
  });
});

// The first step still waiting for the customer, which is where a cascade should
// put their focus. After a plate pick that is step 3; after a plate pick made
// when a head was already chosen it is step 4, which is why this is derived
// rather than hardcoded per call site.
function firstOpenStep(){
  const st = stepStates();
  const d = STEP_DEFS.find(x => st[x.id] === 'open');
  return d ? d.id : null;
}

function hideStage2b(){ applyStepStates(); }
function revealStage2b(){ applyStepStates(); renderStage2b(); }
function hideStage3(){ applyStepStates(); }
// [RULE] THE SEED GOES HERE AND NOT IN renderStage3. This function is the "step 4
// has just become part of the build" path (a head pick, or a plate change with a
// head already chosen), which is exactly the moment a starting length is wanted.
// renderStage3 would look like the tidier home and is the wrong one: it also runs
// when the customer CLEARS the field, and re-seeding 30 there would make an empty
// box impossible to type. applyStepStates runs FIRST so the s3 analytics event
// still fires with the step's own value, before any length exists.
function revealStage3(){
  applyStepStates();
  if(seedDefaultTarget()) return;   // it renders everything itself
  renderStage3();
}

// ---- Stage 1: TV size ----
function renderStage1Out(){
  const box = el('s1out');
  // Nothing at all without a usable size: this line COUNTS heads, and there is
  // nothing to count until there is a size. It empties the div, which Dawn's
  // div:empty rule then collapses, so no gap is left behind.
  if(!sizeCommitted()){ box.innerHTML = ''; return; }
  const n = HEADS.filter(h => headScreenOK(h, state.tvSize)).length;
  box.innerHTML = `<div class="muted" style="font-size:13.5px">${
    fill(COPY('s1_heads_fit'), {count: n, total: HEADS.length, tv: nice(Number(state.tvSize))})}</div>`;
}

// ---- The size control: five presets + a 10 to 90 dropdown (2026-07-29) ----
// The old type-in box could not tell a phone customer when the size had taken:
// a numeric keypad has no Enter key, so nothing committed until they tapped
// elsewhere. #tvSize is a <select> now and the presets drive it, which is why
// this file still has exactly one commit path.

// A size the dropdown does not list gets its option INJECTED rather than
// clamped. Old shared links carry 91 to 120 (the field's historic max) and can
// carry a half inch, and silently rewriting somebody's 100 in TV to 90 would be
// a link that does not restore what it says. Inserted in numeric order so the
// list still reads as a list, and marked data-legacy so it is identifiable.
function ensureSizeOption(v){
  const sel = el('tvSize');
  if(!sel || v == null || isNaN(v)) return;
  const val = String(v);
  for(let i = 0; i < sel.options.length; i++){ if(sel.options[i].value === val) return; }
  const o = document.createElement('option');
  o.value = val;
  o.textContent = val + ' inch';
  o.setAttribute('data-legacy', '');
  let before = null;
  for(let i = 0; i < sel.options.length; i++){
    const ov = Number(sel.options[i].value);
    if(sel.options[i].value !== '' && !isNaN(ov) && ov > Number(v)){ before = sel.options[i]; break; }
  }
  sel.insertBefore(o, before);
}

// Which preset reads as pressed. Driven off state.tvSize rather than the select's
// text, so a dropdown pick clears the pressed preset and a preset click sets it
// through the same one line. Guarded: the buttons are new markup and this JS can
// run against a section Shopify cached before they existed.
function syncSizePresets(){
  document.querySelectorAll('.vmscfg-size__btn').forEach(b => {
    const on = state.tvSize != null && Number(b.dataset.tvsize) === Number(state.tvSize);
    b.classList.toggle('is-active', on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
}

// ---- "Don't know your screen size?" (2026-07-28, owner-requested) ----
// An inline disclosure, not a modal: one short answer does not need a focus
// trap, a close control and a return path. Guarded for the same cached-section
// reason as the step headings above.
const sizeHelpBtn = el('sizeHelpBtn'), sizeHelpPanel = el('sizeHelpPanel');
if(sizeHelpBtn && sizeHelpPanel){
  sizeHelpBtn.addEventListener('click', () => {
    const open = sizeHelpBtn.getAttribute('aria-expanded') === 'true';
    sizeHelpBtn.setAttribute('aria-expanded', open ? 'false' : 'true');
    setHidden(sizeHelpPanel, open);
  });
}

// ---- Thumbnails ----
// One helper for every product image in the configurator (type tiles, pick
// cards, compare modal, build summary). alt is ALWAYS empty: every call site
// sits next to the SKU and title as real text, so a described image would make
// a screen reader announce the part twice. `size` is the CSS box in rem, and
// the box (not the img) fixes the geometry, so a non-square source photo
// letterboxes via object-fit instead of distorting and nothing shifts on load.
// A component with image === null (33CE004HEAD today) gets a neutral
// placeholder box rather than a broken img.
// [NOTE 2026-09-04] The img carries width="200" height="200" purely for the
// lazy-image lint ("Lazy-loaded images should have explicit dimensions") and the
// intrinsic-ratio hint the browser uses to reserve space before the pixels land.
// It is NOT the rendered geometry: every call site requests the source at
// &width=200 and the CSS box (.vmscfg .thumb img etc, width:100% height:100%
// object-fit:contain) still owns how the thumbnail is drawn, so a non-square
// source photo letterboxes via object-fit exactly as before. The 200 x 200 is a
// declared source size, not a layout size.
function thumbHTML(c, cls){
  const k = cls || 'thumb';
  if(c && c.image){
    return `<span class="${k}"><img src="${c.image}" alt="" width="200" height="200" loading="lazy" decoding="async"></span>`;
  }
  return `<span class="${k} ${k}--none" aria-hidden="true"></span>`;
}

// ---- Stage 2: plate type -> plate ----
// [RULE] THE PICKED-TYPE COLLAPSE IS A CLASS, NOT A RE-RENDER (2026-07-28). The
// owner's spec collapses the unpicked type tiles to compact chips the moment a
// type is chosen. Doing that by rebuilding the row would destroy the button the
// customer just pressed, which is the same trap the in-place selection update
// below already avoids; the CSS does the whole job off this one class.
function syncPlateTypeChips(wrap){
  const w = wrap || el('plateTypeChoices');
  if(w) w.classList.toggle('ptypes--picked', !!state.plateTypeId);
}

function renderStage2(){
  const wrap = el('plateTypeChoices');
  wrap.innerHTML = PLATE_TYPES.map(t => {
    const on = state.plateTypeId === t.id;
    // Both labels ship on every tile; the CSS shows the full one on a resting or
    // selected tile and the short one on a chip. Rendering both is what lets the
    // picked row restyle itself with no innerHTML pass under the customer's focus.
    return `<button type="button" class="ptype${on?' sel':''}" data-ptype="${t.id}" aria-pressed="${on?'true':'false'}">
      ${thumbHTML(getPlate(t.thumb), 'ptype__img')}
      <span class="ptype__txt">
        <span class="ptype__t">${t.label}</span>
        <span class="ptype__s">${t.short}</span>
        <span class="ptype__c">${t.conditions}</span>
      </span>
    </button>`;
  }).join('');
  wrap.querySelectorAll('button').forEach(b =>
    b.onclick = () => {
      state.plateTypeId = b.dataset.ptype; state.plateSku = null;
      state.target = null; state.targetWanted = null; el('target').value = ''; hideStage2b(); hideStage3();
      // Tile state updates IN PLACE (class + aria-pressed), never via an innerHTML
      // re-render, so the clicked tile keeps keyboard focus.
      wrap.querySelectorAll('button').forEach(x => {
        const on = x.dataset.ptype === state.plateTypeId;
        x.classList.toggle('sel', on); x.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      syncPlateTypeChips(wrap);
      renderStage2Out(); renderSummary();
    });
  syncPlateTypeChips(wrap);
  renderStage2Out();
}

// ONE PILL PER GROUP, AND ONLY WHERE THERE IS A CHOICE. The "only if needed"
// pill that used to sit on every OTHER plate in a group is retired (owner,
// 2026-08-22: "Every ceiling plate does not need this bubble that says only if
// needed"). Everything that is not the cheapest now carries no badge at all,
// which is also what stops the row of pills reading as a ranking.
function plateCard(sku, isCheapest){
  const p = getPlate(sku);
  if(!p) return '';
  // Capitalised on the owner's word 2026-08-22: "At least capitalize 'Cheapest'".
  // The chart row below (~line 2520) renders the same word and must stay in step.
  const pill = isCheapest ? `<span class="pill rec">Cheapest</span>` : '';
  const unavail = p.available === false;
  const unavailPill = unavail ? `<span class="pill unavail">currently unavailable</span>` : '';
  return `
    <button type="button" class="plate ${state.plateSku===sku?'sel':''}${unavail?' unavail':''}" data-plate="${sku}" aria-pressed="${state.plateSku===sku?'true':'false'}"${unavail?' aria-disabled="true"':''}>
      ${thumbHTML(p)}
      <span class="pbody">
        <span class="pt">${sku} ${pill}${unavailPill}</span>
        <span class="pn">${PLATE_NOTE[sku]||p.title}</span>
      </span>
      <span class="pp">${money(p.price)}</span>
    </button>`;
}

function renderStage2Out(){
  const box = el('s2out');
  const grp = getPlateType(state.plateTypeId);
  if(!grp){ box.innerHTML=''; hideStage2b(); hideStage3(); return; }
  // The "we list the cheaper plate first" note that used to sit here is DELETED
  // (owner, 2026-08-22: "This wording about listing the cheapest first is not
  // needed ... Remove that field entirely"). It was also untrue for the truss
  // group. Nothing replaces it: the pill says which one is cheapest and the
  // prices are on every card, so the sentence was explaining what the cards
  // already show.
  const cheapest = cheapestPlateOf(grp);
  box.innerHTML = `<h3 class="mini">${grp.plates.length > 1 ? grp.label + ' options' : grp.label}</h3>` +
    grp.plates.map(sku => plateCard(sku, sku === cheapest)).join('') +
    compareTrigger('plates', fill(COPY('cmp_trigger_plates'), {count: PLATE_POOL.length}));
  box.querySelectorAll('[data-plate]').forEach(p => p.onclick = () => {
    if(p.classList.contains('unavail')) return;
    selectPlate(p.dataset.plate);
  });
  wireCompareTriggers(box);
  // [FEATURE A, spec 7.6] Auto-select when the chosen type has exactly one plate
  // (today only Unistrut -> 11UN001): the customer should not have to click the only
  // option, and the single card still renders SELECTED and the step reads done,
  // because selectPlate's redraw re-enters this function with state.plateSku set. The
  // guard on the sku is what stops that redraw from looping. Return, because
  // selectPlate has already revealed step 3, redrawn and moved focus, so the
  // revealStage2b below would run a second time for nothing.
  if(grp.plates.length === 1 && state.plateSku !== grp.plates[0]){
    selectPlate(grp.plates[0]);
    return;
  }
  if(state.plateSku) revealStage2b();
}

// Shared by the plate cards and the compare modal's "Choose" buttons, so both
// paths run the identical downstream invalidation (target cleared, stage 3
// re-hidden) instead of one of them drifting.
function selectPlate(sku){
  if(!getPlate(sku)) return;
  state.plateSku = sku;
  const t = plateTypeOfSku(sku);
  if(t) state.plateTypeId = t.id;   // keeps the type tile in sync on a modal jump
  // The length is cleared because the plate's own offset is part of it. The HEAD
  // is deliberately NOT cleared: re-choosing a compatible plate has to keep the
  // rest of the build, which is the whole point of the Change control.
  state.target = null; state.targetWanted = null; el('target').value = ''; hideStage3();
  stepManual = {};            // a pick is a cascade; the flow decides from here
  revealStage2b();
  // [FEATURE A, spec 7.6] The plate is chosen, so step 3 (mounting head) is now
  // reachable. If exactly one head fits the committed size, pick it here on this
  // commit path. selectHead then reveals step 4, redraws and moves focus (landing on
  // step 4, exactly as a manual head pick would), so return and let it own the tail.
  if(maybeAutoSelectHead()) return;
  redraw();
  focusStageHeading(firstOpenStep());   // step 3 normally, step 4 if a head is already chosen
}

// ---- Stage 2b: mounting head ----
// REBUILT 2026-08-22 to the owner's wireframe. The card used to be a run-on
// sentence ("1 screen - Rotates - 5 up / 12 down - 110 lb - adds 4.8 in") with a
// VESA chip row under it. It is now a grid of LABELLED SPEC TILES: same data,
// read the same way, but every value carries its own heading so a shopper can
// compare one field down a column instead of parsing prose. The chip row is gone
// from the card and replaced by a single VESA RANGE tile; the full chip list
// still lives in the comparison chart, which is where an exhaustive list belongs
// and which is also what stopped a head card being 384px tall on a 390px phone.
//
// [RULE] THE CARD IS NOT A <button> ANY MORE, AND CANNOT GO BACK TO BEING ONE.
// The wireframe puts two info buttons inside the card and a button may not
// contain a button. The card is a plain div with a STRETCHED pick button inside
// it: .hcard__pick carries an ::after that covers the whole card, so the entire
// surface is still one click target, while the info buttons sit above it on
// z-index and stay separately reachable by mouse, touch and keyboard. Every
// inner element can be a div again for the same reason. Do not "simplify" this
// back to a button wrapper - the info buttons are the whole reason for the shape.
//
// EVERY TILE IS OMITTED WHEN ITS DATA IS MISSING, with no placeholder and no
// empty box left behind. A pick card is a scan surface; the comparison chart is
// where "not on file" belongs. 33CE004HEAD (retiring, and deliberately left
// empty in the admin) is the live test of that - it has no weight, tilt, VESA,
// configuration or adjustability and no photo either, and its card must come out
// as SKU and price with no tile grid at all.

// The screen COUNT, kept separate from HEAD_CONFIG_TEXT because the tile wants a
// number first and the comparison chart wants a phrase. Same vocabulary, same
// deliberate silence on an unrecognised value.
const HEAD_SCREENS_TEXT = {
  'Single': '1',
  'Back-to-back': '2, back to back',
  'Double-wide': '2, side by side',
  'Double-stack': '2, stacked'
};

// The tile says Yes or No, not "Rotates" / "Tilt only": the tile's own heading
// already asks the question, so repeating the word in the answer reads oddly.
// rotationText() is untouched and still feeds the comparison chart.
function rotatesText(h){
  if(h.rotates === true) return 'Yes';
  if(h.rotates === false) return 'No';
  return '';
}

// Tilt for the tile. This is NOT tiltText(): that helper returns '' for a head
// whose travel is zero, which is right for a prose line but wrong for a labelled
// tile, where the owner's wireframe asks for an explicit "Fixed, 0 degrees" on
// 33HE014. The distinction the data supports is null (nothing on file, omit the
// tile) versus a real zero (on file, and the answer is that it does not tilt).
function tiltRangeText(h){
  const upMissing = (h.tilt_up == null || isNaN(h.tilt_up));
  const dnMissing = (h.tilt_down == null || isNaN(h.tilt_down));
  if(upMissing && dnMissing) return '';
  const up = upMissing ? 0 : Number(h.tilt_up);
  const dn = dnMissing ? 0 : Number(h.tilt_down);
  if(!up && !dn) return 'Fixed, ' + deg(0);
  if(!up) return deg(dn) + ' down';
  if(!dn) return deg(up) + ' up';
  return deg(up) + ' up / ' + deg(dn) + ' down';
}

// Signed inches for the tile. offsetText() writes a whole sentence ("adds 4.8
// in" / "raises 5.1 in") which is right under a prose line and redundant under a
// tile headed "Adds length"; the sign carries it instead. offsetText() is
// untouched and still feeds the chart, the summary and the elevation caption, so
// the two surfaces still derive from the same number.
function addsLengthText(v){
  if(v == null || isNaN(v)) return '';
  const n = Number(v);
  if(!n) return '0 in';
  return (n > 0 ? '+' : '-') + nice(Math.abs(n)) + ' in';
}

// ---- The info affordance (2026-08-22) ------------------------------------
// There was NO tooltip mechanism anywhere in this configurator before today, so
// this is new construction rather than a restyle. Two payloads, two shapes,
// deliberately:
//   - "Adds length" is one sentence, so it is an INLINE DISCLOSURE that opens
//     inside the card. No modal for a single line, and no positioned popover
//     either: a floating tooltip has to be placed, flipped at the viewport edge
//     and dismissed, and every one of those is a bug surface for one sentence.
//   - "VESA range" is a diagram plus a pattern list, so it opens the DIALOG. It
//     mirrors what the product page shows for the same head, which is the point.
//
// The icon is INLINE SVG, not a character. The circled-i glyph is not on the
// keyboard and this project asks before adding one; drawing it sidesteps the
// question and renders identically everywhere.
const INFO_ICON =
  '<svg class="vmscfg-info__i" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">' +
    '<circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.4"></circle>' +
    '<circle cx="8" cy="4.5" r="1" fill="currentColor"></circle>' +
    '<path d="M8 7.1v4.7" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"></path>' +
  '</svg>';

function infoBtn(attrs, label){
  return `<button type="button" class="vmscfg-info" ${attrs} aria-label="${label}">${INFO_ICON}</button>`;
}

// A tile renders only when it has a value, and returns '' rather than an empty
// box so the grid never carries a hole. The info button is passed in already
// built (or as '') so this stays a pure layout helper.
function specTile(key, value, info){
  if(!value) return '';
  return `<div class="hspec">` +
    `<span class="hspec__k">${key}${info || ''}</span>` +
    `<span class="hspec__v">${value}</span>` +
  `</div>`;
}

function headCard(h){
  const unavail = h.available === false;
  const sel = state.headSku === h.sku;
  const unavailPill = unavail ? `<span class="pill unavail">currently unavailable</span>` : '';

  const size    = (h.screen_min != null && h.screen_max != null) ? `${h.screen_min} to ${h.screen_max} in` : '';
  const screens = HEAD_SCREENS_TEXT[h.configuration] || '';
  const rot     = rotatesText(h);
  const tilt    = tiltRangeText(h);
  const cap     = (h.weight_capacity != null && !isNaN(h.weight_capacity)) ? nice(h.weight_capacity) + ' lb' : '';
  const adds    = addsLengthText(h.length_offset);
  const vesa    = vesaRangeText(h);

  // The note id is per card, not per page: the same disclosure markup exists on
  // every head card at once and aria-controls must point at this one.
  const noteId = `hnote-${h.sku}`;
  const addsInfo = adds
    ? infoBtn(`data-info-note="${noteId}" aria-expanded="false" aria-controls="${noteId}"`, 'What adds length means')
    : '';
  const vesaInfo = vesa
    ? infoBtn(`data-info-vesa="${h.sku}"`, `VESA details for ${h.sku}`)
    : '';

  const tiles =
    specTile('Screen size', size) +
    specTile('Screens', screens) +
    specTile('Rotates', rot) +
    specTile('Tilt range', tilt) +
    specTile('Weight cap', cap) +
    specTile('Adds length', adds, addsInfo) +
    specTile('VESA range', vesa, vesaInfo);

  return `
    <div class="hcard${sel ? ' sel' : ''}${unavail ? ' unavail' : ''}" data-head-card="${h.sku}">
      ${thumbHTML(h)}
      <span class="hcard__id">
        <button type="button" class="hcard__pick" data-head="${h.sku}" aria-pressed="${sel ? 'true' : 'false'}"${unavail ? ' aria-disabled="true"' : ''}>${h.sku}</button>
        ${unavailPill}
      </span>
      <span class="hcard__price">${money(h.price)}</span>
      ${tiles ? `<div class="hcard__specs">${tiles}</div>` : ''}
      ${adds ? `<p class="hcard__note" id="${noteId}" hidden>${COPY('info_adds_length')}</p>` : ''}
    </div>`;
}

// Both info affordances, wired on the container rather than per button so a
// rebuilt card list cannot leave a stale handler behind.
function wireHeadInfo(box){
  box.querySelectorAll('[data-info-note]').forEach(b => b.onclick = e => {
    e.stopPropagation();          // the card behind this is one big pick target
    const note = document.getElementById(b.dataset.infoNote);
    if(!note) return;
    const open = note.hasAttribute('hidden');
    if(open){ note.removeAttribute('hidden'); } else { note.setAttribute('hidden', ''); }
    b.setAttribute('aria-expanded', open ? 'true' : 'false');
  });
  box.querySelectorAll('[data-info-vesa]').forEach(b => {
    // No <dialog> support means there is no panel to open, so the button GOES
    // rather than sitting on the card doing nothing. Same call compareTrigger()
    // makes for the "Compare all" buttons.
    if(!CAN_DIALOG){ b.remove(); return; }
    b.onclick = e => { e.stopPropagation(); openVesaInfo(b.dataset.infoVesa); };
  });
}

function renderStage2b(){
  const box = el('s2bout');
  if(!state.plateSku){ box.innerHTML=''; return; }
  const fits = HEADS.filter(h => headScreenOK(h, state.tvSize));
  if(!fits.length){
    box.innerHTML = `
      <div class="capture"><b>No head in stock fits that size right now.</b>
      Call <span class="phone">800-585-8795</span> or <a href="/pages/contact">Contact us</a> and we will find the right fit.</div>` +
      compareTrigger('heads', fill(COPY('cmp_trigger_heads'), {count: HEADS.length}));
    wireCompareTriggers(box);
    return;
  }
  box.innerHTML = fits.map(headCard).join('') +
    `<div class="note" style="margin-top:10px">${COPY('heads_note')}</div>` +
    compareTrigger('heads', fill(COPY('cmp_trigger_heads'), {count: HEADS.length}));
  // [data-head] is the stretched pick BUTTON now, so the unavailable test has to
  // read the CARD it sits in; the class lives there, not on the button.
  box.querySelectorAll('[data-head]').forEach(p => p.onclick = () => {
    const card = p.closest('[data-head-card]');
    if(card && card.classList.contains('unavail')) return;
    selectHead(p.dataset.head);
  });
  wireHeadInfo(box);
  wireCompareTriggers(box);
}

// Shared by the head cards and the compare modal's "Choose" buttons.
function selectHead(sku){
  if(!getHead(sku)) return;
  state.headSku = sku;
  // [FEATURE A] A direct selection is MANUAL by default; maybeAutoSelectHead()
  // flips this true immediately after when it is the one driving. A manual click
  // (the head card onclick, the compare modal Choose button) leaves it false.
  state.headAuto = false;
  state.target = null; state.targetWanted = null; el('target').value = '';
  stepManual = {};            // a pick is a cascade; the flow decides from here
  revealStage3(); redraw();
  focusStageHeading(firstOpenStep());   // step 4, which has just become reachable
}

// [FEATURE A, spec 7.6] Auto-select the mounting head when exactly one fits the
// committed TV size, so the customer is never asked to click the only option. It
// runs ONLY on the commit paths (commitTvSize after its clear, selectPlate when it
// opens step 3, and the deep-link boot), NEVER from the #tvSize 'input' handler:
// per-keystroke a partial size would pick a head and then clear it (the WARN above
// commitTvSize). Returns the chosen sku or null. It stands down entirely during a
// programmatic configureBuild(), which does its own head selection and must not
// double-render or (via commitTvSize) pop a modal at an agent.
let programmaticConfigure = false;
function maybeAutoSelectHead(){
  if(programmaticConfigure) return null;
  if(state.headSku != null) return null;      // never override a chosen head
  if(!stepReachable('s2b')) return null;      // needs a committed size AND a chosen plate
  const fits = HEADS.filter(h => headScreenOK(h, state.tvSize));
  if(fits.length !== 1) return null;
  selectHead(fits[0].sku);                     // same path a click takes
  state.headAuto = true;                       // set AFTER: selectHead resets it to the manual default
  return fits[0].sku;
}

// =====================================================================
// COMPARE MODAL (2026-07-26)
// =====================================================================
// One <dialog> in the section markup serves both tables; the trigger decides
// which. Escape-to-close, the focus trap, and focus restore to the trigger are
// all native to showModal(), so none of that is reimplemented here. Where
// HTMLDialogElement is missing the triggers are never rendered at all, rather
// than rendering a button that does nothing: the server-rendered reference
// layer at the bottom of the page already carries the same tables as the
// no-JS floor, so nothing is actually lost.
const CAN_DIALOG = (function(){
  const d = document.getElementById('cmpDlg');
  return !!(d && typeof d.showModal === 'function');
})();

function compareTrigger(kind, label){
  if(!CAN_DIALOG) return '';
  return `<button type="button" class="vmscfg-cmpbtn" data-cmp="${kind}">${label}</button>`;
}

function wireCompareTriggers(box){
  box.querySelectorAll('[data-cmp]').forEach(b => b.onclick = () => openCompare(b.dataset.cmp));
}

// ---- the INLINE compare link in the step 2 intro (2026-07-29) ---------------
// The owner cut that intro to "Choose a ceiling plate (comparison chart)" with the
// chart as a link inside the sentence. It is SERVER-rendered from his [compare]
// token, so wireCompareTriggers above never sees it: that function only reaches
// the buttons inside the box renderStage2Out just rebuilt. Bound here instead,
// once, at init. Both routes call the same openCompare, so there is one dialog and
// one table, not a second copy.
//
// [WARN] THE TWO CACHES CAN DISAGREE, and both directions are handled. Shopify
// caches section markup and theme assets independently. NEW asset against an OLD
// section: querySelectorAll finds nothing and this is a no-op. NEW section against
// an OLD asset: the button is on the page and nothing binds it, so it renders as
// underlined text that does nothing when tapped. That is the same no-JS floor
// every other surface here has, and the reference tables at the bottom of the page
// are still the crawlable copy of the same comparison, so the trade is acceptable
// rather than invisible.
document.querySelectorAll('.vmscfg-cmplink[data-cmp]').forEach(b => {
  // No dialog support at all: unwrap to plain text rather than leave an
  // affordance that cannot fire. Same rule compareTrigger() follows by returning
  // an empty string, except here the WORDS have to stay or the sentence loses its
  // closing parenthesis. replaceChild rather than replaceWith: this branch exists
  // for old engines, which is the last place to assume a modern DOM method.
  if(!CAN_DIALOG){
    if(b.parentNode) b.parentNode.replaceChild(document.createTextNode(b.textContent), b);
    return;
  }
  b.addEventListener('click', () => openCompare(b.dataset.cmp));
});

// Signed offsets read as plain English. A plate always adds drop; a head can
// SUBTRACT it (33CE004HEAD is -5.126, it raises the TV back up toward the
// ceiling), and "Adds -5.1 in" is a sentence nobody should have to parse.
function offsetText(v){
  if(v == null || isNaN(v)) return 'not on file';
  if(v < 0) return 'raises ' + inch(Math.abs(v)) + ' in';
  return 'adds ' + inch(v) + ' in';
}

function compareRow(cells, cls){
  return `<tr${cls ? ` class="${cls}"` : ''}>` + cells.map(c => `<td>${c}</td>`).join('') + '</tr>';
}

// "Chosen" is rendered disabled, which is the whole visual state, so there is no
// separate modifier class to keep in sync.
function chooseBtn(attr, sku, chosen){
  return `<button type="button" class="vmscfg-cmpchoose" data-${attr}="${sku}"${chosen ? ' disabled' : ''}>${chosen ? 'Chosen' : 'Choose'}</button>`;
}

// The plate chart carries its units in the COLUMN HEADERS, so its adds-to-length
// column wants a bare number where offsetText() above returns a whole sentence.
// Every pooled plate has a positive offset (only a head can raise the TV), so
// there is no sign to spell out here; the guard is for a metafield gone missing.
// The VALUE still comes from the live component data, never from PLATE_SPEC.
function addsCell(v){
  return (v == null || isNaN(v)) ? 'not on file' : inch(v);
}

// Weight rating comes LIVE off custom.weight_capacity, exactly like price and
// adds-to-length, and is deliberately NOT in PLATE_SPEC. All 14 plates carry the
// field in the store as of 2026-08-21 (12 were filled and 11AC005 corrected 200
// to 125 that day, on the owner's ruling that the lower figure is right). Reading
// it live means a rating the owner changes in admin can never sit here disagreeing
// with the product page, which is the trap a hardcoded copy would have set.
function weightCell(p){
  const v = p ? p.weight_capacity : null;
  return (v == null || isNaN(v)) ? 'not on file' : nice(v);
}

// One cell out of PLATE_SPEC. Deliberately NOT written with `||`: tilt is 0 on
// ten of the fourteen plates and 0 is a real answer, so a falsy test would print
// the missing-data placeholder for most of the table. Missing means missing.
function specCell(sku, key){
  const rec = PLATE_SPEC[sku];
  const v = rec ? rec[key] : null;
  return (v === undefined || v === null || v === '') ? 'not on file' : String(v);
}

// The tilt column carries the degree sign ON THE NUMBER (owner ask 2026-08-21),
// which is why its header is a bare "Tilt angle" with no unit in parentheses.
// Every plate gets it, including the ten that read 0 degrees, so the column
// reads as one unit rather than mixing bare zeros with marked values. A part
// with no tilt on file still falls through to specCell's "not on file", which
// must NOT gain a degree sign. The sign itself lives in deg(), the one place in
// this file that character is typed - see the warning on that helper.
function tiltCell(sku){
  const v = specCell(sku, 'tilt');
  return v === 'not on file' ? v : deg(v);
}

// The heads chart's tilt cell. A table fills its cells rather than dropping
// items, so this is where tiltText's empty string turns into an answer: no
// figure on either side at all is "not on file", but both figures present and
// both zero is a real "does not tilt" and prints as 0 degrees, matching how the
// plate chart prints its ten zero-tilt plates.
function tiltCellHead(h){
  if(h.tilt_up == null && h.tilt_down == null) return 'not on file';
  return tiltText(h) || deg(0);
}

// Column count of the plate chart, kept as a named constant because the ONLY
// other place it appears is the rowgroup header's colspan, and a colspan that
// silently disagrees with the header row is invisible until a screen reader
// hits it. Count, in render order: photo, SKU, what makes it different,
// mounting holes, plate dimensions, cable pass through, tilt angle, weight
// rating, adds to length, price, choose.
const CMP_PLATE_COLS = 11;

function comparePlates(){
  // Iterate PLATE_TYPES, not PLATE_POOL, so the table groups by type and the
  // "recommended" steer stays visible. The partition guarantee (self-test 8)
  // is what makes this a complete listing of the pool.
  //
  // One <tbody> PER TYPE, each opening with its own scope="rowgroup" header.
  // That is what actually makes scope="rowgroup" mean anything: the scope is the
  // row group it sits in, so a screen reader announces "Flat ceiling plate"
  // ahead of each plate in that group. Cramming every row into one tbody with
  // several rowgroup headers claims five row groups where the markup declares
  // one, and the association is lost.
  //
  // REBUILT 2026-08-21 from the owner's 14-plate sheet. The old "Plate" column
  // (the product title) is GONE ON PURPOSE: it repeated the SKU in longer words
  // and cost a column the spec numbers needed. The product link moved onto the
  // SKU itself so the route to the product page survived the cut.
  // The chart reads the pill from cheapestPlateOf() too, not from row order, so
  // the chart and the pick cards cannot disagree about which plate is cheapest.
  const groups = PLATE_TYPES.map(t => {
    const cheap = cheapestPlateOf(t);
    return `<tbody>
      <tr class="cmp-grp"><th scope="rowgroup" colspan="${CMP_PLATE_COLS}">${t.label}<span class="cmp-grp__c">${t.conditions}</span></th></tr>` +
    t.plates.map(sku => {
      const p = getPlate(sku);
      if(!p) return '';
      const chosen = state.plateSku === sku;
      const unavail = p.available === false;
      return compareRow([
        thumbHTML(p, 'cmpthumb'),
        `<a href="${p.url}"><b>${sku}</b></a>${sku === cheap ? ' <span class="pill rec">Cheapest</span>' : ''}${unavail ? ' <span class="pill unavail">currently unavailable</span>' : ''}`,
        `<span class="cmp-diff">${PLATE_NOTE[sku] || ''}</span>`,
        `<span class="cmp-num">${specCell(sku, 'holes')}</span>`,
        specCell(sku, 'dims'),
        specCell(sku, 'cable'),
        `<span class="cmp-num">${tiltCell(sku)}</span>`,
        `<span class="cmp-num">${weightCell(p)}</span>`,
        // Adds-to-length and price stay LIVE off the component data, never off
        // PLATE_SPEC, so neither can drift from what the store actually says.
        `<span class="cmp-num">${addsCell(p.length_offset)}</span>`,
        `<span class="cmp-num">${money(p.price)}</span>`,
        unavail ? '' : chooseBtn('choose-plate', sku, chosen)
      ]);
    }).join('') + '</tbody>';
  }).join('');
  return `<p class="cmp-lead">Every ceiling plate we stock for 1-1/2 inch NPT pipe, grouped by what it attaches to. Adds to length is how much the plate itself contributes before any pipe. Price and adds to length come live from the store; the rest is off the spec sheet.</p>
    <div class="vmscfg__twrap"><div class="vmscfg__tw"><table class="cmptable cmptable--plates">
      <thead><tr>
        <th scope="col"><span class="visually-hidden">Photo</span></th>
        <th scope="col">SKU</th>
        <th scope="col">What makes it different</th>
        <th scope="col">Mounting holes</th>
        <th scope="col">Plate dimensions</th>
        <th scope="col">Cable pass through</th>
        <th scope="col">Tilt angle</th>
        <th scope="col">Weight rating (lb)</th>
        <th scope="col">Adds to length (in)</th>
        <th scope="col">Price</th>
        <th scope="col"><span class="visually-hidden">Choose</span></th>
      </tr></thead>
      ${groups}
    </table></div><span class="vmscfg__twfade" aria-hidden="true"></span></div>`;
}

// REBUILT 2026-08-21 to mirror the plate chart. Columns are now photo, SKU,
// screens, rotation, tilt, weight rating, VESA, fits TV size, adds to length,
// price, choose.
//
// Two things went in that rebuild. The old "Head" column (the product title)
// is gone for the same reason it went from the plate chart: it repeated the SKU
// in longer words and cost a column the spec numbers needed, so the product
// link moved onto the SKU itself and the route to the product page survived.
// And the old VESA column came from a REGEX OVER THE PRODUCT TITLE, which
// printed the literal words "not in the title" for every head whose name
// carries no pattern - an answer about our naming, not about the part. Real
// bounds are on file now, so the column is the derived chip row instead.
//
// Unlike the pick card, a missing value here prints "not on file" rather than
// being dropped: a table cell that silently disappears reads as an alignment
// bug, and the gap is itself information a shopper can act on (33CE004HEAD,
// retiring and deliberately empty, is the row that proves it).
function compareHeads(){
  const size = state.tvSize;
  const rows = HEADS.map(h => {
    const fits = headScreenOK(h, size);
    const chosen = state.headSku === h.sku;
    const unavail = h.available === false;
    const range = (h.screen_min != null && h.screen_max != null)
      ? h.screen_min + ' to ' + h.screen_max + ' in' : 'no limit on file';
    // A head the entered TV size rules out is still LISTED (the shopper asked
    // to see everything) but is not choosable, so the modal can never drop the
    // build into the contradictory state the URL restore guards against too.
    const why = !fits ? `<span class="cmp-no">Outside your ${size} inch entry</span>` : '';
    // THE VESA COLUMN IS THE RANGE PLUS A WAY IN, NOT THE CHIP STACK (owner,
    // 2026-08-22: "I want to replace the VESA chips in that comparison, with the
    // range instead. we could have a tooltip or something else to display them
    // all if the user wants"). Measured reason: 13 to 15 chips wrapped inside a
    // 178.5px column set the row height, and the tallest row in the chart was
    // 33HE002 at 229.75px entirely because of them, on a table already 1378.66px
    // tall at 390 wide. Nothing is lost: the button opens the same VESA panel the
    // pick cards use, which carries the full list and the to-scale drawing.
    const vesa = vesaRangeText(h);
    const vesaCell = vesa
      ? `<span class="cmp-vesa">${vesa}${infoBtn(`data-info-vesa="${h.sku}" title="${COPY('vesa_info_cta')}"`, 'VESA patterns for ' + h.sku)}</span>`
      : 'not on file';
    return compareRow([
      thumbHTML(h, 'cmpthumb'),
      `<a href="${h.url}"><b>${h.sku}</b></a>${unavail ? ' <span class="pill unavail">currently unavailable</span>' : ''}`,
      HEAD_CONFIG_TEXT[h.configuration] || 'not on file',
      rotationText(h) || 'not on file',
      `<span class="cmp-num">${tiltCellHead(h)}</span>`,
      `<span class="cmp-num">${h.weight_capacity == null || isNaN(h.weight_capacity) ? 'not on file' : nice(h.weight_capacity)}</span>`,
      vesaCell,
      `<span class="cmp-num">${range}</span>${why}`,
      `<span class="cmp-num">${offsetText(h.length_offset)}</span>`,
      `<span class="cmp-num">${money(h.price)}</span>`,
      unavail || !fits ? '' : chooseBtn('choose-head', h.sku, chosen)
    ], fits ? '' : 'cmp-dim');
  }).join('');
  return `<p class="cmp-lead">${COPY('cmp_lead_heads')}</p>
    <div class="vmscfg__twrap"><div class="vmscfg__tw"><table class="cmptable cmptable--heads">
      <thead><tr>
        <th scope="col"><span class="visually-hidden">Photo</span></th>
        <th scope="col">SKU</th>
        <th scope="col">Screens</th>
        <th scope="col">Rotation</th>
        <th scope="col">Tilt</th>
        <th scope="col">Weight rating (lb)</th>
        <th scope="col">VESA</th>
        <th scope="col">Fits TV size</th>
        <th scope="col">Adds to length</th>
        <th scope="col">Price</th>
        <th scope="col"><span class="visually-hidden">Choose</span></th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table></div><span class="vmscfg__twfade" aria-hidden="true"></span></div>`;
}

// ---- grab and drag panning for the wide comparison charts (2026-08-21) -----
// Both charts are deliberately wider than the dialog (the plate chart floors at
// 112rem against a 104rem cap, and on a 390px phone it measures 1176px against
// about 365px of wrapper), so .vmscfg__tw has always scrolled sideways. The
// trouble was that the ONLY affordance was the scrollbar under a fourteen-row
// table, which on a phone sits far below the fold, so the extra columns read as
// missing rather than as hidden. This adds the affordances that were absent: a
// press-and-drag pan, an always-visible scrollbar, and a right-edge fade. It
// does NOT widen the dialog (that box is shared with the heads chart) and it
// must never let anything escape the wrapper: the PAGE stays unscrollable
// sideways at every viewport.
//
// [WARN] MOUSE ONLY, ON PURPOSE. The pointerdown handler returns at once for
// any pointerType other than 'mouse'. Touch and trackpad already pan this
// wrapper natively, with momentum no script can match, and a touch drag that
// starts inside the table is usually the customer scrolling the DIALOG
// vertically. Taking over touch here would eat that gesture. Do not "fix" this
// later by widening the test to touch or pen.
const DRAG_PAN_SLOP = 4;   // px of travel before a press counts as a drag, not a click

// Returns a sync() for the caller to run AFTER showModal(): a dialog that has
// not been shown yet has clientWidth 0, so overflow cannot be measured while it
// is still closed.
function enableDragPan(elm, label){
  if(!elm) return null;
  // The dialog body is rebuilt from scratch on every open, so these are fresh
  // nodes each time and the old listeners are collected with the old nodes.
  // The flag guards the other case: any future caller that runs twice over the
  // SAME node would otherwise stack a second set of handlers.
  if(elm.dataset.vmsPan === '1') return elm.vmsPanSync || null;
  elm.dataset.vmsPan = '1';

  const wrap = (elm.parentElement && elm.parentElement.classList.contains('vmscfg__twrap'))
    ? elm.parentElement : null;

  // Re-measured on every press and every sync rather than cached at setup: the
  // dialog is sized in vw and the window can be resized while it is open.
  function overflows(){ return elm.scrollWidth - elm.clientWidth > 1; }

  function sync(){
    const can = overflows();
    elm.classList.toggle('vmscfg__tw--pan', can);
    // A scrollable region has to be operable without a mouse (WCAG 2.1.1).
    // tabindex gives the arrow keys, which the browser already handles for a
    // focused scroll container, something to act on; role plus label are what
    // keep it from being an unnamed tab stop. All three come off again when
    // there is nothing to scroll, because a tab stop that does nothing is its
    // own failure.
    if(can){
      elm.setAttribute('tabindex', '0');
      elm.setAttribute('role', 'region');
      if(label) elm.setAttribute('aria-label', label);
    } else {
      elm.removeAttribute('tabindex');
      elm.removeAttribute('role');
      elm.removeAttribute('aria-label');
    }
    // The fade is a sibling span, never an empty div: Dawn's base.css carries
    // `div:empty { display: none; }` and span is not in that list.
    if(wrap){
      wrap.classList.toggle('has-more', can && (elm.scrollLeft + elm.clientWidth < elm.scrollWidth - 1));
    }
  }
  elm.vmsPanSync = sync;

  elm.addEventListener('scroll', sync, { passive: true });
  if(typeof ResizeObserver === 'function'){
    // Fires once on observe as well, which is what settles the state after
    // showModal() finally gives the dialog a real width.
    new ResizeObserver(sync).observe(elm);
  }

  let dragging = false, moved = false, startX = 0, startLeft = 0, pid = null, swallowTimer = 0;

  // A drag ends in a click on whatever sat under the pointer, and this table is
  // full of live controls: every row carries a Choose button and the SKU cell
  // is a link to the product page. Exactly ONE click is swallowed, in the
  // CAPTURE phase so it never reaches those handlers, and the listener takes
  // itself off again either way. A press under the slop threshold never arms
  // this, so a plain click still chooses the plate and still follows the link.
  function swallowClick(e){
    e.preventDefault();
    e.stopPropagation();
    disarmSwallow();
  }
  function disarmSwallow(){
    window.removeEventListener('click', swallowClick, true);
    if(swallowTimer){ clearTimeout(swallowTimer); swallowTimer = 0; }
  }
  function armSwallow(){
    disarmSwallow();
    window.addEventListener('click', swallowClick, true);
    // Belt and braces: if the release produced no click at all (it can happen
    // when the button comes up over the browser chrome) the listener must not
    // sit there waiting to eat an unrelated click later.
    swallowTimer = setTimeout(disarmSwallow, 400);
  }

  elm.addEventListener('pointerdown', e => {
    disarmSwallow();   // a fresh press always outranks a stale pending swallow
    if(e.pointerType !== 'mouse') return;   // see the MOUSE ONLY note above
    if(e.button !== 0) return;              // middle and right click keep their own jobs
    if(!overflows()) return;
    dragging = true;
    moved = false;
    startX = e.clientX;
    startLeft = elm.scrollLeft;
    pid = e.pointerId;
    // Keeps the drag alive once the pointer leaves the wrapper, which it will
    // do constantly at phone width.
    try { elm.setPointerCapture(pid); } catch(err){ /* older engine: drag still works inside the box */ }
  });

  elm.addEventListener('pointermove', e => {
    if(!dragging || e.pointerId !== pid) return;
    const dx = e.clientX - startX;
    if(!moved){
      if(Math.abs(dx) < DRAG_PAN_SLOP) return;   // still a click, leave it alone
      moved = true;
      elm.classList.add('is-grabbing');
      document.body.classList.add('vmscfg-nosel');
      // Drop whatever the press started selecting: user-select: none stops a
      // selection growing but does not undo the first character.
      const sel = window.getSelection ? window.getSelection() : null;
      if(sel && sel.removeAllRanges) sel.removeAllRanges();
    }
    elm.scrollLeft = startLeft - dx;
    e.preventDefault();
  });

  function endDrag(e){
    if(!dragging) return;
    if(e && pid !== null && e.pointerId !== pid) return;
    dragging = false;
    if(pid !== null){
      try { elm.releasePointerCapture(pid); } catch(err){ /* already released */ }
      pid = null;
    }
    elm.classList.remove('is-grabbing');
    document.body.classList.remove('vmscfg-nosel');
    if(moved){ moved = false; armSwallow(); }
  }
  elm.addEventListener('pointerup', endDrag);
  elm.addEventListener('pointercancel', endDrag);

  // Thumbnails and SKU links are natively draggable, and a native drag hijacks
  // the pointer stream halfway through a pan.
  elm.addEventListener('dragstart', e => { if(dragging) e.preventDefault(); });

  sync();
  return sync;
}

function openCompare(kind){
  const dlg = el('cmpDlg');
  if(!dlg || !CAN_DIALOG) return;
  el('cmpTitle').textContent = kind === 'heads' ? COPY('cmp_title_heads') : COPY('cmp_title_plates');
  const body = el('cmpBody');
  body.innerHTML = kind === 'heads' ? compareHeads() : comparePlates();
  body.querySelectorAll('[data-choose-plate]').forEach(b => b.onclick = () => {
    dlg.close(); selectPlate(b.dataset.choosePlate);
  });
  body.querySelectorAll('[data-choose-head]').forEach(b => b.onclick = () => {
    dlg.close(); selectHead(b.dataset.chooseHead);
  });
  // The VESA button opens the detail panel ON TOP of this chart rather than
  // closing it: the shopper is mid-comparison and closing the table under them
  // would lose their place and their scroll position.
  body.querySelectorAll('[data-info-vesa]').forEach(b => b.onclick = e => {
    e.stopPropagation();
    openVesaInfo(b.dataset.infoVesa);
  });
  // Drag-to-pan plus the keyboard and fade affordances, on BOTH charts. Wired
  // here rather than at init because this body is rebuilt on every open, and
  // querying for the wrapper class rather than for a specific table means a
  // third chart would pick it up for free.
  const panLabel = kind === 'heads'
    ? 'Mounting head comparison chart, scrollable'
    : 'Ceiling plate comparison chart, scrollable';
  const panSyncs = [];
  body.querySelectorAll('.vmscfg__tw').forEach(tw => {
    const s = enableDragPan(tw, panLabel);
    if(s) panSyncs.push(s);
  });
  dlg.showModal();
  // Only now is there a width to measure: a closed dialog reports clientWidth 0
  // and every wrapper would look like it has nothing to scroll.
  panSyncs.forEach(s => s());
  // showModal() does NOT stop the page behind from scrolling, so the wheel still
  // moves the configurator under the backdrop. Reusing Dawn's own
  // body.overflow-hidden class (assets/base.css, the same one cart-drawer.js and
  // details-modal.js use) rather than inventing a second convention. Released on
  // the dialog's close event, which fires for Escape and for close() alike, so
  // there is no path that leaves the body locked.
  document.body.classList.add('overflow-hidden');
  body.scrollTop = 0;
  // Focus the title, not the first Choose button: landing on a control that
  // mutates the build is a trap for anyone tabbing in blind.
  el('cmpTitle').focus();
}

// [FEATURE B 2026-09-06] All FOUR modal dialogs (compare, VESA, drop-ceiling and
// now head-cleared) share ONE Dawn body scroll lock (body.overflow-hidden), so no
// close handler may release it while another dialog is still open. Central so a
// dialog added later cannot be forgotten in one branch: it replaces the per-handler
// inline checks that previously each listed the others by hand (and #cmpDlg, which
// used to release unconditionally, now checks too, which is only ever stricter).
function anyOtherDlgOpen(selfId){
  return ['cmpDlg', 'vesaDlg', 'dcDlg', 'headDlg', 'tvMeasDlg'].some(id => {
    if(id === selfId) return false;
    const d = el(id);
    return !!(d && d.open);
  });
}

if(CAN_DIALOG){
  const dlg = el('cmpDlg');
  el('cmpClose').onclick = () => dlg.close();
  // Click the backdrop to dismiss. The dialog element's own box is the backdrop
  // hit area, so a click landing on the element itself (never on a child) is
  // the outside click.
  dlg.addEventListener('click', e => { if(e.target === dlg) dlg.close(); });
  dlg.addEventListener('close', () => { if(!anyOtherDlgOpen('cmpDlg')) document.body.classList.remove('overflow-hidden'); });

  // The VESA info dialog (2026-08-22) takes the same three behaviours. It is a
  // SEPARATE element rather than a second mode of cmpDlg: that one is rebuilt on
  // every open and carries its own drag-pan wiring, and threading a third kind
  // through it would couple a head tooltip to the comparison chart for no gain.
  // THE DROP-CEILING EXPLANATION, 2026-08-22. Owner asked for "a modal (click
  // anywhere to close)", and that is exactly what this does: the backdrop closes
  // it, the panel itself closes it, Escape closes it. The one exception is a
  // control inside it, because the copy carries a phone number and a contact
  // link and a click on those has to reach the link rather than dismiss the box
  // out from under it.
  const dcdlg = el('dcDlg');
  if(dcdlg){
    const dcx = el('dcClose');
    if(dcx) dcx.onclick = () => dcdlg.close();
    dcdlg.addEventListener('click', e => {
      if(e.target.closest && e.target.closest('a, button:not(#dcClose)')) return;
      dcdlg.close();
    });
    // Same guard the VESA panel needs: the modal dialogs share one body scroll
    // lock, so nobody may release it while another one is still open.
    dcdlg.addEventListener('close', () => {
      if(!anyOtherDlgOpen('dcDlg')) document.body.classList.remove('overflow-hidden');
    });
  }

  const vdlg = el('vesaDlg');
  if(vdlg){
    el('vesaClose').onclick = () => vdlg.close();
    vdlg.addEventListener('click', e => { if(e.target === vdlg) vdlg.close(); });
    // [WARN] DO NOT UNCONDITIONALLY REMOVE THE LOCK HERE. This dialog can be
    // opened from a row of the heads chart, which means #cmpDlg is still open
    // underneath when this one closes. Releasing the lock then lets the page
    // scroll behind a modal that is still up.
    vdlg.addEventListener('close', () => {
      if(!anyOtherDlgOpen('vesaDlg')) document.body.classList.remove('overflow-hidden');
    });
  }

  // The head-cleared dialog (FEATURE B, owner 2026-09-06). Same three behaviours as
  // the drop-ceiling panel: the X closes it, Escape closes it (native), and a click
  // ANYWHERE in it closes it EXCEPT on a link or button (the latent guard, so a
  // control added later cannot be swallowed by the dismiss). Owner: "click anywhere
  // to close type." It joins the shared body scroll lock through anyOtherDlgOpen.
  const hddlg = el('headDlg');
  if(hddlg){
    const hdx = el('headDlgClose');
    if(hdx) hdx.onclick = () => hddlg.close();
    hddlg.addEventListener('click', e => {
      // [WARN] IGNORE A CLICK WITHIN 300 ms OF OPENING. commitTvSize opens this on a
      // size 'change', and showModal() then moves focus off #tvSize, which fires a
      // 'blur' that also runs commitTvSize. The pointer gesture that committed the
      // size ends in a 'click' AFTER the dialog is already up; without this guard
      // that trailing click lands in the fresh dialog and dismisses it before it can
      // be read. 300 ms comfortably outlasts one gesture's mousedown-to-click gap.
      if(Date.now() - headDlgOpenedAt < 300) return;
      if(e.target.closest && e.target.closest('a, button:not(#headDlgClose)')) return;
      hddlg.close();
    });
    hddlg.addEventListener('close', () => {
      if(!anyOtherDlgOpen('headDlg')) document.body.classList.remove('overflow-hidden');
      // Land the customer where the next action is: the step 3 (mounting head)
      // disclosure button, scrolled just into view. It is enabled whether step 3 is
      // now open (no replacement) or done (FEATURE A auto-picked one), so it is the
      // right target in both variants. This overrides the dialog's native focus
      // restore to #tvSize, which would drop them back at the collapsed step 1.
      const b = el('s2bbtn');
      if(b){
        try { b.focus(); } catch(_){}
        if(typeof b.scrollIntoView === 'function'){
          try { b.scrollIntoView({block: 'nearest'}); } catch(_){ b.scrollIntoView(); }
        }
      }
    });
  }
}

// ---- Stage 3: target drop -> mast solve -> build cards ----
// Placed TV label (SPEC-THEME-tv-placement-2026-10-12.md, fix round 3 C4; notes assets/vms-tv-lookup.notes.md).
function renderLengthLabel(){
  const lab = el('targetLabel');
  if(!lab) return;
  const h = state.headSku ? getHead(state.headSku) : null;
  const txt = (h && h.horizontal) ? COPY('length_label_horizontal')
    : ((tvPlace() || {}).placed ? 'Measured from mounting surface down to the center of the TV\'s mounting holes' : COPY('length_label'));
  if(lab.dataset.t === txt && lab.lastChild && lab.lastChild.className === 'visually-hidden') return;
  lab.dataset.t = txt;
  lab.textContent = txt;
  const u = document.createElement('span');
  u.className = 'visually-hidden';
  u.textContent = ' (in inches)';
  lab.appendChild(u);
}
function renderStage3(){
  if(!state.plateSku || !state.headSku){ hideStage3(); return; }
  // The drawing is the affordance: say it in the stage subhead rather than
  // relying on the customer noticing a grab cursor.
  el('s3sub').textContent = COPY('step4_intro');
  // A horizontal (face-down) head measures to the BACK of the screen, not its
  // middle, so it carries its own label. Owner ruling 2026-08-26. Vertical heads
  // keep the standard label. Both are owner-editable settings.
  renderLengthLabel();
  // [RULE] SR-ONLY UNIT, CODE-OWNED (2026-07-29). The owner's label no longer
  // contains the word "inches" and the in-box suffix is aria-hidden decoration,
  // so without this span the field's accessible NAME carries no unit at all.
  // Appended AFTER the setting text so the owner's words stay exactly his; it
  // must be re-appended here because the textContent write above wipes children.
  // Screen-reader-only strings are code-owned by standing scope; do not make
  // this a setting and do not fix it by unhiding the suffix (browse-mode noise).
  mountElevScene();   // first visible paint of step 4 is where the scene is born
  // [WARN] REQUIRED SINCE 2026-07-27, not tidiness. The floor now depends on the
  // PLATE and the HEAD (minDrop's shortestMast branch), and selectPlate and
  // selectHead do not run the drop pipeline: they clear the target and reveal this
  // stage. Every other input that moves the floor (tile depth, drop-ceiling
  // answer, TV size) re-runs applyDrop, which sets #target.min itself. These two
  // do not, so without this call step 4 would open carrying the min from the last
  // build, or the section's static min="1", and the browser would accept a number
  // the clamp then silently overrides on commit.
  syncTargetFloor();
  syncTileCap();      // same job for #tiles' max, which moves with the TV size
  // [NOTE] The above-the-tiles note (and its #case3box element) was removed on
  // the owner's word 2026-08-22: "not necessary covered with the new drop
  // ceiling copy". The new stage 4 drop-ceiling copy and its modal already say
  // this; do not re-add it here.
  renderStage3Out();
}

// Stage 4 has TWO output surfaces now and they live in different DOM trees:
// the solved build cards inside #s3out, which is rebuilt by innerHTML on every
// keystroke, and the interactive elevation plus its explanation, which sit
// OUTSIDE #s3out and must never be rebuilt (see the [WARN] in the section).
// This wrapper is the single place both are refreshed, so no exit path in
// renderStage3Body - and it has three - can update one and forget the other.
function renderStage3Out(){
  renderStage3Body();
  renderDropNote();
  updateElevScene();
  // [PHASE 4 2026-09-15] The single "the solved build changed" signal the
  // configurator 3D view (assets/vms-configurator-3d.js) listens for. This is
  // the one additive change to the engine (BUILD-SPEC-phase4 section 2.6). It
  // sits here, not at a tail inside renderStage3Body, because renderStage3Body
  // has three early returns (target null, target <= 0, no-solve) and this
  // wrapper is the ONE place every plate/head/target change lands (the comment
  // above says so), so the 3D view re-poses on every change including a clear.
  // getBuild is a hoisted function declaration (defined later in this file), so
  // it is callable here; LAST_BUILD has already been refreshed by the
  // renderStage3Body call above, so getBuild() reflects the current solve.
  // Wrapped so a listener error or a missing getBuild can never break the
  // engine's own render path.
  try {
    document.dispatchEvent(new CustomEvent('vmscfg:build-changed', { detail: { build: getBuild() } }));
  } catch (e) { /* the 3D view is a progressive enhancement; never let it break the solve */ }
}

function renderStage3Body(){
  const box = el('s3out');
  const t = el('target');
  // Drops the error association on #target (S2: aria-invalid + aria-describedby).
  const clearTargetErr = () => { t.removeAttribute('aria-invalid'); t.removeAttribute('aria-describedby'); };
  if(state.target == null || isNaN(state.target)){ box.innerHTML=''; clearTargetErr(); state._price=null; state._mast=null; renderSummary(); return; }
  if(state.target <= 0){
    box.innerHTML = `<div class="warnbox" id="vmscfg-target-err" role="alert">The length has to be more than zero. The plate and head are never threaded straight together.</div>`;
    t.setAttribute('aria-invalid','true'); t.setAttribute('aria-describedby','vmscfg-target-err');
    state._price=null; renderSummary(); return;
  }

  const head = getHead(state.headSku), plate = getPlate(state.plateSku), target = state.target;
  box.innerHTML = buildSection(head, plate, target);
  // A no-solution result ties back to the target field so a non-visual user hears
  // why their entry was rejected (S2); a solvable build clears the error state.
  if(box.querySelector('#vmscfg-nosolve')){
    t.setAttribute('aria-invalid','true'); t.setAttribute('aria-describedby','vmscfg-nosolve');
  } else {
    clearTargetErr();
  }
  wireBuildCartButtons(box);
  // [BOM PAN 2026-09-22] The panning affordances for the parts tables, wired the
  // same way openCompare() wires the comparison charts and for the same reason:
  // this box is rebuilt from scratch on every render, so these are fresh nodes
  // and the old listeners go with the old ones (enableDragPan's data-vmsPan flag
  // guards the other case, a caller that runs twice over one node). No sync()
  // call is needed after this one: unlike the dialog, the cards already have a
  // real width the moment this HTML lands, and enableDragPan's own
  // ResizeObserver re-measures on every later width change, so a rotate or a
  // window resize adds and drops the affordances by itself.
  // [WARN] THIS LINE MUST STAY IN THE SAME TASK AS THE innerHTML WRITE ABOVE.
  // The wrapper is overflow-x: visible until .vmscfg__tw--pan goes on (see the
  // [BOM PAN] rule in assets/vms-configurator.css: overflow-x auto forces
  // overflow-y auto, which clipped the SKU photo preview). enableDragPan calls
  // its sync() synchronously before it returns, so the class lands before the
  // browser paints and a phone never sees a frame of uncontained table.
  // Deferring this to a rAF, a timeout or the ResizeObserver's first callback
  // would put a sideways-overflowing document on screen for one frame.
  box.querySelectorAll('.vmscfg__tw--bom').forEach(tw => enableDragPan(tw, COPY('bom_pan_label')));
  mountCardMastArt(box);   // must follow the innerHTML write: it re-parents canvases
  renderSummary();
}

// =====================================================================
// ELEVATION: the drop pipeline and the interactive scene (2026-07-26)
// Build brief: SPEC-drag-elevation-2026-07-26.md
// =====================================================================
// Three files cooperate, and the split is the point:
//   assets/vms-elevation.js        PURE GEOMETRY, no DOM. The clamp, the
//                                  one inch increment rule, the scene depths.
//   assets/vms-elevation-scene.js  the drawing and the two drag handles. It
//                                  emits RAW, UNROUNDED inches and never
//                                  clamps, rounds or decides anything.
//   this file                      ALL clamping, resolving and quantising.
//
// [WARN] THERE IS EXACTLY ONE QUANTISER IN THIS SYSTEM AND IT IS
// resolveDropValue() BELOW. Nothing else here may round a drop. Two rounding
// grids is the single worst defect the spec's competitor teardown found: their
// TV heights round to a quarter inch and their hole positions to an eighth, so
// two numbers on the same screen disagree by up to 1/8 in and their drag bottoms
// out 3/4 in short of where their own buttons reach. If you find yourself
// writing Math.round on an inch value anywhere else in this file, that is the
// bug, not the fix.
const TARGET_STEP = 0.5;   // #target's own step attribute. THE quantum.
const TARGET_MIN  = 1;     // #target's server-rendered floor: the pre-existing
                           // "a drop is more than zero" rule, on the step grid.
const TARGET_MAX  = 200;   // #target's max, and the drag's upper stop.
const TILE_MIN    = 0;     // #tiles' min. Named rather than written as a bare 0
                           // in applyTileDepth, because the scene now PUBLISHES
                           // these two as the tile handle's stops and the
                           // published limit has to be the enforced one.
// #tiles' OUTER bound, and it is the same 200 in the drop uses, on purpose.
//
// It was 60 until 2026-07-27 and nothing had ever chosen that number. Real drop
// ceilings routinely hang further than 60 in below the structure (warehouses,
// gyms, big-box retail, atriums), so the cap sent those customers to the phone
// over an invented limit while the drop field beside it accepted 200. Owner call:
// 200 in is the outer bound of the whole tool, and the tile cap rises to match.
//
// [WARN] THIS IS THE OUTER BOUND, NOT THE ENFORCED CAP. The tile line can never
// actually reach 200, because the screen has height and has to hang clear below
// the tiles. The enforced cap is DERIVED per TV size by tileCap() below and is
// what the field, the drag stop, End, aria-valuemax and the at-limit copy all
// use. This constant is only the fallback for "no TV size known", which is
// exactly what VMSElevation.maxTileDepth returns in that case.
const TILE_MAX    = TARGET_MAX;

// Round UP to a field step. A computed minimum like 35.784 written straight into
// an input with step="0.5" makes it :invalid with a stepMismatch, and the
// browser then starts refusing the customer's own perfectly good number.
// Rounding DOWN would undo the clamp, so it is always up.
// Its real home is VMSElevation.fieldFloor(); the local fallback is here so this
// file keeps working if the geometry asset ever ships a version behind.
function stepUp(v, step){
  const E = window.VMSElevation;
  if(E && typeof E.fieldFloor === 'function') return E.fieldFloor(v, step);
  return Math.ceil((Number(v) - 1e-9) / step) * step;
}

// The TV size as a usable NUMBER, or null. Pulled out of elevOpts on 2026-07-27
// because tileCap() needs it too and tileCap() cannot call elevOpts(): elevOpts
// now clamps the tile depth it publishes THROUGH tileCap, so going back through
// elevOpts would recurse. One reader, one set of rules for what counts as a size.
function tvSizeNum(){
  return (state.tvSize != null && !isNaN(state.tvSize) && Number(state.tvSize) > 0)
    ? Number(state.tvSize) : null;
}

// The opts object every VMSElevation call takes. ONE builder, so the clamp, the
// explanation and the drawing can never be computed from different inputs.
//
// [WARN] Everything here is Number()'d, or coerced to a real boolean, before it
// leaves. VMSElevation's internal num() REJECTS NUMERIC STRINGS silently rather
// than erroring: minDrop({hasDropCeiling:true, tileDepth:'18', tvSize:'70'})
// does not throw, it quietly downgrades to the weaker belowTiles rule and hands
// back a floor 17 in too shallow. URLSearchParams and dataset both hand out
// strings, so this is the likeliest way to break the clamp without noticing.
function elevOpts(){
  const plate = state.plateSku ? getPlate(state.plateSku) : null;
  const head  = state.headSku  ? getHead(state.headSku)   : null;
  const size  = tvSizeNum();
  // null (never asked) is NOT yes. It applies no clamp, same as an explicit no,
  // but the distinction is kept everywhere except right here, because minDrop
  // needs a real boolean.
  const yes  = state.hasDropCeiling === true;
  // CLAMPED, via the one accessor. See tileDepth() for why the clamp is here and
  // not in the #tiles input handler.
  const tile = tileDepth();
  return {
    hasDropCeiling: yes,
    tileDepth: tile,
    tvSize: size,
    // These three are what minDrop's structural floors are built from, and they
    // stopped being decoration on 2026-07-27. Until then only sceneModel read the
    // two offsets (to place the pipe ends) and minMastLength did not exist, so
    // minDrop knew nothing about the parts and answered 0 for every build with no
    // drop ceiling. That was the owner's defect: the TV could be dragged above the
    // surface the plate bolts to. A null here SKIPS its constraint rather than
    // guessing, so an incomplete pick draws a partial scene instead of a wrong stop.
    // [WARN] plateOffset now feeds ONE floor, the shortestMast sum. Since the owner
    // ruling of 2026-07-28 evening the ceiling-clearance floor has no plate term at
    // all, so a change to this value no longer moves that stop.
    plateOffset: plate ? Number(plate.length_offset) : null,
    headOffset: head ? Number(head.length_offset) : null,
    minMastLength: MIN_MAST_LENGTH,
    // Orientation, straight from head.horizontal (HORIZONTAL_HEADS). The geometry
    // reads it to skip the tall-screen floors and to draw the flat box. False for
    // every vertical head and when no head is picked yet. Owner ruling 2026-08-26.
    horizontal: head ? !!head.horizontal : false,
    // [2026-09-22] The head top above the VESA disc (HEAD_TOP_ABOVE_VESA_IN), for
    // the horizontal floors headClearsTiles and headClearsStructure, the matching
    // clearanceGap and the tile cap. null for every vertical head.
    headTopIn: head ? head.headTopIn : null,
    tv: tvPlace()
  };
}

// The physical floor, plus that floor moved up onto the field's step grid.
//
// [WARN] minDrop can legitimately BE 0 and that is a real answer, not a missing
// one. Always read .reason, never the number alone.
//
// [WARN] AND DO NOT READ reason === 'ceilingNoDepth' AS "we still need the tile
// depth". It was that, up to 2026-07-27, when the drop ceiling was the only thing
// minDrop knew about. minDrop now returns the MAXIMUM of every constraint that
// applies, and the ceilingNoDepth branch contributes a floor of ZERO, so the
// instant a parts or screen floor binds it loses the max and stops being the
// reason. That is correct: reason names the constraint the customer just hit.
// The outstanding question rides alongside as `needsTileDepth`, which is true
// whenever a drop ceiling is declared without a usable depth, whatever else is
// binding. It is the feature's DEFAULT state, so anything that asks for the tile
// depth keys on the flag. Never word either one as "no drop ceiling".
function dropLimits(o){
  const E = window.VMSElevation;
  const lim = (E && E.minDrop)
    ? E.minDrop(o || elevOpts())
    : {min:0, reason:'none', needsTileDepth:false};
  return {
    min: lim.min,
    reason: lim.reason,
    needsTileDepth: !!lim.needsTileDepth,
    floorQ: Math.max(stepUp(lim.min, TARGET_STEP), TARGET_MIN)
  };
}

// Round DOWN to a field step. The mirror of stepUp, and the mirror reason: a cap
// of 182.216 in an input with step="0.5" is a stepMismatch, and rounding to
// NEAREST can still put the cap ABOVE the real one (a 65 in screen's raw cap of
// 183.44 rounds to 183.5, which is why this always rounds down instead, never
// nearest). Its real home is VMSElevation.fieldCap(); the local fallback keeps
// this file working if the geometry asset ever ships a version behind.
function stepDown(v, step){
  const E = window.VMSElevation;
  if(E && typeof E.fieldCap === 'function') return E.fieldCap(v, step);
  return Math.floor((Number(v) + 1e-9) / step) * step;
}

// THE ENFORCED TILE CAP, in inches, already on the field's step grid.
//
// DERIVED, never a constant. The tile line cannot reach TILE_MAX, because the
// TV has height: a tile depth demands a drop of tileDepth + C/2 + clearance
// (minDrop's tvClearsTiles branch, C the TV cabinet height), and that drop caps
// at TARGET_MAX. Solve it backwards and the deepest usable tile depth is
// TARGET_MAX - C/2 - clearance, which is about 182.0 in for a 70 in screen and
// MOVES with the size. With no TV size known maxTileDepth hands back the plain
// outer bound rather than guessing a TV height, which is the same "weaker but
// never wrong" discipline as
// minDrop's belowTiles branch.
//
// [WARN] IT ROUNDS DOWN, and the half inch that costs is buying a real guarantee.
// At the raw cap the required drop is EXACTLY 200, and floats being floats, a
// value one ULP over would send floorQ to 200.5 against a max of 200: #target
// would then be invalid whatever the customer typed. That failure mode has already
// been seen once in the browser (2026-07-27, the tile commit path). Rounding the
// cap down onto the same 0.5 in grid puts floorQ at 200 with room to spare, and
// scripts/test_vms_elevation.js asserts the whole chain across every screen size.
//
// This is NOT a second rounding grid. It is the same TARGET_STEP grid stepUp uses,
// applied to a maximum instead of a minimum, which is the only direction a cap can
// safely move.
//
// [WARN] THE NO-ARGUMENT PATH MUST NOT CALL elevOpts(). It did until 2026-07-27,
// which was harmless only while elevOpts published the tile depth raw. elevOpts
// now clamps that depth through this function, so elevOpts -> tileCap -> elevOpts
// would be infinite recursion. Only two fields are ever read here, so the fallback
// builds exactly those two and nothing else.
function tileCap(o){
  const E = window.VMSElevation;
  // [2026-09-22] The no-argument path reads the head directly (never elevOpts, see
  // the warning above) so a horizontal head's cap follows its head-top rule.
  let opts = o;
  if(!opts){
    const hd = state.headSku ? getHead(state.headSku) : null;
    opts = {tvSize: tvSizeNum(), horizontal: hd ? !!hd.horizontal : false, headTopIn: hd ? hd.headTopIn : null, tv: tvPlace()};
  }
  const raw = (E && E.maxTileDepth)
    ? E.maxTileDepth(TARGET_MAX, opts.tvSize, opts.minClearance,
        {horizontal: !!opts.horizontal, headTopIn: opts.headTopIn, tv: opts.tv})
    : null;
  const q = stepDown(raw == null ? TILE_MAX : raw, TARGET_STEP);
  return (q == null || q < TILE_MIN) ? TILE_MIN : q;
}

/**
 * THE tile depth, as every consumer must see it: a real number inside
 * [TILE_MIN, tileCap()], or null when we do not have one.
 *
 * WHY THIS EXISTS, 2026-07-27. `state.case3Dist` is written RAW on every
 * keystroke in #tiles, deliberately: the commit path is what normalises the
 * field, because rewriting the box from the value it is halfway through typing
 * eats the decimal point out of "18.5" (see the [WARN] on commitTileDepth). The
 * cost was that everything downstream saw the raw number too, and mid-typing a
 * depth past the cap published an ILLEGAL FLOOR. Measured on dev at a 70 in
 * screen, cap 182, typing 999 one digit at a time: #target got min 1017
 * against max 200, so the field was :invalid with a rangeUnderflow, and the drop
 * handle advertised aria-valuemin 1017 / aria-valuemax 1017 with a valuenow
 * of 200 sitting outside its own range. Typing a PLAUSIBLE number did it too:
 * 190 gave a floor of 208 and #dropNote offered "the shortest length that clears
 * the tiles is 208 inches", which is deeper than the tool can build.
 *
 * So the clamp is at the point of USE, not on the field. The text can stay
 * exactly as typed until the customer commits, while the floor, the drawing, the
 * aria-* stops, the readout, the summary and the shared URL all stay legal.
 *
 * [WARN] EVERY READ OF state.case3Dist THAT FEEDS A NUMBER, A LIMIT OR A
 * SENTENCE GOES THROUGH HERE. Exactly two reads legitimately do not, and neither
 * of them publishes anything: clampTileToCap(), which is working out what to
 * normalise the FIELD to, and the boot restore that seeds #tiles' text before
 * clampTileToCap runs. Presence tests (`!= null`) are not reads of the value and
 * are fine as they are. If you add a reader that shows a number, it uses this.
 *
 * It also carries the "is there a drop ceiling" gate, so no caller has to
 * remember it separately. A depth behind an explicit "no" is a contradiction the
 * radio handler already clears; this makes it unreachable rather than unlikely.
 */
function tileDepth(){
  if(state.hasDropCeiling !== true) return null;
  if(state.case3Dist == null || isNaN(state.case3Dist)) return null;
  const n = Number(state.case3Dist);
  if(!isFinite(n)) return null;
  // TILE_MIN first, and it matters: a negative depth reaches minDrop as
  // "declared a drop ceiling, gave us nothing usable" and the ask for the depth
  // stays up. Clamping to 0 instead is what the commit path already does, so the
  // preview and the commit now agree on that edge too.
  if(n < TILE_MIN) return TILE_MIN;
  const cap = tileCap();
  return n > cap ? cap : n;
}

/**
 * THE drop pipeline. Order is load-bearing and is the one written in
 * SPEC-drag-elevation-2026-07-26.md section 4.5:
 *   1. clampDrop    the hard stop at the physical minimum (owner decision D3).
 *   2. resolveDrop  the whole inch increment rule (D5). Its .drop is the value
 *                   that gets displayed and stored. A clamped-but-unresolved
 *                   drop is never shown.
 *   3. quantise ONCE to TARGET_STEP, then re-apply the floor, which is itself
 *                   rounded UP to that same step.
 * Every readout, the handle position and the solver input are derived from the
 * single number this returns.
 *
 * Honest note for anyone auditing step 2: after step 1 the bump is
 * arithmetically a no-op today. minDrop's tvClearsTiles floor is defined as the
 * drop whose gap is exactly MIN_CLEARANCE, clearanceOK accepts that value, and
 * clearanceGap only returns a non-null gap on the same branch, so a clamped drop
 * always already satisfies the increment rule. It is still run, in this order,
 * because that is the contract and because it is what keeps the invariant true
 * if minDrop's branches ever change. `bumped` should therefore stay false in
 * practice; the UI handles it anyway rather than assuming.
 *
 * [WARN] THAT NOTE WAS FALSE FROM THE DAY IT WAS WRITTEN UNTIL 2026-07-28, and it
 * is worth keeping as a lesson: it reasoned correctly about the algebra and never
 * checked the arithmetic. clearanceOK compared a reconstructed gap with a bare >=,
 * the reconstruction lands a few femtometres short, so on 1124 size-and-tile
 * combinations a freshly clamped drop was rejected by the very predicate its floor
 * came from and this pipeline bumped it a whole inch. Quantising then pulled some
 * of that back, so what the customer actually saw was the typed path overshooting
 * the field's own published minimum by half an inch to a full inch, with "We moved
 * your length" explaining a tile collision that was not happening, while the drag
 * reached the same stop and said nothing. Found by adversarial review, fixed with
 * an epsilon in clearanceOK, and now swept over every integer TV size against every
 * half inch of tile depth in scripts/test_vms_elevation.js. The note is true again.
 * An assertion that something "should stay false in practice" is worth exactly the
 * test standing behind it, and there was none.
 */
function resolveDropValue(raw){
  const E = window.VMSElevation;
  const o = elevOpts();
  const lim = dropLimits(o);
  const n = (raw === '' || raw == null) ? NaN : Number(raw);
  if(isNaN(n)){
    return {drop:null, bumped:false, clamped:false, min:lim.min, reason:lim.reason,
            needsTileDepth:lim.needsTileDepth, floorQ:lim.floorQ};
  }
  const clamped = (E && E.clampDrop) ? E.clampDrop(n, o) : n;
  const res     = (E && E.resolveDrop) ? E.resolveDrop(clamped, o) : {drop:clamped, bumped:false};
  let q = Math.round(Number(res.drop) / TARGET_STEP) * TARGET_STEP;
  // Quantising moves by at most half a step, but half a step DOWN across the
  // floor would quietly undo the clamp, so the floor is re-applied here, on the
  // step grid. This is the second half of "quantise once", not a second grid.
  if(q < lim.floorQ) q = lim.floorQ;
  if(q > TARGET_MAX) q = TARGET_MAX;
  return {
    drop: q,
    bumped: !!res.bumped,
    clamped: clamped > n + 1e-9,
    // reason names the constraint that BOUND; needsTileDepth is a separate
    // question that can be open at the same time. Both travel together so no
    // caller has to re-derive one from the other, which is how they drift apart.
    min: lim.min, reason: lim.reason, needsTileDepth: lim.needsTileDepth, floorQ: lim.floorQ
  };
}

// Last pipeline result, kept for the browser-driven checks under ?vmscfg_diag=1.
let lastDrop = null;

// #target's min has to track the computed floor: the static min="1" in the
// markup is only correct when there is no drop ceiling.
function syncTargetFloor(){
  const t = el('target');
  if(t) t.min = String(dropLimits().floorQ);
}

// And the same job for #tiles' max, which moves with the TV size now that it is
// derived. The server renders max="200", the outer bound, which is the correct cap
// only while no size is entered. Once the JS is running it owns the attribute.
function syncTileCap(){
  const ti = el('tiles');
  if(ti) ti.max = String(tileCap());
}

// A bigger TV LOWERS the tile cap, so a depth that was legal when it was typed can
// stop being legal. Entering 190 with no size and then choosing an 85 in TV is the
// case: the cap drops to about 178.5 and 190 would quietly demand a 211.5 in drop.
//
// [WARN] IT MUST NOT ROUTE THROUGH applyTileDepth. That function ends by calling
// applyDrop('commit'), which calls renderStage3(), which is where this is called
// from: a loop. It writes state and the field directly instead, and lets the
// applyDrop the caller was already about to run pick the new floor up.
function clampTileToCap(){
  if(state.case3Dist == null || isNaN(state.case3Dist)) return;
  const cap = tileCap();
  if(!(state.case3Dist > cap)) return;
  state.case3Dist = cap;
  const ti = el('tiles');
  if(ti){
    ti.value = String(cap);
    // The tile commit guard tracks the text IT last committed. Writing the field
    // from here without telling it leaves a stale guard, so the next blur commits
    // again and rebuilds #s3out underneath whatever click moved the focus.
    lastTileCommit = ti.value;
  }
}

// ---- THE STARTING LENGTH (2026-07-29, owner: "just put a default 30 inches as a
// starting point") ---------------------------------------------------------------
// Step 4 used to open with an empty box, which asks the customer to invent a number
// before the drawing has anything to show them. It opens at 30 now.
//
// [RULE] IT IS A STARTING POINT, NOT A COMMIT, and three things follow from that:
//   1. It is SILENT. It runs through applyDrop's 'seed' phase, which solves and
//      renders exactly like a commit but writes nothing to #dropSay, so nobody is
//      told we moved a number they never typed.
//   2. It stays OUT OF THE URL until the customer touches THE LENGTH ITSELF, or
//      the value stops being derivable from a fresh seed (owner fix 2026-07-31:
//      answering the drop-ceiling question used to count as "touched" and wrote
//      target=30 into the URL). A link that carried target=30 by default would
//      turn every fresh visit into a "shared build", and the deep-link analytics
//      would count it as one. See syncURL and the flag rule in applyDrop.
//   3. It publishes NOTHING. The step_completed hooks key on size/plate/head and
//      on step 4 becoming REACHABLE, none of which this changes; the s3 event has
//      already fired from applyStepStates by the time the seed runs.
// It still goes through the whole clamp/resolve/quantise path, so a floor above 30
// wins and the field lands ON the floor. 30 is never forced.
const TARGET_DEFAULT = 30;

// True while #target holds the seeded default (or a value still derivable from it,
// see the flag rule in applyDrop). Read by syncURL; cleared by the LENGTH-aimed
// gestures only (the #target input and change handlers, stepTarget, the scene's
// onDropChange) and by applyDrop itself the moment the value goes unreproducible.
// Re-runs from the radio answer, a tile entry or a size change leave it standing
// (2026-07-31).
let targetIsDefault = false;
let pendingTvQuiet = false, tvPrevLims = null, tvSizeFromCard = false;

// Returns TRUE when it applied the default, which means it has already run the
// full render and the caller has nothing left to do.
function seedDefaultTarget(){
  // A real value always wins: a restored ?target=, or anything the customer has
  // typed or dragged. This is also what makes an emptied field stay empty, because
  // the routes that empty it do not come through here.
  if(state.target != null && !isNaN(state.target)) return false;
  if(!stepReachable('s3')) return false;      // step 4 is not part of the build yet
  if(inDropPipeline) return false;            // never seed from inside the pipeline
  // [WARN] THE FLAG GOES UP BEFORE THE PIPELINE RUNS, NOT AFTER. applyDrop renders,
  // the render calls renderSummary, and renderSummary calls syncURL, which BUILDS
  // ITS QUERY STRING SYNCHRONOUSLY and only defers the history write. Setting the
  // flag afterwards therefore lets target=30 into that string, which is the exact
  // thing rule 2 above forbids, and the 400 ms debounce hides it from anyone
  // testing by hand. applyDrop's own 'seed' branch never clears it, so this
  // ordering is safe as well as necessary.
  targetIsDefault = true;
  applyDrop(TARGET_DEFAULT, 'seed');
  return true;
}

// ---- THE STARTING TILE DEPTH (2026-07-31, owner: the drop-ceiling question
// pre-populates the depth) --------------------------------------------------------
// Answering "yes, suspended tiles" used to reveal an empty box and a floor note
// saying we cannot do the sum without a number, which asks the customer to invent
// one. It opens at 12 now, which is a real ceiling plenum rather than a placeholder.
//
// [RULE] SAME THREE RULES AS TARGET_DEFAULT ABOVE, for the same reasons:
//   1. It is SILENT. seedTileDepth writes state and the field only; the pipeline
//      run that follows it is the caller's, and on the boot path that run is the
//      length seed's own 'seed' phase, which never writes #dropSay.
//   2. It stays OUT OF THE URL until the customer touches the depth. See syncURL.
//   3. It publishes NOTHING: no analytics hook keys on the tile depth.
// It is quantised and clamped exactly like a typed depth, so it can never hand the
// floor a number applyTileDepth would have refused.
const TILES_DEFAULT = 12;

// True while #tiles holds the seeded default and nothing else has happened. Read by
// syncURL; cleared by applyTileDepth, by the #tiles input handler and by the "no"
// answer, which are between them every route to a real value or to no value at all.
let tilesIsDefault = false;

// Returns TRUE when it applied the default. It deliberately runs NO pipeline of its
// own: every caller below re-solves the drop on the line after, and calling
// applyTileDepth from here would run that solve twice and (through its 'commit'
// phase) speak before the caller's own commit had settled the length.
function seedTileDepth(){
  // A real depth always wins: a restored ?tiles=, a drag, or anything typed.
  if(state.case3Dist != null && !isNaN(state.case3Dist)) return false;
  // The same quantise-and-clamp applyTileDepth runs, so the seed lands on the field
  // grid and inside the derived cap. tileCap() moves with the TV size.
  let q = Math.round(TILES_DEFAULT / TARGET_STEP) * TARGET_STEP;
  if(q < TILE_MIN) q = TILE_MIN;
  const cap = tileCap();
  if(q > cap) q = cap;
  state.case3Dist = q;
  const ti = el('tiles');
  if(ti){
    ti.value = String(q);
    // The commit guard tracks the text it last committed, so seeding without
    // telling it leaves a stale guard and the first focus-then-blur off an
    // untouched seeded field commits again and rebuilds #s3out under the click
    // that moved the focus. Same reason clampTileToCap sets it. See commitTileDepth.
    lastTileCommit = ti.value;
  }
  tilesIsDefault = true;
  return true;
}

// Re-entrancy guard for applyDrop. That function ends by handing a fresh model
// to the scene, and the scene is a separate module: if a future version of
// update() ever emits onDropChange or onTileChange while it is being updated,
// that is an infinite loop that locks the tab rather than an obvious bug. The
// contract says update() is a setter and emits nothing, so this should never
// fire; it costs two lines and turns a hang into a dropped frame.
let inDropPipeline = false;

// The single committed-value path. EVERY input route lands here: the drag's
// commit, the number field's change event, a keyboard step inside the scene,
// and a restored URL. Three phases:
//   'move'        a live pointermove. Repaints the picture and the number only,
//                 which is cheap and silent. No solve, no announcement.
//   'dragcommit'  the drag released. Full solve, but SILENT here, because the
//                 scene owns its own visually-hidden live region and announces
//                 the released value itself. Two polite regions firing on one
//                 release say the same thing twice.
//   'commit'      everything that is not the drag: the number field's change
//                 event and a restored URL. Full solve, and it speaks.
//   'seed'        the starting length (2026-07-29). Full solve and full render,
//                 like 'commit', but SILENT: see TARGET_DEFAULT above. It is the
//                 only phase the customer did not cause.
function applyDrop(raw, phase){
  if(inDropPipeline) return;
  inDropPipeline = true;
  try {
    const res = resolveDropValue(raw);
    // [RULE] THE DEFAULT FLAG SURVIVES RE-RUNS THE CUSTOMER DID NOT AIM AT THE
    // LENGTH (owner fix 2026-07-31: merely answering the drop-ceiling question
    // used to clear it here and write target=30 into the URL). Ownership is
    // declared at the gesture sites now: the #target input and change handlers,
    // stepTarget and the scene's onDropChange each drop the flag themselves, so a
    // radio answer, a tile entry or a size change re-running this pipeline leaves
    // it standing. The ONE thing this function still clears it for is the value
    // going unreproducible: a fresh visit derives max(TARGET_DEFAULT, floor), and
    // while the result still equals that, a link without target= restores this
    // exact page (the receiving end seeds and clamps to the same number). The
    // moment they part ways (a clamp raised the value, then the floor dropped
    // back: the value stays put, the derivation does not) the URL has to carry
    // the number or the link lies. Checked AFTER the resolve, against the same
    // res the render below publishes, and before it, because renderSummary ->
    // syncURL runs inside renderStage3.
    if(phase !== 'seed' && targetIsDefault){
      const derived = Math.max(TARGET_DEFAULT, res.floorQ);
      if(res.drop == null || Math.abs(res.drop - derived) > 1e-9) targetIsDefault = false;
    }
    lastDrop = res;
    state.target = res.drop;
    const t = el('target');
    if(t){
      t.min = String(res.floorQ);
      const shown = res.drop == null ? '' : String(res.drop);
      if(t.value !== shown) t.value = shown;   // programmatic: fires no input event
    }
    // The stepper's disabled states are derived from the SAME result, in the same
    // pass that writes the field, so a button can never advertise headroom the
    // pipeline has already refused. It runs on the drag phase too: the floor moves
    // with the tile line and the TV size, so a drag can strand the down button
    // enabled at a stop it can no longer reach.
    syncStepButtons();
    if(phase === 'commit' || phase === 'dragcommit' || phase === 'seed'){
      // Only 'commit' speaks. 'dragcommit' CLEARS the line because the scene
      // announces its own release, and 'seed' clears it because there is nothing
      // to report: the box starts empty, so sayDrop(null) is a no-op there and the
      // starting length is genuinely silent.
      sayDrop(phase === 'commit' && !pendingTvQuiet ? res : null);
      renderStage3();        // sub + advisory box + #s3out + the note + the scene
    } else {
      renderDropNote();
      updateElevScene();
    }
  } finally {
    inDropPipeline = false;
  }
}

// The drop-ceiling line is the second drag handle. Same rule as the drop: the
// scene hands over raw inches, and this is the only place they are quantised.
function applyTileDepth(raw, phase){
  const n = Number(raw);
  if(isNaN(n)) return;
  pendingTvQuiet = false;
  // Every call is a customer gesture (a drag, a typed commit, a stepper tap), so the
  // depth is theirs from here and belongs in a shared link. The seed deliberately
  // does NOT come through this function. Set before the render, because the commit
  // phase below ends in renderSummary -> syncURL. See TILES_DEFAULT.
  tilesIsDefault = false;
  let q = Math.round(n / TARGET_STEP) * TARGET_STEP;
  if(q < TILE_MIN) q = TILE_MIN;
  // The DERIVED cap, not TILE_MAX. TILE_MAX is only the outer bound for a build
  // with no TV size; with a size the deepest usable tile depth is
  // TARGET_MAX - C/2 - clearance, because past that there is no drop left that
  // still hangs the TV below the tiles. See tileCap().
  const cap = tileCap();
  if(q > cap) q = cap;
  state.case3Dist = q;
  // Dragging the tile line IS the statement that there is a drop ceiling,
  // exactly as typing a tile depth always has been.
  if(state.hasDropCeiling !== true){ state.hasDropCeiling = true; syncDropCeilingUI(); }
  const ti = el('tiles');
  if(ti && ti.value !== String(q)) ti.value = String(q);
  // Moving the tile line moves the FLOOR, so the drop goes back through the
  // pipeline. Repainting is not enough.
  if(phase === 'commit' || phase === 'dragcommit') applyDrop(state.target, phase);
  else { renderDropNote(); updateElevScene(); }
}

// Why the drop was held, in one sentence, keyed on the constraint that actually
// bound.
//
// [WARN] THIS USED TO BE ONE UNCONDITIONAL SENTENCE and it was about to become a
// lie. It said "Anything shorter puts the top of the screen into the tile line"
// whatever the reason, which was safe only while the drop ceiling was the only
// thing that could move the floor. Since 2026-07-27 two structural floors bind
// with no drop ceiling anywhere in the build, so that sentence would have
// explained the stop by pointing at tiles the customer does not have. Saying the
// wrong reason is worse than saying nothing: it is the silent-hard-stop defect
// with extra confidence. Add a branch here for every reason code minDrop gains.
// DRAFT copy, like every customer-facing string in this file, pending the owner
// voice pass; the parts sentence is the wording already shipped on the scene's
// own at-limit line, kept identical so the two surfaces do not contradict.
// [WARN] THE COPY SAYS TV, THE REASON CODES STILL SAY SCREEN. Since the bezel
// allowance of 2026-07-28 these floors clear the physical cabinet, bezel included,
// so the customer-facing sentences say TV. 'screenClearsPlate' was left as an API
// string on purpose: it is switched on here and in assets/vms-elevation-scene.js,
// and renaming it for accuracy would silently drop a copy branch into the fallback
// on whichever surface was missed.
function heldBecause(reason){
  // [WARN] tvClearsTiles AND belowTiles ARE NOT THE SAME SENTENCE, and they shared
  // one for months. belowTiles fires precisely when NO TV SIZE IS KNOWN: it is the
  // weaker fallback that only keeps the MOUNT below the tiles. Saying "the top of
  // the TV" on that path names a TV the customer never entered and claims a
  // protection we are not applying. renderDropNote() has always split these two
  // correctly; this surface now agrees with it. Found by adversarial review
  // 2026-07-28 alongside the bezel allowance.
  if(reason === 'tvClearsTiles')
    return COPY('held_tv_clears_tiles');
  if(reason === 'belowTiles')
    return COPY('held_below_tiles');
  // Word for word what assets/vms-elevation-scene.js says for this code. The two
  // read "This is", not "That is": they drifted by one word and the comment above
  // claimed they were identical, which is the kind of quietly false doc line this
  // project treats as a defect in its own right.
  // [WARN] These are now TWO SEPARATE EDITABLE SETTINGS that happen to ship the
  // same default sentence (held_shortest_mast here, the drawing's own "as high as
  // the mount can go with the parts you have picked" over in the scene), because
  // this one is spoken after a TYPED number is held and that one explains a stop
  // reached by dragging. An owner who rewords one and not the other gets two
  // wordings for one fact; say so in the schema info, do not silently merge them.
  if(reason === 'shortestMast')
    return COPY('held_shortest_mast');
  // [WARN] THE CODE NAME IS HISTORICAL. Since the owner ruling of 2026-07-28
  // (evening) this stop is the TV clearing the CEILING, not the ceiling plate; the
  // plate is not in the constraint at all, because the TV hangs in front of it.
  // The string, and the setting id derived from it, are deliberately unchanged:
  // they are a cross-file API and they hold the owner's live copy edits. Never
  // rename one without migrating the other. See minDrop in assets/vms-elevation.js.
  if(reason === 'screenClearsPlate')
    return COPY('held_screen_clears_plate');
  // [2026-09-22] The horizontal (face-down) head floors, worded identically to the
  // drawing's highHeadTiles / highHeadStructure. [tiles] is read from the one
  // tile-depth accessor, so the WebMCP hand-back (getBuild) gets the same number.
  if(reason === 'headClearsTiles'){
    const td = tileDepth();
    return fill(COPY('held_head_clears_tiles'), {tiles: td == null ? '' : niceIn(td)});
  }
  if(reason === 'headClearsStructure')
    return COPY('held_head_clears_structure');
  return COPY('held_default');
}

// The one sentence we owe the customer when we move their typed number.
//
// Passing null CLEARS it, which is what a drag commit does: the scene has its
// own visually-hidden live region and announces the released value itself, so
// speaking here too would say the same thing twice on one release. Never called
// per pointermove either way; announcing every frame of a drag is exactly the
// accessibility defect this feature exists to avoid.
function sayDrop(res){
  const box = el('dropSay');
  if(!box) return;
  let msg = '';
  if(res && res.drop != null){
    // fill(), never fillHTML(): this lands in textContent, so a markup token would
    // be read out as literal angle brackets instead of a phone number.
    if(res.bumped){
      msg = fill(COPY('say_moved'), {length: niceIn(res.drop), phone: PHONE_TEXT});
    } else if(res.clamped){
      // Keyed on the SAME two reason codes heldBecause() branches on for the tile
      // sentences, so the lead clause and the reason clause can never disagree
      // about whether tiles are involved. Any new tile floor minDrop gains has to
      // be added in both places. See say_held_tiles (owner ruling 2026-07-31).
      const tileFloor = res.reason === 'tvClearsTiles' || res.reason === 'belowTiles' ||
        res.reason === 'headClearsTiles';
      msg = fill(COPY(tileFloor ? 'say_held_tiles' : 'say_held'), {
        length: niceIn(res.drop),
        reason: heldBecause(res.reason),
        phone: PHONE_TEXT
      });
    }
  }
  // Guarded so an already-empty live region is not rewritten on every commit.
  if(box.textContent !== msg) box.textContent = msg;
}

// The standing explanation of where the floor is and why. Deliberately NOT a
// live region: it is rewritten on every keystroke in #target, and a live region
// rewritten per keystroke is noise. The one thing that genuinely needs
// announcing goes through sayDrop() instead.
function renderDropNote(){
  const box = el('dropNote');
  if(!box) return;
  const lim = dropLimits();
  const sizeKnown = state.tvSize != null && !isNaN(state.tvSize) && Number(state.tvSize) > 0;
  const parts = [];
  const tp = tvPlace(), placed = !!(tp && tp.placed), hold = tp ? (placed ? 'where its mounting holes sit.' : 'taking its mounting holes as centered between the top and bottom edges.') : '';

  if(state.hasDropCeiling === true){
    // [WARN] KEYED ON THE FLAG, NOT ON reason === 'ceilingNoDepth'. Since
    // 2026-07-27 minDrop returns the max of every constraint, and ceilingNoDepth
    // contributes a floor of zero, so it loses the reason to any parts or screen
    // floor that binds. Reading the reason here would have made this ask vanish
    // on the commonest path of all: drop ceiling declared, TV size entered, tile
    // depth not typed yet. The flag says it outright and is what to trust.
    if(lim.needsTileDepth){
      parts.push(fillHTML(COPY('note_need_tiles')));
    } else if(lim.reason === 'belowTiles'){
      parts.push(fillHTML(COPY('note_below_tiles'), {floor: niceIn(lim.floorQ)}));
    } else if(lim.reason === 'tvClearsTiles'){
      parts.push(tp
        ? fillHTML('The shortest length that clears the tiles is [floor]. That keeps your TV\'s top edge at least 1/8 inch below the tile line, using its own height and ' + hold, {floor: niceIn(lim.floorQ)})
        : fillHTML(COPY('note_tv_clears_tiles'), {
        floor: niceIn(lim.floorQ),
        tv: nice(Number(state.tvSize))
      }));
    } else if(lim.reason === 'headClearsTiles'){
      // [2026-09-22 round 2] A horizontal (face-down) head: its own top clears
      // the tiles. [floor] is offered too so an owner edit can name the length.
      const td = tileDepth();
      parts.push(fillHTML(COPY('note_head_clears_tiles'), {
        floor: niceIn(lim.floorQ),
        tiles: td == null ? '' : niceIn(td)
      }));
    }
  }

  // The two parts floors, added 2026-07-28 on owner word (the bezel review had
  // flagged their absence: a build with no drop ceiling had no standing note at
  // all). Keyed on lim.reason, which is safe here unlike the tile-depth ask
  // above: neither of these floors is ever zero, so when one of them holds the
  // reason slot it IS the binding constraint. They print with or without a drop
  // ceiling, and never alongside a tiles sentence, because the block above only
  // prints when the reason is one of the two tile codes and minDrop names
  // exactly one binding reason.
  if(lim.reason === 'shortestMast'){
    parts.push(fillHTML(COPY('note_shortest_mast'), {floor: niceIn(lim.floorQ)}));
  } else if(lim.reason === 'screenClearsPlate'){
    parts.push(tp
      ? fillHTML('The shortest length for your TV is [floor]. That keeps its top edge at least 1/8 inch below the ceiling, using your TV\'s own height and ' + hold, {floor: niceIn(lim.floorQ)})
      : fillHTML(COPY('note_screen_clears_plate'), {
      floor: niceIn(lim.floorQ),
      tv: nice(Number(state.tvSize))
    }));
  } else if(lim.reason === 'headClearsStructure'){
    // [2026-09-22 round 2] The horizontal counterpart of screenClearsPlate. It
    // never binds with today's parts (the shortest mast is deeper), but a code
    // minDrop can return gets a sentence here rather than silence.
    parts.push(fillHTML(COPY('note_head_clears_structure'), {floor: niceIn(lim.floorQ)}));
  }

  // [WARN] OWNER DECISION D2, REVERSED 2026-07-28 (see the file header). D2 said a
  // blank TV size must not block the drag and that the benefit of entering one had
  // to be visible rather than assumed; the size is REQUIRED now, so step 1 asks
  // for it before step 2 opens and the only way to be standing here without one is
  // an old shared link. The note stays because that link still has to say
  // something useful, and the drag genuinely still works without a size.
  if(!sizeKnown){
    parts.push(fillHTML(COPY(state.hasDropCeiling === true ? 'note_no_size_dc' : 'note_no_size')));
  }

  box.innerHTML = parts.map(p => `<div class="note">${p}</div>`).join('');
}

// Reflect state into the yes/no radios and show or hide the tile-depth field.
// It stays hidden until the answer is yes, because asking how far it is down to
// a ceiling that does not exist is how a number ends up typed into it anyway.
// [WARN] This does NOT write #tiles' value. It runs from the #tiles input
// handler, and rewriting a field from the value it is mid-way through typing
// eats the decimal point out of "18.5". The two places that legitimately set
// that field (a tile drag, and the initial restore) set it explicitly.
function syncDropCeilingUI(){
  document.querySelectorAll('input[name="vmscfg-dropceiling"]').forEach(r => {
    r.checked = (r.value === 'yes' && state.hasDropCeiling === true) ||
                (r.value === 'no'  && state.hasDropCeiling === false);
  });
  const f = el('tilesField');
  if(f) f.classList.toggle('hidden', state.hasDropCeiling !== true);
}

// ---- the scene ----
// Mounted ONCE, the first time step 4 becomes visible, and never re-mounted on a
// render. #elevScene is a container nothing else ever writes to; a repaint of it
// mid-drag would destroy the handles, their listeners and any live pointer
// capture. Keeping it out of #s3out is what makes that guarantee hold.
let elevScene = null, elevSceneDead = false;

function mountElevScene(){
  if(elevScene || elevSceneDead) return;
  if(!window.VMSElevationScene) return;   // asset absent: no picture, everything else still works
  const root = el('elevScene');
  if(!root) return;
  // [WARN] NEVER MOUNT INTO A BOX WITH NO RECTANGLE (2026-07-28, the four-step
  // disclosure). Step 4 can now be reachable while its body is collapsed, and a
  // collapsed body is display:none, so the mount point measures ZERO width. The
  // scene derives its px-per-inch from that width, and a scene born at zero does
  // not repair itself just because the box later grows: it has already latched a
  // scale and a canvas key. getClientRects().length is the honest "is this laid
  // out" test (offsetParent lies about position:fixed ancestors). Returning here
  // is not a failure and must NOT set elevSceneDead: applyStepStates calls
  // renderStage3 again the moment step 4 is opened, and it mounts then.
  if(!root.getClientRects().length) return;
  try {
    elevScene = window.VMSElevationScene.mount(root, {
      spriteUrl: SPRITE_URL,
      // The scene emits RAW UNROUNDED inches. Both handlers go straight into the
      // pipeline, which is the only place a drop or a tile depth is rounded.
      // A scene commit maps to 'dragcommit', not 'commit': same full solve, but
      // silent, because the scene announces its own release.
      // Dragging the head (or arrowing it: every scene gesture funnels through
      // here) aims at the length itself, so ownership of the seeded default is
      // declared before the pipeline runs. See the flag rule in applyDrop
      // (2026-07-31). The tile handle deliberately does NOT do this: it aims at
      // the tile line, and applyTileDepth owns the TILES flag instead.
      onDropChange: (inches, phase) => { targetIsDefault = false; pendingTvQuiet = false; applyDrop(inches, phase === 'commit' ? 'dragcommit' : 'move'); if(phase === 'commit') state.targetWanted = state.target; },
      onTileChange: (inches, phase) => applyTileDepth(inches, phase === 'commit' ? 'dragcommit' : 'move'),
      // THE AT-LIMIT SENTENCES, owner-edited. We pass the OVERRIDE ONLY: a null
      // here means the scene uses its own shipped default, so each of these
      // sentences has exactly ONE fallback copy, in the file that renders it.
      // Duplicating them into COPY_DEFAULTS would be a second source of truth that
      // silently wins whenever the two drift.
      //
      // [RULE] The scene feeds ONE string to BOTH of its surfaces, the visible
      // reason line under the drawing and the live region that speaks on release
      // (see reasonLines/announce there). That is why these are threaded in rather
      // than written onto the reason line from here: an edit cannot make the
      // printed and the spoken explanation disagree if there is only one string.
      copy: {
        // The ONLY non-sentence in this map: the drawing's drop-ceiling label,
        // not an at-limit message. null means the scene uses its shipped default.
        tileLabel: copyRaw('scene_tile_label'),
        needTiles: copyRaw('limit_need_tiles'),
        highTvTiles: copyRaw('limit_high_tv_tiles'),
        highBelowTiles: copyRaw('limit_high_below_tiles'),
        highPlate: copyRaw('limit_high_plate'),
        highParts: copyRaw('limit_high_parts'),
        highHeadTiles: copyRaw('limit_high_head_tiles'),
        highHeadStructure: copyRaw('limit_high_head_structure'),
        lowParts: copyRaw('limit_low_parts'),
        tileMin: copyRaw('limit_tile_min'),
        tileMaxTv: copyRaw('limit_tile_max_tv'),
        tileMaxHead: copyRaw('limit_tile_max_head'),
        tileMaxPlain: copyRaw('limit_tile_max_plain')
      }
    });
  } catch(err){
    // A broken drawing must never take the configurator down with it.
    elevScene = null; elevSceneDead = true;
    console.warn('vmscfg: elevation scene unavailable', err);
    return;
  }
  updateElevScene();
}

/**
 * The three read-only measurements: how tall the TV is, and how far down its
 * top and its bottom land. Owner request 2026-07-27, in his words: "right now the
 * customer is adjusting the middle of screen because that is something we can
 * know, but they might want to know the other measurements."
 *
 * DISPLAY ONLY, AND DELIBERATELY SO. All three already exist on the model
 * sceneModel() returns and the drawing is positioned from, so they are handed in
 * rather than recomputed: one way of working them out, and no arithmetic of their
 * own. There is no new geometry here and there must not be. They are read-only
 * because the drop is still the number the customer can actually adjust; these are
 * consequences of it.
 *
 * THESE NUMBERS AND THE PICTURE BESIDE THEM AGREE AGAIN, since the owner's drawing
 * correction of 2026-07-28. For part of that day they deliberately differed by half an
 * inch top and bottom, because the numbers described the cabinet while the drawing drew
 * the screen. The owner then ruled that the drawn TV is one 16:9 rectangle off the same
 * inflated height, so both surfaces read the same box off the same model and the same
 * drop. The half inch of daylight at the hard stops is gone, and so is the open item
 * about nothing on screen explaining it. See the file header of vms-elevation.js.
 *
 * [WARN] NOT AN aria-live REGION, AND IT MUST NOT BECOME ONE. This runs on every
 * pointermove of the drag. A live region that speaks per move is the documented
 * accessibility defect section 7.2 of SPEC-drag-elevation-2026-07-26.md condemns,
 * and the drag handle's aria-valuetext already announces on release. The markup is
 * a plain definition list in the section, so a screen reader reaches these as
 * ordinary static content at any time.
 *
 * IT PRINTS THE TV, NOT THE SCREEN, since the bezel allowance of 2026-07-28.
 * Every label used to say SCREEN and that was honest at the time: a 16:9 active
 * area was all we could work out from a diagonal, so "top of the TV" would have
 * been a number the customer could measure against a wall and find wrong by an
 * inch. The owner then researched the difference and ruled the physical TV to be
 * the screen plus one inch, split evenly, so there is now a cabinet figure to
 * print and it is what he asked for in the first place: the distance from the
 * mounting surface to the top of the TV.
 *
 * [WARN] THESE READ tvCabinetTop / tvCabinetBottom / tvCabinetHeight, and they have
 * read those since the allowance landed. KEEP THEM ON THOSE FIELDS. The drawing now
 * draws the same numbers (model.tvTop / tvBottom became the cabinet on 2026-07-28),
 * so the two field sets happen to be equal today, but the clearance-named fields are
 * the ones this readout is about and pointing it at the drawn ones would make it
 * follow any future change to what gets drawn.
 *
 * [WARN] The other caveat SURVIVES and its sentence under the numbers must keep
 * carrying it: the mounting holes are still assumed vertically centred, and a real
 * VESA pattern often sits above centre, which puts the TV lower than we show. The
 * bezel allowance did not touch that. Reading it as having fixed both is the
 * mistake the file header of assets/vms-elevation.js warns about.
 */
function renderTvSpan(model, floorQ){
  const box = el('tvSpans');
  if(!box) return;
  const h = model ? model.tvCabinetHeight : null;
  // No TV size, no screen, so there is nothing honest to print and the block
  // hides rather than showing three dashes. [WARN] OWNER DECISION D2 WAS REVERSED
  // ON 2026-07-28 and the TV size is now REQUIRED (see the file header), so this
  // branch is a FALLBACK for a legacy shared link rather than a supported flow.
  // It stays exactly as it is: the ask is still surfaced once, by
  // renderDropNote(), and hiding a block we cannot fill honestly is right either
  // way. Do not "simplify" it away on the grounds that the size is required now.
  if(h == null){ box.hidden = true; return; }
  box.hidden = false;
  const tp = tvPlace(), cav = !tp || tp.faceDown ? '' : tp.placed
    ? 'Measured from the surface the top plate mounts to down to the top and bottom of your TV, using its real height and where its mounting holes sit.'
    : 'Measured from the surface the top plate mounts to down to the top and bottom of your TV, using its real height. We take its mounting holes as mid-screen, so higher holes put the TV lower than we show.';
  if(box.dataset.tvcav !== cav){
    box.dataset.tvcav = cav;
    const note = box.querySelector('.vmscfg-elev__spans-note:not([data-tvcode])');
    let code = box.querySelector('[data-tvcode]');
    if(note && cav && !code){
      code = document.createElement('p');
      code.className = 'vmscfg-elev__spans-note';
      code.setAttribute('data-tvcode', '');
      note.parentNode.insertBefore(code, note.nextSibling);
    }
    if(code) code.textContent = cav;
    setHidden(note, !!cav);
    setHidden(code, !cav);
  }
  // A row hides when its own value is not known yet, which is the "size entered,
  // drop not set" state: the height is real, the two depths are not.
  const row = (rowId, valId, v) => {
    const r = el(rowId), d = el(valId);
    if(!r || !d) return;
    if(v == null){ r.hidden = true; return; }
    r.hidden = false;
    const txt = niceIn(v);
    if(d.textContent !== txt) d.textContent = txt;   // guarded: this runs per move
  };
  // The TV height is a pure function of the TV size, so it shows whatever the
  // drop is doing. It is the one row that is never a consequence of the drop.
  row('spanHeightRow', 'spanHeight', h);

  // [WARN] THE TWO DEPTHS ARE GATED ON THE ENFORCED FLOOR, 2026-07-27, and the
  // gate is here rather than on the value that feeds it. #target's 'input' handler
  // is a deliberate NO-CLAMP live preview (clamping per keystroke turns "30" into
  // the floor the instant "3" lands), which is correct and must stay. But this
  // runs off that preview, so at a 70 in screen with a floor of 21, the "3" of
  // "30" printed "Down to the top of the TV: -14.659 inches": a depth measured
  // DOWNWARD cannot be negative, and every customer typing a two digit drop
  // flashed one. Below the floor there is no honest answer yet, so the two rows go
  // back to the not-known-yet hidden state they already use before a drop is set.
  // Do NOT "fix" this by clamping the preview instead.
  //
  // Only the FLOOR, not the maximum: a preview past TARGET_MAX prints a number
  // that is deeper than we can build but is still a real measurement, and hiding
  // legal-but-not-final numbers would blank the rows on ordinary typing.
  const dropOK = model.mountDepth != null && floorQ != null &&
                 Number(model.mountDepth) >= Number(floorQ) - 1e-9;
  // Depths measured DOWN from the structure at zero, the same single reference
  // every other number in step 4 uses. Cabinet, not screen: see the header.
  row('spanTopRow', 'spanTop', dropOK ? model.tvCabinetTop : null);
  row('spanBottomRow', 'spanBottom', dropOK ? model.tvCabinetBottom : null);
}

// Feeds BOTH surfaces that read the scene model: the drawing, and the three
// read-only measurements in the controls column. The measurements are refreshed
// even when the scene itself is absent or dead, because they are plain text and
// do not depend on the drawing having mounted.
function updateElevScene(){
  if(!window.VMSElevation) return;
  const o = elevOpts();
  o.drop = (state.target != null && !isNaN(state.target)) ? Number(state.target) : null;
  const model = window.VMSElevation.sceneModel(o);
  // ONE floor, computed once and read by both surfaces, for the same reason there
  // is ONE model: the readout hides its two depth rows below the enforced floor and
  // the scene publishes that floor as the handle's aria-valuemin, and those two must
  // be the same number. It is hoisted above the `if(!elevScene) return` because the
  // readout is refreshed even when the drawing is absent or dead.
  const lim = dropLimits(o);
  // ONE model, read by both surfaces. Do not build a second one.
  renderTvSpan(model, lim.floorQ);
  if(!elevScene) return;
  // Everything the scene needs that is not geometry: whether to draw a tile line
  // at all, which pipe pieces to composite, and the quantum its keyboard steps
  // move by. The step comes from here so the two input paths can never land on
  // different grids.
  model.hasDropCeiling = o.hasDropCeiling;
  model.mastSpec = state._mast ? state._mast.spec : null;
  model.step = TARGET_STEP;

  // ORTHO CUTOUTS for the two drawn parts, straight off the component records
  // (snippets/vms-configurator-data.liquid emits `ortho` per component, null
  // when the product has no custom.ortho_image). The scene decides what to do
  // with them; it draws the real cutouts only when BOTH are present and falls
  // back to its schematic blocks otherwise, which is the all-or-nothing rule
  // from the build spec. Passing one of the two is therefore harmless, not a
  // half-rendered state. Two heads are owner-confirmed to stay without a
  // cutout permanently (LCD-MID-CHB, 33CE004HEAD), so that fallback is a
  // standing state and not a gap waiting to close.
  const selPlate = state.plateSku ? getPlate(state.plateSku) : null;
  const selHead  = state.headSku  ? getHead(state.headSku)   : null;
  model.plateOrtho = (selPlate && selPlate.ortho) || null;
  model.headOrtho  = (selHead  && selHead.ortho)  || null;

  // NAMING FOR THE DRAWING'S PART LABELS (owner ask 2026-08-20: name the actual
  // parts, not their categories). SKUs only; the scene composes the wording, so
  // there is exactly one place that decides how a label reads.
  //
  // [RULE] THE PLATE'S WORDING COMES FROM PLATE_TYPES, NOT FROM THE PRODUCT TITLE.
  // The titles are catalogue strings that do not fit a chip on a drawing and are
  // not all clean ("TV Ceiling Mount Head for - 1 1/2 Pipe - 200x200" carries a
  // stray dash pair). PLATE_TYPES.label is already the owner-approved short name
  // shown on the step 2 tiles, so the drawing and the picker say the same words,
  // and it stays right for a plate that is NOT a ceiling plate: 11UN001 reads
  // "Unistrut adapter" rather than being forced into plate wording.
  const selType = selPlate ? plateTypeOfSku(selPlate.sku) : null;
  model.plateSku       = (selPlate && selPlate.sku) || null;
  model.plateTypeLabel = (selType && selType.label) || null;
  model.headSku        = (selHead && selHead.sku) || null;
  // Read, never hardcoded as '11AC001': if the coupler ever leaves the collection
  // or loses its metafield, COUPLER is undefined and the label falls back to the
  // generic wording rather than naming a SKU that is no longer in the build.
  model.couplerSku     = (COUPLER && COUPLER.sku) || null;

  // THE ENFORCED STOPS. This file does all the clamping, so this file publishes
  // the limits, and the two come from the same constants and the same derivation:
  // the drop stops are TARGET_MIN/TARGET_MAX through dropLimits, and the tile cap
  // is tileCap(), which is the function applyTileDepth clamps with.
  //
  // [WARN] These are NOT model.minDrop. sceneModel already carries that, and it
  // is the RAW geometric minimum: 35.784143387139565 in for a 70 in screen under
  // 18 in of tiles. Nothing ever lands on it. resolveDropValue clamps to
  // floorQ, the same floor rounded UP onto the 0.5 in field grid, which is 36.
  // Until 2026-07-27 the scene worked its own advisory limits out of the raw
  // number and published those, so aria-valuemin said 35.28 while the input said
  // 35.5, the tile handle advertised a geometric 69.72 against an enforced 60,
  // and the at-limit explanation was dead code on the drag and the keyboard
  // because the handle never got within LIMIT_EPS of the stop it was comparing
  // against. Typing the same number explained itself; dragging to it did not.
  // If you add a clamp anywhere in this file, publish it here in the same
  // commit, or you have built a silent hard stop.
  //
  // minDrop and minDropReason stay in the model on purpose: the scene owns the
  // WORDING and minDropReason is what selects it.
  //
  // [WARN] TWO REASON CODES REACHED THE SCENE FOR THE FIRST TIME ON 2026-07-27,
  // 'shortestMast' and 'screenClearsPlate', and sceneModel also gained
  // needsTileDepth. BOTH GAPS ARE NOW CLOSED IN assets/vms-elevation-scene.js and
  // this note is kept only so nobody re-opens them: reasonLines() has its own
  // branch for 'screenClearsPlate' worded identically to heldBecause() above, and
  // it gates the "we need the distance down to the tiles" line on
  // model.needsTileDepth, never on minDropReason === 'ceilingNoDepth', which that
  // reason no longer survives once any other floor binds. Add a branch there for
  // every reason code minDrop gains: its else is the PARTS sentence, so a code
  // that is not about the parts explains the stop by pointing at the wrong thing.
  //
  // `lim` is the one computed at the top of this function, not a second call: the
  // floor the handle advertises and the floor the readout gates on are the SAME
  // number by construction, which is the same discipline as the one model above.
  model.dropMinEnforced = lim.floorQ;    // quantised, and never below TARGET_MIN
  model.dropMaxEnforced = TARGET_MAX;
  model.tileMinEnforced = TILE_MIN;
  // DERIVED per TV size (2026-07-27), so aria-valuemax, the drag stop, End and the
  // at-limit copy all move with the screen instead of quoting a flat 60 nobody
  // chose. Same function applyTileDepth clamps with, so the advertised stop and
  // the enforced one cannot drift apart.
  model.tileMaxEnforced = tileCap(o);
  try { elevScene.update(model); }
  catch(err){ console.warn('vmscfg: elevation scene update failed', err); }
}

// ---- per-build-card mast drawing ----
// REPLACED diagramBar() on 2026-07-26 (owner call). That function drew a CSS
// stacked bar of coloured blocks, labelled "not to exact scale", standing in for
// plate offset / mast / adjustment / head. It was rendered ONCE for the top-ranked
// card even though every card has a different mast, so three of the four cards were
// illustrated by a picture of a different build. Each card now draws its OWN pipe
// stack to scale from the same pieces its parts table lists.
//
// The one thing the old legend did that a pipe picture cannot is show plate, mast
// and head as proportional shares of the finished drop. Those numbers are not lost:
// they are on the card's own Finished length / Adjustment rows, and mastCaption()
// keeps the offsets in a single line under the drawing.
function mastCaption(pOff, hOff, mLo, mHi, isFixed){
  const mast = isFixed ? inch(mLo) + ' in' : inch(mLo) + ' to ' + inch(mHi) + ' in';
  return 'Ceiling end on the left. Plate ' + offsetText(pOff) +
         ', mast ' + mast + ', head ' + offsetText(hOff) + '.';
}

// Canvases persist per card index across repaints, same reasoning as the summary:
// renderStage3Out rebuilds #s3out on every keystroke in the drop field, and up to
// four cards times a worst-case ~160 drawImage calls is not something to redo per
// character. Redrawn only when that card's spec or its measured width changes.
const cardCanvases = new Map(), cardArtKeys = new Map();

// Honest width of one art slot, in CSS px. Call it with the canvas OUT of the
// slot; see the [WARN] in mountCardMastArt for why that is not optional.
//
// [WARN] THE PARENT BOUND IS A BACKSTOP AGAINST CACHING A WRONG NUMBER, not a
// layout rule. A box cannot be wider than the box it sits in, so a measurement
// that says otherwise is wrong by inspection and must never reach the memo key.
// It is deliberately the LOOSE bound (the parent's client box, which still
// includes the parent's own padding) because a sanity check that can reject a
// legitimate measurement is worse than one that lets a few px through. Be clear
// about what that costs: on a card with horizontal padding this bound alone would
// NOT have caught the reported 300-against-281, since 300 still fits inside a
// padded parent. Detaching the canvas before measuring is the actual fix; this is
// the guard that stops any future source of inflation from becoming permanent.
//
// The 120 floor is applied LAST and on purpose. It is a deliberate legibility
// minimum, not a measurement, so it outranks the sanity bound rather than being
// clipped by it.
function artSlotWidth(slot){
  let w = Math.round(slot.clientWidth);
  const parent = slot.parentElement;
  const cap = parent ? Math.round(parent.clientWidth) : 0;
  if(cap > 0 && w > cap) w = cap;
  return Math.max(120, w);
}

// ---- build-card break line ------------------------------------------------
// The card art is horizontal and fit to a MEASURED slot width, and a long mast
// starves the couplers exactly as it does in the scene. The break option on
// VMSMastArt.render is the shared fix; the chooser here is the SCENE's, minus the
// tile line, the TV and the head there is nothing on a card to keep clear of. See
// SPEC-mast-break-2026-09-06.md section 7.
const CARD_BREAK_PPI_MIN = 7;       // girth 14 px; at or above this the card is legible unbroken
const CARD_BREAK_PPI_TARGET = 10;   // redraw a broken card at about this scale
const CARD_GLYPH_PX = 18;           // the fixed break-gap width on a card, in px (never scales)

// Choose the plain-pipe runs to cut from a card mast (rev 2: one per fixed pipe,
// greedy longest first; rev 4: a telescoping piece also contributes ONE plain span,
// its OUTER tube body only, never the exposed inner sliding run), in MAST inches
// from the ceiling end. Returns an array of {fromIn, toIn, glyphPx} sorted by depth,
// or [] for no break. The 3 in stub and 8 in floor match the scene's STUB_IN and
// MIN_BREAK_IN. Each band opened charges its own glyph gap to the remaining need,
// exactly like the scene.
function cardBreakBands(spec, w){
  const M = window.VMSMastArt;
  if(!M || !spec || !spec.length) return [];
  const totalIn = M.totalInches(spec);
  if(!(totalIn > 0)) return [];
  const ppi0 = Math.min(w / totalIn, 22);
  if(ppi0 >= CARD_BREAK_PPI_MIN) return [];               // legible unbroken
  const add = (typeof M.COUPLER_NET_ADD === 'number') ? M.COUPLER_NET_ADD : 1.5;
  // Every candidate's usable interval (3 in stub each end) in mast inches. A FIXED
  // piece is one interval (its whole span); a TELE piece contributes its plain span
  // (rev 4: the outer tube body only, end features and the exposed inner run already
  // excluded). Tele length still advances the cursor so later pieces sit at their
  // true positions.
  let cursor = 0; const cands = [];
  const push = (from, len) => { const u = len - 2 * 3; if(u >= 8) cands.push({ from: from + 3, len: u }); };
  for(let i = 0; i < spec.length; i++){
    const p = spec[i];
    const len = p.kind === 'tele'
      ? Math.min(Math.max(Number(p.current), Number(p.collapsed)), Number(p.extended))
      : Number(p.length);
    if(p.kind === 'tele' && M.telePlainSpans){
      const spans = M.telePlainSpans(p.collapsed, p.extended, p.current);
      spans.forEach(s => push(cursor + s.from, s.to - s.from));
    } else if(p.kind !== 'tele'){
      push(cursor, len);
    }
    cursor += len;
    if(i < spec.length - 1) cursor += add;
  }
  if(!cands.length) return [];
  cands.sort((a,b) => b.len - a.len);                    // longest interval first
  const targetPpi = Math.min(CARD_BREAK_PPI_TARGET, 22);
  const glyphCostIn = CARD_GLYPH_PX / targetPpi;
  let remaining = totalIn - (w - CARD_GLYPH_PX) / targetPpi;   // need with n = 0 (as the scene does)
  const bands = [];
  for(const c of cands){
    if(remaining < 8) break;
    remaining += glyphCostIn;                            // opening a band costs its glyph gap
    const take = Math.min(remaining, c.len);
    if(take < 8) continue;
    const from = c.from + (c.len - take) / 2;            // centre in the usable interval
    bands.push({ fromIn: from, toIn: from + take, glyphPx: CARD_GLYPH_PX });
    remaining -= take;
  }
  bands.sort((a,b) => a.fromIn - b.fromIn);              // by depth for render
  return bands;
}

function mountCardMastArt(box){
  if(!window.VMSMastArt || !LAST_BUILD) return;
  box.querySelectorAll('[data-mastart]').forEach(slot => {
    const idx = Number(slot.dataset.mastart);
    const card = LAST_BUILD.cards[idx];
    if(!card || !card.spec || !card.spec.length) return;
    let cv = cardCanvases.get(idx);
    if(!cv){
      cv = document.createElement('canvas');
      cv.className = 'mastart__cv';
      cv.setAttribute('role', 'img');
      cardCanvases.set(idx, cv);
    }
    // [WARN] MEASURE THE SLOT WITH THE CANVAS OUT OF IT, AND DO NOT REORDER THESE
    // THREE LINES. Until 2026-07-27 the canvas was appended FIRST and the slot
    // measured after, which reads as harmless and is not. A fresh <canvas> carries
    // the HTML default intrinsic size of 300x150, and a re-parented persistent one
    // carries whatever inline width VMSMastArt gave it last time. .mastart is
    // width:100%, so under a min-content-sized track that canvas raises the
    // track's own minimum BEFORE the measurement happens: the slot is asked how
    // wide it is while it is being propped open by the thing that is supposed to
    // be sized FROM the answer. Measured on dev at 375px on a cold load, the
    // slot's real width was 281 and it reported 300, exactly the canvas default.
    //
    // What made that survive a session rather than showing up once is the memo on
    // the next line. The bad 300 goes into the key, so the following render
    // re-parents the stale canvas, measures 300 again, matches the key, and
    // returns without redrawing. Self-sustaining and monotonic: it never comes
    // back down. invalidateArt() does not rescue it either, and that is precisely
    // why this is a DETACH and not merely a reorder: it clears the keys but calls
    // straight back in against the existing #s3out, where the canvas is still
    // sitting in the slot. Reordering alone fixes the innerHTML path and leaves
    // the invalidate path latched.
    //
    // Worth knowing when this looks random: invalidateArt() runs on a debounced
    // resize, and on a phone resize fires when the address bar collapses during an
    // ordinary scroll. So this path re-runs with no deliberate user action at all.
    //
    // This is one half of the owner's "wider than the screen" report. The other
    // half is CSS and is owned elsewhere; fixing this does not close that report
    // on its own.
    if(cv.parentNode) cv.parentNode.removeChild(cv);
    const w = artSlotWidth(slot);          // slot is empty here, so this is honest
    slot.appendChild(cv);
    // A long mast is a hairline at true scale in a card slot (a 145 in mast draws a
    // 6 px girth). Cut a run of plain pipe out of every long pipe and mark it, the
    // same break glyph the scene uses, so the couplers stay readable. The chooser is
    // simpler here: no tile line, no TV, no head to keep clear of.
    const bands = cardBreakBands(card.spec, w);
    // The aria-label gains ", drawn with a break" when any band is engaged, so a
    // screen reader is not told a picture is to scale when part of it is elided.
    cv.setAttribute('aria-label', 'Scale drawing of the ' + card.label.toLowerCase() +
      ' mast: ' + mastLabel(card.b) + (bands.length ? ', drawn with a break.' : '.'));
    // The bands are part of the memo key, so a card that gains or loses a break redraws.
    const bandKey = bands.map(b => 'b' + Math.round(b.fromIn * 100) / 100 + '-' + Math.round(b.toIn * 100) / 100).join('');
    const key = JSON.stringify(card.spec) + '|' + w + (bandKey ? '|' + bandKey : '');
    if(cardArtKeys.get(idx) === key) return;
    cardArtKeys.set(idx, key);
    // With no break the mast fills w at its own scale, exactly as before. With one
    // or more, the point is to RAISE the scale: after the removed runs are cut, the
    // shortened mast is drawn to fill (w - n glyphs) at the recovered ppi, so the
    // couplers read. render() derives ppi from lengthPx / totalIn and caps it at
    // maxPpi, so both are pinned to that recovered ppi (the same trick the scene
    // uses); the removed pixels plus the glyphs bring the visible canvas back to w.
    let ro = {spriteUrl: SPRITE_URL, lengthPx: w, vertical: false};
    if(bands.length){
      const totalIn = window.VMSMastArt.totalInches(card.spec);
      let removed = 0; for(const b of bands) removed += b.toIn - b.fromIn;
      const ppiTarget = (w - bands.length * CARD_GLYPH_PX) / (totalIn - removed);
      ro = {spriteUrl: SPRITE_URL, lengthPx: ppiTarget * totalIn, maxPpi: ppiTarget,
            vertical: false, breakBands: bands};
    }
    window.VMSMastArt.render(cv, card.spec, ro)
      .catch(err => {
        // Never let missing art break a build card: drop the canvas, keep the card.
        cardArtKeys.delete(idx);
        if(cv.parentNode) cv.parentNode.removeChild(cv);
        console.warn('vmscfg: card mast art unavailable', err);
      });
  });
}

// Both art surfaces are fit to a MEASURED width and drawn at the devicePixelRatio
// captured at draw time, so both a resize and a DPR change have to invalidate them.
function invalidateArt(){
  cardArtKeys.clear();
  const box = el('s3out');
  if(box) mountCardMastArt(box);
}

// Trailing-debounced rather than listening per frame.
let artResizeTimer = null;
window.addEventListener('resize', () => {
  clearTimeout(artResizeTimer);
  artResizeTimer = setTimeout(invalidateArt, 200);
});

// devicePixelRatio can change with NO resize event: drag the window to a monitor
// with a different scale factor and the viewport size never changes, so the canvas
// backing store would stay at the old ratio and the art would go soft or over-sharp.
// A media query on the current resolution is the only reliable signal, and it has to
// be re-armed after each change because the query is pinned to the old value.
(function watchDPR(){
  if(!window.matchMedia) return;
  let mq = null;
  const onChange = () => { invalidateArt(); arm(); };
  function arm(){
    if(mq && mq.removeEventListener) mq.removeEventListener('change', onChange);
    mq = window.matchMedia('(resolution: ' + (window.devicePixelRatio || 1) + 'dppx)');
    if(mq.addEventListener) mq.addEventListener('change', onChange);
  }
  arm();
})();

// The mast of a build as one readable line, e.g. "EXT2036 + NPT30 + 1x 11AC001".
// Repeated SKUs collapse to a count (a 3-pipe fixed stack can use the same pipe
// twice) and the couplers are named explicitly, because the coupler is a real
// priced part in the cart and should never look like a hidden extra.
function mastLabel(b){
  const counts = new Map();
  b.pieces.forEach(p => counts.set(p.sku, (counts.get(p.sku) || 0) + 1));
  const parts = Array.from(counts, ([sku, n]) => n > 1 ? (n + 'x ' + sku) : sku);
  if(b.couplers > 0) parts.push(b.couplers + 'x ' + (COUPLER ? COUPLER.sku : 'coupler'));
  return parts.join(' + ');
}

function buildSection(head, plate, target){
  // D6: under a drop ceiling an adjustable build must be able to go at least
  // 1 in further DOWN or it is not offered as adjustable. state.hasDropCeiling
  // is three-valued and !! is what turns the unanswered null into "no rule".
  const dropCeiling = state.hasDropCeiling === true;
  const {base, cards, noDownHeadroom} = computeBuildCards(head, plate, target, {requireDownHeadroom: dropCeiling});
  state._price = cards.length ? {v: cards[0].total} : null;

  // Per-card drawing spec. Built from the SAME pieces the card's parts table lists,
  // so the picture can never disagree with the bill of materials, and clamped to
  // each card's OWN mast range so every card shows the mast it actually sells.
  // The telescoping setting comes from the mast target (not mastLo), which is what
  // the unit is really set to for this drop.
  const netOff = plate.length_offset + head.length_offset;
  cards.forEach(c => {
    c.spec = (window.VMSMastArt && c.b.pieces)
      ? window.VMSMastArt.specFromBuild(c.b.pieces,
          Math.min(Math.max(target - netOff, c.b.mastLo), c.b.mastHi))
      : null;
  });

  // The summary mast row mirrors the TOP-RANKED card and says so: there is no
  // "selected build" state to read, since every card carries its own Add to cart.
  state._mast = cards.length
    ? {label: mastLabel(cards[0].b), fixed: cards[0].isFixed,
       lo: cards[0].b.mastLo, hi: cards[0].b.mastHi, spec: cards[0].spec}
    : null;

  // Read back by wireBuildCartButtons() once this HTML is actually in the DOM.
  // [MCP 2026-09-04] noDownHeadroom is stashed too so getBuild() can report
  // no_build_reason 'no_adjustable_headroom' without re-solving; it is the same
  // D6 flag computeBuildCards returned above (the drop-ceiling case where every
  // adjustable build was removed and only the fixed card remains).
  LAST_BUILD = {head, plate, target, base, cards, noDownHeadroom};

  let out = `<div class="note">The following optional pipe/mast configurations all use the same ceiling plate and mounting head. All pipes join using the <a href="/products/11ac001-aluminum-extension-pipe-coupler">11AC001</a> Coupler which adds about 1.5 inches in length and is listed in the pricing below.</div>`;

  // [UX R3 2026-09-22] Item 14: the "Your picks" check strip, after the coupler
  // note and before the first build card. Built in this same string, so it is
  // rebuilt with the cards on every keystroke and can never disagree with them.
  // Only when there ARE cards: over the no-solve message it would read as a
  // confirmation of a build that does not exist.
  if(cards.length) out += picksStripHTML(plate, head, target);

  if(!cards.length){
    out += `<div class="capture" id="vmscfg-nosolve" role="alert"><b>Your length is outside what a standard mast reaches cleanly.</b>
      We can still build it. Call <span class="phone">800-585-8795</span> or <a href="/pages/contact">Contact us</a>.</div>`;
    return out;
  }

  // D6 took every adjustable build. The spec left this case unspecified, so:
  // show the fixed card, which is still a real buildable answer, and say plainly
  // why it is on its own. Step 4 is never allowed to go blank.
  // [WARN] This sits AFTER the no-cards return on purpose. At the very top of
  // the mast range the adjustable pool can empty at the same moment the fixed
  // pool has nothing within tolerance either, and a Node run over the whole
  // range confirmed that happens. Rendering this line above the "outside what a
  // standard mast reaches" message would promise a fixed length in the sentence
  // directly above the one saying there is no build at all.
  if(noDownHeadroom){
    out += `<div class="capture">${fillHTML(COPY('no_adjustable'))}</div>`;
  }

  const cardsHtml = cards.map((c, idx) => {
    const b = c.b;
    // bomRowHTML renders each parts row: a clickable SKU link plus a photo
    // preview when the part carries a url / image. See the helper above.
    const rows = base.lines.map(l =>
      bomRowHTML(l, '', money(l.price))).join('');
    const mastRows = b.pieces.map(p =>
      bomRowHTML(p, '', money(p.price))).join('');
    const coupRow = b.couplers>0
      ? bomRowHTML(COUPLER, ` (x${b.couplers})`, money(b.couplers*Number(COUPLER.price))) : '';
    const noteHtml2 = c.note ? `<div class="buildnote">${c.note}</div>` : '';
    // Its OWN separate .buildnote line, after the existing note, so a near-match
    // card can show both its saving note and the leave-a-pipe-off note. Rendered
    // through the same (raw) channel the existing note uses, per ruling; the
    // string is plain text built from SKUs and nice() numbers.
    const leaveHtml = c.leaveOff ? `<div class="buildnote">${c.leaveOff}</div>` : '';
    const surHtml = b.surcharge > 0
      ? `<div class="warnbox">Adds ${money(b.surcharge)} oversize shipping surcharge (a piece 48 in or longer).</div>` : '';
    // Under a drop ceiling the fixed card carries the extra sentence D6 calls
    // for: a fixed mast cannot be let out later, so if the screen ends up
    // sitting in the tile line there is no adjustment to fix it with.
    const fixedWarn = c.isFixed
      ? `<div class="warnbox">This length is fixed. There is no adjustment after installation, so measure carefully.` +
        (dropCeiling ? ` With a drop ceiling that matters more than usual: once this is up you cannot lower the TV, so if the TV lands too close to the tiles the only fix is different pipe. Measure to the tile line before you order.` : '') +
        ` If your number might move, pick an adjustable option.</div>` : '';
    const dropRows = c.isFixed
      ? `<div class="sumrow"><span class="k">Finished length</span><span class="v">${inch(b.finLo)} in (your target ${nice(target)})</span></div>
        <div class="sumrow"><span class="k">Adjustment</span><span class="v">none, fixed length</span></div>`
      : `<div class="sumrow"><span class="k">Finished length</span><span class="v">${inch(b.finLo)} to ${inch(b.finHi)} in</span></div>
        <div class="sumrow"><span class="k">Adjustment</span><span class="v">${nice(b.down)} in shorter / ${nice(b.up)} in longer from your number</span></div>`;
    // [DRAWING SHEET 2026-10-04] every card prints its own sheet; the click is delegated in sections/vms-configurator.liquid
    const sheetBtn = `<button type="button" class="button button--secondary vmscfg-sheetbtn" data-vmscfg-sheetidx="${idx}" aria-label="${c.label} build: print dimensioned drawing">Print dimensioned drawing</button>`;
    // [DRAWING PACKAGE 2026-10-04] the one-file package under the print button; same delegated handler, data-vmscfg-pkgidx
    const pkgBtn = `<span class="vmscfg-pkgwrap" style="flex-basis:100%;display:flex;flex-wrap:wrap;gap:0.4rem 1rem;align-items:center"><button type="button" class="link vmscfg-pkgbtn" data-vmscfg-pkgidx="${idx}" aria-label="${c.label} build: download drawing package (PDF)" style="padding:0.6rem 0;min-height:4.4rem;text-align:left">Download drawing package (PDF)</button></span>`;
    const cartHtml = buildIsAddable(base, b)
      ? `<div class="vmscfg-cartwrap" style="display:flex;flex-wrap:wrap;gap:0.8rem;align-items:center">
           <button type="button" class="button button--primary vmscfg-addcart" data-vmscfg-cardidx="${idx}" aria-label="Add ${c.label} build to cart, ${money(c.total)}">Add this build to cart</button>
           ${sheetBtn}
           ${pkgBtn}
           <div class="vmscfg-cart-status muted" role="status" aria-live="polite" style="flex-basis:100%"></div>
         </div>`
      : `<div class="warnbox" role="alert">One or more parts in this build are currently unavailable. Call <span class="phone">800-585-8795</span> or <a href="/pages/contact">Contact us</a>.</div>
         <div class="vmscfg-cartwrap">${sheetBtn}${pkgBtn}</div>`;
    // Art slot left EMPTY: mountCardMastArt re-parents a persistent canvas into it
    // after this HTML lands, so typing in the drop field does not rebuild four
    // canvases per character. No spec (or no vms-mast-art) simply means no picture,
    // and the card still reads fine without one.
    const artHtml = c.spec && c.spec.length
      ? `<div class="mastart" data-mastart="${idx}"></div>
         <div class="mastart__cap">${mastCaption(plate.length_offset, head.length_offset, b.mastLo, b.mastHi, c.isFixed)}</div>`
      : '';
    // [BOM PAN 2026-09-22] The parts table ships inside the SAME
    // .vmscfg__twrap > .vmscfg__tw wrapper the two comparison charts use, with
    // an extra .vmscfg__tw--bom hook for the two CSS neutralisers documented at
    // that rule. renderStage3Body() runs enableDragPan() over every
    // .vmscfg__tw--bom once this HTML is in the DOM, which is what adds the
    // grab cursor, the painted scrollbar, the focusable labelled region and the
    // right-edge fade - but ONLY when the table really is wider than the card,
    // which measured true at 320px and false from 375px up. The fade is the
    // sibling SPAN, never an empty div: Dawn's base.css hides `div:empty`.
    return `
      <div class="buildcard">
        <h3 class="reason">${c.label}</h3>
        ${noteHtml2}${leaveHtml}${fixedWarn}
        ${artHtml}
        ${dropRows}
        <div class="sumrow"><span class="k">Pipes / couplers</span><span class="v">${b.pieces.length} pipe(s), ${b.couplers} coupler(s)</span></div>
        <div class="vmscfg__twrap"><div class="vmscfg__tw vmscfg__tw--bom"><table class="bom">
          <caption class="visually-hidden">Parts list for ${c.label}</caption>
          <tr><th scope="col">SKU</th><th scope="col">Part</th><th scope="col" class="num">Price</th></tr>
          ${rows}${mastRows}${coupRow}
          <tr class="tot"><td colspan="2">Total</td><td class="num">${money(c.total)}</td></tr>
        </table></div><span class="vmscfg__twfade" aria-hidden="true"></span></div>
        ${surHtml}
        ${cartHtml}
      </div>`;
  }).join('');

  return out + cardsHtml;
}

// Wires every per-card "Add to cart" button rendered by buildSection(),
// reading the cards/base it stashed on LAST_BUILD. No-op when the section
// rendered no cards.
function wireBuildCartButtons(box){
  if(!LAST_BUILD) return;
  const {head, plate, target, base, cards} = LAST_BUILD;
  box.querySelectorAll('[data-vmscfg-cardidx]').forEach(btn => {
    const idx = Number(btn.dataset.vmscfgCardidx);
    const c = cards[idx];
    if(!c) return;
    const statusBox = btn.parentElement.querySelector('.vmscfg-cart-status');
    wireAddToCart(btn, statusBox, () => buildCartRecipe(base, c.b),
      // One config id per Add click, shared across the build's lines; buildProperty
      // derives its human code from that SAME id, so the visible "Build" value and
      // the hidden _vms_config_id always agree. The three _vms_* properties are
      // untouched; "Build" is additive.
      () => { const cid = newConfigId(); return {_vms_config_id: cid, _vms_config_summary: configSummary(head, plate, c.b), Build: buildProperty(cid, head, plate, c.b, tvSizeNum(), tileDepth())}; },
      // Analytics, success path only. head/plate/target/c are the SAME values
      // the cart properties above are built from, so the reported build and the
      // one in the cart can never describe different things.
      () => trackAddBuild(head, plate, target, c));
  });
}

// ---- "Your build" summary ----
// RENDERED IN PHYSICAL ASSEMBLY ORDER (owner call 2026-07-26): ceiling plate at
// the top, mast in the middle, mounting head at the bottom, top-to-bottom the
// way the mount actually hangs. Do NOT re-sort these three rows by anything
// else: the order is what lets the list be read straight down against the
// drawing above it.
//
// [D2, 2026-08-19] TEXT ONLY, and the panel now sits INSIDE step 4 rather than
// in its own sticky page column. It used to carry a product photo per part plus
// a composited mast canvas, which made it a second, smaller, differently-scaled
// picture of the very mount the elevation already draws to scale. See the
// retirement note further down.
// TV size and price are NOT parts, so they sit in a separate block below the
// assembly rather than interleaved into it.
// ---- the summary's composited mast picture: RETIRED 2026-08-19, ruling D2 ----
// This held MAST_ART_LENGTH_PX, a persistent canvas and mountMastArt(), which
// drew a fitted picture of the pipe stack into the summary's mast row. Step 4
// now draws the whole mount to scale from the orthographic cutouts, so a second
// mast drawing on the same screen was both redundant and at a different scale
// from the first. Removed rather than left dormant: a no-op function guarded on
// an element id that no longer exists is the kind of thing that reads as live
// code for years. Recover it from git if the summary ever needs its own art.
//
// SPRITE_URL stays: the ELEVATION SCENE and the step 4 build cards both still
// composite real masts and both need it.

// Resolved by Liquid via asset_url onto the .vmscfg wrapper, read once.
const SPRITE_URL = (function(){
  const w = document.querySelector('[data-vmscfg-sprite]');
  return w ? w.getAttribute('data-vmscfg-sprite') : '';
})();

// [D2, 2026-08-19] THE `art` ARGUMENT IS GONE AND IT IS NOT COMING BACK.
// This panel used to carry its own picture of the build: a 3/4 product PHOTO of
// the plate, a composited mast canvas, and a photo of the head. Step 4 now draws
// the real mount from the orthographic cutouts, so that put perspective
// photographs on the same screen as an orthographic drawing of the same three
// parts, which is exactly the collage the 2026-07-26 ruling refused. It simply
// arrived by a different route once the scene gained real art.
//
// The drawing is the picture; this is the words. Do not re-add a thumbnail here.
// Pick CARDS keep their photos on purpose (a perspective shot is the better
// identification cue when you are choosing), and that is a different surface.
function assemblyRow(kind, label, value, sub){
  return `<div class="asm__row asm__row--${kind}">
    <span class="asm__txt"><span class="asm__k">${label}</span><span class="asm__v">${value}</span>${sub ? `<span class="asm__s">${sub}</span>` : ''}</span>
  </div>`;
}

// =====================================================================
// THE BUILD'S WEIGHT RATING  (owner ask 2026-08-23)
// =====================================================================
// Owner, verbatim: "I want to add a 'Weight Rating' to the 'your build section.
// The component chosen with the lowest weight rating will dictate the rating for
// the overall product. If a cutomer choses the 11CP005(110lb rating) and a 2
// screen head with a higher rating, they are limited by the rating of the
// 11CP005."
//
// So the build rating is the MINIMUM custom.weight_capacity across the chosen
// RATED parts. The rated classes are the PLATE and the HEAD only: verified
// against the store 2026-08-23, all 14 plates and 8 of the 9 heads carry the
// field (33CE004HEAD is the one deliberate empty one), and every one of the 25
// pipes, the coupler (11AC001) and the nipple carry nothing at all. The mast is
// standard aluminium NPT pipe with no published rating, so it is not part of the
// chain we can speak to; its absent value must never be read as a strength.
//
// [WARN] Number(null) IS 0 AND Number('') IS 0, NOT NaN, AND THIS EXACT BUG HAS
// ALREADY SHIPPED IN THIS FILE ONCE (see the plate-pill cheapest guard). A
// minimum taken with only an isNaN() guard would crown a value-less part the
// weakest link at ZERO POUNDS, which on a load rating that hangs a TV over
// people is the worst possible direction to be wrong. Three states per slot,
// kept distinct:
//   null    the part is not chosen, so it is not in the chain at all;
//   NaN     the part IS chosen but carries no rating, so its strength is UNKNOWN;
//   a value the part is chosen and rated.
function ratingOf(comp){
  if(!comp) return null;
  const w = comp.weight_capacity;
  if(w == null || w === '') return NaN;   // chosen but unrated: unknown, never zero
  const v = Number(w);
  return isNaN(v) ? NaN : v;              // non-numeric text: unknown, never zero
}

// Returns null (the caller renders NO row), or an object describing the row.
// limiters and unrated are both in physical assembly order (plate before head),
// so a tie names the parts the way the stack is built and the caller joins them
// into one row rather than printing two.
//
// [RULE] MISSING IS UNKNOWN, NOT INFINITY, AND WE QUALIFY RATHER THAN SUPPRESS.
// If a chosen rated part has no value we still print the known minimum, but as
// "at most" that figure (weight_unrated), because adding an unknown part can only
// pull the true minimum DOWN. The known min is therefore a ceiling on the real
// answer and never a promise. Chosen: qualify with "at most" over suppressing the
// row entirely, because the plate is always rated so there is a real safety
// number to show, and hiding it would tell the shopper less than the honest
// "no higher than this, we cannot confirm the rest". Only when NOTHING chosen
// carries a rating (both slots empty, or every chosen part unrated) is there no
// honest number at all, and then the row does not render.
function buildWeightRating(plate, head){
  const slots = [
    { part: plate ? plate.sku : null, r: ratingOf(plate) },
    { part: head  ? head.sku  : null, r: ratingOf(head)  }
  ];
  const chosen = slots.filter(s => s.r !== null);   // chosen rated-class parts
  if(!chosen.length) return null;                   // nothing rated chosen yet: no row
  const known = chosen.filter(s => !isNaN(s.r));
  if(!known.length) return null;                    // everything chosen is unrated: cannot speak to it, no row
  let min = Infinity;
  known.forEach(s => { if(s.r < min) min = s.r; });
  const limiters = known.filter(s => s.r === min).map(s => s.part);
  const unrated  = chosen.filter(s => isNaN(s.r)).map(s => s.part);
  return { weight: min, limiters, unrated, confident: unrated.length === 0 };
}

// =====================================================================
// [UX R3 2026-09-22] ITEMS 8 AND 14 OF THE CONFIGURATOR UX ROUND 3 SPEC (item 6,
// the sticky running total, removed on the owner word 2026-09-22)
// (reports/configurator-ux-round3-2026-09-22/BUILD-SPEC.md)
// =====================================================================

// ---- item 14: the "Your picks" check strip above the build cards ----------
// PURE: plate, head and the length in #target in, one HTML string out. Called by
// buildSection() inside the #s3out string, so it is rebuilt with the cards. No
// role and no live region: #s3out is already the polite region, and this strip
// repeats what the cards under it already say.
const PICK_CHECK_SVG = '<svg class="vmscfg-picks__ck" viewBox="0 0 24 24" width="14" height="14" fill="none" ' +
  'stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
  '<path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
function picksStripHTML(plate, head, target){
  const item = (k, v) => `<span class="vmscfg-picks__i">${PICK_CHECK_SVG}<span class="vmscfg-picks__k">${escHTML(k)}</span> ${escHTML(v)}</span>`;
  return `<div class="vmscfg-picks"><span class="vmscfg-picks__h">${escHTML(COPY('picks_label'))}</span>` +
    item(COPY('picks_plate'), plate && plate.sku) +
    item(COPY('picks_head'), head && head.sku) +
    item(COPY('picks_length'), nice(Number(target)) + ' in') +
    `</div>`;
}

// ---- item 8: the soft backorder note above the card it concerns -----------
// The ONLY trigger is the chosen plate or head carrying backorder_enabled === true
// (custom.backorder_enabled, forwarded by snippets/vms-configurator-data.liquid).
// Pool parts are not normally reserved, so today this renders nothing; it has to
// be right the day one is flagged. The cart gate (reservedPartTitle and
// buildCartRecipe) is untouched and still refuses the add with its own message.
const SOFT_WARN_SVG = '<svg class="vmscfg-softwarn__ico" viewBox="0 0 24 24" width="18" height="18" fill="none" ' +
  'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
  '<path d="M12 3l9.5 17h-19z"/><path d="M12 10v4.5"/><path d="M12 17.5v.01"/></svg>';
// PURE: the note's inner HTML for one part, or '' when the part is not flagged.
function softWarnHTML(part, partWord){
  if(!part || part.backorder_enabled !== true) return '';
  const map = {sku: escHTML(part.sku), part: escHTML(partWord)};
  return SOFT_WARN_SVG + `<div><strong class="vmscfg-softwarn__h">${escHTML(fill(COPY('backorder_warn_heading'), {part: partWord}))}</strong> ` +
    `<span>${fillHTML(COPY('backorder_warn'), map)}</span></div>`;
}
function renderSoftWarns(){
  [['s2', state.plateSku ? getPlate(state.plateSku) : null, COPY('backorder_part_plate')],
   ['s2b', state.headSku ? getHead(state.headSku) : null, COPY('backorder_part_head')]].forEach(([id, part, word]) => {
    const card = el(id);
    if(!card || !card.parentNode) return;
    const wid = 'vmscfgWarn-' + id;
    let box = el(wid);
    const html = softWarnHTML(part, word);
    if(!html){ if(box) box.remove(); return; }
    if(!box){
      box = document.createElement('div');
      box.className = 'warnbox vmscfg-softwarn';
      box.id = wid;
      box.setAttribute('role', 'note');
      box.setAttribute('data-softwarn', id);
    }
    if(box.innerHTML !== html) box.innerHTML = html;
    if(box.nextElementSibling !== card) card.parentNode.insertBefore(box, card);
  });
}

// [HANDOFF 2026-09-28] Human twin of the agent hand-off: the same refuseSlopedShort rule, shown as a
// dismissible pop-over on the drawing plus a note above it. Never blocks. Notes: reports/configurator-human-handoff-2026-09-28.
const HANDOFF_URL = '/pages/tv-mount-for-slanted-wall';
const HANDOFF_SKEY = 'vmscfg-handoff-dismissed';
let handoffTimer = null, handoffInit = false, handoffWasDue = false, handoffReturn = null;
let handoffShown = false, handoffDismissedMem = false;   // once per page view; memory backs private mode

function handoffDismissed(){
  if(handoffDismissedMem) return true;
  try { return sessionStorage.getItem(HANDOFF_SKEY) === '1'; } catch(e){ return false; }
}
function handoffRemember(){
  handoffDismissedMem = true;
  try { sessionStorage.setItem(HANDOFF_SKEY, '1'); } catch(e){}
}
// True when a committed build is on an angled plate and its length is under the agent threshold.
function handoffDue(typeId, size, headSku, len){
  if(typeId !== 'sloped' || !headSku || len == null || !isFinite(len) || len <= 0) return false;
  return !!refuseSlopedShort(size, len, headSku);
}
// Runs after the render pass (a timeout also keeps refuseSlopedShort's constant out of the first redraw).
function scheduleHandoff(){
  clearTimeout(handoffTimer);
  handoffTimer = setTimeout(renderHandoff, 0);
}
function handoffHost(){ const s = el('elevScene'); return s ? s.parentElement : null; }

function handoffNoteEl(){
  let n = el('vmscfgHandoffNote');
  if(n) return n;
  const host = handoffHost();
  if(!host || !host.classList.contains('vmscfg-handoff-host')) return null;
  n = document.createElement('p');
  n.id = 'vmscfgHandoffNote';
  n.className = 'vmscfg-handoff-note';
  n.setAttribute('role', 'note');
  n.hidden = true;
  const t = COPY('handoff_note'), i = t.indexOf('{link}');
  const a = '<a href="' + HANDOFF_URL + '">' + escHTML(COPY('handoff_note_link')) + '</a>';
  n.innerHTML = i < 0 ? escHTML(t) + ' ' + a : escHTML(t.slice(0, i)) + a + escHTML(t.slice(i + 6));
  host.parentNode.insertBefore(n, host);
  return n;
}

function handoffDlgEl(){
  let d = el('vmscfgHandoff');
  if(d) return d;
  const host = handoffHost();
  if(!host || !host.classList.contains('vmscfg-handoff-host')) return null;
  d = document.createElement('div');
  d.id = 'vmscfgHandoff';
  d.className = 'vmscfg-handoff';
  d.setAttribute('role', 'dialog');
  d.setAttribute('aria-labelledby', 'vmscfgHandoffH');
  d.setAttribute('aria-describedby', 'vmscfgHandoffB');
  d.tabIndex = -1;
  d.hidden = true;
  d.innerHTML = '<div class="vmscfg-handoff__panel">'
    + '<h3 class="vmscfg-handoff__h" id="vmscfgHandoffH">' + escHTML(COPY('handoff_heading')) + '</h3>'
    + '<p class="vmscfg-handoff__b" id="vmscfgHandoffB">' + escHTML(COPY('handoff_body')) + '</p>'
    + '<div class="vmscfg-handoff__acts">'
    + '<a class="button button--primary" href="' + HANDOFF_URL + '">' + escHTML(COPY('handoff_link')) + '</a>'
    + '<button type="button" class="button button--secondary" data-handoff-keep>' + escHTML(COPY('handoff_keep')) + '</button>'
    + '</div></div>';
  d.querySelector('[data-handoff-keep]').addEventListener('click', () => closeHandoff(true));
  // Non-modal: the page behind stays usable, so Tab leaves in DOM order; Escape closes.
  d.addEventListener('keydown', e => {
    if(e.key === 'Escape'){ e.preventDefault(); e.stopPropagation(); closeHandoff(true); }
  });
  // a11y O-3 2026-09-28: Escape with focus outside also closes it; capture phase reads the other layers before their own Escape closes them.
  document.addEventListener('keydown', e => {
    if(e.key === 'Escape' && !d.hidden && !d.contains(e.target) && !handoffEscOwnedElsewhere()) closeHandoff(true);
  }, true);
  host.appendChild(d);
  return d;
}
// True when another open layer (menu drawer or panel, predictive search, cart drawer, a dialog or modal, a parts preview) owns Escape.
function handoffEscOwnedElsewhere(){
  if(document.querySelector('.vms-header.is-menu-open, .vms-header [aria-expanded="true"], cart-drawer.active, dialog[open], .vms-hours-modal.is-open, .bom__pv:not([hidden])')) return true;
  return Array.prototype.some.call(document.querySelectorAll('[role="dialog"], [aria-modal="true"]'), n =>
    n.id !== 'vmscfgHandoff' && !n.closest('[hidden]') && n.getClientRects().length > 0 && getComputedStyle(n).visibility !== 'hidden');
}

function openHandoff(){
  const d = handoffDlgEl();
  if(!d || !d.hidden) return;
  handoffShown = true;
  handoffReturn = document.activeElement;
  d.hidden = false;
  d.focus({preventScroll: true});
  // Bring the panel into view once if it is cut off (main's scroll-margin-top carries the stuck header height).
  const p = d.querySelector('.vmscfg-handoff__panel') || d;
  const r = p.getBoundingClientRect(), top = parseFloat(getComputedStyle(p).scrollMarginTop) || 0;
  const behavior = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  if(r.top < top) p.scrollIntoView({block: 'start', behavior});
  else if(r.bottom > window.innerHeight) p.scrollIntoView({block: 'nearest', behavior});
}
function closeHandoff(dismiss){
  const d = el('vmscfgHandoff');
  if(dismiss) handoffRemember();
  if(!d || d.hidden) return;
  const hadFocus = d.contains(document.activeElement);
  d.hidden = true;
  if(!hadFocus) return;
  const back = (handoffReturn && handoffReturn.isConnected && handoffReturn !== document.body) ? handoffReturn : el('target');
  if(back) back.focus({preventScroll: true});
  handoffReturn = null;
}

function renderHandoff(){
  // A half-typed number in #target is not a build yet: hold the current state until it commits.
  if(state.target != null && !(lastDrop && lastDrop.drop === state.target)) return;
  const t = state.plateSku ? plateTypeOfSku(state.plateSku) : null;
  const due = handoffDue(t ? t.id : null, state.tvSize, state.headSku, state.target);
  const note = handoffNoteEl();
  if(note && note.hidden === due) note.hidden = !due;
  if(!due){ closeHandoff(false); handoffWasDue = false; handoffInit = true; return; }
  // The first pass after a page load (a shared link) shows the note only: a load never pulls focus.
  if(handoffInit && !handoffWasDue && !handoffShown && !handoffDismissed()) openHandoff();
  handoffWasDue = true;
  handoffInit = true;
}

function renderSummary(){
  const b = el('sumbody');
  const rows = [];

  if(state.plateSku){
    const p = getPlate(state.plateSku);
    const t = plateTypeOfSku(state.plateSku);
    rows.push(assemblyRow('plate', 'At the ceiling',
      state.plateSku, (t ? t.label + ', ' : '') + offsetText(p.length_offset)));
  }

  // The mast row renders at three levels of completeness, so the slot holds its
  // place in the stack from the moment a plate exists: solved (real pipe list),
  // awaiting a number, or awaiting the head.
  if(state.plateSku){
    // Four states, and the order matters. The unsolvable case used to fall through
    // to "Enter your length in step 4" even when a drop HAD been entered, which read
    // as the panel ignoring the input; step 4 was showing the real call-us escape
    // at the same time. Now each state says what is actually true.
    const hasTarget = state.target != null && !isNaN(state.target);
    let val, sub = '';
    if(state._mast && hasTarget){
      val = state._mast.label;
      sub = state._mast.fixed
        ? 'fixed mast, ' + inch(state._mast.lo) + ' in'
        : 'mast ' + inch(state._mast.lo) + ' to ' + inch(state._mast.hi) + ' in, recommended build';
    } else if(hasTarget && state.target <= 0){
      val = '<span class="asm__wait">Enter a length above zero</span>';
    } else if(hasTarget && state.headSku){
      val = '<span class="asm__wait">No standard mast reaches this length</span>';
      sub = 'see step 4, we can still build it';
    } else if(state.headSku){
      val = '<span class="asm__wait">Enter your length in step 4</span>';
    } else {
      val = '<span class="asm__wait">Pick a head, then your length</span>';
    }
    // Text only since D2: the mast the customer sees is the one drawn to scale in
    // the elevation immediately above this panel.
    rows.push(assemblyRow('mast', 'Mast', val, sub));
  }

  if(state.headSku){
    const h = getHead(state.headSku);
    rows.push(assemblyRow('head', 'At the TV',
      state.headSku, offsetText(h.length_offset)));
  }

  let meta = '';
  const push = (k,v) => meta += `<div class="sumrow"><span class="k">${k}</span><span class="v">${v}</span></div>`;
  if(state.tvSize != null && !isNaN(state.tvSize)) push('TV size', state.tvSize + ' in');
  // The build's weight rating: the weakest link across the chosen plate and head.
  // Placed high and well above Price on purpose. It is the one load-bearing spec
  // on this panel and burying it under the money would be the wrong emphasis; it
  // is also known as soon as a plate is picked, earlier than the length or price
  // rows below. See buildWeightRating() for the weakest-link rule and why a part
  // with no rating on file prints "at most" rather than a confident number.
  const wr = buildWeightRating(state.plateSku ? getPlate(state.plateSku) : null,
                               state.headSku  ? getHead(state.headSku)   : null);
  if(wr){
    push(COPY('weight_label'), wr.confident
      ? fill(COPY('weight_value'),   {weight: nice(wr.weight), part: wr.limiters.join(' and ')})
      : fill(COPY('weight_unrated'), {weight: nice(wr.weight), part: wr.unrated.join(' and ')}));
  }
  if(state.target != null && !isNaN(state.target)){
    // A face-down head measures to the back of the screen, not mid-screen.
    const _sumHead = state.headSku ? getHead(state.headSku) : null;
    const _tp = tvPlace();
    const _ref = (_sumHead && _sumHead.horizontal) ? ' in (to the back of the screen)' : (_tp && _tp.placed ? ' in (to the center of the mounting holes)' : ' in (to mid-screen)');
    push('Finished length', inch(state.target) + _ref);
  }
  // tileDepth(), not state.case3Dist: another inch figure off a field that holds
  // raw mid-typing text. It carries the drop-ceiling gate too, so a depth behind
  // an explicit "no" can never be summarised. See tileDepth().
  const tileSum = tileDepth();
  if(tileSum != null) push('Above the tiles', inch(tileSum) + ' in of the length');
  if(state._price && state.target != null && !isNaN(state.target)) push('Price', money(state._price.v));

  // meta alone is enough to render: a TV size or a tile depth entered before any
  // part is picked is still real feedback, and swallowing it back to the empty
  // prompt reads as the panel ignoring the input.
  b.innerHTML = (rows.length || meta)
    ? (rows.length ? `<div class="asm">${rows.join('')}</div>` : '') + meta
    : `<p class="muted">Pick a plate and a head to begin.</p>`;
  syncURL();
  refreshBuildJSON();
  // [UX R1 FIX 2026-09-21] Every length outcome (typed, stepped, dragged, refused)
  // ends in renderSummary, after state._price says whether the build is valid, so
  // this is where the rail's Length segment learns it. Steps are unchanged here, so
  // the last step map applyStepStates wrote is the right one to repaint from.
  renderRail(lastStepStates, railLengthDone());
  // [UX R3 2026-09-22] Same reason: every render path ends here, after state._price
  // and LAST_BUILD describe the current solve, so the backorder notes are written
  // from exactly what the summary above just printed.
  renderSoftWarns();
  scheduleHandoff();
  renderTvFit();
}

// [MCP 2026-09-04] Keep the embedded #vmscfg-build-json block in step with the
// page for an agent that has no WebMCP (it reads the last snapshot straight from
// the DOM). renderSummary() runs on every render path, so this is the one place
// that catches them all. It is a plain-data serialisation of getBuild() and can
// never throw into the render: on any failure the block is emptied rather than
// left carrying a stale build. Same idea as the slanted wall's #vmsw-build-json.
function refreshBuildJSON(){
  const node = el('vmscfg-build-json');
  if(!node) return;
  try { node.textContent = JSON.stringify(getBuild()); }
  catch(e){ node.textContent = ''; }
}

// =====================================================================
// EVENTS
// =====================================================================
function redraw(){
  renderStage1Out();
  renderStage2();
  // applyStepStates() decides open/closed/locked for all four at once; these two
  // calls are here for the RENDER each stage still needs when it is live. The
  // reachability test is stepStates(), not a CSS class: a step the customer has
  // collapsed by hand is still part of the build and still has to be rendered.
  applyStepStates();
  if(stepReachable('s2b')) renderStage2b();
  if(stepReachable('s3')) renderStage3();
  renderSummary();
  // LAST, and it is a no-op unless step 4 is live with no length. It is here as
  // well as in revealStage3 because changing the PLATE with a head already chosen
  // clears the length (the plate's offset is part of it) and comes back through
  // this function rather than through a reveal, so without this line that one path
  // would be the only way to see an empty length box. Both calls are free when
  // there is nothing to do: seedDefaultTarget checks the value first.
  // It cannot fire on a cleared FIELD, because that route goes through applyDrop
  // and renderStage3, never through redraw.
  seedDefaultTarget();
}

// The selected head no longer qualifies at the entered size: clear it and
// re-hide the build stage, same pattern as any upstream selection change.
//
// [WARN] THIS RUNS ON COMMIT, NEVER ON EVERY KEYSTROKE, and that is a fix, not
// tidying. It used to sit inside the 'input' handler. Head minimums are 24 and
// 32, so the FIRST keystroke of any two digit size is out of range for every
// head in the pool: measured 2026-07-27, picking 33HE001 and then typing "70"
// cleared the head on the "7" and it never came back, so the customer had to
// re-pick the head every time they typed a size. (Owner decision D2 made "leave
// the size blank, fill it in later" a primary supported flow; D2 was REVERSED on
// 2026-07-28, see the file header, and the size is now required. The commit split
// matters more after the reversal, not less: the first keystroke of "70" would
// now relock steps 2 to 4 and shut the page in the customer's face.) A partial
// number is not a statement, a committed one is, and this is the same split
// #target already runs on: 'input' previews, 'change' commits. The control became
// a <select> on 2026-07-29, so there is no partial number to protect against any
// more, but the split stays: it is what keeps state.tvSize parsed before the
// commit reads it, and #target still types.
//
// Bound to BOTH change and blur so a head that is genuinely wrong for a finished
// size cannot survive by the customer never leaving the field. blur fires before
// the click that moved focus, so anything they press next sees the cleared state.
// change alone would in fact cover every route (it fires on blur after an edit,
// and on Enter), but only while nothing else ever writes #tvSize, and the cost of
// the second binding is one early return.
//
// [WARN] IT DELIBERATELY DOES NOT CALL renderStage2b(). Both events fire on the
// mousedown that moved focus, i.e. in the middle of the customer's click, and
// renderStage2b rewrites #s2bout by innerHTML: rebuilding the head buttons under
// a click in progress eats that click, and the head cards ARE buttons. It is
// also unnecessary, because the head list is a pure function of the TV size and
// the plate, and a head this function clears is by definition one headScreenOK
// rejects, so it was never in the rendered list to begin with. The 'input'
// handler below has already drawn the correct list for this size. The summary
// DOES have to be redrawn, because it names the head we just cleared.
//
// SINCE 2026-07-28 IT ALSO RUNS THE CASCADE, because the committed size is what
// unlocks step 2 (D2 reversed, see the file header). The cheap exit is the same
// pattern #tiles uses: it tracks THE TEXT THIS FUNCTION LAST COMMITTED, so the
// blur that follows a change returns instead of re-running the whole pass, and
// applyStepStates plus renderSummary are not paid for twice on one interaction.
let lastSizeCommit = null;

// ---- The arrival preselected note (2026-09-07, the no-mast builder link) ----
// A product page's "Build a TV ceiling mount" link lands here with a head and/or a
// plate in the URL and NO size (the product page cannot know the TV size). On that
// one arrival we show a single sentence in step 1 above the size control naming what
// is already picked, and put the customer on the size control: focus it on a pointer
// device, or scroll step 1 into view on touch (the same scroll-follows-the-open-step
// move commitTvSize already makes). It is a one-time affordance: rendered once at
// boot and removed the moment a size is committed, so it never shows on a plain
// arrival, a ?size= arrival, or again after the size is cleared.
//
// arrivalNoteKey is the PURE choice: which COPY key to use, or null for no note. It
// is sliced by scripts/test_vms_arrival_note.js, so keep it TOP-LEVEL and side-effect
// free, and keep its closing brace at column 0. The returned keys are the short COPY
// names (the copy_js_ prefix is only the schema/section id), matching head_cleared_*.
function arrivalNoteKey(hasSize, headSku, plateSku){
  if(hasSize) return null;
  var hasHead  = headSku  != null && headSku  !== '';
  var hasPlate = plateSku != null && plateSku !== '';
  if(hasHead && hasPlate) return 'arrival_preselected_both';
  if(hasHead)  return 'arrival_preselected_head';
  if(hasPlate) return 'arrival_preselected_plate';
  return null;
}

let arrivalNoteEl = null;
function showArrivalNote(){
  const key = arrivalNoteKey(state.tvSize != null, state.headSku, state.plateSku);
  if(!key) return;
  const field = el('tvSizeField');
  if(!field || !field.parentNode) return;
  // {head} and {plate} render the SKU alone, substituted the same curly-brace way as
  // headClearedCopy(). state.headSku / state.plateSku already ARE the SKUs.
  const map = {head: state.headSku || '', plate: state.plateSku || ''};
  const sub = (s) => Object.keys(map).reduce(
    (acc, k) => acc.split('{' + k + '}').join(String(map[k])), String(s));
  const p = document.createElement('p');
  p.className = 'vmscfg-arrival-note';
  p.setAttribute('role', 'status');
  // [WARN] Dawn's base.css hides :empty p; this node is built with text so it is
  // never empty, and the explicit display keeps it visible regardless (and is the
  // spec's ask). Inserted ABOVE the TV size field, below any step-1 intro.
  p.style.display = 'block';
  p.textContent = sub(COPY(key));
  field.parentNode.insertBefore(p, field);
  arrivalNoteEl = p;
  // Put the customer on the size control: focus on a pointer device, scroll on touch.
  const pointerDevice = window.matchMedia ? window.matchMedia('(hover: hover)').matches : true;
  if(pointerDevice){ const sel = el('tvSize'); if(sel){ try { sel.focus(); } catch(_){} } }
  else { scrollStepIntoView('s1'); }
}
function removeArrivalNote(){
  if(arrivalNoteEl && arrivalNoteEl.parentNode) arrivalNoteEl.parentNode.removeChild(arrivalNoteEl);
  arrivalNoteEl = null;
}

function commitTvSize(){
  const raw = el('tvSize').value;
  if(raw === lastSizeCommit) return;
  lastSizeCommit = raw;
  state.targetWanted = null;
  const tvCleared = !!(state.tv && !state.tvNoOption && raw !== String(Math.round(Number(state.tv.screen_size_in))));
  if(tvCleared){ tvMark(); state.tv = null; setHidden(el('tvLookupPick'), true); }
  // [F] Commit the element's value into state BEFORE deciding anything from it.
  // state.tvSize is otherwise written only by the 'input' listener, so a change or
  // blur that arrives with no preceding input (browser autofill, some assistive
  // tech) would run the headScreenOK gate below against a stale size the state
  // never captured. Reading the element here makes the commit self-contained.
  state.tvSize = raw === '' ? null : Number(raw);
  if(tvCleared) tvChanged();
  // [FEATURE B, owner 2026-09-06] Capture a head this size CLEARS, before nulling it,
  // so the modal at the end of this function can name it. prevHead covers a head
  // chosen either by hand or by FEATURE A (state.headAuto is tracked, not gated on).
  let clearedHead = null;
  if(state.headSku){
    const prevHead = getHead(state.headSku);
    if(prevHead && !headScreenOK(prevHead, state.tvSize)){
      clearedHead = prevHead;
      state.headSku = null; state.headAuto = false; state.target = null; state.targetWanted = null; el('target').value = '';
    }
  }
  stepManual = {};            // a commit is a cascade; the flow decides from here
  applyStepStates();
  // [FEATURE A, spec 7.6] After the clear, if exactly one head now fits the size,
  // pick it on this commit path. When it fires right after a clear it is the swapped
  // replacement the modal names ({new_head}); when it fires with no head previously
  // set it is a plain auto-select and no modal opens (clearedHead is null below).
  const replacement = maybeAutoSelectHead();
  renderSummary();
  // [2026-09-07] The arrival preselected note is a one-time affordance: once a real
  // size is committed, step 1 has its answer, so tear it down. A bare empty blur
  // (sizeCommitted() false) leaves it standing until the customer actually picks.
  if(sizeCommitted()) removeArrivalNote();
  // [RULE] MOVE FOCUS ONLY IF THEY ARE STILL STANDING INSIDE STEP 1. 'change' and
  // 'blur' both fire on the click that moved focus, and that click is usually
  // somewhere else on the page: pulling focus to a step heading then would yank it
  // out of whatever they just deliberately reached for.
  //
  // [WARN] IT IS "INSIDE #s1body", NOT "IS #tvSize", AND THAT WIDENED ON
  // 2026-07-29. A committed size COLLAPSES step 1, and every control that can
  // commit one (the dropdown and the five presets) lives inside the body being
  // hidden. Focus left on a preset button is handed to <body> the moment that
  // body hides, which drops a keyboard or switch user back at the top of the
  // document with no idea where they are. The identity test only ever covered the
  // one control that existed when it was written.
  //
  // Same reason it no longer waits for the step 2 UNLOCK: changing a size that was
  // already committed collapses step 1 just as hard, and that pass never unlocked
  // anything.
  //
  // It lands on firstOpenStep() rather than on step 2 by name, and that is not
  // cosmetic: a LEGACY LINK carrying a plate and a head but no size unlocks all
  // three later steps at once when the size arrives, and steps 2 and 3 are
  // already finished. Sending focus to step 2 there would put the customer back
  // at a decision they had already made; firstOpenStep() puts them on step 4,
  // which is the only one still asking for anything. With every step finished
  // there is no open step at all, and step 1's own heading is the honest landing:
  // it is the control that reopens what we just closed.
  //
  // preventScroll because the scroll below is the deliberate one. Without it the
  // focus jumps the page to the step 2 heading and the smooth scroll then travels
  // back up from wherever that landed. Browsers that ignore the option simply get
  // the old behaviour.
  const s1body = el('s1body');
  if(sizeCommitted() && s1body && s1body.contains(document.activeElement)){
    focusStageHeading(firstOpenStep() || 's1', {preventScroll: true});
  }
  // And the scroll (2026-07-29, owner). Step 1 has just collapsed to one line, so
  // without this the customer is left staring at the space it used to fill with
  // step 2 somewhere below the fold on a phone. Deferred a frame so what gets
  // measured is the COLLAPSED layout applyStepStates just wrote, not the open one.
  // [FEATURE A/B 2026-09-06] SKIP it when this commit auto-selected a head
  // (replacement) or cleared one (clearedHead, which opens the modal). In both cases
  // the flow reframes elsewhere - selectHead() has already advanced focus to the
  // first open step (step 4) and scrolled it into view, and the modal's close handler
  // scrolls the step 3 button into view - so pinning step 1 to the top here would
  // frame the WRONG step and leave the focused step lower in (or below) the viewport.
  // Only a plain size change that neither cleared nor auto-selected a head keeps the
  // original collapse-scroll.
  if(sizeCommitted() && !replacement && !clearedHead) requestAnimationFrame(() => scrollStepIntoView('s1'));
  // [FEATURE B, owner 2026-09-06] Explain a head we actually cleared, exactly once.
  // The change+blur pair opens it once: 'change' does this work and 'blur' returns at
  // the lastSizeCommit guard at the top with the same raw value, so the second commit
  // sees no head and does nothing. Never during a programmatic configureBuild(): the
  // agent path picks its own head and must not be shown a modal. replacement is the
  // swapped head's sku (or null); getHead turns it into the object for {new_head}.
  if(clearedHead && !programmaticConfigure){
    openHeadDlg(clearedHead, state.tvSize, replacement ? getHead(replacement) : null);
  }
}

// [RULE] NEVER SUBTRACT A HEADER HEIGHT HERE. The site header is not sticky, so
// block:'start' already lands the card against the top of the viewport; taking a
// header offset off it opens a ~126px dead gap above the card, which is the
// documented trap this rule exists for.
//
// Called ONLY from a real commit. A deep-link restore must not scroll: the
// customer asked for that URL, and yanking the page on arrival reads as a bug.
// commitTvSize is the usual caller and its lastSizeCommit guard already makes an
// untouched restored field a no-op. The one deliberate exception is showArrivalNote()
// on a TOUCH-device arrival with a head/plate deep link and no size yet: that scroll
// is wanted, because the customer still has a step to finish and the size control must
// be on screen (a pointer device gets focus instead, no scroll).
//
// [UX R1 FIX 2026-09-21, owner: "step 1 pins to the top of the viewport, causing the
// status bar to be too high and not visible"] The progress rail sits directly above
// step 1, so pinning #s1 itself to the top pushed the rail just off screen at the
// very moment it first changes. A scroll to step 1 now lands on the RAIL, which
// brings step 1 along right under it. Every other step id is unchanged.
function scrollStepIntoView(id){
  const rail = (id === 's1') ? el('vmscfgRail') : null;
  const n = rail || el(id);
  if(!n || typeof n.scrollIntoView !== 'function') return;
  const still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  try { n.scrollIntoView({block: 'start', behavior: still ? 'auto' : 'smooth'}); }
  catch(e){ n.scrollIntoView(true); }   // older engines take only the boolean form
}

el('tvSize').addEventListener('change', commitTvSize);
el('tvSize').addEventListener('blur', commitTvSize);

// The preset buttons WRITE THE SELECT AND DISPATCH ITS EVENTS. They do not call
// commitTvSize themselves and they do not touch state: a native pick fires input
// then change, so firing the same pair keeps one commit path with one order of
// operations, and anything bound to #tvSize later is bound for the presets too.
// Guarded, and the guard is not ceremony: Shopify caches theme assets and section
// markup independently, so this JS can run against a section from before the
// presets existed, and an unguarded null would kill every listener below it.
document.querySelectorAll('.vmscfg-size__btn').forEach(b => {
  b.addEventListener('click', () => {
    const sel = el('tvSize');
    if(!sel) return;
    sel.value = b.dataset.tvsize;
    sel.dispatchEvent(new Event('input', {bubbles: true}));
    sel.dispatchEvent(new Event('change', {bubbles: true}));
  });
});

const tvNum = v => v != null && v !== '' && isFinite(v);
function tvTierMsg(r){
  const u = tvNum(r.offset_uncertainty_mm) ? r.offset_uncertainty_mm : null, fl = u == null ? null : 'Manufacturer figures, accurate to about +/- ' + u + ' mm. Verify before relying on them.';
  const t = {
    pass_2mm: 'From the manufacturer\'s drawing. Check the hole positions on your TV before drilling.',
    flagged_2_5mm: fl, flagged_5_10mm: fl, flagged_10_12mm: fl,
    photo_measured: u == null ? null : 'Estimated from a product photo, about +/- ' + u + ' mm. Verify on your TV before drilling.',
    calculated_one_side: 'One edge is from the maker\'s drawing or a photo; the other is calculated. Verify on your TV before drilling.',
    customer_measured: 'Your own measurements. Check them on the back of your TV before drilling.'
  };
  return (Object.prototype.hasOwnProperty.call(t, r.offset_tier) && t[r.offset_tier]) || 'Rough estimate, about +/- ' + (u == null ? 25 : u) + ' mm. Verify on your TV before drilling.';
}
function tvActive(){
  const r = state.tv, sel = el('tvSize');
  if(!r) return null;
  if(r.explicit !== true && !(sel && tvNum(r.screen_size_in) && String(Math.round(Number(r.screen_size_in))) === sel.value)) return null;
  return r;
}
// TV placement (SPEC-THEME-tv-placement-2026-10-12.md; notes assets/vms-tv-lookup.notes.md).
function tvPlace(){
  const E = window.VMSElevation, r = tvActive();
  if(!E || typeof E.tvPlacement !== 'function' || !r) return null;
  const h = state.headSku ? getHead(state.headSku) : null;
  const P = E.tvPlacement(r, {tvSize: tvSizeNum()});
  if(P && h && h.horizontal){ P.faceDown = true; P.topEdge = h.topEdge || null; }
  return P && P.source !== 'generic' ? P : null;
}
function tvLims(){ return { floorQ: dropLimits().floorQ, cap: tileCap() }; }
function tvMark(){ tvPrevLims = tvLims(); }
function tvQuietEnd(){
  if(!pendingTvQuiet) return;
  pendingTvQuiet = false;
  if(lastDrop && (lastDrop.clamped || lastDrop.bumped) && lastDrop.drop === state.target) sayDrop(lastDrop);
}
function tvFitBelow(c, top, dl){
  return (Number(c) < dl.floorQ - 1e-9 && (dl.reason === 'screenClearsPlate' || dl.reason === 'tvClearsTiles')) || top < 0;
}
function tvURLParams(p, r){
  if(!r) return p;
  if(r.source === 'manual'){ p.set('tva', nice(r.a_in)); p.set('tvb', nice(r.b_in)); p.set('tvc', nice(r.c_in)); return p; }
  const m = r.model_number == null ? '' : String(r.model_number).trim();
  if(m) p.set('tvm', m);
  return p;
}
function readTvURL(search){
  const p = new URLSearchParams(search);
  const a = tvMeasRead(p.get('tva'), 100), b = tvMeasRead(p.get('tvb'), 50), c = tvMeasRead(p.get('tvc'), 50);
  if(!tvMeasCheck(a, b, c)) return { manual: [a.value, b.value, c.value] };
  const m = (p.get('tvm') || '').trim();
  return m && m.length <= 64 ? { model: m } : null;
}
function restoreTvModel(m, tries){
  const root = document.querySelector('#s1 [data-vms-tvl]');
  if(!root) return;
  const w = root.vmsTvl;
  if(w){ w.input.value = m; w.lookup(m, false, { prefill: true }); return; }
  if((tries || 0) < 20) setTimeout(() => restoreTvModel(m, (tries || 0) + 1), 100);
}
// [3A 2026-10-18] the saved TV stands in for a link with no TV and no size, rendered from its stored row (no app call); notes: assets/vms-saved-tv.notes.md
function savedTvForUrl(search){
  const S = window.VMSSavedTv, p = new URLSearchParams(search), rec = S && S.get();
  if(p.get('size') || !rec || rec.source !== 'catalog') return null;
  const m = String(rec.row.model_number || '').trim();
  return m && m.length <= 64 ? rec : null;
}
function restoreSavedTv(rec, tries){
  const root = document.querySelector('#s1 [data-vms-tvl]');
  if(!root) return;
  const w = root.vmsTvl;
  if(w){ w.renderRow(rec.row, { prefill: true, catalog_version: rec.catalog_version }); return; }
  if((tries || 0) < 20) setTimeout(() => restoreSavedTv(rec, (tries || 0) + 1), 100);
}
function tvChanged(){
  const prev = tvPrevLims;
  tvPrevLims = null;
  clampTileToCap();
  syncTileCap();
  const now = tvLims(), w = state.targetWanted;
  // [NOTE 2026-11-01] a cap-only move commits only when the resolved wanted length differs (no re-spoken hold)
  const moved = !prev || now.floorQ !== prev.floorQ || (now.cap !== prev.cap && (w == null || resolveDropValue(w).drop !== state.target));
  const seed = targetIsDefault && w == null ? Math.max(TARGET_DEFAULT, now.floorQ) : null;
  if(stepReachable('s3') && seed != null && (moved || seed !== state.target)) applyDrop(seed, 'seed');
  else if(stepReachable('s3') && seed == null && (moved || (w != null && resolveDropValue(w).drop !== state.target))) applyDrop(w != null ? w : state.target, 'commit');
  else {
    updateElevScene();
    renderDropNote();
    renderSummary();
    try { document.dispatchEvent(new CustomEvent('vmscfg:build-changed', { detail: { build: getBuild() } })); } catch(e){}
  }
  renderLengthLabel();
  renderTvFit();
  renderStepPicks(lastStepStates);
  syncURL();
}
function tvPickSuffix(r){
  if(!r) return '';
  if(r.source === 'manual') return ', your measurements';
  const s = v => v == null ? '' : String(v).trim();
  if(!s(r.model_number)) return '';
  return ', ' + (s(r.manufacturer) ? s(r.manufacturer) + ' ' : '') + s(r.model_number);
}
// Face-down readout (spec 6.3): null unless a face-down head, a row and a length at or above the floor.
function tvFaceFit(r){
  const h = state.headSku ? getHead(state.headSku) : null, c = state.target, P = r && h && h.horizontal ? tvPlace() : null;
  if(!P || c == null || isNaN(c) || !(c > 0) || Number(c) < dropLimits().floorQ - 1e-9) return null;
  const td = tileDepth(), sfc = td == null ? 'ceiling' : 'tiles';
  const face = 'its face hangs about ' + inch(Number(c) - (td == null ? 0 : Number(td)) + P.panelDepth) + ' in below the ' + sfc + (P.depthFrom === 'assumed' ? ' (we take the TV as ' + inch(P.panelDepth) + ' in thick)' : '') + '.';
  if(!P.placed) return { placed: false, txt: 'This TV is about ' + inch(P.panelHeight) + ' in tall and lies flat, screen down. With this length ' + face + ' We don\'t know where its mounting holes sit between the top and bottom edges, so we show them centered. ' };
  return { placed: true, txt: 'Your TV lies flat, screen down. With this length ' + face + ' From the mast, its top edge reaches about ' + inch(P.vesaCentreFromTop) + ' in toward the room and its bottom edge about ' + inch(P.vesaCentreFromBottom) + ' in toward the wall. ' + tvTierMsg(r) };
}
function renderTvFit(){
  const box = el('tvFitOut');
  const r = tvActive();
  if(!box) return;
  let txt = null, meas = null, real = false;
  const can = !!(el('tvMeasOpen') && el('tvMeasDlg'));
  const fd = tvFaceFit(r);
  if(fd){ txt = fd.txt; real = true; if(!fd.placed){ if(can) meas = 'Enter your TV\'s measurements'; } else if(r.source === 'manual' && can){ txt += ' '; meas = 'Change your measurements'; } }
  else if(r && tvNum(r.vesa_from_top_mm) && tvNum(r.vesa_height_mm) && tvNum(r.height_mm)){
    const h = state.headSku ? getHead(state.headSku) : null, c = state.target, P = tvPlace();
    if(c != null && !isNaN(c) && c > 0 && !(h && h.horizontal) && P && P.placed){
      const td = tileDepth(), tile = td == null ? 0 : Number(td), sfc = td == null ? 'ceiling' : 'tiles';
      const top = Number(c) - tile - P.vesaCentreFromTop;
      const bot = top + P.panelHeight;
      const dl = dropLimits(), below = tvFitBelow(c, top, dl);
      txt = below
        ? 'At this length the top of this TV would reach the ' + sfc + '. Choose a length of at least ' + Number(dl.floorQ).toFixed(1) + ' in. ' + tvTierMsg(r)
        : 'With this length your TV\'s top edge sits about ' + inch(top) + ' in below the ' + sfc + ' and its bottom edge about ' + inch(bot) + ' in below the ' + sfc + '. ' + tvTierMsg(r);
      real = true;
      if(r.source === 'manual' && can){ txt += ' '; meas = 'Change your measurements'; }
    }
  } else if(r && tvNum(r.height_mm)){
    txt = 'This TV is about ' + inch(r.height_mm / 25.4) + ' in tall. We don\'t know where its mounting holes sit between the top and bottom edges. ';
    if(can) meas = 'Enter your TV\'s measurements';
  } else if(!state.tv && can){
    const h = state.headSku ? getHead(state.headSku) : null, c = state.target;
    if(c != null && !isNaN(c) && c > 0 && !(h && h.horizontal)){
      txt = 'Want to see where your TV\'s top and bottom edges will sit? ';
      meas = 'Enter your TV\'s measurements';
    }
  }
  const sp = el('tvSpans');
  if(sp) sp.classList.toggle('vmscfg-elev__spans--real', real);
  const key = String(txt) + '|' + String(meas);
  if(box.dataset.t === key) return;
  box.dataset.t = key;
  box.textContent = txt || '';
  if(meas){
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'vmscfg-cmplink vmscfg-tvmeas-link';
    b.textContent = meas;
    box.appendChild(b);
    box.appendChild(document.createTextNode('.'));
  }
  setHidden(box, !txt);
}
if(el('s1')) el('s1').addEventListener('vms:tv-selected', e => {
  const r = e.detail || {}, sel = el('tvSize');
  if(r.explicit !== true && state.tv && state.tv.source === 'manual') return;
  tvMark();
  pendingTvQuiet = false;
  state.tv = r;
  tvMeasShowState(null);
  const v = tvNum(r.screen_size_in) ? String(Math.round(Number(r.screen_size_in))) : null;
  const ok = !!(sel && v && Array.prototype.some.call(sel.options, o => o.value === v && !o.disabled));
  state.tvNoOption = !ok;
  setHidden(el('tvLookupPick'), ok || r.explicit !== true);
  if(r.explicit === true && ok && sel.value !== v){
    sel.value = v;
    tvSizeFromCard = true;
    try {
      sel.dispatchEvent(new Event('input', {bubbles: true}));
      sel.dispatchEvent(new Event('change', {bubbles: true}));
    } finally { tvSizeFromCard = false; }
  }
  tvChanged();
  const useBtn = el('s1').querySelector('.vms-tvl__use');
  if(useBtn) setHidden(useBtn, !!tvActive());
});
if(el('s1')) el('s1').addEventListener('vms:tv-cleared', () => {
  if(state.tv && state.tv.source === 'manual') return;
  tvMark();
  tvQuietEnd();
  state.tv = null;
  state.tvNoOption = false;
  setHidden(el('tvLookupPick'), true);
  tvChanged();
});
// TV manual measurements dialog (phase B2); notes: assets/vms-tv-lookup.notes.md
function tvMeasRead(raw, th){
  const t = String(raw == null ? '' : raw).trim();
  if(t === '') return { value: null, invalid: false, converted: false, mm: null };
  const n = /^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(t) ? Number(t) : NaN;
  if(!isFinite(n) || n <= 0) return { value: null, invalid: true, converted: false, mm: null };
  return n >= th ? { value: n / 25.4, invalid: false, converted: true, mm: n } : { value: n, invalid: false, converted: false, mm: null };
}
function tvMeasCheck(a, b, c){
  if(a.value == null || b.value == null || c.value == null) return 'Enter all three measurements as numbers greater than zero.';
  if(!(b.value + c.value < a.value)) return 'The hole measurements add up to more than the TV\'s height. Check B and C.';
  return null;
}
function tvManualRow(aIn, bIn, cIn){
  const mm = x => Math.round(x * 25.4 * 10) / 10;
  return {
    lookup_status: 'customer_measured', offset_tier: 'customer_measured', source: 'manual',
    height_mm: mm(aIn), vesa_from_top_mm: mm(bIn), vesa_height_mm: mm(cIn), vesa_from_bottom_mm: mm(aIn - bIn - cIn),
    screen_size_in: null, model_number: null, manufacturer: null, explicit: true,
    a_in: aIn, b_in: bIn, c_in: cIn
  };
}
const TV_MEAS = [['A', 100], ['B', 50], ['C', 50]];
function tvMeasShowState(r){
  const st = el('tvMeasState'), tx = el('tvMeasStateTx'), rm = el('tvMeasRemove');
  const on = !!(r && r.source === 'manual');
  if(tx) tx.textContent = on ? 'Using your measurements: ' + nice(r.a_in) + ' in tall, top hole ' + nice(r.b_in) + ' in from the top edge, holes ' + nice(r.c_in) + ' in apart.' : '';
  setHidden(st, !on);
  setHidden(rm, !on);
}
function tvMeasNote(L){
  const f = el('tvMeas' + L), n = el('tvMeas' + L + 'Note');
  const th = TV_MEAS.find(x => x[0] === L)[1];
  const m = tvMeasRead(f ? f.value : '', th);
  if(n){
    n.textContent = m.converted ? 'That looks like millimeters. We read ' + nice(m.mm) + ' mm as ' + nice(m.value) + ' inches.' : '';
    setHidden(n, !m.converted);
  }
  return m;
}
function tvMeasErr(msg){
  const e = el('tvMeasErr');
  if(e){ e.textContent = msg || ''; setHidden(e, !msg); }
}
function openTvMeas(){
  const dlg = el('tvMeasDlg');
  if(!dlg || !CAN_DIALOG) return;
  const r = state.tv && state.tv.source === 'manual' ? state.tv : null;
  if(r){
    el('tvMeasA').value = nice(r.a_in); el('tvMeasB').value = nice(r.b_in); el('tvMeasC').value = nice(r.c_in);
  }
  TV_MEAS.forEach(x => { tvMeasNote(x[0]); const f = el('tvMeas' + x[0]); if(f) f.removeAttribute('aria-invalid'); });
  tvMeasErr(null);
  tvMeasShowState(r);
  document.body.classList.add('overflow-hidden');
  dlg.showModal();
  const t = el('tvMeasTitle');
  if(t) t.focus();
}
function setTvManual(a, b, c){
  pendingTvQuiet = false;
  state.tv = tvManualRow(a, b, c);
  state.tvNoOption = true;
  setHidden(el('tvLookupPick'), true);
  tvMeasShowState(state.tv);
}
function useTvMeas(){
  const m = TV_MEAS.map(x => tvMeasNote(x[0]));
  TV_MEAS.forEach((x, i) => { const f = el('tvMeas' + x[0]); if(f){ if(m[i].value == null) f.setAttribute('aria-invalid', 'true'); else f.removeAttribute('aria-invalid'); } });
  const err = tvMeasCheck(m[0], m[1], m[2]);
  if(err){
    if(m[0].value != null && m[1].value != null && m[2].value != null){ el('tvMeasB').setAttribute('aria-invalid', 'true'); el('tvMeasC').setAttribute('aria-invalid', 'true'); }
    tvMeasErr(err);
    return;
  }
  tvMeasErr(null);
  tvMark();
  setTvManual(m[0].value, m[1].value, m[2].value);
  el('tvMeasDlg').close();
  tvChanged();
  if(typeof window.clarity === 'function') window.clarity('set', 'vms_tv_measure', 'used');
}
function removeTvMeas(){
  tvMark();
  // [NOTE 2026-11-01] Remove also clears the widget's card (never the saved TV); notes: assets/vms-tv-lookup.notes.md
  const w = (document.querySelector('#s1 [data-vms-tvl]') || {}).vmsTvl;
  if(w && state.tv && state.tv.source === 'manual'){ clearTimeout(w.timer); w.abort(); w.clear(); w.input.value = ''; }
  if(state.tv && state.tv.source === 'manual') state.tv = null;
  state.tvNoOption = false;
  TV_MEAS.forEach(x => { const f = el('tvMeas' + x[0]); if(f){ f.value = ''; f.removeAttribute('aria-invalid'); } tvMeasNote(x[0]); });
  tvMeasErr(null);
  tvMeasShowState(null);
  const dlg = el('tvMeasDlg');
  if(dlg && dlg.open) dlg.close();
  tvChanged();
}
if(el('tvMeasDlg')){
  if(!CAN_DIALOG){
    ['tvMeasOpen', 'tvMeasState'].forEach(id => { const n = el(id); if(n) n.remove(); });
    document.querySelectorAll('#s1 [data-vms-tvl]').forEach(w => { w.setAttribute('data-measure-action', '0'); if(w.vmsTvl) w.vmsTvl.measureAction = false; });
    renderTvFit();
  } else {
    const dlg = el('tvMeasDlg');
    ['tvMeasOpen', 'tvMeasChange'].forEach(id => { const n = el(id); if(n) n.addEventListener('click', openTvMeas); });
    if(el('s1')) el('s1').addEventListener('vms:tv-measure', openTvMeas);
    if(el('tvFitOut')) el('tvFitOut').addEventListener('click', e => { if(e.target.closest && e.target.closest('.vmscfg-tvmeas-link')) openTvMeas(); });
    el('tvMeasClose').onclick = () => dlg.close();
    el('tvMeasCancel').onclick = () => dlg.close();
    el('tvMeasUse').onclick = useTvMeas;
    el('tvMeasRemove').onclick = removeTvMeas;
    TV_MEAS.forEach(x => {
      const f = el('tvMeas' + x[0]);
      if(!f) return;
      f.addEventListener('input', () => tvMeasNote(x[0]));
      f.addEventListener('keydown', e => { if(e.key === 'Enter'){ e.preventDefault(); useTvMeas(); } });
    });
    dlg.addEventListener('click', e => { if(e.target === dlg) dlg.close(); });
    dlg.addEventListener('close', () => { if(!anyOtherDlgOpen('tvMeasDlg')) document.body.classList.remove('overflow-hidden'); });
    renderTvFit();
  }
}

// [WARN] STILL THE LIVE-PREVIEW HALF OF THE SPLIT, even though a <select> cannot
// be typed into a character at a time. It runs BEFORE 'change' on every pick and
// it is what parses the field into state.tvSize, which commitTvSize then reads;
// collapsing the two would put the commit ahead of the value it commits.
el('tvSize').addEventListener('input', e => {
  pendingTvQuiet = false;
  state.tvSize = e.target.value===''?null:Number(e.target.value);
  renderStage1Out();
  renderStage2b();
  // [WARN] STEP 4 MUST BE REFRESHED HERE, and this is required rather than
  // tidy. Until 2026-07-26 nothing in step 4 depended on the TV size, so this
  // handler never touched it. minDrop does depend on it: with an 18 in tile
  // depth the floor moves from 18 to 35.78 the moment a 70 in TV is entered,
  // and without this call step 4 would sit there showing an out-of-range drop
  // with an unmoved handle and a stale #target min. (Owner decision D2 made
  // "leave the size blank, fill it in later" a primary supported flow; D2 was
  // REVERSED on 2026-07-28, see the file header. The call is still required: a
  // size can still be CHANGED after a length is set, from the step 1 Change
  // control, and that is the same re-clamp.) Same pattern as #tiles below.
  //
  // [WARN] IT MUST NOT CASCADE. This is the 'input' handler. It fired per
  // keystroke while the control was a number box, where opening or locking steps
  // from here relocked the page on the "7" of "70"; since 2026-07-29 it is a
  // <select> and fires once per pick, immediately before 'change'. Keep the
  // cascade in commitTvSize regardless: this handler's job is to have state.tvSize
  // parsed by the time the commit reads it, and one owner of the cascade is what
  // stops a size arriving twice by two routes.
  //
  // [WARN] THE TILE CAP MOVES WITH THE SIZE TOO, AND IT HAS TO BE SETTLED BEFORE
  // applyDrop RUNS, not after. The cap is TARGET_MAX - C/2 - clearance, so a
  // bigger screen lowers it: a tile depth of 190 typed with no size is legal, and
  // the moment an 85 in TV lands it demands a 211.5 in drop. Clamp the tile first
  // and applyDrop picks up the new floor in the same pass. Do it the other way
  // round and #target spends that pass carrying a min above its own max.
  clampTileToCap();
  syncTileCap();
  if(stepReachable('s3') && !tvSizeFromCard) applyDrop(state.target, 'commit');
  renderSummary();
});
el('target').addEventListener('input', e => {
  // LIVE PREVIEW ONLY. No clamp while they are still typing: clamping per
  // keystroke means entering "30" jumps to the minimum the instant "3" lands,
  // and the next character then appends to a number the customer never chose.
  // The pipeline runs on 'change' below, which is what "committed" means for a
  // number field: Enter, blur, or a spinner click.
  // This route bypasses applyDrop, so it has to drop the default flag itself: the
  // first keystroke is the customer taking ownership of the length, and syncURL
  // runs from the render below. See TARGET_DEFAULT.
  targetIsDefault = false;
  pendingTvQuiet = false;
  state.target = e.target.value===''?null:Number(e.target.value);
  state.targetWanted = state.target;
  renderStage3Out();
});
el('target').addEventListener('change', e => {
  // A change on THIS field is the customer settling a length, so ownership is
  // declared here rather than inside applyDrop (which since 2026-07-31 preserves
  // the default flag across re-runs other controls trigger). The input handler
  // above already cleared it for the typed path; this covers a commit that
  // arrives without a keystroke.
  targetIsDefault = false;
  pendingTvQuiet = false;
  state.targetWanted = e.target.value === '' || isNaN(Number(e.target.value)) ? null : Number(e.target.value);
  applyDrop(e.target.value === '' ? null : e.target.value, 'commit');
});

// ---- The length stepper (2026-07-28, owner-requested) ----------------------
// The native number spinners are ~13px wide on desktop, under the 44x44 CSS px
// WCAG 2.5.5 asks for, and mobile browsers HIDE them, so on a phone there was no
// way to nudge the length short of typing it. The CSS turns the native ones off
// and these two buttons replace them at every width.
//
// [RULE] IT GOES THROUGH applyDrop AND DOES NOT TOUCH THE FIELD. #target's value
// is the OUTPUT of clamp -> resolve -> quantise, never an input to it. Stepping
// the field directly (`t.value = Number(t.value) + TARGET_STEP`) would walk
// straight past the computed floor and leave the drawing disagreeing with the
// number until the next commit, which is the same class of defect as the two
// rounding grids this file's header warns about. applyDrop writes the field.
//
// [WARN] IT STEPS FROM state.target, NOT FROM THE FIELD TEXT. Mid-typing the
// field can hold "3" of an intended "30", or "18." with a trailing point. Reading
// that back and adding half an inch commits a number the customer never chose.
// state.target is what the 'input' handler above already parsed.
function stepTarget(dir){
  // A stepper tap aims at the length itself: ownership declared here, same as the
  // field's own handlers. See the flag rule in applyDrop (2026-07-31).
  targetIsDefault = false;
  pendingTvQuiet = false;
  const lim = dropLimits();
  state.targetWanted = (state.target == null || isNaN(state.target)) ? lim.floorQ : Number(state.target) + dir * TARGET_STEP;
  // Nothing entered yet: the first tap lands on the shortest length we can
  // actually build rather than on an arbitrary zero, and that is a genuinely
  // useful answer by itself. Same for either button, because there is nothing
  // below the floor to step down to.
  if(state.target == null || isNaN(state.target)){
    applyDrop(lim.floorQ, 'commit');
    return;
  }
  applyDrop(Number(state.target) + dir * TARGET_STEP, 'commit');
}

// Reflects the two limits onto the buttons. A disabled button is the honest
// signal that a stop has been reached; the reason line beside it says WHY, which
// a disabled attribute alone never can.
//
// It syncs BOTH steppers since 2026-07-31, and the two pairs are guarded
// separately: one missing pair (a stale cached section, see the listener warning
// below) must not silently stop the other pair being updated.
function syncStepButtons(){
  const dn = el('targetDown'), up = el('targetUp');
  if(dn && up){
    const lim = dropLimits();
    const v = (state.target == null || isNaN(state.target)) ? null : Number(state.target);
    // Never disabled with the field empty: the first tap is what SETS the length.
    dn.disabled = v != null && v <= lim.floorQ + 1e-9;
    up.disabled = v != null && v >= TARGET_MAX - 1e-9;
  }
  // [3D STEP 2026-09-22] The 1 inch pair on the 3D drawing takes the SAME two
  // stops as the half inch pair above, because it moves the same number through
  // the same applyDrop. Guarded on its own, like the other two pairs: it is the
  // newest markup on the page and a stale cached section without it must not stop
  // the field's own buttons being updated.
  const d3dn = el('elev3dStepDown'), d3up = el('elev3dStepUp');
  if(d3dn && d3up){
    const lim3 = dropLimits();
    const v3 = (state.target == null || isNaN(state.target)) ? null : Number(state.target);
    // Never disabled with the field empty: the first tap is what SETS the length.
    d3dn.disabled = v3 != null && v3 <= lim3.floorQ + 1e-9;
    d3up.disabled = v3 != null && v3 >= TARGET_MAX - 1e-9;
  }
  const tdn = el('tilesDown'), tup = el('tilesUp');
  if(tdn && tup){
    // The tile cap is DERIVED from the TV size, so it moves under a depth that was
    // legal when it was entered. Riding this function is what keeps that honest:
    // applyDrop calls it on every phase, and every route that changes the size or
    // the depth ends in an applyDrop.
    const cap = tileCap();
    const d = (state.case3Dist == null || isNaN(state.case3Dist)) ? null : Number(state.case3Dist);
    tdn.disabled = d != null && d <= TILE_MIN + 1e-9;
    tup.disabled = d != null && d >= cap - 1e-9;
  }
}

// [WARN] GUARDED, unlike the other listeners in this file, and deliberately so.
// Shopify caches theme ASSETS and SECTION markup independently, so a visitor can
// hold this JS against a stale cached section that has no stepper in it. Every
// other listener here binds an element that has existed for months; these two are
// new today. An unguarded null would throw mid-module and silently kill every
// listener and init line BELOW it, including the tile field and the URL restore,
// and the console would show one error for a feature that looks unrelated.
// [WARN] NOT stepUp / stepDown. Those names are TAKEN, by the two ROUNDING
// helpers at the top of this file (stepUp rounds a floor up to the field grid and
// feeds dropLimits().floorQ; stepDown rounds a cap down and feeds tileCap()).
// Binding DOM buttons to those identifiers threw a redeclaration error here, which
// was lucky: in a nested scope it would have silently shadowed the rounding
// function and broken the clamp floor instead of failing loudly.
const stepBtnDown = el('targetDown'), stepBtnUp = el('targetUp');
if(stepBtnDown) stepBtnDown.addEventListener('click', () => stepTarget(-1));
if(stepBtnUp) stepBtnUp.addEventListener('click', () => stepTarget(1));

// ---- [3D STEP 2026-09-22] the 1 inch pair ON the 3D drawing ----------------
// Owner ask, right after the 2026-09-22 live push: "Let's work on length
// adjustment in the 3d preview for the products and configurators." Step 4's spin
// field is often a screen away from the drawing by the time somebody is turning
// the model around, so the same gesture lives on the canvas too.
//
// [RULE] IT IS THE SAME PIPELINE, NOT A SECOND ONE. A click ends in applyDrop(...,
// 'commit') exactly like stepTarget above, so the price, the gates, the reason
// line, the 2D scene, the progress rail, the URL and the 3D re-pose all follow
// today's single route. Nothing here writes #target, reads its text, or re-poses
// the 3D view by hand: the engine already listens for vmscfg:build-changed, which
// applyDrop fires, and it re-fits its camera there as it does for every other
// length change on this page. That existing behaviour is deliberately left alone.
//
// [RULE] A WHOLE INCH, NOT HALF. TARGET_STEP (0.5) is the FIELD's quantum and the
// rounding grid; this pair is a separate, coarser gesture the owner asked for by
// name, so it has its own constant. It does NOT round the customer's number: a
// typed 30.5 steps to 31.5, not to 31. resolveDropValue still quantises to the
// 0.5 grid, and computeChain still snaps the telescoping pipe to whole inches from
// its own minimum, so a 1.0 move lands with no residual either way.
//
// [WARN] The buttons live INSIDE #elev3d (see the section), which the engine hides
// whenever the 3D view is off, so they are only reachable while the drawing is on
// screen. They are guarded here for the same reason the half inch pair is: a
// visitor can hold this JS against a stale cached section that has no such buttons
// in it, and an unguarded null would kill every listener below this line.
const STEP_3D_IN = 1.0;   // the 3D pair's own step. NOT TARGET_STEP, on purpose.
function step3dTarget(dir){
  // Same ownership rule as stepTarget: a stepper tap aims at the length itself.
  targetIsDefault = false;
  pendingTvQuiet = false;
  const lim = dropLimits();
  state.targetWanted = (state.target == null || isNaN(state.target)) ? lim.floorQ : Number(state.target) + dir * STEP_3D_IN;
  // Nothing entered yet: the first tap lands on the shortest length we can build,
  // the same first-tap floor rule the half inch pair uses, and for the same reason
  // (there is nothing below the floor to step down to, so either button does it).
  if(state.target == null || isNaN(state.target)){
    applyDrop(lim.floorQ, 'commit');
    return;
  }
  applyDrop(Number(state.target) + dir * STEP_3D_IN, 'commit');
}
const step3dBtnDown = el('elev3dStepDown'), step3dBtnUp = el('elev3dStepUp');
if(step3dBtnDown) step3dBtnDown.addEventListener('click', () => step3dTarget(-1));
if(step3dBtnUp) step3dBtnUp.addEventListener('click', () => step3dTarget(1));

// The bordered box around a spin field and its "inches" suffix (2026-07-29). The
// border, the background and the unit belong to the WRAPPER now, so a good part of
// what looks like the field is not the field: tapping the word "inches" or the
// padding beside it has to land in the input, or the control lies about where it can
// be typed. Guarded for the same cached-section reason as the stepper above.
//
// [WARN] ALL BOXES, NOT querySelector. This bound the first box only, which was
// correct while #target was the only one; #tiles got the same box on 2026-07-31 and
// a single-element lookup would have left the second one silently dead. Each handler
// focuses the input INSIDE ITS OWN box, never a looked-up id.
document.querySelectorAll('.vmscfg-spin__box').forEach(box => {
  box.addEventListener('click', e => {
    const input = box.querySelector('input');
    if(!input || e.target === input) return;   // the input already has it
    input.focus();
  });
});

// ---- Parts-list SKU photo preview: delegated once on #s3out (2026-09-06) ----
// #s3out is the build-cards container. renderStage3Body rebuilds its innerHTML on
// every keystroke, but the element itself persists, so these delegated listeners
// survive every re-render and never need re-wiring (the pattern the D2 note above
// calls for). The preview <img> is filled from data-src the FIRST time a row is
// shown, never on render, so a page with four cards never preloads two dozen large
// images. Hover SHOWS (guarded by (hover: hover) so a touch device does not get a
// hover-open that fights the tap toggle); keyboard focus on the SKU link SHOWS; a
// click or tap on the thumb TOGGLES (the phone path) and flips aria-expanded;
// Escape hides; only one preview is open at a time.
(function(){
  const box = el('s3out');
  if(!box) return;
  const canHover = window.matchMedia ? window.matchMedia('(hover: hover)').matches : true;
  function pvOf(cell){ return cell ? cell.querySelector('.bom__pv') : null; }
  function loadOnce(pv){
    const img = pv && pv.querySelector('img');
    if(img && !img.getAttribute('src') && img.dataset.src) img.src = img.dataset.src;
  }
  function hideAll(except){
    box.querySelectorAll('.bom__pv:not([hidden])').forEach(pv => {
      if(pv === except) return;
      pv.hidden = true;
      const t = pv.closest('.bom__skucell').querySelector('.bom__thumb');
      if(t) t.setAttribute('aria-expanded','false');
    });
  }
  function show(cell){
    const pv = pvOf(cell);
    if(!pv) return;
    hideAll(pv);
    loadOnce(pv);
    pv.hidden = false;
    const t = cell.querySelector('.bom__thumb');
    if(t) t.setAttribute('aria-expanded','true');
  }
  function hide(cell){
    const pv = pvOf(cell);
    if(!pv || pv.hidden) return;
    pv.hidden = true;
    const t = cell.querySelector('.bom__thumb');
    if(t) t.setAttribute('aria-expanded','false');
  }
  const closestCell = e => e.target && e.target.closest ? e.target.closest('.bom__skucell') : null;
  // Tap / click on the thumb TOGGLES (phone path).
  box.addEventListener('click', e => {
    const thumb = e.target.closest ? e.target.closest('.bom__thumb') : null;
    if(!thumb) return;
    const cell = thumb.closest('.bom__skucell');
    const pv = pvOf(cell);
    if(pv && pv.hidden) show(cell); else hide(cell);
  });
  if(canHover){
    box.addEventListener('pointerover', e => {
      // A touchscreen laptop reports hover:hover but also fires pointerover on a
      // TAP; ignoring pointerType 'touch' lets the click toggle own the tap and
      // stops a hover-open-then-tap-close flicker on hybrid devices.
      if(e.pointerType === 'touch') return;
      const t = e.target.closest ? e.target.closest('.bom__thumb, .bom__sku') : null;
      if(t) show(t.closest('.bom__skucell'));
    });
    box.addEventListener('pointerout', e => {
      const cell = closestCell(e);
      if(!cell) return;
      if(e.relatedTarget && cell.contains(e.relatedTarget)) return;   // still inside the same cell
      hide(cell);
    });
  }
  box.addEventListener('focusin', e => {
    const link = e.target.closest ? e.target.closest('.bom__sku') : null;
    if(link) show(link.closest('.bom__skucell'));
  });
  box.addEventListener('focusout', e => {
    const cell = closestCell(e);
    if(!cell) return;
    if(e.relatedTarget && cell.contains(e.relatedTarget)) return;
    hide(cell);
  });
  // Escape is bound on DOCUMENT, not #s3out, so it closes a hover-opened preview
  // even when focus is elsewhere on the page (e.g. still in #target). Guarded to
  // act ONLY when a preview is actually open, and it neither preventDefault nor
  // stopPropagation, so the four dialogs keep their own native Escape handling.
  document.addEventListener('keydown', e => {
    if((e.key === 'Escape' || e.key === 'Esc') && box.querySelector('.bom__pv:not([hidden])')) hideAll(null);
  });
})();

// #tiles is SERVER-rendered, and as of 2026-07-26 it lives in STEP 4 beside the
// elevation rather than in the #dropHelp panel in step 2. One home, one element,
// one value. It is still never subtracted from the target, because the
// customer's drop is measured from the real structure above the tiles in the
// first place, but it is no longer display-only: it sets the floor.
const tilesEl = el('tiles');
if(tilesEl) tilesEl.addEventListener('input', e => {
  // [C] LIVE PREVIEW ONLY, mirroring the #target 'input' handler documented above.
  // This used to run applyDrop(state.target, 'commit') on EVERY keystroke, so a
  // half-typed depth committed the length pipeline against the raw value: typing
  // "999" (or "180" on the way to "18") raised the floor and clamped the length UP
  // to it, and correcting the field never brought them back down, because applyDrop
  // only ever clamps a target UP to the floor, never lowers a target already above
  // it. The commit now happens on change/blur (commitTileDepth -> applyTileDepth),
  // which recomputes the floor DOWN as well as up and writes the clamped depth back.
  // Per keystroke we only update the preview, which cannot raise the committed
  // floor: renderStage3Out repaints the cards and the scene from the current
  // state.target, it does not re-clamp it. Same split #target runs on.
  tilesIsDefault = false;
  state.case3Dist = e.target.value===''?null:Number(e.target.value);
  // Entering a tile depth has always been the implicit "yes, drop ceiling". The
  // field only appears after the answer is yes now, but a restored link or a
  // browser autofill can still get here first.
  if(state.case3Dist != null && state.hasDropCeiling !== true){
    state.hasDropCeiling = true; syncDropCeilingUI();
  }
  renderStage3Out();
});

// AND THE COMMIT, which the field did not have until 2026-07-27. Typing 999 left
// the box reading 999 while the model clamped it (to 60 back then, to the derived
// cap now) and the reason line explained the cap: the number on screen and the
// number in the drawing disagreed, which is
// the same class of defect as the two rounding grids at the top of this file.
// #target has never had it, because applyDrop writes its clamped value back.
// This is that, for #tiles, routed through the one function that already
// quantises, clamps to TILE_MIN and TILE_MAX and writes the result back.
//
// [WARN] COMMIT ONLY, NEVER FROM THE 'input' HANDLER ABOVE. Rewriting the field
// from the value it is halfway through typing is what eats the decimal point out
// of "18.5", because Number("18.") is 18. Same split #target and #tvSize run on:
// input previews, change and blur commit.
//
// Bound to both change and blur, like #tvSize, with the same cheap exit: change
// fires first and normalises the field, so the blur that follows sees text that
// already matches state and returns. That exit is doing real work here, not just
// saving a cycle: a commit re-runs the pipeline, and the pipeline rebuilds #s3out
// by innerHTML. Without it every blur would rebuild the build cards underneath
// whatever click moved the focus, and the build cards are buttons.
// [WARN] THE CHEAP EXIT MUST NOT COMPARE AGAINST state.case3Dist. The 'input'
// handler above already wrote `state.case3Dist = Number(value)` on EVERY
// keystroke, so for any canonically formatted number `raw === String(state...)`
// is ALWAYS true by the time a commit runs, and the function returned before it
// ever clamped. That made the whole commit path dead code: typing 999 left the
// field, the state, the slider and the drawing all on 999, and #target then got
// min 1017 against max 200, a permanently invalid input. Typing "0999" DID
// clamp, because that text is not canonical, which is the tell that the guard
// and not the clamp was at fault. Found in the browser 2026-07-27.
// So the guard tracks THE TEXT THIS FUNCTION LAST COMMITTED, which is the actual
// question being asked ("have I already normalised exactly this?"), and is
// re-read AFTER applyTileDepth so the value it wrote back counts as committed.
let lastTileCommit = null;
function commitTileDepth(e){
  const raw = e.target.value;
  if(raw === ''){ lastTileCommit = ''; return; }  // clearing is the input handler's job
  if(raw === lastTileCommit) return;              // this exact text is already normalised
  const n = Number(raw);
  if(isNaN(n)) return;
  lastTileCommit = raw;
  applyTileDepth(n, 'commit');
  renderSummary();
  // applyTileDepth writes the clamped value back, so the field may now read
  // something else (999 becomes tileCap(), which is 182 for a 70 in screen and
  // 200 with no size entered). Record what is actually there, or the blur
  // that follows a change sees new text, commits again, and rebuilds #s3out
  // underneath the click that moved the focus. The cards are buttons.
  lastTileCommit = e.target.value;
}
if(tilesEl){
  tilesEl.addEventListener('change', commitTileDepth);
  tilesEl.addEventListener('blur', commitTileDepth);
}

// ---- The tile-depth stepper (2026-07-31, owner: match the length box) -------
// The same control the length has had since 2026-07-28, for the same reason: the
// native number spinners are under the WCAG 2.5.5 target size on desktop and hidden
// outright on a phone. Both of stepTarget's rules apply here word for word and are
// not restated: it goes through applyTileDepth so the clamp writes the field, and it
// steps from state.case3Dist rather than from the field text, which mid-typing can
// hold "1" of an intended "12". See the [RULE]/[WARN] pair above stepTarget.
function stepTiles(dir){
  if(state.case3Dist == null || isNaN(state.case3Dist)){
    // Nothing entered: the first tap lands on the pre-populated depth rather than on
    // zero, which is the same number answering "yes" would have seeded. Same for
    // either button, because a depth of nothing has no direction.
    applyTileDepth(TILES_DEFAULT, 'commit');
  } else {
    applyTileDepth(Number(state.case3Dist) + dir * TARGET_STEP, 'commit');
  }
  // applyTileDepth('commit') runs applyDrop and the whole step 4 render; the summary
  // row is the one thing outside it, which is why commitTileDepth calls this too.
  renderSummary();
  // And the commit guard is re-pointed at what the field now reads, exactly as
  // commitTileDepth does after its own clamp: without this the next blur off the
  // field sees text it has not committed and rebuilds #s3out under the click that
  // moved the focus. The cards are buttons. See commitTileDepth.
  if(tilesEl) lastTileCommit = tilesEl.value;
}

// [WARN] GUARDED, and NOT named stepUp / stepDown: both points are the length
// stepper's, unchanged. See the two warnings above stepBtnDown.
const tilesBtnDown = el('tilesDown'), tilesBtnUp = el('tilesUp');
if(tilesBtnDown) tilesBtnDown.addEventListener('click', () => stepTiles(-1));
if(tilesBtnUp) tilesBtnUp.addEventListener('click', () => stepTiles(1));

// "Is there a drop ceiling?" The answer moves the floor, so it runs the drop
// back through the pipeline like any other committed change.
// The link under the tile-depth field, which opens the explanation that used to
// be a <details> in step 2.
(function(){
  const b = el('dcHelpBtn');
  if(b) b.onclick = openDcHelp;
})();

document.querySelectorAll('input[name="vmscfg-dropceiling"]').forEach(r => {
  r.addEventListener('change', () => {
    if(!r.checked) return;
    state.hasDropCeiling = r.value === 'yes';
    state.dcTouched = true;
    pendingTvQuiet = false;
    // "No" CLEARS the tile depth rather than just hiding it. A depth stashed
    // behind a "no" is a contradiction the summary row and the shared URL would
    // both repeat, and the back-compatibility rule in readURLState would read it
    // straight back as a yes on the next load.
    if(state.hasDropCeiling === false){
      state.case3Dist = null;
      tilesIsDefault = false;   // nothing is seeded any more; see TILES_DEFAULT
      if(tilesEl) tilesEl.value = '';
    }
    // "Yes" pre-populates the depth (2026-07-31). ORDER IS DELIBERATE: seed BEFORE
    // syncDropCeilingUI so the field is already carrying its number the first frame
    // it is revealed, and before the applyDrop below so that commit solves against
    // the floor the seeded depth creates. If the length then has to be held, the
    // sentence it speaks is the tiles wording, which is the whole point of the pair.
    if(state.hasDropCeiling === true) seedTileDepth();
    syncDropCeilingUI();
    if(stepReachable('s3')) applyDrop(state.target, 'commit');
    renderSummary();
  });
  r.addEventListener('click', () => { if(!state.dcTouched){ state.dcTouched = true; syncURL(); } });
});

// Reference tables are SERVER-rendered by the section (logic spec section 7).

// =====================================================================
// ACCEPTANCE SELF-TEST (rebuilt for the true-parts-only architecture)
// =====================================================================
(function runChecks(){
  if(!el('diag') || !/[?&]vmscfg_diag=1/.test(location.search)) return;
  const results = [];
  const flat = getPlate('11CP005');
  const h33HE001 = getHead('33HE001');
  const h004 = getHead('33CE004HEAD');

  // 1) Pool counts: components >= 40 (raw DATA.components length), heads >= 9, plates = 14
  //    (6 -> 9 heads and 13 -> 14 plates on 2026-08-04: the V3 offsets round pooled
  //    11TR001, LCD-MID-CHB, PDS-LCHB as mounting_head and 11TR002 as ceiling_plate.)
  //    [D] The head count is a FLOOR, not an equality: it was hardcoded === 9 and
  //    started FAILing on every load the day 33CE008HEAD joined the pool (10). HEADS
  //    is derived straight from DATA.components, so the honest gate is >= 9 with the
  //    live pool listed for the eye; the actual roster is printed in the detail line.
  results.push(['1. Pool counts: components >= 40, heads >= 9, plates = 14',
    DATA.components.length >= 40 && HEADS.length >= 9 && PLATE_POOL.length === 14,
    `components=${DATA.components.length}, heads=${HEADS.length} (${HEADS.map(h=>h.sku).join(',')}), plates=${PLATE_POOL.length}`]);

  // 2) Head filter math: size 60 excludes 22HE001/22HE021 (max 43), includes
  //    33HE001/33HE002/33HE021 (range 32-70); a synthetic no-range head is
  //    ALWAYS offered regardless of size, and so is every real head when no
  //    size is entered at all.
  const at60 = HEADS.filter(h => headScreenOK(h, 60)).map(h => h.sku);
  const excl22 = at60.indexOf('22HE001') < 0 && at60.indexOf('22HE021') < 0;
  const incl33 = ['33HE001','33HE002','33HE021'].every(s => at60.indexOf(s) >= 0);
  const noRangeHead = {sku:'TEST-NORANGE', screen_min:null, screen_max:null};
  const noRangeAlwaysOn = headScreenOK(noRangeHead, 60) === true && headScreenOK(noRangeHead, 5) === true;
  const noSizeAllOffered = HEADS.every(h => headScreenOK(h, null) === true);
  results.push(['2. Head filter: size 60 excludes 22HE001/22HE021, includes 33HE001/33HE002/33HE021; null-range head always offered; no size = all offered',
    excl22 && incl33 && noRangeAlwaysOn && noSizeAllOffered,
    `at60=[${at60.join(',')}]`]);

  // 3) Known mast solve (stable case, reused from the mount-based build):
  //    EXT2036 + NPT30 + coupler -> mast 51.5 to 67.5.
  const ext2036 = comp('EXT2036'), npt30 = FIXED.find(f => f.sku==='NPT30');
  const mLo = ext2036.length_min + npt30.nominal_length + COUPLER_ADD;
  const mHi = ext2036.length_max + npt30.nominal_length + COUPLER_ADD;
  results.push(['3. Known mast solve: EXT2036 + NPT30 + coupler = 51.5 to 67.5 in',
    mLo===51.5 && mHi===67.5, `mast ${mLo} to ${mHi}`]);

  // 4) Finished-drop math, plate + mast + head, with a NEGATIVE head offset
  //    (33CE004HEAD, -5.126 in): net plate+head offset with the flat plate
  //    is 3.126 + (-5.126) = -2.0 in, and buildMasts must reflect that in
  //    both the mast-target back-solve and the finished-range output.
  // [NOTE] 2026-08-26: 33CE004HEAD is being archived. If it has left the pool, getHead
  // returns undefined; skip this check (report it skipped) rather than dereferencing
  // .length_offset and throwing. The assertion is unchanged when the head is present.
  if(!h004){
    results.push(['4. Finished-drop math with a negative head offset (33CE004HEAD -5.126)',
      true, 'skipped, 33CE004HEAD retired']);
  } else {
    const netOffset = r3(flat.length_offset + h004.length_offset);
    const bm4 = buildMasts(h004, flat, 50);
    const build4 = bm4.out.find(b => b.pieces.length === 1 && b.tele);
    results.push(['4. Finished-drop math with a negative head offset (33CE004HEAD -5.126): net plate+head offset = -2.0',
      netOffset === -2.0 && !!build4 && r3(build4.finLo + (build4.finHi-build4.finLo)) >= 0,
      `net offset ${netOffset}, mastTarget for target=50 is ${bm4.mastTarget}`]);
  }

  // 5) Guardrails: no 48in pipe, no EXT-4872, no close nipple, no bundle
  //    -extension kits anywhere in the pool or in a spread of solved builds.
  const badPool = FIXED.some(f => f.nominal_length===48 || f.sku==='EXT-4872' || f.component_type==='nipple' || /-extension$/i.test(f.sku));
  const teleBad = TELE.some(t => t.sku==='EXT-4872');
  let scanBad = false;
  HEADS.forEach(h => PLATE_POOL.forEach(p => {
    for(let t=10;t<=90;t+=8){ buildMasts(h,p,t).out.forEach(b => b.pieces.forEach(pc => {
      if(pc.nominal_length===48 || pc.sku==='EXT-4872' || pc.sku==='112-NPT-PIPE-Close' || /-extension$/i.test(pc.sku)) scanBad=true; })); }
  }));
  results.push(['5. No 48in pipe, EXT-4872, close nipple, or bundle -extension kit in any output',
    !badPool && !teleBad && !scanBad, `pool clean=${!badPool&&!teleBad}, build scan clean=${!scanBad}`]);

  // 6) Cart recipe shape: coupler count = pipes-1, and the payload invariant
  //    the cart depends on. It used to be "every line carries the identical
  //    properties OBJECT"; since 2026-08-21 each line gets its OWN object so
  //    it can carry _vms_unit_qty, so the invariant is now stated in values:
  //      - _vms_config_id and _vms_config_summary IDENTICAL on every line
  //        (the app's grouping/merge key, SPEC-configurator-cart-transform.md)
  //      - _vms_unit_qty PRESENT on every line and equal to that line's own
  //        per-unit quantity (what the cart's group-level stepper divides by)
  const cc6 = computeBuildCards(h33HE001, flat, 66);
  const threePiece = cc6.cards.map(c=>c.b).find(b => b.pieces.length === 3) || bm4.out.find(b=>b.pieces.length===3);
  let couplerOk = true, shapeOk = false, sharedOk = false, unitOk = false, buildOk = false;
  if(threePiece){
    couplerOk = threePiece.couplers === threePiece.pieces.length - 1;
    const recipe = buildCartRecipe(basePriceFor(h33HE001, flat), threePiece);
    if(!recipe.blocked){
      // The visible "Build" property rides on the same shared object as the hidden
      // ids: identical on every line, and its human code is the SAME 6-char code
      // buildCode derives from the shared config id.
      const buildVal = buildProperty('TEST-ID-123', h33HE001, flat, threePiece, 55, null);
      const fakeProps = {_vms_config_id:'TEST-ID-123', _vms_config_summary:'test', Build: buildVal};
      const payload = buildPayloadItems(recipe.items, fakeProps);
      sharedOk = payload.every(it => it.properties &&
        it.properties._vms_config_id === 'TEST-ID-123' &&
        it.properties._vms_config_summary === 'test');
      unitOk = payload.every((it, i) => it.properties &&
        it.properties._vms_unit_qty === String(recipe.items[i].quantity) &&
        it.quantity === recipe.items[i].quantity);
      buildOk = payload.every(it => it.properties && it.properties.Build === buildVal) &&
        buildVal.indexOf(buildCode('TEST-ID-123')) === 0;
      // The shared properties object must NOT be mutated by the build: the
      // caller hands one object in and reuses it nowhere else, but a stray
      // _vms_unit_qty on it would leak the last line's count onto everything.
      const callerClean = !('_vms_unit_qty' in fakeProps);
      shapeOk = payload.length === recipe.items.length && sharedOk && unitOk && buildOk && callerClean;
    }
  }
  results.push(['6. Cart recipe shape: coupler count = pipes-1; shared _vms_config_id + _vms_config_summary + Build, per-line _vms_unit_qty',
    !!threePiece && couplerOk && shapeOk,
    threePiece ? `pieces=${threePiece.pieces.length}, couplers=${threePiece.couplers}, sharedIds=${sharedOk}, unitQtys=${unitOk}, sharedBuild=${buildOk}` : 'no 3-piece build found to test with']);

  // 7) Summary format string: "Head: X | Plate: Y | Finished length: N in",
  //    pipe separators (never a middot, the store's banned character). Since [B]
  //    the length is the BUILD's own figure, not the target: a fixed stack prints
  //    finLo, an adjustable stack prints "finLo to finHi".
  const fixedB = {tele:null, finLo:66, finHi:66};
  const adjB = {tele:{sku:'EXT0812'}, finLo:64, finHi:68};
  const summary = configSummary(h33HE001, flat, fixedB);
  const summaryAdj = configSummary(h33HE001, flat, adjB);
  const fmtOk = summary === 'Head: 33HE001 | Plate: 11CP005 | Finished length: 66 in' &&
    summaryAdj === 'Head: 33HE001 | Plate: 11CP005 | Finished length: 64 to 68 in' &&
    summary.indexOf('\u00B7') < 0 && summaryAdj.indexOf('\u00B7') < 0;
  results.push(['7. Summary format: "Head: X | Plate: Y | Finished length: N in" (fixed) / "lo to hi" (adjustable), no middot',
    fmtOk, `"${summary}" / "${summaryAdj}"`]);

  // 8) PLATE_TYPES must PARTITION the plate pool: every pooled plate reachable
  //    from exactly one type, and every SKU a type names actually in the pool.
  //    This is the guard the old STRUCTURES array never had - it silently
  //    orphaned three plates for months (fixed 2026-07-22, commit 08e24d8),
  //    and plateTypeOfSku()'s 1:1 reverse lookup now depends on the property.
  //    Adding a plate to the collection without listing it here fails HERE.
  const listed = PLATE_TYPES.reduce((a,t) => a.concat(t.plates), []);
  const dupes = listed.filter((s,i) => listed.indexOf(s) !== i);
  const orphans = PLATE_POOL.map(p => p.sku).filter(s => listed.indexOf(s) < 0);
  const ghosts = listed.filter(s => !getPlate(s));
  const reverseOk = PLATE_POOL.every(p => {
    const t = plateTypeOfSku(p.sku);
    return !!t && getPlateType(t.id) === t;
  });
  results.push(['8. PLATE_TYPES partitions the plate pool: no orphan, no duplicate, no ghost SKU, reverse lookup total',
    !dupes.length && !orphans.length && !ghosts.length && reverseOk,
    `listed=${listed.length}/pool=${PLATE_POOL.length}, dupes=[${dupes.join(',')}], orphans=[${orphans.join(',')}], ghosts=[${ghosts.join(',')}], reverseOk=${reverseOk}`]);

  // 9) Every type's thumbnail SKU is its own first listed plate and
  //    resolves to a component carrying a real image, so no type tile renders
  //    the placeholder box. 33CE004HEAD is the only imageless component today
  //    and it is a head, not a plate, so this must hold for all five.
  //    The `short` label joined this check on 2026-07-29: it is the ONLY text on
  //    a chip once a type is picked, so a type added without one renders a
  //    button with no accessible name and nothing to read, which is a defect
  //    that looks like a styling glitch.
  const thumbBad = PLATE_TYPES.filter(t => {
    const p = getPlate(t.thumb);
    return t.thumb !== t.plates[0] || !p || !p.image ||
      typeof t.short !== 'string' || t.short.trim() === '';
  }).map(t => t.id);
  results.push(['9. Every plate-type tile thumbnail is its first listed plate with a real image, and every type carries a short chip label',
    !thumbBad.length, thumbBad.length ? `bad=[${thumbBad.join(',')}]` : 'all 5 tiles resolve to an image and a chip label']);

  // 10) Image plumbing end to end: the data snippet emits an "image" key on
  //     every component (string URL or null, never undefined), and thumbHTML
  //     renders an <img> when there is one and the placeholder when there is
  //     not. A missing key would mean the Liquid edit got reverted.
  const keyOk = DATA.components.every(c => Object.prototype.hasOwnProperty.call(c, 'image'));
  const typeOk = DATA.components.every(c => c.image === null || typeof c.image === 'string');
  const withImg = DATA.components.filter(c => c.image).length;
  const imgHtmlOk = /<img /.test(thumbHTML({image:'x.jpg'})) &&
    /--none/.test(thumbHTML({image:null})) && /--none/.test(thumbHTML(null)) &&
    thumbHTML({image:'x.jpg'}).indexOf('alt=""') >= 0;
  results.push(['10. Every component carries an "image" key (string or null) and thumbHTML handles both plus a missing component',
    keyOk && typeOk && imgHtmlOk,
    `keyPresent=${keyOk}, typeOk=${typeOk}, withImage=${withImg}/${DATA.components.length}, htmlOk=${imgHtmlOk}`]);

  // 11) Mast art geometry. The picture must agree with the solver's arithmetic or
  //     it is worse than no picture. Asserts the one-inch-column model (a part's
  //     key count IS its inch count), that the telescoping offset hits collapsed
  //     and extended exactly at its endpoints, and that totalInches reproduces
  //     "pipes plus 1.5 in per joint" for a representative multi-piece build
  //     (3 pipes / 2 couplers here; since the 2026-09-07 six-piece budget the
  //     buildMasts enumeration can emit up to 6 pipes / 5 couplers, and this
  //     arithmetic holds for any joint count).
  const MA = window.VMSMastArt;
  let maOk = false, maWhy = 'vms-mast-art.js did not load';
  if(MA && MA._internals){
    const I = MA._internals;
    const colsOk = I.fixedKeys(30).length === 30 && I.fixedKeys(2).length === 2 &&
      I.innerKeys(44,72).length === (72-44+4) && I.outerKeys(44).length === (44-2);
    // endpoints: collapsed -> 2 + (collapsed-2) = collapsed; extended -> (innerCols-2) + outerCols = extended
    const offLo = I.teleOuterOffsetIn(44,72,44), offHi = I.teleOuterOffsetIn(44,72,72);
    const endsOk = offLo === 2 && r3(offHi + (44-2)) === 72;
    const worst = [{kind:'fixed',length:42},{kind:'fixed',length:42},
                   {kind:'tele',collapsed:44,extended:72,current:58}];
    const totOk = MA.totalInches(worst) === 42+42+58+2*1.5;
    const clampOk = MA.totalInches([{kind:'tele',collapsed:20,extended:36,current:99}]) === 36 &&
                    MA.totalInches([{kind:'tele',collapsed:20,extended:36,current:1}]) === 20;
    maOk = colsOk && endsOk && totOk && clampOk;
    maWhy = `cols=${colsOk}, teleEndpoints=${endsOk} (${offLo}/${offHi}), total=${MA.totalInches(worst)} (expect 145), clamp=${clampOk}`;
  }
  results.push(['11. Mast art: one-inch-column model, telescoping endpoints exact, worst-case total = pipes + 1.5 per joint',
    maOk, maWhy]);

  // 12) specFromBuild must round-trip: the spec drawn has to be the same length as
  //     the mast the solver asked for, or the drawing lies about what is in the cart.
  let rtOk = false, rtWhy = 'no telescoping build found to test';
  if(MA){
    const cc = computeBuildCards(h33HE001, flat, 66);
    const adj = cc.cards.map(c => c.b).find(b => b.tele);
    if(adj){
      const mt = Math.min(Math.max(66 - flat.length_offset - h33HE001.length_offset, adj.mastLo), adj.mastHi);
      const spec = MA.specFromBuild(adj.pieces, mt);
      const drawn = MA.totalInches(spec);
      // half-inch tolerance: the telescoping setting is quantised to 0.5 in
      rtOk = spec.length === adj.pieces.length && Math.abs(drawn - mt) <= 0.5;
      rtWhy = `pieces=${adj.pieces.length}, spec=${spec.length}, mastTarget=${r3(mt)}, drawn=${r3(drawn)}`;
    }
  }
  results.push(['12. specFromBuild round-trip: drawn mast length matches the solved mast target',
    rtOk, rtWhy]);

  // 13) D6 (owner ruling 2026-07-26): under a drop ceiling an adjustable build
  //     must be able to extend at least 1 in FURTHER DOWN or it is dropped from
  //     the ranking, and the fixed-length card is left alone. Three things are
  //     asserted, and the third is the one that protects tests 6 and 12: a
  //     THREE-argument call (opts undefined, which is exactly how those two call
  //     it) must return the ranking untouched, or this rule has quietly changed
  //     what the rest of the suite is measuring.
  const mastNames = cc => cc.cards.map(c => mastLabel(c.b)).join(' / ');
  const fixedNames = cc => cc.cards.filter(c => c.isFixed).map(c => mastLabel(c.b)).join(',');
  const threeArgInert = mastNames(computeBuildCards(h33HE001, flat, 66)) ===
                        mastNames(computeBuildCards(h33HE001, flat, 66, {requireDownHeadroom:false}));
  let d6Sweep = 0, d6Survivors = 0, d6FixedDrift = 0, d6WentEmpty = 0;
  for(let t = 10; t <= 90; t += 4){
    const off = computeBuildCards(h33HE001, flat, t);
    const on  = computeBuildCards(h33HE001, flat, t, {requireDownHeadroom:true});
    d6Sweep++;
    on.cards.forEach(c => { if(!c.isFixed && c.b.up < 1) d6Survivors++; });
    if(fixedNames(off) !== fixedNames(on)) d6FixedDrift++;
    // Step 4 is never allowed to go blank: if there was anything to show without
    // the rule, there has to be something to show with it.
    if(off.cards.length > 0 && on.cards.length === 0) d6WentEmpty++;
  }
  results.push(['13. D6 downward headroom: every ranked adjustable build extends at least 1 in further, the fixed card is untouched, step 4 never empties, and a three-argument call is inert',
    threeArgInert && !d6Survivors && !d6FixedDrift && !d6WentEmpty,
    `targets=${d6Sweep}, adjustable survivors with up<1=${d6Survivors}, fixed-card drift=${d6FixedDrift}, emptied=${d6WentEmpty}, threeArgInert=${threeArgInert}`]);

  // 14) Data invariant behind FEATURE A auto-select (owner 2026-09-06). The single-
  //     option auto-select only has a case if the live pool actually leaves exactly
  //     ONE head fitting at the extremes: 11TR001 at the small end and PDS-LCHB at
  //     the large end, with the Unistrut plate type a single-plate group (11UN001).
  //     Asserted at the endpoints (size 10 and size 90) rather than over a hardcoded
  //     band, because a head range edit in the admin moves the band and this test
  //     should still pass while REPORTING the true extent in its detail line. The
  //     endpoints also catch the drift that would break auto-select everywhere: a
  //     head given a null screen range is offered at every size, so it would show up
  //     in both endpoint lists and fail this row.
  //     [NOTE 2026-09-06] The live low singleton band measures 10 to 18, not the
  //     "10 to 23" the task named, because DD-HEAD is rated 19 to 43; the detail line
  //     prints the measured band so the real extent is visible. The high band is
  //     89 to 90 as expected (PDS-LCHB is 32 to 90, the next-widest head 32 to 88).
  const fitsAt = s => HEADS.filter(h => headScreenOK(h, s)).map(h => h.sku);
  const singles = [];
  for(let s = 10; s <= 120; s++){ const f = fitsAt(s); if(f.length === 1) singles.push([s, f[0]]); }
  const bandOf = sku => singles.filter(x => x[1] === sku).map(x => x[0]);
  const bandStr = b => b.length ? (b[0] + ' to ' + b[b.length - 1]) : 'none';
  const at10 = fitsAt(10), at90 = fitsAt(90);
  const loOne = at10.length === 1 && at10[0] === '11TR001';
  const hiOne = at90.length === 1 && at90[0] === 'PDS-LCHB';
  const uniT = getPlateType('uni');
  const uniOne = !!uniT && uniT.plates.length === 1 && uniT.plates[0] === '11UN001';
  results.push(['14. FEATURE A data invariant: exactly one head fits the small end (11TR001) and the large end (PDS-LCHB), Unistrut has one plate (11UN001)',
    loOne && hiOne && uniOne,
    `size10=[${at10.join(',')}], size90=[${at90.join(',')}], 11TR001 singleton band=${bandStr(bandOf('11TR001'))}, PDS-LCHB singleton band=${bandStr(bandOf('PDS-LCHB'))}, uni plates=[${uniT ? uniT.plates.join(',') : ''}]`]);

  el('diag').innerHTML = results.map(r =>
    `<div><span class="${r[1]?'ok':'bad'}">${r[1]?'PASS':'FAIL'}</span> ${r[0]}<br><span class="muted" style="font-size:12px">${r[2]}</span></div>`).join('<hr style="border:none;border-top:1px solid #eee;margin:8px 0">');

  window.__CHECKS__ = results;
})();

// initial paint: read any URL state, reveal the enhanced UI (server
// reference layer stays as the no-JS floor).
readURLState();
// Analytics boot, immediately after the URL is read and before ANY render:
// seeds the step keys from the restored state so a shared link does not report
// four completions nobody performed, publishes the deep-link arrival while
// location.search is still the URL the customer actually landed on, and arms
// the step tracker for everything after this line. Suppressed entirely under
// ?vmscfg_diag=1 (see vmsPublish).
startConfiguratorAnalytics();
document.querySelectorAll('[data-vmscfg-stages]').forEach(n => n.removeAttribute('hidden'));
const diagWrap = document.querySelector('[data-vmscfg-diag-wrap]');
if(diagWrap && /[?&]vmscfg_diag=1/.test(location.search)) diagWrap.removeAttribute('hidden');
// ensureSizeOption FIRST, and it is not optional: assigning a <select> a value no
// option carries silently leaves it on the placeholder, so a link at ?size=100
// would restore a page that says "Screen Size" while the build below it is solved
// for a 100 in TV. The option is injected instead, and nothing is clamped.
if(state.tvSize != null){ ensureSizeOption(state.tvSize); el('tvSize').value = state.tvSize; }
// Seed the commit guard with what the field already holds, so the first blur off
// an untouched restored field is a no-op instead of a full cascade pass.
lastSizeCommit = el('tvSize').value;
if(state.target != null) el('target').value = state.target;
// Drop-ceiling restore. The tile field is written explicitly here (and only
// here and from a tile drag), never from syncDropCeilingUI, which runs while
// the customer is mid-way through typing into it.
if(state.hasDropCeiling == null) state.hasDropCeiling = false;
syncDropCeilingUI();
if(tilesEl) tilesEl.value = state.case3Dist == null ? '' : String(state.case3Dist);
// A link carrying dc=1 and no tiles= is either an old share (made before this
// default existed) or one whose sender never touched the depth, and both arrive at
// the same place: the depth is seeded here so the boot solve below runs against the
// floor a 12 in plenum creates, rather than against the weaker no-depth rule. That
// re-floors an old link on arrival, which is the same class of behavior as the
// [WARN] on the boot applyDrop further down: a restored value is settled by the
// LIVE rules, not trusted as sent. Before clampTileToCap and redraw so the first
// paint of step 4 and the scene both see it. See TILES_DEFAULT.
if(state.hasDropCeiling === true) seedTileDepth();
{
  const t = readTvURL(location.search);
  state.targetWanted = state.target != null && !isNaN(state.target) ? Number(state.target) : null;
  if(t && t.model && !t.manual){ pendingTvQuiet = true; setTimeout(tvQuietEnd, 10500); }
  if(t && t.manual){
    setTvManual(t.manual[0], t.manual[1], t.manual[2]);
    ['A', 'B', 'C'].forEach((L, i) => { const f = el('tvMeas' + L); if(f) f.value = nice(t.manual[i]); });
  } else if(t && t.model) restoreTvModel(t.model);
  else {
    const sv = savedTvForUrl(location.search);
    if(sv){ pendingTvQuiet = true; setTimeout(tvQuietEnd, 10500); restoreSavedTv(sv); }
  }
}
if(/[?&]vmscfg_diag=1/.test(location.search)) window.__VMSCFG__ = {
  DATA, PLATE_POOL, FIXED, TELE, COUPLER, HEADS, headScreenOK, buildMasts, computeBuildCards,
  basePriceFor, buildCartRecipe, buildPayloadItems, configSummary, buildCode, buildProperty,
  // Step 2 restructure + compare modal (2026-07-26). PLATE_TYPES replaced the
  // old STRUCTURES export; anything driving the browser tests off the old name
  // needs updating with it.
  PLATE_TYPES, plateTypeOfSku, selectPlate, selectHead, openCompare, mastLabel,
  // Phase 4 cart-testing hooks: state is a live object reference (set fields
  // directly, then call redraw()); getLastBuild() re-reads LAST_BUILD since
  // it is reassigned (not mutated) on every buildSection() render.
  state, redraw, getLastBuild: () => LAST_BUILD, postCartAdd,
  // Drag-elevation hooks (2026-07-26). resolveDropValue is THE quantiser, so a
  // browser check can assert every input path lands on the same number without
  // driving a pointer. getLastDrop() re-reads lastDrop, which is reassigned.
  elevOpts, dropLimits, resolveDropValue, applyDrop, applyTileDepth,
  // The starting length (2026-07-29). Exported so a browser check can assert both
  // halves of the rule without driving the UI: that a fresh step 4 lands on
  // TARGET_DEFAULT or on the floor, whichever is greater, and that isTargetDefault()
  // is still true at that point, which is what keeps target= out of the URL. It is a
  // getter because the flag is reassigned, not mutated.
  TARGET_DEFAULT, isTargetDefault: () => targetIsDefault, seedDefaultTarget,
  // The starting tile depth (2026-07-31), exported for the same two assertions in
  // its own terms: that answering "yes" lands the depth on TILES_DEFAULT or on the
  // cap, and that isTilesDefault() is still true there, which is what keeps tiles=
  // out of the URL. A getter, because the flag is reassigned.
  TILES_DEFAULT, isTilesDefault: () => tilesIsDefault, seedTileDepth,
  // tileCap is the DERIVED tile maximum (2026-07-27). Exported so a browser check
  // can assert that #tiles.max, the tile handle's aria-valuemax and the value
  // applyTileDepth clamps to are all the same number at every TV size.
  tileCap,
  // MIN_MAST_LENGTH is derived from FIXED + TELE at load, so a browser check can
  // assert the buildable floor tracks the live pool instead of a hardcoded 2.
  MIN_MAST_LENGTH,
  // Card-art width memo (2026-07-27). getCardArtKeys() is the direct regression
  // check for the append-then-measure bug: at a 375px viewport no key may carry
  // the canvas default of 300, and no key's width may exceed the viewport.
  artSlotWidth, getCardArtKeys: () => new Map(cardArtKeys),
  getLastDrop: () => lastDrop, getElevScene: () => elevScene,
  // Four-step disclosure (2026-07-28). stepStates() is the derived truth a
  // browser check compares the DOM against; sizeCommitted() is the D2-reversal
  // gate. stepManual is read through a getter because it is REASSIGNED (not
  // mutated) on every cascade, so a captured reference would go stale.
  stepStates, sizeCommitted, applyStepStates, firstOpenStep,
  getStepManual: () => Object.assign({}, stepManual)
};
// A restored ?tiles= is not clamped by readURLState, and the cap it has to respect
// depends on the restored ?size=. Settle it BEFORE the first paint, for the same
// reason the TV size handler does: a tile depth over the cap would hand #target a
// min above its own max. Before redraw() rather than after, so the first render of
// step 4 and the scene both see the clamped value and nothing has to be redrawn.
clampTileToCap();
syncTileCap();
redraw();
// A restored drop is a committed value like every other, so it goes through the
// same pipeline rather than being trusted as typed. Without this a shared link
// could restore a drop the live rules refuse (?target=20&tiles=18&size=70 is a
// 15 in violation of the clamp), and #target would keep its static min="1".
// Runs AFTER redraw() so step 4 is revealed and the scene is mounted first.
//
// The seed is the ELSE of that: a link carrying size, plate and head but no target
// (a legacy share, or one made before this default existed) opens step 4 as a fresh
// open, so it gets the 30 rather than an empty box. It is deliberately not treated
// as "the customer already chose", so it stays out of the URL until they act. When
// step 4 is not reachable at all, seedDefaultTarget returns false and the two
// original calls run exactly as before.
//
// [WARN] THE `!targetIsDefault` GUARD IS LOAD-BEARING, not defensive. redraw() above
// ends with its own seed call, so on a legacy link the length is ALREADY 30 by the
// time this line is reached, and re-running it as a 'commit' would undo both halves
// of the rule: it would clear the default flag, putting target=30 into the URL, and
// it could speak a held sentence about a number nobody typed. With the guard, a
// seeded value falls through to the else, where the seed is a no-op and the two
// syncs are simply re-run against a value that is already correct.
if(state.target != null && !targetIsDefault) applyDrop(state.target, 'commit');
else if(!seedDefaultTarget()){ syncTargetFloor(); syncStepButtons(); }

// [FEATURE A, spec 7.6] Deep-link / share-URL arrival that carries a size and a
// plate but no head (an incomplete share). If exactly one head fits, pick it here
// on the same commit path a click takes, so the customer lands on a finished step 3
// rather than an open one. The scroll position is saved and restored around it
// because a deep-link restore must NOT yank the page (the rule scrollStepIntoView
// documents): selectHead moves focus, which would otherwise scroll the auto-picked
// step into view on arrival.
if(state.tvSize != null && state.plateSku && state.headSku == null){
  const _sx = window.pageXOffset, _sy = window.pageYOffset;
  maybeAutoSelectHead();
  try { window.scrollTo(_sx, _sy); } catch(_){}
}

// [2026-09-07] The no-mast builder link lands here with a head and/or plate and NO
// size. Render the one-time arrival note and put the customer on the size control.
// Runs after the full restore above so it reads the settled state; removeArrivalNote
// in commitTvSize tears it down on the first committed size. A plain or ?size=
// arrival returns immediately inside showArrivalNote (arrivalNoteKey is null).
showArrivalNote();

// =====================================================================
// PUBLIC API (2026-09-04) - window.VMSConfiguratorPage
// =====================================================================
// [MCP 2026-09-04] The ONE surface the standalone WebMCP layer
// (assets/vms-configurator-mcp.js) talks to, and the one an in-page agent or a
// browser test reads. It is additive and NOT diag-gated: the whole point is that
// it exists for every visitor, where window.__VMSCFG__ above stays behind
// ?vmscfg_diag=1. Nothing here reaches into the DOM or the engine on the MCP
// file's behalf; the setters run the SAME code paths a shopper's click or
// keystroke runs (the select's input+change, selectPlate/selectHead, the
// #target change, the drop-ceiling radio change, the #tiles change), so the step
// cascade, the clamps, the analytics events and every render happen exactly as
// they do for a click. The reads return plain data only, never DOM nodes and
// never functions, so a snapshot is safe to JSON.stringify.
//
// TERMINOLOGY: the snapshot says "length" for the finished measurement, never
// "drop" (the owner rule in this file's header). "drop_ceiling" is the
// suspended-tile ceiling and keeps its name.

// A "lo to hi" string for a numeric pair, or null when either side is missing.
// Ranges are written "X to Y" (no dash), per the store copy rule.
function mcpRange(lo, hi){
  if(lo == null || hi == null || isNaN(Number(lo)) || isNaN(Number(hi))) return null;
  return Number(lo) + ' to ' + Number(hi);
}
// A price off a component, tested for null/'' before coercing (Number(null) is 0,
// not NaN); Infinity when absent so a sort pushes an unpriced part to the end.
function mcpPriceSort(c){
  return (c && c.price != null && c.price !== '' && !isNaN(Number(c.price))) ? Number(c.price) : Infinity;
}
function mcpPrice(c){
  return (c && c.price != null && c.price !== '' && !isNaN(Number(c.price))) ? Number(c.price) : null;
}
// [MCP 2026-09-09, review C5] Round a dollar figure to whole cents before it leaves
// in a tool result, so 141.39000000000001 reads back as 141.39 (float noise from
// summing offsets). null passes through as null (never coerced to 0 or NaN); every
// price, line_total, surcharge and total in the snapshot goes through here.
function mcpCents(n){
  return (n == null || n === '' || isNaN(Number(n))) ? (n == null ? null : n) : Math.round(Number(n) * 100) / 100;
}
function mcpImg(c){
  return (c && typeof c.image === 'string' && c.image) ? bomPreviewUrl(c.image) : null;
}
// [MCP 2026-09-09] Parse an agent-supplied VESA pattern "WxH" (millimetres) to a
// [width, height] pair, case-insensitive on the x and tolerant of spaces. null when
// it is not a clean "number x number". The MCP layer validates the shape first; this
// is the defensive re-parse on the page side.
function mcpParseVesa(s){
  if(typeof s !== 'string') return null;
  // Strip spaces and a "mm" after either number (review 2026-09-09, finding 3):
  // "400mm x 400mm" and "400 X 400mm" read the same as "400x400".
  const m = s.toLowerCase().replace(/\s+/g, '').replace(/mm/g, '').match(/^(\d+)x(\d+)$/);
  return m ? [Number(m[1]), Number(m[2])] : null;
}
// A head takes a given [w,h] VESA pattern when it is in the head's DERIVED pattern
// list (vesaPatterns, the same filtered canon the card shows), never the raw bounds.
function headTakesVesa(h, wh){
  if(!h || !wh) return false;
  return vesaPatterns(h).some(p => Number(p[0]) === wh[0] && Number(p[1]) === wh[1]);
}
// A head covers a TV weight when its own rating is a real number at or above it.
function headCoversWeight(h, lb){
  if(!h || lb == null) return false;
  if(h.weight_capacity == null || h.weight_capacity === '' || isNaN(Number(h.weight_capacity))) return false;
  return Number(h.weight_capacity) >= lb;
}
// Heads that fit a TV size, and (when asked) a VESA pattern and a weight, price
// ascending. The single source for the auto pick and for every refusal's list.
function headsFitList(size, wh, lb){
  return HEADS.filter(h => headScreenOK(h, size) && (!wh || headTakesVesa(h, wh)) && (lb == null || headCoversWeight(h, lb)))
    .slice().sort((a, b) => mcpPriceSort(a) - mcpPriceSort(b));
}
// "33HE001 $56.95, 33HE002 $64.95" for the fitting heads, or '' when none fit.
function headsFitText(size, wh, lb){
  const list = headsFitList(size, wh, lb);
  return list.map(h => h.sku + ' ' + money(mcpPrice(h))).join(', ');
}
// The customer phrase for what was asked, e.g. "a 65 inch TV with a 600x400 VESA
// pattern and a 120 lb weight". Only the parts that were given are named.
function reqPhrase(size, wh, lb){
  let s = 'a ' + nice(size) + ' inch TV';
  if(wh) s += ' with a ' + wh[0] + 'x' + wh[1] + ' VESA pattern';
  if(lb != null) s += (wh ? ' and' : ' with') + ' a ' + nice(lb) + ' lb weight';
  return s;
}
// The tail sentence for a refusal with no fitting head: name the phone.
function noFitTail(size, wh, lb){
  const alts = headsFitText(size, wh, lb);
  return alts
    ? ('Heads that fit ' + reqPhrase(size, wh, lb) + ': ' + alts + '.')
    : ('No head on this page fits ' + reqPhrase(size, wh, lb) + '. Call ' + PHONE_TEXT + '.');
}
// [review 2026-09-09, finding 1] Why the auto head pick found nothing, in customer
// words, naming the binding constraint (weight, then VESA, then size) and the phone.
function noHeadReason(size, wh, lb){
  const bySize = HEADS.filter(h => headScreenOK(h, size));
  const byVesa = bySize.filter(h => (!wh || headTakesVesa(h, wh)));
  if(lb != null && byVesa.length && !byVesa.some(h => headCoversWeight(h, lb))){
    let maxLb = null;
    byVesa.forEach(h => { const c = Number(h.weight_capacity); if(!isNaN(c) && (maxLb == null || c > maxLb)) maxLb = c; });
    const top = byVesa.filter(h => Number(h.weight_capacity) === maxLb).map(h => h.sku);
    return 'No head on this page is rated for a ' + nice(lb) + ' lb TV. The heaviest rating here is '
      + (maxLb != null ? maxLb + ' lb' : 'lower') + (top.length ? ' (' + top.join(', ') + ')' : '')
      + '. Call ' + PHONE_TEXT + '.';
  }
  if(wh && bySize.length && !byVesa.length){
    return 'No head on this page takes a ' + wh[0] + 'x' + wh[1] + ' VESA pattern for a ' + nice(size) + ' inch TV. Call ' + PHONE_TEXT + '.';
  }
  return 'No head on this page fits a ' + nice(size) + ' inch TV. Call ' + PHONE_TEXT + '.';
}
// [review follow-up 2026-09-09] The cheapest AVAILABLE ceiling plate OF A TYPE whose own
// weight rating carries a TV weight, as {sku, lb, price}, or null when none of the type
// does. The build carries the weight only if BOTH plate and head do; the head that
// reached here already covers it (the head-level filter), so a plate at or above the
// weight is enough to lift the build there. A currently-unavailable plate (available ===
// false) is skipped, so we never point the shopper at a heavier plate they cannot buy.
function plateCarryingWeight(type, lb){
  if(!type || !type.plates) return null;
  let best = null;
  type.plates.map(getPlate).filter(p => p && p.available !== false).forEach(p => {
    const w = (p.weight_capacity == null || p.weight_capacity === '' || isNaN(Number(p.weight_capacity))) ? null : Number(p.weight_capacity);
    if(w == null || w < lb) return;
    const price = mcpPrice(p);
    if(!best || (price != null && (best.price == null || price < best.price))){
      best = { sku: p.sku, lb: w, price: price };
    }
  });
  return best;
}
// The sentence for a build whose weight rating is under the TV: name the build rating
// and the limiter (plate or head), then a heavier plate for this ceiling type if one
// exists (with its price), else say no plate for this ceiling carries it. Always the
// phone. Used both as build_weight_note and as the hand-back for that build.
function buildWeightNote(snap, lb, type){
  const wr = snap.weight_rating;
  const lims = (wr && wr.limited_by ? wr.limited_by : []).map(sku => {
    if(snap.plate && sku === snap.plate.sku) return sku + ' plate';
    if(snap.head && sku === snap.head.sku) return sku + ' head';
    return sku;
  });
  const limPhrase = lims.length ? lims.join(' and ') : 'this build';
  let msg = 'This build is rated for ' + wr.lb + ' lb, limited by the ' + limPhrase + '. Your ' + nice(lb) + ' lb TV needs ';
  const better = plateCarryingWeight(type, lb);
  if(better){
    msg += 'a heavier plate: ' + better.sku + ' carries ' + better.lb + ' lb'
      + (better.price != null ? ' for ' + money(better.price) : '') + '. Call ' + PHONE_TEXT + '.';
  } else {
    msg += 'more than any ceiling plate for a ' + (type ? type.id : 'this') + ' ceiling on this page carries. Call ' + PHONE_TEXT + '.';
  }
  return msg;
}
// [review follow-up 2026-09-09] Apply the whole-build weight verdict to a finished
// snapshot. When a TV weight was checked (lb) and the build rating is a real number
// UNDER it: build_weight_ok false, the head-fit weight flag drops to false, the note
// names the limiter and a heavier plate, and the hand-back becomes that note so an
// agent never relays the build as fine. When it clears: build_weight_ok true and the
// hand-back the caller already set stands. When there is no weight or no rating to
// judge: nothing is touched. Extracted from configureBuildInner so the review suite can
// exercise the wiring (the three fields plus the hand-back override) as real code.
function applyBuildWeightGate(snap, lb, type){
  if(lb == null || !snap.weight_rating || snap.weight_rating.lb == null) return;
  if(lb > Number(snap.weight_rating.lb)){
    snap.build_weight_ok = false;
    if(snap.head_fit) snap.head_fit.weight_checked = false;
    snap.build_weight_note = buildWeightNote(snap, lb, type);
    snap.handback = snap.build_weight_note;
  } else {
    snap.build_weight_ok = true;
  }
}
// Case-insensitive plate / head resolution (review C2, C7): a shopper who types
// "33he001" or "11cp006" is not a wrong SKU, and a case difference must never fall
// through to the cheapest auto pick.
function getPlateCI(sku){
  if(sku == null) return null;
  const key = String(sku).trim().toLowerCase();
  return PLATE_POOL.find(p => p.sku && p.sku.toLowerCase() === key) || null;
}
function getHeadCI(sku){
  if(sku == null) return null;
  const key = String(sku).trim().toLowerCase();
  return HEADS.find(h => h.sku && h.sku.toLowerCase() === key) || null;
}
// "11CP005 flat $39.95, 11CP006 sloped $20.95, ..." for every pooled plate, the
// list configure-build's plate_sku is chosen from (review C7 refusal text).
function plateListText(){
  return mcpPlates().map(p => p.sku + ' ' + (p.type_id || '') + ' ' + (p.price != null ? money(p.price) : 'price on request')).join(', ');
}
// The plates of ONE type: "11CP005 $39.95, 11CP001 $54.95, ..." (the mismatch
// refusal offers the plates that DO match the ceiling type).
function platesOfTypeText(type){
  if(!type || !type.plates) return '';
  return type.plates.map(getPlate).filter(Boolean)
    .sort((a, b) => mcpPriceSort(a) - mcpPriceSort(b))
    .map(p => p.sku + ' ' + (mcpPrice(p) != null ? money(mcpPrice(p)) : 'price on request')).join(', ');
}

// The plate object shape in the snapshot. It carries the FULL plate comparison
// chart row (owner word 2026-09-04), the same columns comparePlates() renders and
// from the SAME sources so the snapshot and the chart can never drift: the live
// component data for price, adds-to-length and weight rating (never PLATE_SPEC,
// never rendered text), PLATE_NOTE for "what makes it different", PLATE_SPEC for
// the four transcribed columns, cheapestPlateOf() for the pill, and PLATE_TYPES
// for the type label and the owner's conditions wording. tilt_angle is null only
// when the plate has NO tilt on file; a flat plate's real 0 is kept as 0, never
// invented and never suppressed (PLATE_SPEC carries tilt as a number, 0 included).
function mcpPlateObj(p){
  if(!p) return null;
  const t = plateTypeOfSku(p.sku);
  const spec = PLATE_SPEC[p.sku] || null;
  const cheapest = cheapestPlateOf(t);
  return {
    sku: p.sku,
    title: p.title,
    price: mcpCents(mcpPrice(p)),
    url: p.url || null,
    type_id: t ? t.id : null,
    type_label: t ? t.label : null,
    type_conditions: t ? t.conditions : null,
    adds_length_in: (p.length_offset == null || isNaN(Number(p.length_offset))) ? null : Number(p.length_offset),
    cheapest_in_type: (cheapest != null && p.sku === cheapest),
    what_makes_it_different: PLATE_NOTE[p.sku] || null,
    mounting_holes: (spec && spec.holes != null && spec.holes !== '') ? String(spec.holes) : null,
    dimensions: (spec && spec.dims != null && spec.dims !== '') ? String(spec.dims) : null,
    cable_pass_through: (spec && spec.cable != null && spec.cable !== '') ? String(spec.cable) : null,
    tilt_angle: (spec && spec.tilt != null) ? Number(spec.tilt) : null,
    weight_capacity_lb: (p.weight_capacity == null || p.weight_capacity === '' || isNaN(Number(p.weight_capacity))) ? null : Number(p.weight_capacity),
    image_url: mcpImg(p)
  };
}

// Every pooled ceiling plate as the snapshot's plate shape (mcpPlateObj, the full
// comparison-chart row), so an agent has the list configure-build's plate_sku is
// chosen from. Ordered by PLATE_TYPES order, then price ascending within a type
// (an unpriced plate sorts last). cheapest_in_type is computed inside mcpPlateObj
// (null-cheapest, so false everywhere, for a single-plate group or the noCheapest
// truss group). The groups partition the pool (self-test 8), so no plate twice.
function mcpPlates(){
  const out = [];
  PLATE_TYPES.forEach(t => {
    t.plates.map(getPlate).filter(Boolean).slice()
      .sort((a, b) => mcpPriceSort(a) - mcpPriceSort(b))
      .forEach(p => out.push(mcpPlateObj(p)));
  });
  return out;
}

// The head object shape in the snapshot. horizontal is the stamped orientation;
// screens is the human phrase, VESA and screen ranges are "lo to hi" strings in
// their own units, and every missing value is null rather than a guessed zero.
function mcpHeadObj(h){
  if(!h) return null;
  return {
    sku: h.sku,
    title: h.title,
    price: mcpCents(mcpPrice(h)),
    url: h.url || null,
    adds_length_in: (h.length_offset == null || isNaN(Number(h.length_offset))) ? null : Number(h.length_offset),
    screens: HEAD_SCREENS_TEXT[h.configuration] || null,
    rotates: (h.rotates === true) ? true : (h.rotates === false ? false : null),
    tilt_up_deg: (h.tilt_up == null || isNaN(Number(h.tilt_up))) ? null : Number(h.tilt_up),
    tilt_down_deg: (h.tilt_down == null || isNaN(Number(h.tilt_down))) ? null : Number(h.tilt_down),
    weight_capacity_lb: (h.weight_capacity == null || h.weight_capacity === '' || isNaN(Number(h.weight_capacity))) ? null : Number(h.weight_capacity),
    vesa_width_mm: mcpRange(h.vesa_width_min, h.vesa_width_max),
    vesa_height_mm: mcpRange(h.vesa_height_min, h.vesa_height_max),
    // [MCP 2026-09-04, owner word] The FILTERED VESA pattern list the head card and
    // the compare chart show (vesaPatterns(h)), written as "WxH" strings, empty when
    // none. NOT the raw min/max bounds above: a bound like 33HE001's 490 mm is a
    // hole-spacing LIMIT, not a pattern the head takes (2026-08-22 rule), so the card
    // reads this derived list and so does an agent.
    vesa_patterns: vesaPatterns(h).map(p => p[0] + 'x' + p[1]),
    // The card's own blurb (product custom.picker_blurb), null when absent.
    picker_blurb: (h.picker_blurb == null || h.picker_blurb === '') ? null : h.picker_blurb,
    screen_range_in: mcpRange(h.screen_min, h.screen_max),
    horizontal: !!h.horizontal,
    image_url: mcpImg(h)
  };
}

// The parts list for one build card: plate + head lines, then the mast pieces
// aggregated per SKU (a fixed stack can repeat one pipe), then the coupler(s).
// Priced from the component data, so it agrees with the rendered bill of
// materials by construction (same source as buildCartRecipe/aggregateItems).
function mcpParts(base, b){
  const parts = [];
  base.lines.forEach(l => {
    parts.push({sku: l.sku, title: l.title, quantity: 1, price: mcpCents(Number(l.price)), line_total: mcpCents(Number(l.price)), image_url: mcpImg(l)});
  });
  const map = new Map();
  b.pieces.forEach(p => {
    const cur = map.get(p.sku);
    if(cur) cur.quantity += 1;
    else map.set(p.sku, {sku: p.sku, title: p.title, quantity: 1, price: mcpCents(Number(p.price)), line_total: null, image_url: mcpImg(p)});
  });
  map.forEach(v => { v.line_total = mcpCents(v.price * v.quantity); parts.push(v); });
  if(b.couplers > 0 && COUPLER){
    parts.push({sku: COUPLER.sku, title: COUPLER.title, quantity: b.couplers,
      price: mcpCents(Number(COUPLER.price)), line_total: mcpCents(Number(COUPLER.price) * b.couplers), image_url: mcpImg(COUPLER)});
  }
  return parts;
}

// One build card as plain data. A fixed card carries a single finished length; an
// adjustable card carries a lo-to-hi range and the shorter/longer adjustment.
function mcpBuild(base, c, idx){
  const b = c.b;
  const fixed = !!c.isFixed;
  const parts = mcpParts(base, b);
  const surcharge = mcpCents(Number(b.surcharge || 0));
  // [review 2026-09-09, finding 4] The total is the sum of the ALREADY-ROUNDED line
  // totals plus the rounded surcharge, so it can never be a cent off from its own
  // parts (summing raw then rounding could differ from summing rounded). This equals
  // c.total by construction; deriving it makes that guarantee, not an assumption.
  const total = mcpCents(parts.reduce((a, p) => a + (p.line_total || 0), 0) + surcharge);
  return {
    index: idx,
    label: c.label,
    roles: c.roles,
    fixed: fixed,
    finished_length_in: fixed ? Number(b.finLo) : null,
    finished_length_range_in: fixed ? null : {min: Number(b.finLo), max: Number(b.finHi)},
    adjustment_in: fixed ? null : {shorter: Number(b.down), longer: Number(b.up)},
    parts: parts,
    pipes: b.pieces.length,
    couplers: b.couplers,
    surcharge: surcharge,
    total: total,
    addable: buildIsAddable(base, b),
    note: c.note || null
  };
}

// tv_placement for getBuild (SPEC-THEME-tv-placement-2026-10-12.md section 8).
function tvPlacementOut(P, r, target, td, dl){
  if(!P) return null;
  let top = !P.faceDown && P.placed && target != null && !isNaN(target) && Number(target) > 0 ? Number(target) - (td == null ? 0 : Number(td)) - P.vesaCentreFromTop : null;
  if(top != null && dl && tvFitBelow(target, top, dl)) top = null;
  const o = {
    source: P.source, placed: P.placed, has_offsets: P.hasOffsets,
    model_number: P.modelNumber, manufacturer: P.manufacturer, catalog_version: P.catalogVersion,
    tier: P.tier, tier_note: P.placed && r ? tvTierMsg(r) : null,
    height_in: P.panelHeight, width_in: P.panelWidth, depth_in: P.panelDepth, width_from: P.widthFrom, depth_from: P.depthFrom,
    hole_centre_below_top_in: P.vesaCentreFromTop, holes_below_middle_in: P.offsetFromCentre,
    top_hole_below_top_in: P.vesaFromTop, hole_spacing_in: P.vesaHeight,
    top_below_in: top, bottom_below_in: top == null ? null : top + P.panelHeight,
    below: td == null ? 'ceiling' : 'tiles'
  };
  if(P.faceDown){
    const ok = target != null && !isNaN(target) && Number(target) > 0 && !(dl && Number(target) < dl.floorQ - 1e-9);
    o.face_below_in = ok ? Number(target) - (td == null ? 0 : Number(td)) + P.panelDepth : null;
    o.top_edge_from_mast_in = P.placed ? P.vesaCentreFromTop : null;
    o.bottom_edge_from_mast_in = P.placed ? P.vesaCentreFromBottom : null;
    o.top_edge_side = P.placed ? (P.topEdge || null) : null;
  }
  return o;
}

// getBuild(): a plain-data snapshot of the whole page, inches everywhere. Never
// DOM nodes, never functions.
function getBuild(){
  const size = (state.tvSize != null && !isNaN(state.tvSize)) ? Number(state.tvSize) : null;
  const plate = state.plateSku ? getPlate(state.plateSku) : null;
  const head  = state.headSku  ? getHead(state.headSku)   : null;
  const st = stepStates();

  // Builds come from LAST_BUILD, but only when it reflects the CURRENT plate and
  // head and the state actually has a length. LAST_BUILD is left standing from a
  // prior render when the shopper backs out of a step, so an identity check on
  // the head and plate references (getHead/getPlate hand back the same objects)
  // is what keeps a stale set out of the snapshot.
  const complete = !!(plate && head && state.target != null && !isNaN(state.target));
  let builds = [];
  if(complete && LAST_BUILD && LAST_BUILD.head === head && LAST_BUILD.plate === plate && LAST_BUILD.cards){
    builds = LAST_BUILD.cards.map((c, i) => mcpBuild(LAST_BUILD.base, c, i));
  }

  // no_build_reason only speaks when the inputs are all present: an empty card
  // list then means no stock mast reaches the length, and the D6 flag means the
  // drop-ceiling rule removed every adjustable build (a fixed card may remain).
  let no_build_reason = null;
  if(complete){
    if(!builds.length) no_build_reason = 'no_pipe_fits';
    else if(LAST_BUILD && LAST_BUILD.noDownHeadroom) no_build_reason = 'no_adjustable_headroom';
  }

  let length_floor_in = null, length_floor_reason = null, needs_tile_depth = false;
  if(stepReachable('s3')){
    const dl = dropLimits();
    length_floor_in = dl.floorQ;
    length_floor_reason = dl.reason || null;
    needs_tile_depth = !!dl.needsTileDepth;
  }

  const wr = buildWeightRating(plate, head);
  const weight_rating = wr
    ? { lb: wr.weight, limited_by: wr.limiters.slice(), confident: !!wr.confident }
    : null;

  const given = state.mcpGiven || {};
  const eligible_heads = HEADS
    .filter(h => headScreenOK(h, size) && (!head || h.sku !== head.sku))
    .slice()
    .sort((a, b) => mcpPriceSort(a) - mcpPriceSort(b))
    .map(h => mcpGivenFit(mcpHeadObj(h), h, given));
  const length_in = (state.target != null && !isNaN(state.target)) ? Number(state.target) : null;
  const tvP = tvPlace();

  return {
    view: (complete && builds.length) ? 'configured' : 'incomplete',
    steps: { size: st.s1, plate: st.s2, head: st.s2b, length: st.s3 },
    tv_size_in: size,
    plate: mcpPlateObj(plate),
    head: mcpHeadObj(head),
    drop_ceiling: state.hasDropCeiling === true ? true : (state.hasDropCeiling === false ? false : null),
    tile_depth_in: tileDepth(),
    length_in: length_in,
    length_reference: (head && head.horizontal) ? 'back-of-screen' : (tvP && tvP.placed ? 'hole-center' : 'mid-screen'),
    length_floor_in: length_floor_in,
    length_floor_reason: length_floor_reason,
    // [MCP 2026-09-09, review C6] The customer sentence beside the machine code, so
    // an agent relays "The top of the TV has to stay just clear of the ceiling."
    // rather than "screenClearsPlate". The page's own heldBecause() copy, verbatim.
    length_floor_note: length_floor_reason ? heldBecause(length_floor_reason) : null,
    needs_tile_depth: needs_tile_depth,
    weight_rating: weight_rating,
    plates: mcpPlates(),
    builds: builds,
    no_build_reason: no_build_reason,
    no_build_note: mcpNoBuildNote(no_build_reason),
    eligible_heads: eligible_heads,
    share_url: shareURL(),
    length_choice: mcpLengthChoice(length_in, length_floor_in, length_floor_reason),
    heads: listHeads(size).map(o => { mcpGivenFit(o, getHead(o.sku), given); if(o.takes_given_vesa === false || o.covers_given_weight === false) o.fits = false; return o; }),
    tv_placement: tvPlacementOut(tvP, tvActive(), state.target, tileDepth(), dropLimits()),
    currency: 'USD'
  };
}
// [R3] G4 C13 a change that is only the half inch step names its own reason; units on the values.
function mcpLengthAdjusted(req, settled, dl){
  const rounded = req >= dl.floorQ - 1e-9 && settled === Math.round(req / TARGET_STEP) * TARGET_STEP;
  return { requested: req, settled: settled, reason: rounded ? 'rounded_to_half_inch' : dl.reason,
    note: rounded ? 'The page sets lengths in half inches.' : (dl.reason ? heldBecause(dl.reason) : null), unit: 'inches' };
}
// [R3] G2 flags from the VESA and weight the last configure-build was given; null when not given.
function mcpGivenFit(o, h, given){
  o.takes_given_vesa = (given && given.wh) ? headTakesVesa(h, given.wh) : null;
  o.covers_given_weight = (given && given.lb != null) ? headCoversWeight(h, given.lb) : null;
  return o;
}
// [R3] C1 G3 G5 the length used, whether it was the default, and the range an agent offers.
function mcpLengthChoice(len, floor, reason){
  if(len == null || floor == null) return null;
  const d = !!targetIsDefault, raised = d && len !== TARGET_DEFAULT;
  const rng = 'Any length from ' + nice(floor) + ' to ' + TARGET_MAX + ' inches is available';
  const fixed = [];
  FIXED.forEach(f => { const n = Number(f.nominal_length); if(!isNaN(n) && fixed.indexOf(n) < 0) fixed.push(n); });
  return { defaulted: d, default_in: TARGET_DEFAULT, used_in: len, raised_to_floor: raised,
    range_in: { min: floor, max: TARGET_MAX }, floor_note: reason ? heldBecause(reason) : null,
    stock_pipes_in: fixed.sort((a, b) => a - b),
    telescoping_in: TELE.map(t => ({ sku: t.sku, min: Number(t.length_min), max: (t.length_max == null ? null : Number(t.length_max)) }))
      .sort((a, b) => a.min - b.min),
    note: !d ? null : (raised
      ? 'No length was given. ' + TARGET_DEFAULT + ' inches is shorter than this build allows, so the page used ' + nice(len) + ' inches, the shortest it can go. ' + rng + '.'
      : 'No length was given, so the page used ' + nice(len) + ' inches. ' + rng + '; ask the shopper which they want.') };
}

// [MCP 2026-09-09, review C6] The plain sentence beside no_build_reason, in the
// page's own words. no_pipe_fits echoes the #vmscfg-nosolve alert; the adjustable
// case echoes the standing #dropNote copy. null when there is a build.
function mcpNoBuildNote(reason){
  if(reason === 'no_pipe_fits')
    return 'Your length is outside what a standard mast reaches cleanly. We can still build it. Call ' + PHONE_TEXT + ' or use the Contact us page.';
  if(reason === 'no_adjustable_headroom')
    return 'No adjustable build has enough downward travel for this length, so the fixed-length build shown is the only one we will put our name on. Call ' + PHONE_TEXT + ' and we can usually put an adjustable version together from other parts.';
  return null;
}

// listHeads(sizeIn): every pooled head as the snapshot's head shape, plus a
// `fits` flag for the given TV size. Price ascending. sizeIn null means every
// head fits (the page's own rule via headScreenOK).
function listHeads(sizeIn){
  const size = (sizeIn == null || isNaN(Number(sizeIn))) ? null : Number(sizeIn);
  return HEADS.slice()
    .sort((a, b) => mcpPriceSort(a) - mcpPriceSort(b))
    .map(h => { const o = mcpHeadObj(h); o.fits = headScreenOK(h, size); return o; });
}

// configureBuild(opts): drive the whole page from an agent's numbers, each step
// through the REAL path a shopper takes, then return getBuild() plus the
// hand-back line and the auto-selection / clamp flags.
function configureBuild(opts){
  opts = opts || {};
  // [FEATURE A/B] Drive the whole page programmatically. The flag stands the
  // interactive auto-select (FEATURE A) and the head-cleared modal (FEATURE B)
  // down for the duration: this function picks its own plate and head below, so
  // maybeAutoSelectHead would only double-render, and commitTvSize must not pop a
  // modal at an agent when firing 'change' on #tvSize clears an out-of-range head.
  // try/finally so every return (including the head-does-not-fit refusal) clears it.
  programmaticConfigure = true;
  try {
    return configureBuildInner(opts);
  } finally {
    programmaticConfigure = false;
  }
}
// [MCP 2026-09-09, review C2 / C4 / C7] Refusal objects for a named plate or head
// that cannot be used. Each is a LEAN object, not a getBuild() snapshot: the old
// code committed the size and plate, then handed back a full build for a different
// length that an agent read as the answer (C2). These leave the page untouched and
// hand back only the reason, the fitting alternatives, and the phone.
function refuseHeadUnknown(sku, size, wh, lb){
  return { refusal: 'head_unknown', head_sku: String(sku),
    message: String(sku) + ' is not a head on this page. ' + noFitTail(size, wh, lb),
    alternatives: headsFitList(size, wh, lb).map(mcpHeadObj), phone: PHONE_TEXT };
}
function refuseHeadSize(h, size, wh, lb){
  const rng = mcpRange(h.screen_min, h.screen_max);
  return { refusal: 'head_does_not_fit_size', head_sku: h.sku,
    message: h.sku + ' fits ' + (rng ? rng + ' inch screens' : 'a different screen range') + '. ' + noFitTail(size, wh, lb),
    alternatives: headsFitList(size, wh, lb).map(mcpHeadObj), phone: PHONE_TEXT };
}
function refuseHeadVesa(h, size, wh, lb){
  return { refusal: 'head_vesa_mismatch', head_sku: h.sku,
    message: h.sku + ' does not take a ' + wh[0] + 'x' + wh[1] + ' VESA pattern. ' + noFitTail(size, wh, lb),
    alternatives: headsFitList(size, wh, lb).map(mcpHeadObj), phone: PHONE_TEXT };
}
function refuseHeadWeight(h, size, wh, lb){
  const cap = (h.weight_capacity == null || h.weight_capacity === '' || isNaN(Number(h.weight_capacity))) ? null : Number(h.weight_capacity);
  return { refusal: 'head_weight_exceeded', head_sku: h.sku,
    message: h.sku + ' is rated ' + (cap != null ? cap + ' lb' : 'lighter') + ', under the ' + nice(lb) + ' lb TV. ' + noFitTail(size, wh, lb),
    alternatives: headsFitList(size, wh, lb).map(mcpHeadObj), phone: PHONE_TEXT };
}
function refusePlateUnknown(sku){
  return { refusal: 'plate_unknown', plate_sku: String(sku),
    message: String(sku) + ' is not a plate on this page. The plates you can pick are in vms-get-build\'s plates list: ' + plateListText() + '.', phone: PHONE_TEXT,
    alternatives: mcpPlates().sort((a, b) => (a.price == null ? Infinity : a.price) - (b.price == null ? Infinity : b.price)) };
}
function refusePlateMismatch(pl, type){
  const t = plateTypeOfSku(pl.sku);
  const art = (t && /^[aeiou]/i.test(t.label)) ? 'an ' : 'a ';
  return { refusal: 'plate_type_mismatch', plate_sku: pl.sku,
    message: pl.sku + ' is ' + (t ? (art + String(t.label).toLowerCase()) : 'a different plate type') + '; pass ceiling_type ' + (t ? t.id : 'to match') + ', or pick a ' + (type ? type.id : 'matching') + ' plate: ' + platesOfTypeText(type) + '.', phone: PHONE_TEXT,
    alternatives: (type && type.plates ? type.plates.map(getPlate).filter(Boolean) : []).sort((a, b) => mcpPriceSort(a) - mcpPriceSort(b)).map(mcpPlateObj) };
}
// [R4] G1 the sloped plate gate: PLATE_SPEC tilt against the ceiling angle, price ascending.
function mcpPlateTilt(sku){ const r = PLATE_SPEC[sku]; return (r && r.tilt != null) ? Number(r.tilt) : 0; }
function mcpAnglePlates(type, ang){
  return (type && type.plates ? type.plates : []).map(getPlate).filter(p => p && mcpPlateTilt(p.sku) >= ang)
    .sort((a, b) => mcpPriceSort(a) - mcpPriceSort(b));
}
function refusePlateAngle(pl, type, ang){
  const fit = mcpAnglePlates(type, ang);
  return { refusal: 'plate_does_not_fit_angle', plate_sku: pl.sku,
    message: pl.sku + ' pivots to ' + mcpPlateTilt(pl.sku) + ' degrees, not ' + nice(ang) + ' degrees. '
      + (fit.length ? 'Sloped plates that fit: ' + fit.map(p => p.sku + ' ' + (mcpPrice(p) != null ? money(mcpPrice(p)) : 'price on request')).join(', ') + '.' : 'Call ' + PHONE_TEXT + '.'),
    phone: PHONE_TEXT, alternatives: fit.map(mcpPlateObj) };
}
function mcpPlateAngleNote(type, ang, sku){
  const picked = getPlate(sku);
  const passed = (type && type.plates ? type.plates : []).map(getPlate)
    .filter(p => p && p.sku !== sku && mcpPlateTilt(p.sku) < ang && mcpPriceSort(p) < mcpPriceSort(picked))
    .sort((a, b) => mcpPriceSort(a) - mcpPriceSort(b));
  return passed.length ? passed.map(p => p.sku + ' pivots to ' + mcpPlateTilt(p.sku) + ' degrees').join(' and ') + ', so ' + sku + ' was picked for this ' + nice(ang) + ' degree ceiling.' : null;
}
function mcpAngleNote(ang){
  return 'The plate is checked against the ' + nice(ang) + ' degree ceiling, but this page sets the TV against a flat ceiling. For a TV close to a sloped ceiling, the slanted wall page, https://videomountstore.com/pages/tv-mount-for-slanted-wall, sets it against the actual slope with its own vms-configure-build (ceiling angle, TV height, VESA spacing, top hole).';
}
// [R5B] a sloped length under 18 inches or the screen floor is refused with the hand-off, before any page change.
const HANDOFF_SHORT_IN = 18;
function refuseSlopedShort(size, len, hsku, wh, lb){
  const h = hsku ? getHead(hsku) : headsFitList(size, wh, lb)[0];
  const min = Math.max(HANDOFF_SHORT_IN, dropLimits({ tvSize: size, horizontal: !!(h && h.horizontal), headTopIn: h ? h.headTopIn : null }).floorQ);
  if(len >= min) return null;
  return { refusal: 'sloped_short_handoff',
    message: 'A ' + nice(size) + ' inch TV ' + nice(len) + ' inches from a sloped ceiling belongs on the slanted wall page, https://videomountstore.com/pages/tv-mount-for-slanted-wall, which sets the TV against the actual slope: call that page\'s vms-configure-build with the ceiling angle, TV height, VESA spacing and top hole. This page builds sloped-ceiling mounts only at ' + nice(min) + ' inches or more.',
    length_in: len, handoff_threshold_in: HANDOFF_SHORT_IN,
    handoff: { url: 'https://videomountstore.com/pages/tv-mount-for-slanted-wall',
      tools: 'vms-configure-build on that page (ceiling angle, TV height, VESA spacing, top hole)',
      why: 'This length is short for a sloped ceiling. The slanted wall page sets the TV against the actual slope and can often sit it higher.' },
    phone: PHONE_TEXT };
}

// [MCP 2026-09-09, review C9] The hand-back line, state-aware. With a build it
// names the plate, head, mast pieces and total, then the VESA and weight caveat and
// the phone, then the shopper-editable guidance (mcp_handback). With no build it
// names the reason in customer words and the phone, and no false "on the page" line.
function mcpConfigHandback(snap){
  const head = snap.head, plate = snap.plate;
  const b0 = (snap.builds && snap.builds.length) ? snap.builds[0] : null;
  if(b0 && head && plate){
    let pipes = '';
    if(LAST_BUILD && LAST_BUILD.cards && LAST_BUILD.cards[0] && LAST_BUILD.cards[0].b.pieces){
      pipes = LAST_BUILD.cards[0].b.pieces.map(p => p.sku).join(' + ');
    }
    const hraw = getHead(head.sku);
    const pats = hraw ? vesaPatterns(hraw) : [];
    const maxV = pats.length ? (pats[pats.length - 1][0] + 'x' + pats[pats.length - 1][1]) : null;
    const lb = snap.weight_rating ? snap.weight_rating.lb : null;
    let s = 'Your build is on the page: ' + plate.sku + ' plate, ' + head.sku + ' head';
    if(pipes) s += ', mast ' + pipes;
    if(b0.total != null) s += ', total ' + money(b0.total);
    s += '. ' + COPY('mcp_handback') + ' Check your TV\'s VESA pattern ('
      + (maxV ? 'this head takes up to ' + maxV : 'confirm the head takes your pattern')
      + ') and weight (' + (lb != null ? 'rated ' + lb + ' lb' : 'confirm the rating')
      + '). Not sure? Call ' + PHONE_TEXT + '.';
    return s;
  }
  if(snap.no_build_reason === 'no_pipe_fits')
    return 'Your length is outside what a standard mast reaches cleanly. We can still build it. Call ' + PHONE_TEXT + '.';
  if(snap.no_build_reason === 'no_adjustable_headroom')
    return 'The only build we can stand behind for this length is the fixed-length one shown; no adjustable build has the downward travel to spare. Call ' + PHONE_TEXT + ' and we can usually put an adjustable version together.';
  if(snap.no_head_reason)
    return snap.no_head_reason;
  if(!head)
    return 'No mounting head on this page fits this TV with the details given. Call ' + PHONE_TEXT + ' and we will help you find one.';
  return 'The build is not complete yet. Call ' + PHONE_TEXT + ' if you would like a hand.';
}

function configureBuildInner(opts){
  const size = Number(opts.tv_size_in);
  const type = getPlateType(opts.ceiling_type);
  const wh = mcpParseVesa(opts.vesa_pattern);
  const lb = (opts.tv_weight_lb != null && !isNaN(Number(opts.tv_weight_lb))) ? Number(opts.tv_weight_lb) : null;
  const ang = (opts.ceiling_angle_deg != null && !isNaN(Number(opts.ceiling_angle_deg))) ? Number(opts.ceiling_angle_deg) : null;

  // 0. PRE-VALIDATION, NO PAGE CHANGE (review C2, C4, C7, C12). Nothing on the page
  //    is touched until every named plate and head is known good, so a refusal
  //    leaves the size, plate, head and length exactly as the shopper left them.
  if(opts.plate_sku != null){
    const pl = getPlateCI(opts.plate_sku);
    if(!pl) return refusePlateUnknown(opts.plate_sku);
    if(type && type.plates.indexOf(pl.sku) < 0) return refusePlateMismatch(pl, type);
    if(ang != null && type && type.id === 'sloped' && mcpPlateTilt(pl.sku) < ang) return refusePlateAngle(pl, type, ang);
    opts.plate_sku = pl.sku;   // normalize case so selectPlate matches
  }
  if(opts.head_sku != null){
    const h = getHeadCI(opts.head_sku);
    if(!h) return refuseHeadUnknown(opts.head_sku, size, wh, lb);
    if(!headScreenOK(h, size)) return refuseHeadSize(h, size, wh, lb);
    if(wh && !headTakesVesa(h, wh)) return refuseHeadVesa(h, size, wh, lb);
    if(lb != null && !headCoversWeight(h, lb)) return refuseHeadWeight(h, size, wh, lb);
    opts.head_sku = h.sku;     // normalize case so selectHead matches
  }
  if(opts.tile_depth_in != null && opts.drop_ceiling !== true){
    return { refusal: 'tile_depth_without_drop_ceiling',
      message: 'A tile depth only applies under a drop ceiling. Pass drop_ceiling true with the tile depth, or leave the tile depth out.',
      phone: PHONE_TEXT };
  }
  if(ang != null && (!type || type.id !== 'sloped')){
    return { refusal: 'ceiling_angle_without_sloped',
      message: 'A ceiling angle only applies to ceiling_type sloped. Pass sloped with the angle, or leave the angle out.',
      phone: PHONE_TEXT };
  }
  if(type && type.id === 'sloped' && opts.length_in != null){
    const rs = refuseSlopedShort(size, Number(opts.length_in), opts.head_sku, wh, lb);
    if(rs) return rs;
  }

  // 1. TV size: inject the option if the list lacks it, then write the select and
  //    fire input + change, which is exactly what the five preset buttons do.
  ensureSizeOption(size);
  const sel = el('tvSize');
  if(sel){
    sel.value = String(size);
    sel.dispatchEvent(new Event('input', {bubbles: true}));
    sel.dispatchEvent(new Event('change', {bubbles: true}));
  }

  // 2. Plate: a named SKU wins (already validated above); otherwise the cheapest
  //    plate of the type, or the type's first plate when there is nothing to be
  //    cheapest among. selectPlate sets the type itself and clears the length.
  let plate_auto_selected = false;
  if(opts.plate_sku){
    selectPlate(opts.plate_sku);
  } else {
    plate_auto_selected = true;
    const fitA = (ang != null) ? mcpAnglePlates(type, ang) : [];
    const sku = type ? ((fitA[0] && fitA[0].sku) || cheapestPlateOf(type) || (type.plates && type.plates[0])) : null;
    if(sku) selectPlate(sku);
  }

  // 3. Head: a named SKU (already validated to fit size, VESA and weight above)
  //    wins; an omitted head picks the cheapest head that fits the size and, when
  //    given, the VESA pattern and the weight (review C3, C4).
  let head_auto_selected = false;
  let auto_head_none = false;
  if(opts.head_sku){
    selectHead(opts.head_sku);
  } else {
    const fit = headsFitList(size, wh, lb);
    if(fit.length){
      head_auto_selected = true;
      selectHead(fit[0].sku);
    } else {
      // [review 2026-09-09, finding 1] No head fits the size with the VESA / weight
      // filters. selectHead was never called, so a head a PRIOR call or a ?head= URL
      // preselect put on the page would survive and be reported as auto-selected. Clear
      // it exactly as commitTvSize's clearedHead path does, so the snapshot reports
      // head null with a plain reason, never a survivor the shopper never chose.
      auto_head_none = true;
      state.headSku = null; state.headAuto = false; state.target = null; state.targetWanted = null;
      const te = el('target'); if(te) te.value = '';
      stepManual = {};
      applyStepStates();
    }
  }

  // 4. Drop ceiling, FULL RESET each call (review C1). Every call describes the
  //    whole build, so an omitted drop_ceiling means NO drop ceiling: fire "no",
  //    which clears any tile depth a prior call left on the page. Fire "no" first
  //    even when a drop ceiling is wanted, so a stale depth is cleared before "yes"
  //    seeds the default fresh; then "yes" seeds the 12 in default, and an explicit
  //    tile_depth_in overrides it.
  let tile_depth_defaulted = false;
  const noRadio = document.querySelector('input[name="vmscfg-dropceiling"][value="no"]');
  if(noRadio){ noRadio.checked = true; noRadio.dispatchEvent(new Event('change', {bubbles: true})); }
  if(opts.drop_ceiling === true){
    const yesRadio = document.querySelector('input[name="vmscfg-dropceiling"][value="yes"]');
    if(yesRadio){ yesRadio.checked = true; yesRadio.dispatchEvent(new Event('change', {bubbles: true})); }
    if(opts.tile_depth_in != null){
      const t = el('tiles');
      if(t){ t.value = String(opts.tile_depth_in); t.dispatchEvent(new Event('change', {bubbles: true})); }
    } else {
      // The page seeds a sensible 12 in default on "yes" (seedTileDepth); use it and
      // say so, rather than refuse for a number the shopper may not know (review C1).
      tile_depth_defaulted = true;
    }
  }

  // 5. Length, RESET to the page default when omitted (review C1). selectPlate above
  //    already cleared the length; firing change on #target commits a given number
  //    (clamped to the floor, quantised to 0.5), and an omitted length re-seeds the
  //    fresh default (30 or the floor) exactly as a fresh page open would show.
  const requested = (opts.length_in != null) ? Number(opts.length_in) : null;
  if(requested != null){
    const t = el('target');
    if(t){ t.value = String(requested); t.dispatchEvent(new Event('change', {bubbles: true})); }
  } else {
    seedDefaultTarget();
  }

  // 6. The snapshot, plus the state-aware hand-back line and the flags.
  state.mcpGiven = { wh: wh, lb: lb };
  const snap = getBuild();
  if(plate_auto_selected) snap.plate_auto_selected = true;
  // head_auto_selected only when a head was actually auto-picked and is on the page.
  if(head_auto_selected && snap.head) snap.head_auto_selected = true;
  // [review 2026-09-09, finding 1] When the auto pick found nothing that fits the size,
  // VESA pattern and weight, name why in customer words so the agent does not read a
  // survivor head as the answer.
  if(auto_head_none && !snap.head) snap.no_head_reason = noHeadReason(size, wh, lb);
  // An omitted head_sku always re-picks (or clears), overriding any ?head= URL
  // preselect, so the URL never decided the head: head_from_url is false. It would be
  // true only if a URL-preselected head were the head on the page after the call, which
  // the auto pick never leaves standing (reviewer B's URL-preselect question).
  if(opts.head_sku == null) snap.head_from_url = false;
  if(wh || lb != null){
    // A check flag is true only when the head ON THE PAGE actually passed it: a failed
    // check (or no head) reports false, never a stale true (review finding 1).
    const hraw = snap.head ? getHead(snap.head.sku) : null;
    snap.head_fit = {
      vesa_checked: !!(wh && hraw && headTakesVesa(hraw, wh)),
      weight_checked: !!(lb != null && hraw && headCoversWeight(hraw, lb))
    };
  }
  // [review follow-up 2026-09-09] The weight the customer actually gets is the BUILD's
  // rating (weight_rating.lb, the LOWER of plate and head), not just the head's. The
  // head-level filter picked a head that covers the TV, but the cheapest plate of the
  // type may cap it lower. Resolve the ceiling type from the plate when ceiling_type was
  // omitted with a named plate, so the note can still name a heavier plate of the type.
  // The default hand-back is built FIRST (it reads no_head_reason and the rest of the
  // finished snapshot), then applyBuildWeightGate overrides it for an under-rated build.
  const wtype = type || (snap.plate ? plateTypeOfSku(snap.plate.sku) : null);
  snap.handback = mcpConfigHandback(snap);
  applyBuildWeightGate(snap, lb, wtype);
  // Remember the TV weight the shopper had us check, so add-to-cart can refuse an
  // under-rated build (even after a manual plate change); a configure-build with no
  // tv_weight_lb clears it, the full-reset rule (review follow-up 2026-09-09).
  state.mcpCheckedWeight = (lb != null ? lb : null);
  if(tile_depth_defaulted) snap.tile_depth_defaulted = true;
  if(requested != null && state.target != null && !isNaN(state.target) && Number(state.target) !== requested){
    snap.length_adjusted = mcpLengthAdjusted(requested, Number(state.target), dropLimits());
  }
  if(ang != null){
    snap.ceiling_angle_deg = ang;
    const pn = (plate_auto_selected && snap.plate) ? mcpPlateAngleNote(type, ang, snap.plate.sku) : null;
    if(pn) snap.plate_note = pn;
    snap.angle_note = mcpAngleNote(ang);
  }
  return snap;
}

// addToCart(buildIndex): add one build card to the cart through the same recipe
// and payload the per-card button uses. It NEVER navigates (the button click
// does; an agent has to keep working), so it resolves a result object instead.
// buildIndex defaults to 0, the recommended (top-ranked) card. Always resolves,
// never rejects: a guard or a thrown fetch comes back as { ok:false, reason }.
function addToCart(buildIndex){
  const idx = (buildIndex == null) ? 0 : Number(buildIndex);
  if(!LAST_BUILD || !LAST_BUILD.cards || !LAST_BUILD.cards.length){
    return Promise.resolve({ ok: false, reason: 'The build is not complete yet. Pick a TV size, a ceiling plate, a mounting head and a length first.' });
  }
  if(isNaN(idx) || idx < 0 || idx >= LAST_BUILD.cards.length){
    return Promise.resolve({ ok: false, reason: 'That build number does not exist. There are ' + LAST_BUILD.cards.length + ' build(s) to choose from, numbered from 0.' });
  }
  const {head, plate, target, cards, base} = LAST_BUILD;
  const card = cards[idx];
  const recipe = buildCartRecipe(base, card.b);
  if(recipe.blocked){
    // [PAGE TOOLS 2026-09-11] Relay the reserve gate's product-naming message when it fired;
    // otherwise the generic unavailable reason. Nothing is added either way (no /cart/add below).
    return Promise.resolve({ ok: false, reason: (recipe.reason && recipe.reason !== 'unavailable') ? recipe.reason : 'One or more parts in this build are unavailable right now. Call 800-585-8795 or contact us and we will sort it out.' });
  }
  // [review follow-up 2026-09-09] Weight gate. When the shopper had us check a TV weight
  // (state.mcpCheckedWeight, set by the last configure-build and cleared by a
  // configure-build with no tv_weight_lb) and THIS build's rating is a real number below
  // it, refuse rather than add an under-rated build with no signal. The rating is
  // recomputed from the plate and head on the page now, so a manual plate change since
  // the check is honored. A null rating (no value on file) cannot be judged, so it does
  // not block. The refusal carries the same note the snapshot shows plus how to fix it.
  const checkedW = (state.mcpCheckedWeight != null && !isNaN(Number(state.mcpCheckedWeight))) ? Number(state.mcpCheckedWeight) : null;
  let buildRatingLb = null;
  if(checkedW != null){
    const wr = buildWeightRating(plate, head);
    buildRatingLb = (wr && wr.weight != null && !isNaN(Number(wr.weight))) ? Number(wr.weight) : null;
    if(buildRatingLb != null && checkedW > buildRatingLb){
      const noteSnap = { plate: {sku: plate.sku}, head: {sku: head.sku},
        weight_rating: { lb: buildRatingLb, limited_by: (wr.limiters ? wr.limiters.slice() : []) } };
      const note = buildWeightNote(noteSnap, checkedW, plateTypeOfSku(plate.sku));
      return Promise.resolve({ ok: false, build_weight_ok: false, build_weight_note: note,
        reason: note + ' Pick a heavier plate with plate_sku or confirm the TV weight.' });
    }
  }
  const config_id = newConfigId();
  const summary = configSummary(head, plate, card.b);
  // Same visible "Build" value the button path adds, so an agent-placed build in
  // the cart/order reads the same as a shopper-placed one. Additive; the three
  // _vms_* properties are unchanged.
  const buildText = buildProperty(config_id, head, plate, card.b, tvSizeNum(), tileDepth());
  // [MCP 2026-09-09, review C8] An id-keyed lookup so each added line can carry its
  // SKU, title and price, not just the variant id and quantity. Sourced from the same
  // plate/head lines, mast pieces and coupler the recipe was built from.
  const partInfo = new Map();
  base.lines.forEach(l => partInfo.set(l.id, {sku: l.sku, title: l.title, price: mcpCents(Number(l.price)), image_url: mcpImg(l)}));
  card.b.pieces.forEach(p => partInfo.set(p.id, {sku: p.sku, title: p.title, price: mcpCents(Number(p.price)), image_url: mcpImg(p)}));
  if(card.b.couplers > 0 && COUPLER) partInfo.set(COUPLER.id, {sku: COUPLER.sku, title: COUPLER.title, price: mcpCents(Number(COUPLER.price)), image_url: mcpImg(COUPLER)});
  // The finished-length text the Build property carries (adjustable range or the
  // single fixed length), so an agent quotes the same number as the cart line.
  const finLen = card.b.tele ? (nice(card.b.finLo) + ' to ' + nice(card.b.finHi) + ' in') : (nice(card.b.finLo) + ' in');
  // The client-side build total, kept for comparison against the server (review 2).
  const buildTotal = mcpCents(Number(card.total));
  return Promise.resolve(postCartAdd(recipe.items, {_vms_config_id: config_id, _vms_config_summary: summary, Build: buildText}))
    .then(() => {
      try { trackAddBuild(head, plate, target, card); } catch(e){}
      // [review 2026-09-09, finding 2] Read the SERVER's prices and quantities, not the
      // request and the client's cached prices, and reconcile. The source is /cart.js
      // (not the /cart/add.js response, whose price format is inconsistent on this store
      // - price can come back as a dollar STRING with no final_line_price): /cart.js
      // always returns integer CENTS (final_price, final_line_price). We read back only
      // the lines carrying THIS build's config_id, match them to the request by variant
      // id, and list any requested variant the cart did not return in not_added[].
      return Promise.resolve(fetch('/cart.js', {headers: {'Accept': 'application/json'}}))
        .then(r => r.json())
        .then(cart => {
          const lines = (cart && Array.isArray(cart.items))
            ? cart.items.filter(it => it.properties && it.properties._vms_config_id === config_id) : [];
          // Keyed by variant id. A build's recipe names each variant id at most once, so
          // no two of THIS config's lines share an id and the set-overwrite here can never
          // hide a distinct line (it would only matter if one recipe added a variant twice).
          const byId = new Map();
          lines.forEach(l => byId.set(String(l.id), l));
          const added = [];
          const not_added = [];
          recipe.items.forEach(reqIt => {
            const l = byId.get(String(reqIt.id));
            const m = partInfo.get(reqIt.id) || {};
            if(l){
              const unit = (l.final_price != null ? l.final_price : l.price);
              const lineC = (l.final_line_price != null ? l.final_line_price : l.line_price);
              added.push({
                sku: (l.sku != null && l.sku !== '') ? l.sku : (m.sku != null ? m.sku : null),
                title: (l.title != null && l.title !== '') ? l.title : (m.title != null ? m.title : null),
                quantity: l.quantity,
                price: (unit != null && !isNaN(Number(unit))) ? mcpCents(Number(unit) / 100) : (m.price != null ? m.price : null),
                line_total: (lineC != null && !isNaN(Number(lineC))) ? mcpCents(Number(lineC) / 100) : null,
                variant_id: reqIt.id,
                image_url: (m.image_url != null ? m.image_url : null)
              });
            } else {
              not_added.push({ sku: (m.sku != null ? m.sku : null), variant_id: reqIt.id, quantity: reqIt.quantity });
            }
          });
          const serverTotal = mcpCents(added.reduce((a, x) => a + (x.line_total || 0), 0));
          return { ok: true, added: added, not_added: not_added,
            total: serverTotal, build_total: buildTotal,
            finished_length: finLen,
            // [review follow-up 2026-09-09] Echo the weight verdict for the build that
            // was actually added: true when a TV weight was checked and this build's
            // rating clears it, null when no weight was checked or the rating is unknown.
            // A failing check never reaches here (it refused above), so the note is null.
            build_weight_ok: (checkedW != null && buildRatingLb != null) ? true : null,
            build_weight_note: null,
            drop_ceiling: { enabled: state.hasDropCeiling === true, tile_depth_in: tileDepth() },
            build_property: buildText, config_id: config_id, summary: summary,
            // [review addition 2026-09-09] The way out, so an agent that added a wrong
            // build can undo it in one call.
            remove_hint: 'To take this build back out, call vms-remove-build with config_id ' + config_id + '.',
            cart_url: (window.routes && window.routes.cart_url) || '/cart',
            handback: 'Added to the cart. To take this build back out, call vms-remove-build with config_id ' + config_id + '.',
            length_reference: 'ceiling structure straight down to the ' + (head && head.horizontal ? 'back of the screen' : ((tvPlace() || {}).placed ? 'center of the TV\'s mounting holes' : 'middle of the screen')),
            share_url: shareURL(),
            currency: 'USD' };
        });
    })
    .catch(err => ({ ok: false, reason: 'This build could not be added to the cart right now. Call ' + PHONE_TEXT + '.', detail: (err && err.message) ? err.message : String(err) }));
}

// [review addition 2026-09-09] A summary of the cart AFTER a remove: item count,
// total in dollars, and the config ids of the builds still in it. Reads /cart.js or
// /cart/update.js response shape (item_count, total_price in cents, items[].properties).
function cartAfterSummary(cart){
  const items = (cart && Array.isArray(cart.items)) ? cart.items : [];
  const ids = [];
  items.forEach(it => {
    const cid = it.properties && it.properties._vms_config_id;
    if(cid && ids.indexOf(cid) < 0) ids.push(cid);
  });
  return {
    item_count: cart ? cart.item_count : 0,
    total_price: mcpCents(Number((cart && cart.total_price) || 0) / 100),
    builds_remaining: ids
  };
}

// removeBuild(configId): take a build back out of the cart. Same shape as addToCart
// (a fetch chain that resolves a result object, throws on a failed POST). With a
// config_id it removes every line carrying that _vms_config_id; with none, every line
// carrying ANY _vms_config_id (every build added from the two build pages). It NEVER
// touches a line without our property, so the shopper's other items stay; Shopify's
// native update_cart / cancel_cart tools cover those. One /cart/update.js call removes
// all matched lines at once.
function removeBuild(configId){
  const wantId = (configId == null) ? null : String(configId);
  const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
  return Promise.resolve(fetch('/cart.js', {headers: {'Accept': 'application/json'}}))
    .then(r => r.json())
    .then(cart => {
      const items = (cart && Array.isArray(cart.items)) ? cart.items : [];
      const matched = items.filter(it => {
        const cid = it.properties && it.properties._vms_config_id;
        if(!cid) return false;
        return wantId == null ? true : String(cid) === wantId;
      });
      if(!matched.length){
        return { ok: true, config_id: wantId, removed: [], removed_total: 0,
          cart_after: cartAfterSummary(cart), cart_url: cartUrl,
          handback: 'No build lines are in the cart.' };
      }
      const updates = {};
      matched.forEach(it => { updates[it.key] = 0; });
      const url = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
      return Promise.resolve(fetch(url, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
        body: JSON.stringify({updates: updates})
      }))
        .then(res => { if(!res.ok) throw new Error('Cart update failed (status ' + res.status + ').'); return res.json(); })
        // [review addition 2026-09-09] Re-read /cart.js after the update instead of
        // trusting the /cart/update.js response, so a concurrent cart change (another
        // tab, the shopper, an app) is visible. Any targeted line KEY still present in
        // the fresh cart did not come out; report it as not_removed[] with a sentence,
        // and count only the keys that actually went away as removed. The re-read also
        // feeds cart_after, so the summary is the true post-update state.
        .then(() => Promise.resolve(fetch('/cart.js', {headers: {'Accept': 'application/json'}})))
        .then(r2 => r2.json())
        .then(fresh => {
          const stillKeys = {};
          ((fresh && Array.isArray(fresh.items)) ? fresh.items : []).forEach(it => { stillKeys[it.key] = it; });
          const removed = [];
          const not_removed = [];
          matched.forEach(it => {
            const survivor = stillKeys[it.key];
            if(survivor){
              not_removed.push({
                key: it.key,
                sku: survivor.sku || null,
                title: survivor.title || survivor.product_title || null,
                quantity: survivor.quantity,
                config_id: (survivor.properties && survivor.properties._vms_config_id) || null
              });
            } else {
              removed.push({
                sku: it.sku || null,
                title: it.title || it.product_title || null,
                quantity: it.quantity,
                variant_id: it.id,
                line_price: mcpCents(Number(it.final_line_price != null ? it.final_line_price : it.line_price) / 100),
                config_id: (it.properties && it.properties._vms_config_id) || null
              });
            }
          });
          const removedTotal = mcpCents(removed.reduce((a, x) => a + (x.line_price || 0), 0));
          const summary = cartAfterSummary(fresh);
          const tail = summary.item_count > 0 ? (summary.item_count + ' item(s) remain.') : 'The cart is now empty.';
          let handback = (removed.length === 1 ? 'Removed 1 build line' : 'Removed ' + removed.length + ' build lines')
            + ' from the cart, ' + money(removedTotal) + '. ' + tail;
          if(not_removed.length){
            handback += ' ' + (not_removed.length === 1 ? '1 build line is' : not_removed.length + ' build lines are')
              + ' still in the cart after the update, which usually means the cart changed while we worked. Call ' + PHONE_TEXT + ' or check the cart.';
          }
          const result = { ok: true, config_id: wantId, removed: removed, removed_total: removedTotal,
            cart_after: summary, cart_url: cartUrl, handback: handback };
          if(not_removed.length) result.not_removed = not_removed;
          return result;
        });
    })
    .catch(err => ({ ok: false, reason: 'This build could not be removed from the cart right now. Call ' + PHONE_TEXT + '.', detail: (err && err.message) ? err.message : String(err) }));
}

window.VMSConfiguratorPage = { getBuild, configureBuild, addToCart, removeBuild, listHeads };
})();
